import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  analyzeContract,
  getWallet,
  getWalletTransactions,
  verifyBankAccount,
  initiateWithdraw,
  confirmWithdraw,
  transferToUser,
  getTopupReference,
  initializeWalletTopup,
  confirmWalletTopup,
  getDeals,
  getMe,
  searchUsers,
  createDeal,
  initializeDealPayment,
  confirmDealPayment,
  initializeDealCheckout,
  confirmDealCheckout,
  checkDealInstantTransfer,
  getDeal,
  getDealTermQueries,
  postDealTermQuery,
  respondDealTermQuery,
  acceptDeal,
  declineDeal,
  cancelDeal,
  claimMilestone,
  approveMilestone,
  partialReleaseMilestone,
  rejectMilestone,
  raiseDeal,
  releaseDespiteDispute,
  uploadDisputeAttachments,
  appendDisputeEvidence,
  getScopeMessages,
  postScopeMessage,
  respondAmountProposal,
  requestOTP,
  verifyOTP,
  signin,
  signup,
  recoverPin,
  initiateSafeHavenIdentity,
  validateSafeHavenIdentity,
  formatSafeHavenInitError,
  checkTagAvailability,
  checkSignupAvailability,
  signout,
  getToken,
  updateProfile,
  getProfileDisputes,
  updateNotificationPreferences,
  getSessions,
  revokeSession,
  freezeAccount,
  getCloseAccountEligibility,
  closeAccount,
  changePin,
  getFrequentContacts,
  listEvents,
  getDealAnalytics,
  getLinkedBanks,
  linkBankAccount,
  unlinkBankAccount,
  sendInvitation,
  resolveInvitationLink,
  acceptInvitationLink,
} from "./api/client";
import { useAppConfig, bankApiCode } from "./hooks/useAppConfig";
import { useNotifications } from "./hooks/useNotifications";
import { registerPushToken, onForegroundPush } from "./services/push";
import { openPaystackCheckout } from "./services/paystack";
import { openSafeHavenCheckout } from "./services/safehavenCheckout";
import { formatNairaFromKobo, formatNairaCompactFromKobo, koboToNaira, resolveWalletAccountNumber, resolveWalletBankName, isVirtualAccountPending } from "./utils/money";
import { buildArbitrationTimeline } from "./utils/arbitrationTimeline";
import {
  resolveDealTerms,
  deriveTermsFromContractText,
  milestoneReceiveRows,
  parseDealMilestonesList,
  deriveMilestoneUi,
} from "./utils/contractTerms";
import WalletTransactionList from "./components/WalletTransactionList";
import EventsScreen from "./components/EventsScreen";
import UpcomingEventsStrip from "./components/UpcomingEventsStrip";
import { useColors, useTheme } from "./context/ThemeContext";
import { ToastProvider, useToast } from "./context/ToastContext";
import {
  CUSTOMER_TYPE_OPTIONS,
  GENDER_OPTIONS,
  formatAddressLines,
  formatDobLabel,
} from "./constants/profileEnums";

/** Match backend `sanitisePhone` for API calls. */
function formatPhoneForApi(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.startsWith("234")) return `+${d}`;
  if (d.startsWith("0")) {
    const national = d.slice(1);
    return national ? `+234${national}` : "";
  }
  if (d.length === 10) return `+234${d}`;
  return d ? `+${d}` : "";
}

/** Digits only; allow optional leading 0 (0XXXXXXXXXX = 11 digits). */
function sanitizeNgLocalPhoneInput(raw) {
  return String(raw || "").replace(/\D/g, "").slice(0, 11);
}

/** True when local input is a full NG mobile (10 digits, or 11 with leading 0). */
function isCompleteNgPhone(digits) {
  return /^\+234\d{10}$/.test(formatPhoneForApi(digits));
}

function displayPhoneLocal(digits) {
  const api = formatPhoneForApi(digits).replace(/\D/g, "");
  const national = api.startsWith("234") ? api.slice(3) : api.slice(-10);
  if (national.length >= 10) {
    const d = national.slice(-10);
    return `+234 ${d.slice(0, 3)} ··· ${d.slice(-4)}`;
  }
  return national ? `+234${national}` : "";
}

/** PendPay phone verification codes are always 6 digits (must match backend OTP_LENGTH). */
const OTP_CODE_LENGTH = 6;
const emptyOtpDigits = () => Array.from({ length: OTP_CODE_LENGTH }, () => "");

function otpChannelsFromResponse(data) {
  if (Array.isArray(data?.channels) && data.channels.length) return data.channels;
  return String(data?.channel || "")
    .split("+")
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Signup verification is the email. A combined email+sms channel must not show the phone. */
function describeOtpDelivery({ purpose, channels, email, destination, phoneDisplay }) {
  const list = Array.isArray(channels) ? channels : [];
  const emailed = list.includes("email");
  const texted = list.includes("sms");
  const typedEmail = String(email || "").trim();
  const maskedEmail = String(destination || "")
    .split(" and ")
    .map((part) => part.trim())
    .find((part) => part.includes("@"));
  const emailLabel = (typedEmail.includes("@") ? typedEmail : "") || maskedEmail || "your email";

  if (purpose === "signup") {
    if (list.length && texted && !emailed) {
      return {
        title: "Verify your phone",
        lead: "Enter the PendPay code sent to",
        hint: phoneDisplay,
      };
    }
    return {
      title: "Verify your email",
      lead: "Enter the PendPay code sent to",
      hint: emailLabel,
    };
  }

  if (emailed && texted) return { hint: `${emailLabel} and ${phoneDisplay}` };
  if (emailed) return { hint: emailLabel };
  return { hint: phoneDisplay };
}

const MOBILE_MAX_WIDTH = 430;
/** Keeps fixed/full-screen UI in the centered mobile column on desktop. */
const mobileShell = { maxWidth: MOBILE_MAX_WIDTH, margin: "0 auto", left: 0, right: 0 };
const AUTO_RELEASE_HOURS = 48;

function HorizontalScroll({ children, style, innerStyle }) {
  const C = useColors();
  const ref = useRef(null);
  const [track, setTrack] = useState({ show: false, thumbWidth: 100, thumbLeft: 0 });

  const updateTrack = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflow = scrollWidth - clientWidth;
    if (overflow <= 2) {
      setTrack({ show: false, thumbWidth: 100, thumbLeft: 0 });
      return;
    }
    const thumbWidth = Math.max((clientWidth / scrollWidth) * 100, 20);
    const thumbLeft = (scrollLeft / overflow) * (100 - thumbWidth);
    setTrack({ show: true, thumbWidth, thumbLeft });
  }, []);

  useEffect(() => {
    updateTrack();
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(updateTrack);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, updateTrack]);

  return (
    <div style={style}>
      <div
        ref={ref}
        onScroll={updateTrack}
        onWheel={(e) => {
          const el = ref.current;
          if (!el || el.scrollWidth <= el.clientWidth + 2) return;
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }}
        className="pp-scroll-x"
        style={{
          display: "flex",
          paddingBottom: 6,
          ...innerStyle,
        }}
      >
        {children}
      </div>
      {track.show && (
        <div
          aria-hidden
          style={{
            height: 4,
            marginTop: 4,
            borderRadius: 2,
            background: C.border,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${track.thumbLeft}%`,
              width: `${track.thumbWidth}%`,
              borderRadius: 2,
              background: C.lime,
              opacity: 0.85,
              transition: "left 0.05s linear",
            }}
          />
        </div>
      )}
    </div>
  );
}

const sv = (d, size = 18, color = "#2EC4A0", sw = 1.5) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d={d} />
  </svg>
);

const IC = {
  home: "M3 12L5 10M5 10L12 3L19 10M5 10V20A1 1 0 006 21H9M19 10L21 12M19 10V20A1 1 0 0118 21H15M9 21V15H15V21M9 21H15",
  send: "M12 19V5M5 12L12 5L19 12",
  list: "M9 5H7A2 2 0 005 7V19A2 2 0 007 21H17A2 2 0 0019 19V7A2 2 0 0017 5H15M9 5A2 2 0 0011 3H13A2 2 0 0115 5M9 5H15",
  wallet: "M21 12V7A2 2 0 0019 5H5A2 2 0 003 7V17A2 2 0 005 19H19A2 2 0 0021 17V12ZM21 12H17A2 2 0 0015 14A2 2 0 0017 16H21",
  user: "M20 21V19A4 4 0 0016 15H8A4 4 0 004 19V21M12 11A4 4 0 1012 3A4 4 0 0112 11Z",
  bell: "M18 8A6 6 0 006 8C6 15 3 17 3 17H21C21 17 18 15 18 8M13.73 21A2 2 0 018.27 21",
  lock: "M19 11H5A2 2 0 003 13V20A2 2 0 005 22H19A2 2 0 0021 20V13A2 2 0 0019 11ZM7 11V7A5 5 0 0117 7V11",
  shield: "M12 22S8 18 8 12V5L12 3L16 5V12C16 18 12 22 12 22Z",
  check: "M20 6L9 17L4 12",
  chevron_r: "M9 18L15 12L9 6",
  chevron_l: "M15 18L9 12L15 6",
  up_right: "M7 17L17 7M17 7H7M17 7V17",
  down_left: "M17 7L7 17M7 17H17M7 17V7",
  hub: "M12 9A3 3 0 1012 15A3 3 0 0012 9ZM12 3V6M12 18V21M3 12H6M18 12H21M5.64 5.64L7.76 7.76M16.24 16.24L18.36 18.36M5.64 18.36L7.76 16.24M16.24 7.76L18.36 5.64",
  plus: "M12 5V19M5 12H19",
  bank: "M3 21H21M3 10H21M5 6L12 3L19 6M4 10V21M20 10V21M8 10V21M12 10V21M16 10V21",
  del: "M21 4H8L1 12L8 20H21A2 2 0 0023 18V6A2 2 0 0021 4ZM18 9L12 15M12 9L18 15",
  warn: "M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 003.54 21H20.46A2 2 0 0022.18 18L13.71 3.86A2 2 0 0010.29 3.86Z",
  refresh: "M23 4V10H17M1 20V14H7M3.51 9A9 9 0 0120.5 15M20.49 15A9 9 0 013.5 9",
  enter: "M5 12H19M13 6L19 12L13 18",
  calendar: "M8 2V6M16 2V6M3 10H21M5 4H19A2 2 0 0121 6V20A2 2 0 0119 22H5A2 2 0 013 20V6A2 2 0 015 4Z",
};

function getTierMeta(tiers, tierId, colors) {
  const C = colors;
  const id = Math.min(Math.max(Number(tierId) || 1, 1), 3);
  return tiers.find((x) => x.id === id) || tiers[0] || {
    id,
    name: `Tier ${id}`,
    color: C.muted,
    maxDeal: 0,
    maxBalance: 0,
    maxDaily: 0,
  };
}

function WavyCard({ children, style = {}, cornerAction = null }) {
  const C = useColors();
  return (
    <div style={{
      position: "relative",
      background: C.forestMid,
      borderRadius: 28,
      padding: "28px 24px 36px",
      overflow: "hidden",
      ...style,
    }}>
      {cornerAction ? (
        <div style={{ position: "absolute", top: 16, right: 16, zIndex: 3 }}>
          {cornerAction}
        </div>
      ) : null}
      <svg viewBox="0 0 390 80" style={{
        position: "absolute", bottom: 0, left: 0, width: "100%", height: 80,
        display: "block",
      }} preserveAspectRatio="none">
        <path d="M0,40 C60,10 120,70 200,40 C280,10 340,60 390,30 L390,80 L0,80 Z"
          fill={C.forestBright} />
        <path d="M0,55 C80,25 160,75 240,50 C300,30 360,65 390,45 L390,80 L0,80 Z"
          fill={C.forest} opacity="0.8" />
      </svg>
      <div style={{ position: "relative", zIndex: 2 }}>{children}</div>
    </div>
  );
}

function PillNav({ screen, setScreen }) {
  const C = useColors();
  const EVENTS_TAB_ENABLED = true;
  const tabs = [
    { id: "home", icon: IC.home },
    { id: "send", icon: IC.send },
    { id: "deals", icon: IC.list },
    ...(EVENTS_TAB_ENABLED ? [{ id: "events", icon: IC.calendar }] : []),
    { id: "wallet", icon: IC.wallet },
    { id: "profile", icon: IC.user },
  ];
  return (
    <div style={{
      display: "flex", justifyContent: "center",
      padding: "12px 16px 28px",
      background: C.surface,
      borderTop: `1px solid ${C.border}`,
    }}>
      <div style={{
        display: "flex", alignItems: "center",
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 50,
        padding: "6px 8px",
        gap: 4,
      }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setScreen(t.id)} style={{
            width: 44, height: 44, borderRadius: 50, border: "none", cursor: "pointer",
            background: screen === t.id ? C.lime : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s",
          }}>
            {sv(t.icon, 18, screen === t.id ? C.bg : C.muted, screen === t.id ? 2 : 1.5)}
          </button>
        ))}
      </div>
    </div>
  );
}

const FAVICON_URL = "data:image/png;base64,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";
const LOGO_URL = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiAgem9vbUFuZFBhbj0ibWFnbmlmeSIgdmlld0JveD0iMCA0MjAgMTUwMCA2NjAiICBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJ4TWlkWU1pZCBtZWV0IiB2ZXJzaW9uPSIxLjAiPjxkZWZzPjxmaWx0ZXIgeD0iMCUiIHk9IjAlIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBpZD0iZTNkZjA5ZmQ3MyI+PGZlQ29sb3JNYXRyaXggdmFsdWVzPSIwIDAgMCAwIDEgMCAwIDAgMCAxIDAgMCAwIDAgMSAwIDAgMCAxIDAiIGNvbG9yLWludGVycG9sYXRpb24tZmlsdGVycz0ic1JHQiIvPjwvZmlsdGVyPjxmaWx0ZXIgeD0iMCUiIHk9IjAlIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBpZD0iMDA3ZjFiZjRlMyI+PGZlQ29sb3JNYXRyaXggdmFsdWVzPSIwIDAgMCAwIDEgMCAwIDAgMCAxIDAgMCAwIDAgMSAwLjIxMjYgMC43MTUyIDAuMDcyMiAwIDAiIGNvbG9yLWludGVycG9sYXRpb24tZmlsdGVycz0ic1JHQiIvPjwvZmlsdGVyPjxjbGlwUGF0aCBpZD0iMTdiMWFkNDRjOCI+PHBhdGggZD0iTSAwIDQyMiBMIDE1MDAgNDIyIEwgMTUwMCAxMDc4LjU0Njg3NSBMIDAgMTA3OC41NDY4NzUgWiBNIDAgNDIyICIgY2xpcC1ydWxlPSJub256ZXJvIi8+PC9jbGlwUGF0aD48bWFzayBpZD0iYjhkMjZjNGQ3OCI+PGcgZmlsdGVyPSJ1cmwoI2UzZGYwOWZkNzMpIj48ZyBmaWx0ZXI9InVybCgjMDA3ZjFiZjRlMykiIHRyYW5zZm9ybT0ibWF0cml4KDEuNzU2NDQsIDAsIDAsIDEuNzU2Njg0LCAwLjAwMDAzMzM1LCA0MjEuNTQ1NjQpIj48aW1hZ2UgeD0iMCIgeT0iMCIgd2lkdGg9Ijg1NCIgeGxpbms6aHJlZj0iZGF0YTppbWFnZS9wbmc7YmFzZTY0LGlWQk9SdzBLR2dvQUFBQU5TVWhFVWdBQUExWUFBQUYyQ0FBQUFBQklCRmFvQUFBQUFtSkxSMFFBLzRlUHpMOEFBQjdRU1VSQlZIaWM3ZDE1Z0JURnZRZndYOC9zTGlBSUxEY3NpTWdWTHdRMEVjVmJFWlVuSnZFMmVUdzBoSGdHUlVXTVJzV0hRalFLaWk4ZWVCQVRGRVdOR0U4UUQvQUFGVkFqQXFLaUlQZk5jdTdzZEwwL1puZm4xejNkMVZmVlRPL3U5L01IelBUVS9McG1kbjU5VkZkWEVRRUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFCQUFSaUZya0FncDVlUUlESklzR1hDdFRRUmliMDd0cGFYNzlWY0xRQ3JXcFZXemJhRmZPT3lEOTZadVVscFZRRHFpbElSd2NMTG14VzYvZ0F4MUNKS1dna2hKblV2OUNjQUtMU2lSc1hXQlZIVFNvaVp2UXJ6VWFCK3ljKzVWYktzWDU4akR5emJML0FiM3ppTFAydXhPWHBWbmh5eE0zb1FnSUxyT25GRDJMM0w2NVpBMGZkV1FvajFweFhvYXdCUXArdlVDRG53cWlWVVN4VnBKY1RvQW4wVEFJbzBHUk1wQTJaWmdpbEtLL0Zrb2tEZkJ0UVRSWHJEOS90M3EwanZsMTdyRGUzU3pvTjNhUWtNUUVSRWVyZmJJejZPbGxWVTdGMGtqRlBlM2w5UFlBQWl2V21WbURFeGFnaGREWlg5M3NXMVlkQkhZMXJ0TjJkdzVCaUc1RmtrUjc3VlFGMHdBQ3Q5YWRYMnEvN1JnK2k3ckhiMHc5cENRNzJuTGEwNkxlNmlJSXJHcTlXWER0VVhHK281WFduVlltRkxUWkdWZWFSZG9Xc0FkWldtdEVyT2p0Z0VXRVZQQTN0R2cwYzFCb2Q2VFZOYS9hMjNtamlWYXNJNEczeXV6dWhRaitsSnEwSERGUVhTZTFsdGN1d1BWS0YyMHZLNzdUeGRWU1RUOGt6MUlXSHBSWW9EQWhDUm5yUktQTmxJVmFpVXFrRE9ibW1vTno3VVV6clM2dUpUbElYUzNDZTJQYzZ1UUFjTnY5dXlmNnFQbWFHK1hYQ0NzdjBxUUphR3RCcWpNSmJPQm5ZaW90WUtlb0lBMktsUHE4Ti9wenhrdGJUNmtMOVdIeEpBZlZyZHF6S1lkVzlsdXBTSzREejFJUUdVcDlWeEExVkcwOXZBVGtTdHU2bVBDZldlOHJRYXBUb2dvNlBqN2RFYVlrSjlwenF0dXArdE5KdzFrWkpLWTJlY3JDRW0xSGVxMCtwS3hmRXNOSnhiVVI4Tk1hRytVenhFVFBOci9aYzFVMm1xT2wrcU9Xa3lpSWlFSUJKQ2tKbHV1TS95am9xdDZWUWlrVEFNTWd3eStLN01xUDVISkFLTzhka2pXSEVBSHhTbjFTRFBFbXZlWDdSaTlhWjE1YjdDTmJmZXRyKzNoYjlhTkczVHNsMzduc2NmNmFkc0UzOGhBUXJuUGVuNGZFdiszRGZnNmRGbFVTcFQxSFBvaTk2REJrWlpBMEFldEpiOWZzOE1IcS80OHNoVjZqYk5JNjB3RkNmRTNPOGtQOSsvaElpWEdCNjlUZDFyS0Z4TlF4RkNmYVowVzEzMGU4bUx4N1FOdmk2ek12SXgybjZIUkkwQVVGRFNZMEFobmpxMWNkQ0kveE90UXNrZUkzN3cyRmtKSFJmRG9KNVQyaExvMGZRMmRDaDkrZHJjeGV2U3BzOTlVS0pCMkNPMFpDTFI5T0NqVGo3THh3NVN4OFV3cU9lVXB0VUpuaVY2OWJxWlNLeGR1M2JqdGsxYmR0cXl5M0llSmNnb0xoSFc4WnVLZm1zSUljZ1FSSUpJVUxZWnIvcUJZVFJzWHRxeVRadXk5cjd2KzBWVElDaW50SnZkQjhydlhwcHJ5ZFQ5ZDZpT1Q3Ulp6Y2hyQUl6S0pvdUUrbnNDcmZQbDZOaXhmS0VoSnRSM0t0Tkt3ejBXMXVycE9BMTZXME5NcU85VXB0WFBGY2Fxb3Y5YTdWenRhNEQ2UitYdjFsY2Z2R0EwVG0xUTVYUHRhNEQ2UjJGYUpYNmhMbFkxNitnVkdwTHNrNTNxWTBLOXB6Q3RqQ1BVeGFwV0pIbW14QXZxUXdJb1RLdkdHdTZ4c082ZkF0NUs1Y2RMNmtNQ0tFeXJnOVNGcW1HdG52cUR3QVUvS2c4Sm9ES3RkSXhocEdGa1FJdS9hSjNwQitvcmhXblZRVjJvR2hyT3ByZ1ZPQVlFSFJUK2JuWE1HYXE1Z1gyMDdyMWhGRWE5N0szWXBOdkJMWlovdGM3SGxmODRmejhLMDZxdHVsQXVWQ2Zad2dEdGdFUE9LS3J1UGVWY0RVR1UzckpoNDlMM1hQL2FMU1llWmxCVmtLb1lOYzg1ZzBnUWZYUFR5dHpmVm90K2ZYc1drVUcwcjN6ejlLOWs5ZTB4b2lVbGlNajhaSkxUYkVZSG5Qc0wyd2ZKUGtsdjNiaGgwOHA1c3VoRUhhL3BTS1pwSkJJR0c2Q25La2gxVjJqYXZYYjk2bmwrVXFSYXAydEhFaEhScHBGVFBkNDE4TUw5RXVrMEdjbWlieDVjNXhuM2xBdEwwOEpNR29uRWQrTzMrcTlPSEx6cWRXZFRDTzlhMXRCQmNmVEQvWCs0Yy96R05MZE5QOE1seGhQQmF2ZGE3bDB4NTVXYmJFM3ZOSmRVZUVWTnVSdHpOd1A5UHpWejEyZjdIRHRldjY2ckpQNWkzNS9qNjl2OEhzY1UzOHJlMVZOZWxxMWdjVk92d0dleDB2cW1DTkJqanU4djJyODVsalYwVkJ0OGZJQVA5MGlBdU9hTWpvNHhsZ2FyM29ZU2U0Q2JiY253bWZzMFF0MnlSZC9PT1gvdXY5UG5CM25CdFJtcWFaQlBzdFhuVC9rZS9xWlYzYVZsK1ZjeHppTnU2VVpXK0pmKzZoSWI4NE44MHo3cFRLdlBndHdYL0hLZzBPVjluV0tzRDFhL2xIMXYxYm5DWHVSNTEvcjJ6LzdzdnJDblZSK2ZXU1dFU0QzbWNrTjNwMkNmeGIyaXpMblc5L3hVS2l2OFFJb1ZsZTFXaWVnT1ZuU0x6MEh4WW1OUnNHL2FGNDFwOVZPZ2xzc1p3WUp2ZHRyVUJrMHIrOTdxbHB3aXB0dnhwaVd0N0srOUZLUVd5NXhQbVE4TTlsbkVvOTVmY1JON3R2OVpWcnAwTHl2NWQybmdzbjJzcUd5NGxWanlmN1R0bnpXdEFtNGlwZllHNjI4Zk1LM0VDb2QyalEzQlF1VHNyUnlPc3BjMWNLbnZzZG0wc25jbWJwQ3owNU5hNmJnck9DallaeEhpRDU1ZjhUWDJ0MVJJZHl6WDg2SzlaU1VuczRMZmUxWkRDWVhYclhKT0JSU3dYcTFWMlJJNFY5cU9GdG1CdDBTUFlXOGlkRGlKN3hGZ2NPNXFqWU8xL25iNnlPbE1NZkR2WnFKWFEzSGoyK3hMaXFVREJFMWF4WjdjSXlsNHlERDJaS1JITFJSUm1GWjcxSVdxWVQzOVVabFdKMStvTUppRDBibk5YMEd2czlpYW1JdGJPNVQ1VTB2bjkwcldGZlJiYk9QakFNNWJ3eHM5Q2x5UU8vckJuOXoyeFVSRUZWZXpKd01HdUJlOGp6MmVQY09qRm9vb1RLdDkza1ZpSlBtb2h2dFltTVpEbzBZUXRyUnlQSU52T2pid3RpYndWZFN6QnVjdUMzNmo5alVlZy9iY25MdW9sU1JaaUY1WndKN2M1MXJzT0g3K2VYMmVMaUVyVENzTjQ3ZllmZ0pLTHdlWFBPMTV1WU5aSHpqK0JUbExOZ1lMc05iMjNMbE5ibGlBaTI5aFRjeHR5QTkrVGJYa0pPbkx2WjBhZWVSamhWL0ZIaC91ZXZEeGYrenhvL2thdVNUbUI0RTY5UXh5WHZKcTRQQzljM3IwVHd1MnFiUTNTanVmdXhZRkhvUTcrTWFweTVDY1JlWEJmNkMvbGI1Nm5kUENRUWZJM2pLZjkraTgxK1hVL2xlOXNvOTMzeTJ0Z2tJS095OXRVUmVxaHNhOUZkRWRNL3ovTmw0NTc5UWlFb0oxejhtcWVwSTg2UEEyZlBGSjlsYW51ejd2Um1RSWc0Z01Jb01NSS9OZlpxcXVxc1VKTWd5RERLSnYvdW12WmdNSHZ1WDdVK1I0ZGxjeW5UbWVZNStydUVXcjN0WmQ0OFU1cDFlVlp3NXJJNFFRWkJpR1laREQzMFpRcXo1ZCtibnhhYktLTlA5dng4VkR4c3JlTkhKdzl1ZmI2Y3FKVGtXSzcyZFBibDhwaXhaVDl3VnRjL1hoSGNzYWdsNHM4ZktsNms2R3Y5bkZvdCt2T0hwUGx3NUhYem9OL1h1TWV3TjdxK3hMN21kSS9mL0ZWNUdTZFQ4eXFqTXJSN0wxT3p5S2U1OFFvaXVjUDl0bTJYdUk3bVpGdHptZWUxN09TbndyYXdHSnJWSFNuM0E0c3kxcjZLSTZ2UEtPTEFQWlR6L0NUc1JSRDVlME1wM09RS0ttRmRGZ2Zua3JSRHMrRVJIdHY1QUZrYzBudXlyVVg2anBEbGIwTG9jQ3pYaUJpMEoraU1JYTR2RWJEc002aXAveXRGcWsvRXRZa2cyK3lydDBJQ3l0MGd2NHAxaS9mMjdoNkdsRnc5Z3FQZ3hiYWQ3SlZYSVpxaDhybGhyUG5zeDJmdzhSMGUvNTk5QWw5L1U3MmN2djFzNlp6RTd6ODBNTzZFM0xHbFFmQkFweG5Pb3Y0YVpzYk5VdG95eXR0clF0NTUvQzRVeGNrbGF0ZmFZVmZaNWR3N2F3bFc3SXFpbTVjdlVjS3phcEErL3U1NUFySEo4UTVzbWNWenZ6SGJ5R1lTeGRLY3pnRmVwQzFiQmVEbFovMGVHdnFyZGdLN0oxMURDZ1RZMzFsazdiMXgyb1pTV3NEYUJwMkZ1LzkyN0tQbmJZcVZacHc2OUdQTG1HTnpONDlPSGpmYUl1emVuQ3hDL3FUZmxVSGltdUd2bmJRUVF5eTdJR2xYMENxMGc3azRWd0l0c1ZCSjdOUzY0NzIxdFI0aWYrS1o3SktheGliOVVxblYyRDlNS3NoUEZ4TnNiOXJxVjRkOEN2aTZtTVBTMzMrQmJuc3JMdjJGNDdqTDJXOHRqdHFhVndhNTNXM28xUncxQ1pxaHN0K0dWakRRTzgxVEF0blJJdU9rYkhPamF4UCtpaFlZT3dPM2RkRThUZ0grYVJGSzFtTzVZbVo4dmpYOEVlbjJ5Ym5wbzN1WS9SY1REbFNtRmFWYWh2QUxDTnZLUmhhZ1A1TmNyZytFK25RbkZzaTMvTVowOE05eDFCRkN5dHdrNFZLOWloc05QTi8wUkU5SXYyN01sMEl1S1h1SytYcitDcmFlekpYeTB2blhCcTl2RXE2MHU2cVR5M1dLZ3dWaFhyMVdvTkhicTZ0dkV1RXdTL3dGT3VOclROdGZ6Yk9EcTNxNVFyLzVmVFdGbzVkZlAxSThHT3ZYYTdGYnFCUFg1OExSRzl4VDdiVVQza2E3aUJiYjBPc1hSaDR0ZXd4K3lWUjFGTVpWcDlvakJXRmYxVEd4eWxOaHk3L1dHZjNqRUk1L0hOdERGZXh4VElhN0lQd3g3UUNyWWpjdHZNdERxUFBabE1STFR6UWJaa0dFbXRIc09lM01kMmp1ZitMUHQ0L2hSNUVOVml2cmN5Sk0vVWtIYXBDZTZrYkIxMWJ4OUg4MXNHRHJRZEt5blpzYk04Q0p0V25ka2IzYTQ0OEYzTTBzeTJtZmVQL2IxSGsrcGYyZWxiV1hiVURPTWhWbVpVbmtldVU1bFdXMVlyREphaGZjWVE2cWMwV2hOMk1LYjdISGtsUDZFeWJ2WjltT2IvVzJSTlJPNk40M0xYc01mZnVwUVp6UjQvbHZsdk9SdE9yZmw1SkZYQmUrbitiODBkeFNQWjhmaHoxcnZNYTVtQUkzYjVZTzFsRVdpNEg1K1VkaEF1ZXBGRmZscnhOVEZMQXpzUlVSUEw0QmdQV1FyM2MyOWdiK3UzZ1owdXlVWVB1WTA0azlmUWVUd3FPb1lWTWFzM0RyOWtDNzJPZ2d3KzNFTjFGNlpXZk0wL2s3NWZBNldqTWMrK3pIL1ppdlNlWFR1ZHg1OFVsUG1USjZqSU9nYmtqZ1pHd2lCQm92b3RJdnR2OVQrSk5pMWF0R3JkdXF5c2g4ZXBiaFhwOEQ1YzY2S2EzeUh2eFc3VVZMaWsreEZYSE1iZXNOajVSM3R3d2lCREdGVzkxY25TaVYwWWxDQktVSUlTcVVXZU45cnN2TzFodHVmNXcwTkwvWDRVdjlpeFFtNlgxeVpOS1owV3dpQ0RNbDN1cy8zeGhSQ21NSkl0ZWh6UCt4S2FhM0ppRUJFUkwvTnM5VDFwcjIvTi9tSDZIUEsxdEpyaU12WXpHZjE0Wmd0d095c3dicG4wL1hIWFdiWmJxTEo5K2xYSGRkRjVSYWRHU1o4clh2RlJJWC9CU3I4d3ZWa0RuK29RNXN3cG0zM0VNVTNUTkgreWI5eHo5bFpFLytIcmU0MjNXa2oyVnUxODc2MHV6QWJmWUg5dENPOWg1SXRMTDRkMi9HdkxUdW8ramkxOVFGNU5vamRaNFV3WEp0N1BiVTNZSTlpWUtQbFcvc1h1Zk9Uc2R2c3I3bnZnNGZBSHZmN2EvazQxYnZVS2syT3pRK0FiUFFlVFpVYmJidmpvbHB0V1ovQjQ1a0JXV0hkYUpRTi9IOEx4UmtXaTYxZ1IxcUxQODJKdk0zazlxUnRmVDE4aUlyNUI5UjcwS2ViNEppYlhyL1UzbHpzNHd1T3Y3ZTh3K0JsZnZ4eHVTbTZRSzROa2xYak1kbTdta0ZiMEJuOERIMmRUa2xidFZhUlZpQ0VibmUvMFRhNWhSWGhuWE5icnlYdHd2NG1zOEtzR1VRLzJmRkVCZXE2clhhVjBYcHZYM3k3SUZBKzdQRjczbCt0T3R3cktUY3RaMG1WU29PMUt3a2Vua2h2NHRiRmVlUnhlM0dPOEZ3Y0xuRy9OUFk3M3NKaktIdk51RWJ3OTBkRWQ3THNhTk1BYWFKU0d6amxlMUtiVlFwZXpVaUlpT3JwTVo1OXVGMzBmWHU1UlF0TVZqWGx2NWl5NlR2MW1jL0ZrL3V6T3ZKeXpodVF5UmkwL05Kek9mejZ2cytiOXc0LzBDTDZOdDN2YzNYb2d1OGovNzFrNXBXdWQyNldIQWE5Y2ZFQnB2bElydVYvcDhkZS82K1BZeEYrNDZUNGljV21ISDhLOFlDRWV0NzNkNlNDUVdtM25iOG1PL0NEcHdkNUJ4VUdnNVd6R2oxZWNWMkdaQk9ZVXkwdDhvb1BKenU5bVZyTFMxN043amRPOVBOK3FnZUxwRHArOVEvYnEyV2ZUdWpmbkxGcGRhVmFtVWlHT0NFc1RaQWdTSkVTbVVUc3ppMUptS3FYcTl1NlM1aTFLVzdmdTBLSFRZWXA3K3dWejc0TGNaVUg3Ri9tWkYyM1RXRDZnNjhoSEhXNUpMc2dacmQzNnE1eVg4NUZoMXJ4bmVla0pkcVoxMmMyYlNPN0tmMmNmOCtQSENWOTZWMDQ5eFdtMWZPN3g4Z0x0aGc0bEl0cTBadDJXRFZ1M3BxM2pHQm5Wd3c4SklZUXd5RENYV0NhNGFxUytHNGVXMFEySkpvOTJXQmowdTA3NE9UNmRjRGtiT0szUjNjNERHS2tYY0tPNGJvRHpFQVFOK09ETmoxbDNuc3Urek81b0V1Yy83TEdHVnhjNnp0S3l3MnVTbnRyaHY0SWVITWpOdG16aDI2c05Mb1FRWW9HL0U1NUFCNEg3blArV1h3YXIyWU8ya2U4Y0R3S0pMclEwc2xjUDFjc09BdTJqdG5VTWN4Qm9IMzYwYTZDUDhvWGJHS0duODFMMmdSVXZZSzk5NzdtclA4cHh6UjYzbGVpaStpeDYxazlLdzZVczM2YU9KcDMzbFFjMTN6ck1ZZUJqQ3JxM0VvdGM3MCt5ZUU3ZmpWZUtEaCszWDlYM1B5NHY4WEhVWDdiZkJqdUQzZkxSeGF2UmdqNTd6bUhoOTByR2p3OU9kVnJ0VXp0d3FHbTVGZERmN3l3WSs0M2FBZVJ1R3l2WEw1a3h2T3dNbDhiSEl0bGI3U3FtVGZWNW9IVWQzekFjZTc1bitYeWVhMVdzV1hqWDBhMy81blkwMjRuZitwdlRMTEdQOTY1d09UZGpuQ1lEdVVIRERlVitLRDYzSXBwNnI4cHVGTlpmbG9hOWxRaCtrOWowMjB4aGlyUXdCWm1aaGhNaFNKZ2tSRnArall4OTE2TmxNOGNFTkcvYUpka254dmlYZFd4OGN2QS96S0FmOTFCU1ZLWk5Ja09ZWkZiMWtSVEMzT05SRjM1djlrKzV2Y3duczBhTElhTzhoc0ZmODVlYjdJcysrSmZIZTJxUHk2VWI0WUNzUTFnMlZ4azZZN25QN1FvN3Qzb3M3RGZEbW4ydjhDNmR3NkZQWU1hQnUva255cHhOOUhNL3QrcmsrOXpxb214VSs3VmNQbTFjem1EemZwWHdIaGFUMmpTa1JNSjY5TVI3V25pUEFOcDRzLzJ2ZTNUWW1rV2wvZ3JsMHhxYTYvUjVLZmd0dktHL01wYkJTdmNvUDFobW9iblZheUlVL3dlQnJDdUY2dzN6Rk9GK3pSTjVENHVyVisxSnA5TnBZYVplcTVrSW5PL1QvK2daYnBlOU04YmsrWTdsOGtCOVd1MVdPZUZkd3ZJYjBOQzU2K1hnYndsOUtKcXR2bEE3dThvNFBtZFBjNmRCazhOaG5UWms1eWloKzZsWW0rbEtNbCtRVVhUV0M5V0RwYi9Gc3JtTDkxQ3B6MWhQYXZmYzdsSk9QdzIvMU9mbmVaZnh5MmYvOHRDK0NiRTlDOTJ6a1czVjFaNy83TGJNRDNwNUQxSTBHeU03UzdhbkZZOGZkanZUY2FETEMwZFV2N0I3QWx0NnRWTlJLK3Z1YW94OWhyRDgwZEc3VitFbHlTSzlhWlc2UFovZE1ObFVhMkUrbHVROWp5OW1UNHJjWnlZTXVHNDJnYXBzRkttdzM2SDdUYTgxbzhJOHhSWmUyTjZocE5WYkg3TW5QL3d0VEtYVTBKRlczK2EweUlSbW5lQkZlWTV0ZU1GM1JSU3NqYVZWSndYaHVCR1dscmxURlAxZDJXQmtHZ1puSzNadmhEaTdlczNmeldWTGYrTWRrOTlhZFd1Qkd0ZUo5S1FWVFZqc1hjWW55OEdNOHNxTzF6dm1tRTAyclF6VlRWU3orV3lSeG9TaWhLUlRndjh0Qkd2aXN3L3ZvdUFlbndHUzhRNXFXdDc1NVczdlJndmlQN3l2QzNJZlVvYVd0RXI1Mks3NG8zY2NxcS96ZTVqQTlsYW51SmNLNTBaK3R0YnJkNmFaVFo3UXY2NUV6K3hqZFJ2S0dzNWRVVEwrVUgyZi9CdDhGMytTWjB4K1BLcDFVR0VQZXU2Yy9FTFZERjA2QnBXc1VUa3MxRmxCNk9OQk5ucFJxWHl5NmVDV1dicWlqa21xT0djOE9kdGtJV1NEdElUN1FnNlF0ZXlWVlRkYTdPTWpCZ2Fidks0QWR5L1cwSFJEOG5OM0tncGsyZFFxUHJkNjltUHZNaXBYdllTRkczdVllN2xRVmJpTnp6M2ZkbXlKK3o3Szk1Lzg0dXpEbFBxeGkrU0QxZFljN3p6QkZwNGpuYUhicm9ESGdMclNpbTUzdVcwdG9FcU4zODF5SHkyMlNpMWhINmJsbkd2VnptUzczYkloRzNGaTlPMUFOemEvL1dyN0VWWGt2MHV4L0ViNm1pL25oL2ZZMG56ZDloS1o4ajZCMWM3NVdNVjRzUnBIOTloOWZzaGJyVUwvWUwrdVpDTmlsTjUvMzVKVlcvYXdjSUxGTm9pV1BSeXNmaE92N0o1OTBpajYwVUt6bDFodFovb1pXQ09RMDV2TFhxM0ludlhlZjFKMjhWWDM1S1hEWTNUYTBvcE9tS01nci96ZUV4L0NEZmErY2xKS2pqOC9Qb0ZITkE2VlR4cjFzK0hCZmtNM3V2UVlzWCtGL2o3S2dFbXN3WUtlVm42aXdoc3NmcGlaTkloSW1DU0lUR0dLSGE5bWo4L2YzSjRkenF6OWFXLzRYME1oRHdMMXBWWHF4UGVqNTVXK0J2QkhIdEVXMnRVTEozaVh5VG85WUNyUGVQL0VZRzhnb3ROM0cya2l5b3hNbTJHSTRxWXQrcDVrU2ZtVkg4bUNoUG45ZHVuUG50dzV0Y0p3dlJVNk5aSDFRUm9SSUszcXFnYXZlZmNnOTJBZE5hZk0rdzErUGVWU1p6Y3ZaZC9xUFZ5Sm13NkJ4Z2xNMlk2QWU3cjFZSy9XTiswWXh6NmJIK3NKTDB6aFBUcXZFT0xlbkhWMVpxKzJDdkZWM00zZVg1NDdFalZubWRyV3E3YzhLNXIzZ2RjWm5VTVQ3aHZrTW9pVmY5Wk5XTmdwQVhNOWM2bXlVQUdzaVRSVmtlZk9hK0hmSFJmTHJ2MFpaRGl3bDZxd0R3SVZXWU1SN01sVDhvN0hxejVrVDRiNlgwY0JSdDNNMDdySG51QThNb2h2MXIrd3NzSFFwaWk3WGgzTVE5NUZtTUFIVjdjNDNrWVp2YmY4QS9MbTlSRG5uUVA1bi9JSjEySVp2RXYrY1Avam9HcTk1dWxCYzByUFBmU21TQ2U3MXBOMlZkZXQ3aTNJdm9xSXByZ003Ni9JV3NkN2puZlp2cmJBZi9JZi81Uzd6SEI1N0JNZkZmcFRyN2FqbVd3d3M3YURmSzlEWDdPQk45MTd5dko3eXNhSDcvSW9UQTMxV3pOb2xQcWdQbDBkWVNQajQ5YzczdW5nWUUzRXpiWVk0ZEJ3RkMydHV2TWVGcDY3OERUdlkrYS9HM2RkVGl1aWRUZVhYVG96NUhzTkRUMVEzajNxZGVVeGZmdGtnbmVaQ0NwdWNUaHcvTVoyY2hXd003cjQ0d3lIcGRIU2FqaDdYUEc4WjNGK2F0ZFAyaExCcTFMSWM2dDhwUFNPS1ZNYTllM2FvZU9CN2FxU3hNZzliYWdhbDlZZ1FTUk1JWVNnaEdFV2ZXZ3ZGOVdubzk1VEhUS1FHNHpRTjAvNytmWCtZMlR2bkdWemJGLzJ1cy83QkZpck9kengxQ2ZTOFhnRFBwVEg0OTczN0svNjZOanNrMkUzU0VyR0phMEt6dG9HbFVna0VvbGtNcGxNSkl4RVVkTDJ4K016WDRheUxIUVg0SDlsZzRSdllDY2lvanY5VmpabCsvU0hlald3RXhIOXZNSWVKbWRvRWVQcUFOL1kxZ3VkMW1JWklxYXliZEN2NEZkOERaNERBSkoxVU5mdHNzbEtFcXlnMmxtaDY3QnpBdndlSEh4NVR2aHVlTTludzB5SytDbjZ2K3V2dXR0dDUwVHNlcE5rRko0cmJWRk1oM3R3SC9QN2plMmQ0RFpoVzNaS2dzb0tyMG5kY2t4anEvamN6NWxma28rcEpMMVhqWlZ6RzBrWGJDS2tsYmwwM01sUmpncituQTNsT1llWnArTmYzT0xqd3ZBYzJ3RjZzcnptcGJlZDR4SVIwUWhMNkFySG9kTWU4bk5kMnZ4Mmtzc2sya1JVWERXSW1wbXFDRDVsOFFkc0xSN1QyRmZoVXphZUt5djRYVTJ4M2ZtZDlyTVdHK3pqeCtCZy9hdzdCalFMUHUrYlJlbjg2aTRJTTVYMFBPOTcwM096UGx1K2JxT2JEZXMvT3NMMmx1U1E4cW9xckpWdXNIdFAvbkYzcGx4cTZUU1gva3lYdkxkRE1tMXhhc00zODE2OG9vdnpXNnY4ZXQydXZmdjI3dDY1YTUzYlFDL3VMdGt1aERDRktVVGFjMXJnak9JWGFnNXV2MmduSzloL2hWa3BoQkRteHQvS2l1a1dpMWxhL0RyTmRRYXdjVS9zU3BNd1RlTHozaE1SbVpVVmlob1RteGNMSVlSUlVjQVJFb3BLRTBSQ3BOMVByYW8xYWtCa0dPV3l2cnI3RjlYTVlVVEU1aklTVkxuUFQyVVNEWW1FRUpRSzhmMG1HeGxDRUFtUjh0M3BzOGwrWmlwdENoSWVMUnhGUmxxUUlFcnF2YkhjUXlFYjl3TnovNmJXZmFkOTVkdTByOEZUNVVhL0pmZDRkNjJJT3VpTEtSdVIwME02K0xacHA4KzNWT2RwUWJPcXJyUkM1bldvRndBUGRTU3RJcDQ1QVNoVlI5S3FWaDNMUXAxWFI5SUtJRTZRVmdES0lhMEFsRU5hQVNoWHE5TEsvZHAxcmJxcURYVmVyVW9yZDBncmlCT2tGWUJ5ZFNTdEFPSUVhUVdnSE5JS1FEbWtGWUJ5U0NzQTVlcElXaFZ5ZGdnQXV6cVNWZ0J4Z3JRQ1VBNXBCYUFjMGdwQXVUcVNWbWl5Z0RpcEkya0ZFQ2UxS3Eyd1Q0TGFvVmFsRlVEdFVFZlNDdnN4aUpNNmtsWUFjWUswQWxBT2FRV2dITklLUURta0ZZQnl0U3F0ME40SHRVT3RTaXQzR0hrSjRxU09wQlZBbkNDdEFKU3JJMm1GZzBDSWt6cVNWZ0J4Z3JRQ1VBNXBCYUJjSFVrcm5GdEJuTlNSdEFLSUU2UVZnSEpJS3dEbGtGWUF5dFdSdEVLVEJjUkpETk9xS0laMUFnaWlxTkFWc0drLzRKYzlXemZmc25YNWpIZFdGTG91QUhWQys0ZEYxaHVkN1MrZkpOemNWSWphQXRRR1F6ZGJjbVhuNWJiWGtWWUFBWlZNejhtV1dZMHRKVTUwVGF2UkJhb3pnSk1ZTlErOGYxN09vdE1XTkNsQVJRQWlpazlhM2RMUFlXSFBwL05lRDRESVlwTld4NDkxWFB5cllYbXVCMEIwY1VtcjVBTXVMNHh2bHRkNkFDZ1FsN1E2czQvTEN5Mkg1N1VlQUFyRUphMHVjWDJGdGJLaml4TFVEakZKcTVLTFhGODZxRU1lNndHZ1FrelNxb2RrUjNScy9xb0JvRVJNMHVvZ3lXc0g1NjBXQUdyRUpLME9DUGthUUJ6RkpLMWsxVUJEQmRRMk1VbXJWWkxYVnVhdEZnQnF4Q1N0ZnBDODlwMlA5Mk9LSG9pVG1LVFZFdmVYeE56OFZRTkFpWmlrMWQ3cHJpOTlpNE5BcUcxaWtsWTAxZldWZjJRZnVqZGU0Q0FRNGlRdWFUVmp2c3NMbXg3TWF6MEFGSWhMV3RFMUxzdEhiYzlyTlFBVWlFMWFmWHFMNCtJWlQvbDZONjV0UVp6RUpxMW8zSWNPQy85ekFYK0c1SUhhSVQ1cEpRYTluN05zL3JFVi9LbDdaZEZrQVhFU243U2k3YWZlWVZ2eVFyK2RoYWdJUUVReFNpdEtqemw0R25zNnYvLzV0Z0x1QjRGSkxSVUNDQ2RlZzBVdnZYamt3TE42dEd5MWJkdjNzOTVjbXZQeWJoSXBRVW5EaU5QR0FLRFdjTjR4bFRndlRqWXExbGtYQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBZ0VMN2YwUnU1Y1NkZnlPL0FBQUFBRWxGVGtTdVFtQ0MiIGhlaWdodD0iMzc0IiBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJ4TWlkWU1pZCBtZWV0Ii8+PC9nPjwvZz48L21hc2s+PC9kZWZzPjxnIGNsaXAtcGF0aD0idXJsKCMxN2IxYWQ0NGM4KSI+PGcgbWFzaz0idXJsKCNiOGQyNmM0ZDc4KSI+PGcgdHJhbnNmb3JtPSJtYXRyaXgoMS43NTY0NCwgMCwgMCwgMS43NTY2ODQsIDAuMDAwMDMzMzUsIDQyMS41NDU2NCkiPjxpbWFnZSB4PSIwIiB5PSIwIiB3aWR0aD0iODU0IiB4bGluazpocmVmPSJkYXRhOmltYWdlL3BuZztiYXNlNjQsaVZCT1J3MEtHZ29BQUFBTlNVaEVVZ0FBQTFZQUFBRjJDQUlBQUFEaURaNGpBQUFBQm1KTFIwUUEvd0QvQVArZ3ZhZVRBQUFnQUVsRVFWUjRuT3pkKzVNYzlaVW8rSFBPOTV1WjllaUhKQkFTTm1EQWpEMGpYZ0poWXpBWTVNRnJEM2M4TjJJalVNeU1YNE05ZDcweE44TC9ndVYvWUNmQ3NSRjNtTEV0WTROOVI5emR2WGU5d3pVRHRzQmdHMnpFUzBaK1lUQUc4NURRbzlYZFZabjUvWDdQMlIrcXU5VlN0enF6dXF1NnNxdk9CeUgxbzdyeVc5WDVPUGw5bkFPZ2xGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBaUlNTaW1sbEZKS0thV1VVa29wcFpSU1NpbWxsRkpLS2FXVVVrb3BwWlJTU2ltbGxGSktLYVdVVWtvcHBUWVNISFFEMU9COTVNZjNlV0VHWUJCQUJFQUFBUkVBRVpqYlI4NjVvd2dBSUo3K3Zzei9XUXpuLzVSdytyRUlnRWhJaENZd01sRG1IdDE5ZC9ldlR5bWxsRkpuMHhCdzFOM3g4UDVXbkZzTEtRY3dBRUp6MzBDR0FFQVNBTXlLejRBTFB6SW5MUGVvbFovalRIYnVhUVVSQUNJRGFDSXpOWHNLeFF1VEVLSWxOQ0I0Y1BmZmR2RzBTaW1sbEpxbkllQ291LzNBZDYwM1IrcXRRVGVrbkhhR3hscVRBRk9BSEFNaUdKVDQ0TWYyRExwbFNpbWwxRWFpSWVDb3UvV3hid3ZSS1hTRGJralhNUFdFaHJnWlRBdUlBZkc1MjNTWVdDbWxsQ3BGUThDaGN2dUJBOTdNQnRNT3RoVk1ldkNHTHhiK3lNMC8zaWNZWnRtdVEvUDZJbk9HakJDaUFJbFlZNTc2eU9jRzNTYWxsRktxNmpRRTdNSmQrL2RQMXlDMzNFcllvdzhTR0ZnSXdDQVFGZjk4LzFsT0pzTDJMRG1heHlkNGx2QVVQL1BKZ2lqd3BwOThEUUEyY0FnNGoxS2Z4RkhLM2djdjVBL3YvcytEYnBGU1NpbFZYWlVJWEtwdjF3Lys2M1VQMy9kNk14MVBJWXNJRUJCQk91dGVSU29TL3dHQXAreDQ5T29zdDF5YTRDbVdNaEgreGhzQlhoN1hiSnVFUFFyNWlHbzNQSDcvb0Z1a2xGSktWZGVHNy92cHQ1dC84RjhkdXdDT0tHcEgyUytieDZoZW4vOW1WU0svWmJIQlRsS1hsVWxBYkF1TXJVT0wxZ1BXS0FyMWlCSVd2K3V4ZXcvZXBvUENTaW1sMURJMEJEeW5IUWYyRzNZdGNBMktuRzl4TXdBQVFiM3dCNnVDUUxqNFVlS0pMUzNKNUxlQmlTRW51UldhT3RIYXR1OGYzMzUxQ3ZidUhYU2psRkpLcVdxcGREL1dBTjE0NEw0b3BJRWlsRENOTTl6Y2VHOFUrMUNxRjdBeW85ZzlKSWpPU0hNaWVmdlZxVXZldi9XYWg3NDE2QllwcFpSUzFUS0VsLysxMjNYZzJ3NUN6UkJseDBNTk1ONlFmYVVJSUZ5aUczQjRTVXpYM0hMeHBpM2piTVA3bi9qNm9KdWpsRkpLVllpR2dHZmF1M2ZiMS82UGt5ZG1FS2lGR1kzVkJ0MmcxV09XRXAyQXc2NW0wSGlYY094azE4UDNETG8xU2ltbFZGVm9DSGphcnUvZGMra1ZXOTUrL1ZTekVia29vTzJtcGxrRmlaUWFDTVloVHd3a05hcDVpbjNJUks1NDhLdURibzVTU2lsVkNSdHlpTE1mYm56a096Nms1MDNDNUljdmx2b0dELzQ2eW5VQ0NoSVRBZVI5YjgvZ2lFVVhiSnJOeGpZZWRGdVVVa3FwU3RCZVFBQ0FEenoyWFkvQm1wcHYyaUdKL3dCWXNNd3lYOEhSMkFjU00xWWJNOVpjLzZOOWcyNktVa29wTlhpamNmbGYwVTNmLzVxNGxLeHRRVXZpSVluL0FLQlVWc0JSd2pGRjFnckxCdzVvRktpVVVtclVqWG9JZU5mKy9iRzNrUStabThXYURvc1BPYy9CQ2pKcWJLeVVVbXJValhvSWVLeWVYZEJxdEF6QTBNVi9nb2pEdnRTamE5WUdFakRteGlmL2RkQk5VVW9wcFFacHBFUEFYVC84ZGp1UjMyNlpsdnF3eFg4QVlEVCtXNDQzWktNYU0zL3dxZjk3MEcxUlNpbWxCbVowUThEcmZuQi80SkJiNU5yd3pQOWJUQURLOUFLaUdibjAwUzV2b1VtWS9hQWJvcFJTU2czTXFJYUFlL2NlZi9zRVl1UjhhOUJONlJja3doTEYzOUFZMDNEcjBKN3E4SVErWkJUYzdib3VSQ21sMUtnYTBSRHd3a3MydmZxYmQwSStEWTFrMEczcEZ5U0NNcjJBbGdERzE2RTlsU0tTTlZ5ZTJ5R2NBS0NVVWtxVk1Zb2g0RTAvdUhkc0lyNytsa3RoZkFQWGZ5c2tBS1ZDUUJuT2NmQ1ZCWUhaV3QyejNQU1QvWU51aTFKS0tUVUFveGdDQnVEek56ZHozeDUwUXlvQlJVd1l4Vmx4N1prcEU5VUNoRUUzUkNtbGxCcUFrUnNJMi9Yd3ZvQVF3Tk5ZZmRCdDZTOEVsQkxsUVFSbE5OUGtVVDF4NklCMTNiUlNTcWxSTkhLOWdDSUNBWHcwQWk4Y29WUjBJNVNQYXVIY0VEaklLUGFBS3FXVVVpTVFDUzJ5NitHdml3RVdscEhJbVNkbDVnSWFobG82dEd0aVZpYnNCZkI2blE2b2xGSnE5SXhXQ01nZ3hCSWFvN0VBQWt2OWNpbFE1TEordDZXYW1KQW9nUkxENVVvcHBkU1FHYUVRY09kajk3TXhIa2NuRTNLcHlDWnc3cUlSN1FVRUFFQ1drVndObzVSU2FzU04wSElRQW1FVCsyaGt3bDRSa09Jb01CaHV1K01RamNMSStESkN1NjJWbEpWU1NvMmdVUW1IZGg3WXgrd3RDbzNNOVo2WU1SU0hnQ2g1VW8vV29UMFZsVHQyK2FBYm9aUlNTcTIzVWVrRlJBQUJTZE1aYWd3c0hYVEljblFCUlBDc1dYcExndExPV3Q3NThPMWNZUndDemYyb3lFSlBGaUloRXJGM21MT2w0bG1QRmpGcXV6UjRJVUpqT24ySHN0Q0RPUGYzL0VkeksweFdqQ3psOU4rbi8xcG9ORkpBb2NpaXJjU01UTUVBTkNwSGdWSktLYlZnVkM1K3drSkU2eEgvbldwSEZOZmlPTEtKc0JXTWd1RVdUUHVRSTZBZ29LQXNDdGtBQU9Uc0tGQk9mMm1GWUV0QVVIQStQbHYwbENiblRhMllnWU1ybnVVV2VXNHpSMGc1Z0RBakxzb1N1Q1QrbTkvU1NvMWErSHRwL0FjQURJS0lJcEtBM1dxM01Ec0pqc1Zud2VmZXBTR1R5RUN5anIyU3pUcDR6UTZ0bEZKcTVJeEVDSGpkajc0aEFieHpFUFVsdHVBVHM4M21SR3diMjk4S3I0K0xReFlCWmtFRWdZQk1EUnIvOFczL3NSK2JYdFpOOTMzTmt5czV1Ky9SM1hmM3VUbkwrNHNuSHhRSm5XaVhwWk93VVdJejFxUjNJeHpOTUxUVHRoZVB0YjRuTFp6WjRrRDJBdTd0OTRhVVVrcXA2aGlKRUJERW9BbG9iYytUZjhTWmRaamJlbzBEeDdtYkdUTVhUVGNmMkxPbjE5dnB6cVZ2bmZ6TitEald3NmtMM3cxU0tqdmcrdnVmSDdwejZSZHZQM0FBYURad0xEQ0xoQVRHT0U5aThyaVBMK0hscTkrNjhJMnhOL3UzQWFXVVVxcDZoajhFdlAzQTN1blFZak1oNUhyN3pEVGpaOXFwbXcyL2Ura283TjNiMnlkZm8wKysrZVlQLzJUNzgzZjgxYUFiMHAxSGQrOWUvT24xRC8wekdVdEE0bjMvNWc3dU9uamhpYzFhTUZvcHBkUm9HZjRRTU1YSmNYajdTSnZzV0krejM3bmdKc2srOWRKYmYvWm4yODUvNk50MWtILy8rR2Q3dTRuVmVmL016RFB2M2dKQzF6enl2UmZ1K09TZ205TzF1L2J2UHpubW5KRnB5cVR0WUR6cTk5cVJpMTZiZUxtdkcxQktLYVVxWnZoRFFJWm95bHhpeDNxZi9zWk0xdHZ0N01vYnRqZmlzYnJ3Q1hTN0h2cEdSRWx3dHA3aE85c3Vnb21KdzFkZXVkN2pzSHYzZm0xcmNyNlJDWkRKdDkrbzVpandncnYyN3oreHVZM3MwaHBZWjNPeUxyZy81alB2bmhsNzljSTIrVGlQdkxQOVhhNXgrY3ViZi9YK0MvcTZDYVdVVXFwcWhqOEVkRkxIZ1AxS2dGaFBUQjB5OEc4QnlGUVcyNW9oWXlKOFp3YXVmUDMxQis2NjY3MVBmVy9zKy8rQ1JJUmtpQXdpTFB4WmxzeXR3SjMvUnpvWldwWmJHWXlML2dDQWlBaUhjUEw0N011L09YcnhqaVQ0SUNWZTl1MEhEbmgvMG9YY2hWbEdKRU9FaUozbHhRdlBQZmVGdVhYS2lBQW81d3d0bDMxMW5Ud3p3Q0lnd3NMQ0xNTDhtcHU1K01UWXk1ZW5GeHpKWnNhMkcrY2NRc3Y2WDQyL2c5aUFLRUQvSzVjOGNOZGRPMTU4c2Q5YlVVb3BwU3BsK0VOQVlSRUpFUFg5bGVKa3d3RTRTQUhBMXVHWEo2YXYvcmYvUW1UUkxJUlJjN244ZUlXZXVia2tMd3YvSUtESThqWHRaRDZ6OTBKS1FBSEM4Zkg2aml1M3RSQVRnd0RGL1dlQk1tczNlZi83d0lCMkxzU2JDL1JrTG9aRTZjVFFDQ0lBdEZMODEvbkI1YjZJd2dBa0dFQVE1djdIdHVWZk50NlJOOElmNndsUUM1TE95NGtSK3I0UWVBN3o1Yy85MitHZC8yR2ROcWVVVWtwVnc3Q0hnTEwzMURQcDJNa0IxTUNsemVPdzBKc0h3QUIrN3JPaUlzVkxwNzJkTTVYTmNtbi9HbVNoTGdEdXlDd3Q4MXhuUTNFdXlsb1JBcUFBTU1oeWdXTXZobUxOV1I5MCtoaUpCcm9UOG16YXpLbytYSzZVVWtyMTNKQVhpSHYzSDg1LytmcDMvRWhlM3pHT3VVUnc1YVBweUdYOWIwNUZrWW42Tmt0QUthV1VxcTRodi9qVld0RUhuL2hURzQ5a0RWeGtvT0xnTjFBNlBYNWlIWnBUVFZTcmt4MUFKN0ZTU2lrMVdFTStFTHo1blRIQkVTMy9KZHdwdlZIQVJ6MU9sN2l4R0l3OUZRM05LNldVVWtObnFIc0I5KzZkT2pZVGVFUXY4RndpL2dPQU9PdHZ5cjJLYTNzb3MyNWFLYVdVR2pMRGZQSGJzWFhyYjU5NzAyY2pPdEhObEJnRkJnQncwWG5INm4xdVMwVmhMcFl5UDZMM0NFb3BwVWJhTUllQXRobmR0V01IOEhMTFprZUFjTG1TeU40ZW8xcWYyMUpSVVp4WXhNTzdCMXpUV1NtbGxGcC93endYY05ONXRUK1lISm9qR3Q4d0FFaUp1WUFXZ1VhMG8xU1FJaGpSSGxDbGxGSWpicGg3QWFkdG50bldvRnN4TU1JbEFrQUFZNlJHdzN3bmNDNlk4bmkrYmJCWkNaVlNTcWxCR2VZUU1JRG5FWjduTlZmUnJZamdNTzhESzdCSkk3aDNrakEyNklZb3BaUlNBekRVWFNBaU1KSkpvVHQ0cnRad2dRQ1E5Nzh4VldQSVhQNDZuUmpqUi82WDNZTnVpK29ObWUvMVJxMzFvb2JSamdQN0l3TUVUTWpNTEFnbmo4eW1yZnl0bDQvQjNyMTkzL3pDcUpJZVgwTmttRU5BZ1JWTDJRNjlVdVBBSU1ERDNSbThMT1Q0MTVkUHYvL2w4WUZzdlQzVFp1SE8vUWtpemdmcUFtZnVzYXNQWmVacVBBUE1kd1lMQ0tBSUNCbUxTTmJHUFltVFpFb2dCa0VXQXdBQ2dESmZFM0dsWjErNll5N3phRHozYzBnbjN1czhEZUxjUy9RdXMxRVNRaTRpM2I0NkVXSFBJc0xNblNNSEJRRG1ONElnSU1Bc0FzU1NqUGQ0dHhFUjRibXRpd2d6ekw5SGdnQkVoaEFwc3F2N2xVbXJGYkJUKy9HYzR3S0xuM2VaUjhqWkgwbm4vUmVaQzdzN1JjTkpHbzNKVmJTdzBGODgvMkE3YmZ2Zzhqemx6dm1xVThGYzVIUWJaRzZ2dy9rV0x2TmlscjUvQ0tlUGxzVUhIeElSR1VSRU9uWmtwdFZ5cjYxUG1IVU9IM2pzdTRMc09jOUQzSXhKbUJHWWhUWnZuM2oya1Y5dnUyUWN2L0dQYi8xaHFrOHR2T21wLys1OE9uWG9rZDllZmNjVnp6NDRlZURyZ0FRSUlpS0JKVEFpRWlJWjgvUGRkL2VqQVFzKytPUHZCTzlZV0RnZ0FIYUdzRVM0Yytpd1NHQVVBWUZEZi9rUGZXM0owQmp1YXo5THVUQm9TTW01enZobnFkRm85UU5hZ1djKy9NdWpXOXNQN0JuQVd1RFpVN05raUpCRWVPNmlQbmZwd1Q3RWZ3aUFjLzhCQWtJSTN0cVlRM0JaNXRKMDlTOERBQUFrRXNnQkl3TGcrY3NweXNyeEh5ejU5dktQWHVFNUZyMUNBQVJFUWdRUW9UUTlhVXhjdHZYenhBc0lrQ1ZnUUJUcHZBWmF0QkVrQWdpSXhNTEdUTS9NbkRqUjQ0STZTQVJ6cnhublFqV2MzMzVnaW16d3Z0dXptYlJFTWdFVEd4SEJ1YWRiUGdvNjk2ZG5mbW4rL2VqOG5oZmVIa0VnaWFKNm51ZnQyWFo3dXAyZVhPdXV0ZGpzN0hTOVZ2ZmVDL0RDcjUzb3pEME41Nk81MHkwODE2c0FtRDhjNW43eTlOM1NBZ0ZFUkVMaDg3ZFB2dmJ5c2ZkZHQrMERQLzdtQjM2MnIyZXZxcHhkVDkrejZ5ZGZENVNpa0RWaTVGUW1yUnh6YjFpTUQ1QmQ5ZUh0NzNyUGxyZitNSFhabFZ0dmVPTGIvV2hEN2xxUnJUVk92bkh0by9zbVRyd3BBTlRablJBN2dTQ3drREhpOGNZSDcrdEhBenB1ZVBKK0Q4SFlxSE8vTkw5OUJBUkVRSnpmSHdUSWpuU3kyNjRNZFMrZ0FNTG96Z1VFUWlpUkY0WkVVbzRCUm1oUk1KckdaYi9lL3NyNzN4ckkxbG5FTzk4WWJ3eGs2d3R0QUJFaTRpekRFTEN4eXNZSWlUU0ZpR2pSaWlKYWp4dExnalB2WDVHSUFEanJldm4vWEhjaUF4Z3dpUUU0NThXalUyVXljODdQenRhaExxOEpYdHlMbnRSMHJpT2VqQUZqbHQxOGUzYTIzbXgyOTdST1FBQThRR3dncnE5UGljdzh6d1dGQWxHTnNpd1RrVnB0clFrWi91TEpCNmQ1NXNqUk4xM05BQzM4emhrQWdGYm9LbDQ3bnRzS1pOZmRlckUxRVVTQkhYN3dpZnQvZHN1bityYlJKVndFc2NQWmVpb3RSSVB4Mlpkc3F0Y0N3TTViTDdaSkRDdzNQbmIvVTdmMXVIa2h1QmwvREswQklnOEVBUE9KMWhDTWhkZ0dBTWhEN0pJUTlhdlA1ZXJINy9jK1dHdGFMcVBZQUpnQU1GLzRhKzZ3N2V3Y0pnMWNNaUdhR3ZaZVFPQmhmNEVyS1hrWUJJemRDTlhRaXpCSlVyL2wrQmJBdlFOcWdneThZbzIxMWlZSkVtRUlZR1BKVjN2R3RHWDNzdldSSk4wRkhQUGptQ3NFZmtzMkVVWGo0K1BKVE1MTkh2MFNFYUNvUm1PNXhmMm5TUzV6WTZMMUxsN2Eyc1Z4WEsvWGs4a0VEWXFJQ2NhZGRQTFdtbmFSSE1NNGpibmFJUHQxUW1JeXk2NHRSaUxQL3ZvRDMxaWY3ZDc0NUhkQUNGc05aeGhYN05uaXhBUUpSa3dPNGVySDcrOXRNNElFZGdYN2FJaE4zbVNQNGFvZmZMTzNXKzhnNENBbTVCa1ZEWStJaVNVZTRldCtsNGI1blJMQUpZTUI2bXcwU21QbENIajVIMDBqbzRNM0R5d2RkTkZFdWZXRHhrQzlEbUFBUWRKVjdRYUkxbTdnYzBqWjlPbG5Nc2I0Q3dJazRJcXVpNldVQ0VCcCtjN0JjMkpnNEVHTzhWaHJreVFKSnRqVVF0UEx6TXlxbjBxSVo2c3hVNFVOcGVBZ0JDYlkrV2pmbzhBZEIvYW5PVnVLY3lwMWl4NFFVazQ5R1ZOdS9rOFhrR3hqSElyS1RmbVFld1RzK2RZQnJ2M0p2UUFjZ1E5VWRMWmh5Wk5NU2xiR1VzTWRBc0xDcEdCMWJxT3phanFLWXdMLzV1WjA4L1JBZC91K0RsNnRRZ1FRQUN5c1l1SnNaMUwreHNVaXE4c2JaV1BETVJOUkQyWWJsOWdaQkNBdFhlaFNSSkRRNCtDNzlwTWtnVzBBbUFXS1ZqMDcwR09ndmcwdnJvS1BDQVFBOFliSDd1M3JoaExMTHNUdHRGMytSeVF5TVhJUXQrTW5YK3RoUzlCRXpPQjl3UjZGc1NWa0lkaHhvTmN6Sm4wQUF6NFU3OUlDaG9JSmR2QTcvMFl4ekNHZ1NObjFFS05NU0ppR2YvSXNFb1lXRThKczNRMWtGY2ppdG1EVmNqRW1BS2JzRXZJemxFczhWR1ZCVmptZWE2MGxvbldiYk16TTVWY0lvVUZibVJueDBtejZYRERCVm1zMWlmb1ppNE9QZGNhV0xKRHo3cXBIL3FWUG03am1vVzlKOEJNMUQxMytIck9zSFd5SW9KZVRQMFZBSW9uaTRsa1dIQ0VEOUxZYjhyb2YzUWRBbURrb0duRGczQUVIRkhQNDVyL3ZZUU9HVzhVdVJUMkZnQnU2aTJLTlNsNHdtSUNyTWM3U1Y4eFNqNDhBdzdPM2ZHSFFiYWtrWmdpaDZ3RzdjdW5IcTZtVE95YUsxakJjNmdHQ2wxT25ldGVvNVZXcTE3aGJDSkJNSmtIQzZxYmxjTUJBNjdPYXBRdVp5NEpJdHdQMDVWRU1uSEdhZDMxbXBpUnVZTk9uK1JVUGZyVlhqVGwwNjZjZ3RtNnNPQm9seEJnRWtYWSsyck8xeWN5ZXdQb1NvVENpUFhuK0pZYys4dGxlYlhvVURITUlLQUNFVmJrVlhuOGl4ZGs1QUlCUmVBUUt4Q0ZpSnR2QTRBMC82ZkZjNmE3TlozNnJGaUxJTXU1K0RnMXU1R2szdE1iR1c0QzBMVWx0SFpKUGJlQjNHUUFBREJvaVNtZFhOUnhjdlhRRldJdWpKUEVnbC9aODBCTUFBRVRZamlHdjZ2S1ZUazFsYVR1eVhXZEhXZ0hIWkZzZVRISHFBSTZJd0FqN3drZVdjZTJUOTRLQkVJcFhnUVFuaDI3L3U2eTVwU2ZiSFIzREhBSWlRb0JxalNDc3IxSy8zRkhwS0JVSnhBaEpsdkVWL1V4ZVZhaXl5U3A5UkZBNDIvcE1YTDFZZHAyYkZCcE5qS05RWXBiU2lJdHJzUVRKby93MWVhMnJIMFR2VGRTTFpUZTlscnVzQlZUdjhwQXBZK2VQOTRFVm42NHk4cVd4V3JNK1ZoZ3pkZVh3Vlh0TXNJSHpNamNqRWxJZ3VMWVhjeVhGTTBRSVVWRXN6QnlMdmVURng0NWN1blB0R3gwcHd4MENZdFNINDNQRHdIS1JCck4wUDl5d0VVa0kzdnQyRzVOa2tGMHFpQmdxR1FRU1JVd203U1pmdExDa3E3MVE5WU56cnFzNWMydG5ET1Y1dm5GSHc5ZVRUZXhVTkZWejNTWHVJWTZnU3Z2WUFoTkhXNnp0UzBhRklHZ0FrdFVQem1DUXlFYTdIdS9sZ3BWbmI5b0RBRkFpOWJva0VTQnhpanYyNzEvTEZxOS82ajVFbEt5NFF4RUZBN3J6anJ5bXhldTZOY3dSRWlMS1VML0FZaVVPQjZwVVlyYytZNUxKQ1NJSzF6M2VselQ2SlNXVlhIOUQxZ2JtYnVmUDJpcTlGbUZlLzQ3SkNBeUl1TFNLUFZXVllveloxTnBrdWJ2SUJnTloveTZiVnJHZmxiMlBlaDMrN3p5d0R4alFyZWxwSlVKakRmUjhIaFFaREE3THJLQVNINlpxZG56MXY3VmRUOThqNG95MVVEVGhra053ZWM3Q3ozNzBNNnZlM01nYTdrbGdPTklGNDh1K2Rvb0owcEVwbytJNHRUWTZlZDdGSURLUVcwYUxaQXkxcDJZRXVEUHdTckIwU3QzQ3IyUHhQVXh4M1kyRlh1OU9JVlZDSW12UmxIMlpKQUpFNVd2c0VoRVpDcTJNOGV6OTU2elROZ01zM2NYT1hsQmJ2RkZhZUE4NmtSNERBd0FCTUxBQk1TWUs2MzVMZzdHUnpKUHQ0bjJyQXBmT0JtOUNjTVlZN1B3bVRwZmVPRjJsZURGY05LaUNBSUJrTEpsdTF0Tll0RmJzek16TTJOaFl5UjhoSHMvaGFET09VKzRVcFJBUVFoSVdCR0FRRWNINTlGK0xpaG5QallFVWRnR2MzZ0ZKQUFnNjVXWWhJaXd6Z2lTTVpHLzZ5ZjZmOWpEUEtCSUk4OXBPeUdJSU1JYmdlOXUyRjI3Nm01MVAzSXVVTUR0WmNVOFhhMnJ2eW4wS1Z6OSsvNkZiVjFPdFJEZ2htNFcwK0xkZ1RaeFRZSjJNc1NyREhBSmlaOUhmb0pzeEtJaFladGFaSU9ac3dWUnhxS1V2aUpEREszOTIyNFd2SFhwekVOdHZUalJiSjZlbFV3VldCRHFsTGM4SUh4Yi8xaGF2NlJHUWdsV1ZuYkQyOU0vTVZmQUVGQzY4bVFZQVJrbmlMbWFSa3lGMkhneENPS1BMR2M5dTkvSUxoL21zQlV2RlFibUFvTXd2UTE3SStzblFDUWVBZ2t2cVpXT0xIcktSQVVJSkc2bERuV3dTZkRhWDJwQUZGL1liT1dlbW44VTdxY0JjcW1CMm5sT0g5Y2pZNHF0SnJWNmJuWjN0NnRicjBkMjdiLzNSdC8xY1BrM3BsSENlYTZPQUFNeHN1dUIzMTM3OHJKKzYvTG52ajAyOWRlWnV1TXdMV3Z5aElBQkxQYWVMcDhaZTJqNFRNbDg0Rk11eGFWSTk1TDA4ZVFvSHNoR1hTd2U5Z3J3MU05YWNkTDdIazN3UXJVYzJrUlZmTUQ0YlFpNUpzcnEwU2J1ZTNzOCtGWm5nb3FWQUJEaHo2a1NjMUE1LzdJdXIySkFhNWhBUUFNMUlGd2t1bFJjbUFBQ04xaGlXSjlqMTArOGUzM0xSb0JyUTJEUytucHNUNzB1djg2RGM1WEZVTmdyY1FKMWUvVVhJamp0VjBUYktlMktzdGVNOVNMYmlUclhBRWdwQUNHVnVNNkQ3Zk5xUGYyVEZBYjdsbnUzbG5aOVlYUi8vWGZ2M0h4L0xveGxxT1lsZGhtUEp5bzkzczZld1JPeGIwdlVIOWdVUWw3YW9VYkRkUWxpUDJ0bHN6M05pUFB2aFQrMTQ4cjQyUUlONTViT0tHSXJBZThmWFBQUXZMM3o4UDNXMUZXUW1Xd3V6TTJDS2NnRXkrNHdBVnBsNFhBM3pWRGtrR3VVYXdZamx4c0VyTkpWci9YQndrKys4ZXUzajN4bDBROVlEV2d1SXpPSjkwYVVYY2RTblQ1d3B6MTA3SzVVcmtTSkNRbDVGUitBR2Y3T2ppVWJVcUJFaGk3Z1NhNGxFeFBSMi9pamk4bjlXNVlFOWUzNXc1NmVuWE1BYVFJa2VjVUVJdm1lMzBDeHNBQXZqdjVEbFdhdkVXeDNZUzdqbXNXLzFxSFZ6Y29CYUNOWVVCNzQreXlIM1dCVEduZVc2SDk4ZnZDY2Z1T2dIRFpnWGZ2aHErNVI3NmM0dmRiVUp0V0NZSXlRa01wVkprYi8raUVwZHloRXR4WlhMdk5wdkFVWEVuSHp6Sk96ZE8raTJyQWRFRkFZcW1yb2VSZGFNOGlMNkplSTRxaWRsYTFyNG5GRlkybDFVOUFJQXdHRVlqRUZyZy9kSTFydUNjWmQ2VWlQQzJkblo5V25ZNnZ6NlAzNkJJNXBHa0tMRnlLRjNPWjZ1ZWVpL2lIQ1E0Z1d3RkRVQkhMU0wycFlRVU9tSndLVzk5S0ZQeDRCQkJMbG90RHFKS0ltRWNHYzMyUk5GQWdpbWFjRnhoeUxpL2FWWFhmRDJLMVBsbjF5ZFpaaFA5OVpHVkxWS1hPdElCTXIwTUJoRHlMMU1JcnBoNUsxWER4L1pmdm1vcEJKRkJDazZIQWlwTjNWdmgwclpQY1RHaE41SnQwTnZ3M0tLaXV0MURDaEZzL0tOb2VDOVZLQ0U4Y3FPWExZVGF4T2hhS0UzeW1xS2F5L1BXdkdlUzl5RFVlWVRXeThjNUNWRWd5QStYUE5RanpzQ24vbndad0VBYkhGK0gyK0VFS1V3V0p4M3pZKy9KU0FzRGd1N2J4Q0ZaTlA1OVJHNWplK1RZVG45TElkekc5Y2I2S3Arcmhrc0ViSFJTT1FGUElza2R1ZkgvcVEyTmlyaEx4S1dtYW1GaUFDanVEK2Ntd1dBRUVwTkttWVRDNkZ6b3pXNTlqU0d5QlVQS1loeFV2bGNWQ2N2M2ZuS2gvN1h3dnFCWFdkUk9oZVJtVTNic1Y0ckRuMHliemVuUkNoWS9GYjd0QVhlUUQ4Q2JyTEN2bkN1UFNHS2R3SjQ5VU5mTC9Pc3lDeUloUlVMdlBjU25JQThkL3ZkSmR1cmxqWE1JYUNJblhvZHhJL3lncEFTU05Kc1JOOGlBcDQ4dno3b1ZxeVQwcE9qWm9mN3RMQUt3WHQycGNKaWlvZzNjdDNrTlRKc2ZCVHlyQ0RnQ0psSW1keHlGVkNpL3FIMFpEYm5oWDk0OXVYci85Sm54YnNaZThsUElWb1FFaWxLbDRpTkdwaWNTd3d1ZCt2NW16OEZBb1hMTlFCQTZyRjRrQklyTTYvNzJmMUFhSHlRb3JPVk1kWUp3UWJaaTZwc21NLzEyU2t3a3ltWG1MVTZsQkNoekxVSVJlTDZ5TTBGN0hCWkppalgvTGpIb3lRYlhIUFFEYWdjOXQ1WVc3SUtIQW9RWVdIS2pLR0VFK2hEY1lZMkZOa0lDWFFldmVqd3Z4WmZJSkdrRnlGL2JlYkVyaWYybVhyQlFoQ2Z1eUFjY25qMmxpOHdlQ3l4WU1YbEdScTg5cEZlVmdxWlF5Z2MyQlh2Nm9LQkluUDlZd1dWT2NWNWltd29tcjVvZ0thOUJLUVhidXR1b2JGYWFwaER3TU43OXBnNjJaNVd5OTVBbUV0Vm96VUV0dGU1b3pZS2pDMFE0ZWptamp5WEViMXJPcGU0WGdjQVU2N3VpSWtzd1dwU253d0hZWUdpaGVmY3lmRlhlYS92K0ZYaDZwYlFpNDZvOXgyNForekl5eUV2dnNlSUtTSnI1b0pPQkI5T3dXekIwdUI0dkVFUW9lMzlHLzdDaHo4TElpYUpzR2hZbnhxeE5iSGsyYTd2M1hPdXg5enc4MzhGSkc0VkoxbDBnY1diWEhzQWUyR1lRMEFBc01iYWJwTFhEeFdSMGJ3SWRZWEVRREVtQmUwQUFDQUFTVVJCVk9XbkpmVkdpVmNwQU90ZllHMURJQkV4cHV5Yll3d2dWbis2VzErd2NGRjRaMHJtcXhxb1RVZG1BYjRzUlpYV0krekJSVFEyVVFZY1NneXE1c0VUMFM4LzhVVUFZTWtNUjF6VXh5R0lwa2JvL1FyaDE2cVJOVUtsS3NUNDBLYlV5N215VDRnY203alFSckhZb2lWclpIL3hkcU9kbTkvdjFsbUFQVERrSVNBaFJxUGF4eU1nWlM3N0hJd2YxWTVTQUpEY2lSLytCVU56TndORkFZekxjeDlDOVMvUDZ3L2pHQkNwZE1ZYzlnd1lSS2I3MnFvS0VpaVJLMlFqaElDYmp6WjJQdnBOR2l1WUswemNpNndUTEdOSnMzZ2hpR2REdE5EdmVIajNmMGF5dGtUM0h2dWMyazZpM3FkSWUvN216MEZrMHZHa3VCRnh4T1BOZ0xMaisxOWIrczN0cjczd3l2dHVkZTNpQkV6QitmZHN6by9NanVqa3BaNGI4aDR5QTVpNnRuaUh5VWp1TWFXNkllSXdEVENTYnc4QWNKNE5wRkx3ZWhLUnVkcHJSUy9VaDBEZDFBZ2VLWWpJek43N3VNUUVMTElFMEFJWndadXI0cUtjMWQrN2JuajAzbkRrRlNKd2hTbCtVSVRYOUhJKytPTjdIWXR2dDZIb0lvVU15SERvanI5ZitBb1JpaUNtTExXVkFsRzJCcHRHOHI0c1ZQZUpUYzNteExyQ0ZOa2lxV093MGRsTjNYWGdPMVBIM3J6dXRVT2hhUEdOQVdMa2lkakRuayt2cWRGcTNwQ0hnRCs5ZWMvT1IvZFpZNGUvbjJjSkZDeVY1amVZcEc3Ym96aDVIUUJBMlBld3ZsTVgyNTJlUGgxNmRycm9saDIxWDN5eFBOZXdmdWN4Qzk5ZC9HeGt4Vmh4QVNOVG9nSXZCTStOOFRVdGtaYnMzRk41R0lBWGJrdHc0YStWTFA4QWhJVm5Ja0FFb3ZrU0VTWHpvYThLSXBhTS8rYndHSVBMc2l4SjFscnNhd05CQUNnYVBLWHVxNFBjL292OUlSZ1JERjVZUExNZ0VBb0JBSk1JQmc1Q0xJYVpTQVJSa0FSUVNCZ0ZBZ3VqQkNRMEJwbHdMcWt4Q3dvTEFSbGtJbVJHbnI5ZkNzSUdiT2JUd3A0NTUralVpYkxKdzVmRmdTTkRvVEQrYzRGU2xEUFBWYy9kZHZmVjM3L1BORWlLRWpuNVBJTW8zdkhJZllmdjZISHdkUGlxUGR0ZmUrSGxQNzFqNTQvMnJWelBneU5iTStqemNNMUQzM3JoNDU5ZCtMcGdtRHg1UEEreldGdnBTQkVmU09Ua3RzdGUyWEY3cnhxdmhqd0VCQUEwaHBBQ0RETEdrVFJISDd3WEVrWWlCRE4vSVZ4OHJzU3ovZ1dBc3hlOUl5eU0zYzlkNlRvcGxBd2hnQUJLWndZZ0NQb3dGaWQ1S0g3Vk5ySUp6Vnl5cVRIemg3R1RtMmJPaitzQThFN2VCb0FrbXdXQWVudmIwcDhhbXptN2F0YkxsNTlZL09uNDlQc1dmN3IxNk5HRmgwMU9iZmFjaTFEQXdDUWl3WVVRQWdzeWpCZm5HdTB0MDZ3WExtRHNMVGtwa0FCZ0FOY0dvcm5JckREK0ExaitZUXVQV2ZqdXdnY2lnSUpvbUJrOFl0RWtHd0FnNExUVnJqVzZqZ0xGaTRBQWdvamd1Zlk2NmxTa2hyWEhmNTBuOHdBZzgyVnBSY0FZQ0VGYUxXdzB1bXgrV2NZWUVSWUpSQ1c2elFsOExvYk1TUFdxRWtBbk1sdUJOWkZJRndmZEhULy83KzEyR3RYQjVRa1JjSjZ4QlJOaVloK01SUUVtaHhaQ3k4WW1CUUVtQXdBSUltQUJNN0NBTFJFeURCS2pCV2dMR2dQZ0pCREV3QjVqUThHTHNjR2pRWWRnZ2tocUF0bUMyRjJ5L05EUDN0ajI3dFdYL041eFlKOFhJQmVnNlBCa3RCQUxMVjBFd1RaL2kyb1habjdGZlF6SEVwL2FMa3UxbGZYV1JWZGY5cXNmbWJqT29XQXhoL2NPUWd4MCtqeTI4OEEraG9BVU1DcDR0MjJjSEx6cFV4ZisvcmtldEZqTkcvNFFrR01MdmlmTDlrdnhieDVQVEZ4TGtvVGltRENQYTZldzdSQVpnRkFFRUFVRUJHRnA5akNadTd6Ti83dk05VjQ2ZDlqWStTWWlMQVFRZ2pnMy9ROEFXQ3laakgxU0lpSE9VeCs2ODY1ZjdNK25ZcDVNeDN5UzJRQUFNUm52c2tBbWRqYVkxSVF6SWpQcnZiZldMa3A3TWRQTW16UFI3TmpjUUlCMVk4NU9SMzd1ekpoMGVrS3liS2FaYnpyUlRPUFU1cFk3RTlOa2Z0SThna2dTM21pY3Y3bWRRNTU1NXdGd3ZGL1g4Z1ZpYUhiemhXVjZ5SHFGRFZOR01Ha2dHVnVmTFpZLzdSc2lwSzduYVhVU2ZLQWdDNU1oTU9mdUordnB1T2d5TzNlZXMrbmpRRFlpaHBBRElJdFFpVTFFRktIRmtWcGhVenpiQUFDQm9mUU11cnYyN3o4MjFhNVAxdDgrZmh4cHZyL05nK3YwZTNYZTJzNkpoL0taenNua2RJcUQrUThJT3ZjZnM1M0g4OXk1SzBBV0NGeG5OckRQQVNBSUFEQVFVSmsxeXdIZmUvWFczeDA2V3ZLMUxHV3R5WDFZTWpTNkRNT0dEVDk3NTkrZDNZUVppamFuQ0tad3FtOFNBWHUzNitGN0RuN3NpNnR0N3prZ2J2bnBkMzF3SEtTZ0lsMXNFWmhGcnZuQk4xNzQ4ODhEZ0RBakVhT3NmS0l5U0MzT0x2NzFZNis5LzdiZXRuM0VqVUFJYU5BMUxiM1pvcjVsditOVHN6SGJoaGhMeHNXVHpWclVjcmtBZTR4TThCT20vdWlmZjY1UG0rNkpCNjdhTStnbUFBRHMyTCsvVVhQdEhNa0NJcEl4dGkxQTdKTCtGbnArK2VwUFhQcjczLysrcjl0WWhKRTVZVnZKUTYvV2JQcTh1d3hCSXZQcmpnelFvSmVYNWNCeDNOOWMzOGJFM2p2aHZMQ0xDQURRb3ZmZW1KSHFDSlRpRGw2aThqVTFaaWZ4dk9QbVYvWlk4V3FKZFJmRnlmaWtYWFdCc3J2MjcvOURtcnF4MkllaVdYUk9tQUx4TXUvQTRUMTdybnZzbThFTFJBVnZPL3UyWmVadUN4aVdjL0NtdjduMjhYdmp5T2JCcjN4M0ZDS2hYRTV0dXhSRXJuM2lXeEpZZklEQ0tpeUMrUW5YZ04vQm45N2V5M2FQdkNwZWgzb3IxQXhONVZpclF6L0dnalBYYUljbi84TS92Ty9KNzlXT0hRbk1rYVhjaDBZVS8vdWl1UTZxak1ON3pvaEVkejI4RDFBWXlZaHhVSEJhV1l2YkgzMzBqUXZYYndDYURiTlU4Y0RMMDV3czJmSnozUllXR2xjbXk1dDBQOGxzTlZ1UmdHVHlFT0lTRmZlc3RRREE1ZXJMRFFFQndSSjFJTXAzdWdjTFJ5OHg2Q3NYL3dFQU9pbS9TSHlwNDJQK2toTzFYNW9wT2xlcWxIa0dTUUkvZDQ2dUJHRVJCRWs5MWxZNnIwZ1NoVXdXZWtCN3poamlFR0lpdjJKOFQ0ald3dSt2M0wzdGR3ZkJCN1JHaWhZMUc3TFAvdnRMMnk3Zi9Nb24vL2VlTmxsVjhVclVZNGV2MnZQaGg3ODcyOEIrRk1taDZaYkVlT1hqLyszNHRzdCtjOU5mOWZ6NVIxbzdGMEt4U0htZ0J2VnZvSFo2L0Rkd2VQME9CQUZZdzFXamp3UWwrQkJGWFhTV013c1ZWOUJhVDVqbmVSY3JObFlsaW1vKzVMRmhnRkp4U1hDQitqazhYU25TNjZKZFdSMmdNTXZNSUdEcXZERTJyRDQyelUxNGFic3ZuRmNxUG1DK1VpbTI1M2JmZmYxajM2YllCaWg0b3lSNHNmYnFIOTUvNktPZldrMkxWL1RNelorNTdrZmY1SndETzVPc2RBeDZnOWMrL00vdHlmTkJBcVpCaWdib0hOTjdydG4rNmd0djliUzlDbUFVUWtBQVNEeTk2NC80bTIwc3ZaNE5HNGd3cFZvNC91NFRKeTc5Zi85RlVBU0NCenI0eVY1UHRoaHFkKzNmZjhLMkEvbVp5TEZBenN3Z2VTU3hBMlRNakRQVXg5RzlnLy9mbXhlK2I1Mm01VUVYM1IvcnF0VnFXV01MRStHZVJZUkRRTk9uU2ViZEV4Rm1Yb2RJeTVDSVdCWXVzK2plUkFiT3ZaNTd5UFRoVlZiMGpXTWtaRDc0c2MrdjdzZXZlK0xyMDhGakFDak9oR0xaTXExWTdONFlZOG1tMlhSWU1mbWZOQ0xKSTB2OWVrc2xpQkRVYW1NdUZNd25vVGhxSHZzajEycGNMN3BoRTBMdzR4ZE13Rjd0QXV5OWtRZ0J6NXZHMTdia1FxYm5aeFBjTXQ0Q3dIZGFFVVVFTWVKc1d3d0Z1Zm4vK21ja3NDd21VQjdpdHkrNi9PVG16VWV2dkxLQzEvOWJIci9mZUVZUm1pLzFHQkNETUFlaWdCU1FoTkV5V2c2RUlWRHdhQXhSWkNtS2lBTnhjTjdsSEhJT0ZDSUtNWVVZQUNqeGxIaVRuTDRyRFpueHpCd0ViRUFtUVJSZ0VHRUliL2lwZDJXVEw5ZFAxSVZiTnBKTUlIaE95RzJwQVlEcGM5TEN5M2R0Zi9uZ3FOOWZSaENGRU9yTjdrSnRaaEhoNm9TQXpMdytFKzhRazhCc1N2ZUFNZ1BLR2xjclZ6UVNHazNXQ3h0Y1V5NEJzV2g4d09KWkZJdzRkYXo5NmwzL3NGSjd5TG84TXhnWGRnVFdMRHZ2ZGgyNDUrRHUzdmRUUExmNzdsMC8vUTZ6TjRKaHhmdkpZR2xxdWoyUjFGZCs4Umk0azZyK0Y3ZitiVyticWpwR0lnUjhZTStlOS96eVI1dmVlVU9nb0p6aTZzajVqUndnQitFalBrWWhORUJBem9aR0dyV2c0VEI2ODgyanQ5enkzaWYreC9qLytDZENRaVJDaExOejljcHljNm82UjVISVhCNk51Vy9qWEhwVjdId0VnSUtBSWdJUVdFVEVJbHFreUpqSDdpam84QmVISWFJazgyQk1hS2VtWHJNQXVROUFBYmhPa2dPQWVESTFUd0JwcUZuSnhETTE2Z0RBWkt4M0Naa1pud01BRzJmenVlNDB6bXcwY1VhQ0FKTUVmN1RwTjU5SWNpTm9HQUlCTWdvS3RveC9xWGJFZVhaYkp3Q0F4aUtBL2s3cVg2dzVIcTE2UXZlcWxKZ3Z2NzdZZTRvSVZwTTRWaW8xRGt4RTY1YUVqeEJGUkNRUUZaOUZpUURhT1pSWW9YOE9veHYvbFZ4OHZaNVF4S0Nnd0hOLy9vWFZQY01WRDM3VmUyZHRIYWhvSVlqM2h4NTlmZHVsbTFkKzJFOXYzblBEZ1crWDJVM1N2QlVia0hKekdGYmg0RTEvZTkwVDkyTHhBbVdZMkhaKzhSS2ZwR1l6NTZ2MSt4OHFJeEVDQWtEYTJQejhMYmRlL2VnK0xMUCtmclhvZ2dtL2FOVkpPQ1VpTXhEUEV0TFYvOC8vU1dTUURBZ1NnaERTMlRubGNibVVxZ2pBblNoUEZwMEg1enM1RUVFNktSWlFCQWlRRVVHOGlBR0lyS0ZXdUd2Ly9nZjJyTFRnTjZDWU5oMXRDQUJBTFptLzJIUjJETGNvV0VrQUFPcCtmdjdUZkhhR1RnOWRjKzY2NnphZnpwSTZ1elRRZVZjTElIRjE2TXlJNjd4bkM5OGNTRmVTdE5LWmFRZDc5NjViRkRpWEdhZ3lBZ2NrUkphNDJmVVVPb1NLRFd5dlkvb1ZSR1F1RmYvTlNlckFYdkljK3p4VmNjaHdKK2xrWlVUV1lyQUJNck9HR1lwSmxMVGJyV1lEb1NnR2lpUzY0cG9MWG5yaFNQR1RDamgyWVNvemt5dmRQMk10WVEvVEtjUCsvYkRpcFdIVmtJQ0RtQkRDaXErdU1QNGpsbmpHK1poKzhaSFA5TFNCNnJSUkNRSGZ2dVNxUzUvNU43S3hyR09PYURQUmhJbm13cWN5bnhuMzNDMVk5bXE2UW1na1p6N3h3bGNvQS9ESFQxMlJubmRrYTBFamhTR3RqV3B0RUFEeDhzcWhZK3U3eVFwTkMvUGVJNkVBMkc1V2daeUd2U2lRMmp2cnZONENrVVNZMlpzVlVpRXVJSUFnNWZQaGJWUzk3dU91MUZKcUt3Z2NDd2FpNU9lMy92V3FuOGNRVFRZblhHRlBIRE1GbUp5c2w3bEJmZnFqbjlueFAvOHBpdXVGWnhlWHBpK2Qyckt0N3Q0dTE5cHVQWFB6NTY3NzBUNncwUnAvZTRKUlp2T2t1eXhWcWp2RGZqNWFnRmcvOGViczlQRSsxVW1zb3NpK3VLUDRVV0xSNWxWTXVMQStUSzErK2RVWERMb1ZBeEM4aHl3akFHR3hxeCtnck5nVnVpaTdSRzhoSXJNSHdkeVZPNnRRQklqaXo3empxdFE3dUhaUy9JbzI1TXJvd0lTMTk3NVpiODRHQVBQMGgxY2YvMTMxMkRmUTJqS1pFV05NMkhTUlExRVFaOTBwYVJlVTZLQ3gyclhudFd1Mm4zc2VHUkV3dlBxYlhXRnlmaW9nLyt5T1NtZlYzZWhHSmdRRWNIbTdVVy9ZTXBXZGhnTmhkSFQyYUdFdm9BZzFScVV6ZUJtUkhkczZ0cTV6QVFmVUJTZ2l6dVh0ZGp0TlU1aWRKV1pHcE5CZEZwZ3FZd0FUMVdSOUU0aFltNEJ3WEhKc3dRQ0FBSkVzdnFoTFh6S1dWbHdYVWVDQStzelpCOGtEcEk3RXh0U0ltcTZlNThmR1pkc0pjL0RtTlkyZkdxUUFrcGE1NzNhQmtaKytvK3lpWThkWkxhNUZKZnFrMGNoa25GLzF5TCtVZk9adVBYdkxaeEVRcUNaK2xjY2pJUkhWbVF2Q1diVkdJM1R0ZituT0w5M3d4TGVNcFhTMkpTdG1MUm9hbVBuRFJaVS9HRU03eXdzenl3OHJJWTVEMzh2UUxkbHF3ZmZ6ckJXakJaRXNtNXVFbWNpWjQ4ZUxQMTMyQTJiWUJBQ2I1cjhnSWd3QWlFak1IdEU2UjJNOVNJVlQ0bHJ1UmFqZkk4YWQ0VWNCUURMcjNNbGtnQmxNbnJ0YVVZSmZBSUNsMmFTNWFxdUQxcVRrT0hENW9ua29nc0xGQlVkYUxyS1JjSlFsWURnaFpvWVFFZVR6U1ZJa001Z0U2ek1BeUhJREFGU3ZBWUFKbm9JM29XWWFmaVk0NHh0SmZkYmJXZGMyd2dER0FBdmJGRU10SDIvL2RQZGFFLzd2Mkw5ZjhqU3BrU3ZxTEkyRVdqaU5JZDYwN3g5UHZqb0ZPd0MyQXR3T2dIdlA5U012M2ZtbDZ3N3M0eEtIR2dmSEF0VFhveEl0Kzl3YVdrVU1HRkpQRm95SmYzSGJLblB1cUpKR0tBUUVBR1BKK1p5aVdoaTIwWmRsU0xtVGJLaGsydFgxSVFITzQ0dmJabXJRRFRrYkI4alJHekJrZ1FNUVNWaWNodmlzV0hEcEJ5SVFBK1FSeEhtbkxpOGlkckZ3b2JReXdSYXpNTHNvV3IvNksrc000NFpMMDVvd2xNOWU1SndjTzRibm5UZjNhV1htaHE2ZFFIRzg1cnd2SDZWanVYZkhrZ2xCd0xJSlZqQXdBQUY2QkdKaFFna0lsdEV6STBrUUltRTBFZ0pSWjJXV0JldWRrd2dqc1psbklJNml1anQ0YSsvVHBrVGozaDJ2MGJaVzRaTGN0SjFhb1ZPeDNidzVQdmtxd0dHQUw4TmxCN2MwSDdrSGdBZ05vakZJeit3K1k2bUVFTEFJdEQyc25HODVpYXdUN3VmSi85bGIvdWJhSDM0elpNWTBRdWptcm94RkRNVWN3dk8zZjdwL3pWTWRveFVDUHZXaFQxLy8wNjlUcmUybkxhNllRbk00ekd5K3NMTUFkWVhIR0RJakd3V2FPSjcyUjhkOTBXRDV1bk11SnpMSm9yVkVHMWVldW9MSzhSdWZGZUVvOHQ3SHR0d1pkWG9hYXV1WDltZzlDUlF2ZUJjSlBWKzZIVmdNOHRPNzcrN3g4L1ljaGNhN2l2SW1keDVvQ1lqR2ZRN2owZFczWEFnQThJTzViM1hpN0JqSlNYN0ZnMTk5NmM0dkxmelVjN2ZkdmZQaHIwa3RscUxRbVlNSEUxMTc0RHZQNys1WHlqMXhSRFdoT0FxdWk3a09Wb3dZb1RXVVhWSGxqZEJjd0E2TXZFOHQwUWdNQkRPOGRPTmZ3cU9QcnZ3b1E3YWVERU9vc1FySWNPeUNFNC91M2ozb2hwd055WlFmS1J1c3docXBSRkt5UTNyanducGRFTXZHZndDd2FRc1lrclF2YVVvSERGR2tJT0wzbmtQNW9zbmw1Z0xHeEQydVROY0gxeHk0RDJvaDVLWFd1R0pzd1Zxc0paakVXRXNXLzZGYVRBbEtETzEwTnE2ZDNiK09nUFprV3J6d3NSWWh4SWg5dlA5LzRlT2ZOVFVUUXFEeWFiQkNNSUNFK01KSDF6cm1yc29ZdVJEdzRBMWZoQkFEQ3F4WWIyY1lJTUJYdm5KUlZqQ2QxcXhoTmVpR0ppRThlL052MG5vbFV3N3cyVWtqcTBsRXZDKzZ2eGNaaGJPTUlXSm1WM1pwY09kbit0VFBNZGhPZmNTaWRPSE0zTlZjUUNveFRKSG5wbUlaaXBhQktNTFc5U2czYlpBd3Zua3pMZG1MS1BYY3RGR0pIT2xFTGpELzJmZS8zWlAyTE92Z2paOUdFY0t5dXpwQ25MdEF3elE5dHRxcWZzejB3L08zZlFhUmJEeTI2c1ZLR3dJejM3Vmp4OWFqUjFkK21QVHpMckM2aUd6U2ZQOExsNzExOGZvbUJTeUpOOGI4TUJIQm9wTzFBVE5VNngzT0FSRkRDSVNRbDB3N2xTUUFLTzJTT2FxNmVnTUQ5S2NNVWhsSVZIaFpFUkVwdmM2MzVGekFLRFlWdjV6dGV2b2VKR2NNOTdEWUNZdFFaSzk1Nmx1THYzandrMS9FeEdLSitlNWV2TThwNm5NbkFDSnhDSkFWNytvRW1KN01UNXozbm9PYUMzcTlWUHFZNlI4eXNVQTdTWVp6T2s2SGlCemR1dlhpaVltVkh4WVlnbW12VDVPcUk3SjJzcjF0ODZrdEt5eXZHNlFOY2x4aTBaQWZBQWdSMFVoTTY0bmpHSVRqOGlzZ3ZVQkV5NVdGWEZiNXVHR1E0eHRraUlwNnViQ2I4amhjdWdRaFZhcFk0VkxlbW9ZTHJwZFpUa0lJWUJDWDlMRWhpQThBcm1CUFFHc2FkWUErOXdJOGMvTm5UNTQzRTlXTEk4MFE2TmQvOWZsMmJiS3Y3VkdMYlpCTFRhODljL01lTWl6Uk5QbU4wTm15V29WZGdBQVFCRncyektId3NrVGs3YTJ2MVh3UHNxSjB2ZW1TajZ2MjVheERTdVFCTnNaaXYwWThLOGVnQ0pqaXdmR08ySUFoc0xDcTZzeFZKQ0pSYkswcHVLd0VrZklkWVV5VWw4bTJVM25zQk5NNkpMMThMWVJvVE1JaHZQK0pyeS8rK3NFUC81MXpUa3p4dGx6STBNRE9BMzBjQ3dhQWJNd2Z2T25Wd29lRm5DOTk5cUZqNzcrdXI0MVJpNDFvQ0FnQVlITE9FalIxeVljek1Xdm5KSnNVelFXVU5TUnczNkRJbW1jKytOdVRtMDRPWkNGSWlhZ0pnS2hhdFhmUFJZcm4vcU0xYUV6NWdiOE5EYU82c05oUXJsdUZrRE1uZ2Nzbms2azRuK1kyTWxGUmxHTXNZbEdZdUlDTDFoc3RxUEtjbGwyUDN5dE1hYXYzNHkyU1ozbHdac21FV3hacHo1eWd3am1Yc1VVeDB1ZjVvMjllUEhYNW9lM0ZqelBTT1BuR3hqajFEWXZSRFFFUDN2QkY0QnFIdEwvcE1RZUhSUURndFZPblZuNllZQUNZV1pjV1ZRTVJCblBwYjdhLytlN2pBMnBCdVdCb0k1d0dFYkJ3TGlCMDBnY3lTOUhkeUhBZ0ViRzJaRWNnSlJFYUFqc2szWUFza2s3TkZqN014amJ1ZGNkZU1HbVZRMEFCTUdSTXZmZVpLSmc1WVN0TFZsajdQSzNYRzJWVyt6RTRGdGp4OEwwOWI5dHB1SGZzVkhGbVVESk93NzkxTnB6UlQwblAzdnJYSUN3SVE5b1JpQThjUHZ6NlJSZXQvQ0RCRUVacFVUQUNBZkxtZHlZR05RdFFTdlVEOHFEcVluVUZEV0xKVGhydndWcGhIdnJ1UUt6VkJOR1dUeERqdVE5cmVBZHdJVTJucGtTWVM1UnBic1pOVTM1dVFCY1ZjaXVhNUdIWDAvY0lla2JvWXZ5N0cxbVdMcDFjK2RLZFg3TEdocng0MTBLQ0VOQ1l3UitZeUxiazZoL1ZLeU4wN1YvVzg3cy9mL1VQdndZSTBzNnhEN2RvQTRRRThPVXZ2LzdpaXlzL1RJYTBFM1JaSm9waUVTOXc4T1orWlVNdG8vZ2tWOUZyMmRtUXNPeEtqemdHWmtCazVqekxoVm40ZEMyeFpic1NsK2Eyblh2WU11c25CQURJZTJReFBqOWRkV05BaUVpRVJRS1ZxVWh1NmZqSjFwWk5QYXllTW9ENDcvang0NEhJZU5jb1duOTJhblkyaWFJa0xudXlSUkhjNExjTjZDT3FPVDh0MEovRnR5YUtscDJPSVVHWUFEMkxYZWtrTDRiaVdBUjQxOVAzSEx5aDl3VlJ1b0tqM1MyMS92VHRoa01mL1h2d2pCSDQ2ZUZhR050SkxYZmxsWU51UjFXUU1RREdBNWJvcCtpbnN0ZXpEWERaSXdOZDlPck45UmVpZ0NBVXhIOUx2Mzc2MDZVUEp3SVFOcEh4T1RjYUErOW9SRVNSQUlKY2JxTHRsazFiQ2g1QmxaNFlrSjZjcVVWeDVweXBGeThzODNsZVB2NkRTci91c3RpenlSclNuL2lQREZFOVhwb2RFQUNlMjMwM1daWVNnenpDWXBzdStNRlBTdVZCSDd5alp0UjdBVHNPZmV4L3UrYXhmNnB2SlhmQ0ZaUlczRUFRTG4veWV5OS82Sk9EYmtkVklORDAySG5qTThlZXV2R3ZCOWtPS1JNRmJvenpJQ0o2enlCaVNvZlZSQmgzRXdHVUZnRkFic2ZpcUJKOStjYkUzbVVRY29nYmEzODJBcEl1OHFpVUZVTG9WSlJldGllQWdZVkZaSzQzdHBPcVVqci9NQXNDb0FBaUlSSVNaSDV5Zkp6S0RIOWJPOU5xalRWNjhMYWNCU3VaZy95YXg3N0ZJYVI1RzRwR21ZSm55d0tlQVlXUk9tODdvdWwwZXlNaE1nT0JJSGEraUNpQ0ZwUEllQmZPVmUrVUlFcmFmc2FLWGFtN1hpTE0wNmFCQWVmSno0V3IrQ3NjYXZxR3o0bWE2RTdFL3NTa254MlN2a0EwMUR6K1p2SHFxckF4b28wMVFvQm5QL1MzTXhQYm5oMXMvRGVNcW5QalhwSDRidzU3UUpQMWFCRk1tV1UzM1NJaVBuZnRGZ0lTT2owYVQzUEQ3WFAvSUxBZ0lnQUlBR0V5MlN3VC80VVE2bkdjaDRDOW5oVm5RZzlIMG5zSkVSaE1ZZndIQUJUQXZmUGFiSFBpK1R2KzA2SGpFelBlZExyTTU3N05JamgzOHloejk1RUlCT0lDMmVnWE4zNTYyZWMwa1pjMGlVb2NGemJQZ3NlckgvbEcrWmZXRDlWZDBUT2t0QmR3enNFYnZyaGovMzZ1VC9uZ1RNdGdvMHJYa2xVUmxqSlhEV1FFSjFKY1RHZ0RFeTh6bTdkdis4TnpiMTJ5YzlCdEdVSlJqd3BlRFJtYk5GM1dTcWdxOGZHeVZsNjJZc0V1dmtTcy9XclJTbk5qNmJ5aStZS3JFRXhhd1Y3QUt4NzhhdkI1Rk1lRmtZMEVIcXVQdFNlM0g1MjhBRjU4RVhic2VPWHdZZGl6WjQwTk9IakRGMjk0L0g0TUJLYWdDUzRpektSMEJwNitFQ1FjdlNSbGcxVzVZMmFBRHUvWjQwM2JrbVZDM1BoWkd0Q1V1czhXNXZJNXVqWWtFU0Jvbm5qcjdmZGNweG1uZXM0WURONW5KYW8valNDTElzYUdrbWtDVjlUTkJNY0tIODZXUXRmWCtQSVpCQ3NYUFRTU0pFMWJJU3V1MTBkb1V0ZXVqMjg1ZXRWdXVPb3F1T3FxdGNkL0hjamswQlhXWnlORWF4RXQ3M3I2bnA1c2R4VnM2Tk9hYVhWT0ZUNVpETUpMZDM3cFY1LzRCMnNpSUxUZWJPaGtNU0pRV0s4ZEFBQWg4TkJldjlFTENDRENvZHZ2SG5SYmhoTWlCaDlJZEFCbkdSZzNCY1NZdFk0RnN6QWlsbzRDQ2FDS3ZmcFRzN014RVhSWnhrMEFBWW9IZWRsRVhNVTZoSGoreEFRa0pVYUJ2UmZobjk3Y203QnZzWi9mOWpkaktabkdlT0VqQmRrMEFtY0RleHM1S3IrVHE5N1FFSEFaeisvK095T1VnOHRqakRHV2JNQ1RaRmVIRE15Y2w0UHNYZmxoSWp5c2ZXTVdEQkFpd3d1M1Zpdis2OGU4cmdGQ0VESW1keHZ5TU9rM29pQVNlNyttTjBkWWtxU0tVVjFYSnB0TkVabW9kVGRwRDRtWWl0KzlZQ3lWcUllMm5uWSt0czlZbTVib1J4REh3UkFXVnZKWXJZU2pLMTRqVS9UMGJFeVlqdGtQTENXVno4UFNWRkNxcnpRRVhONHp0My9HV1luQk9wL1ZLSXFFb0cvSFo1OEkwY3MzdkZQOHVHR00vNkxZeGpZU0ZFUG0rWXIxL3lHV1NYMjFrWDRwY2IwT0lQR0dhdk82UVd5S29MVnJtbHZjWmRkSUFLaGNJWlpXbm52bVd2ZUx3WkVrTHRHOVZ6ZTJhaGxPRVVBQU9TNmVRb2syY29MUGZ2UUxmV3JKbGxQNHhwYVVWc3dPMk9IekREenVlcWpYWThFaU14UGJDaDlGeUV2TG5LaStxdGhCVXlXL3Z1VUxoMjc1WE13Z2hBQmNqMk9MaWZnTmM1MHpRZjdrNEFXRGJzVWd1QUFrUW1LTWVlYVd6d3k2TlV1VW02SzVzZTZGRFNBak9sZTV5S01LaUl4SUNLRjRRdGc1ZFgzL1dhMXhlY2NjR1pON3Y0cUZ3SWhZcGt3d1UwWlZtZ3Q0eFlOZjljRlRpZGNiOHZ5VTk5elBXL0VIOXV6SkVpWlgzQjlwR29uMUhIcmZtRWRmdnZvVGhRK1NJQnZ0ekxmaDZZcmdBai9mZlRjQTNQamtmWUVSSkZoamFoU0xOeXlwc0ErQm5jK0Z6engrVCsvRGVQcnpwY2ZVMGwwZG9ST1VuNjZDTVA5bE9mdGg4NzFKQmhBQkJRRlJBQVJZUkFLQnlVTVVOeVpPMmtHVlFSc0l5OEJvSklLUUJaT1lweis0ZktLRXdSS1J3Z3RocWRTQlZZSnhuT2NwSVhtWDJXakREMW4yRmlKNjN3YkJMTXRXTjU2YkFMaDJPeXFSZUJrQUFBaWdrV1Y1VW1JSzJqcHd6aUdSSURaWDlkb0ZRWERqemNtdTE1SjIyaloxQkNxNHlCb1QxVm5hZlI1bFF1WU13TFd5cUxIU2IwRVFuU0ZHdVBUQXZ0L3Y3dUg0eVZHQXJ3QmN1dktERmxjTVV1dERROEJTbnZyUVhEQngwMC8yQzZPZ0IwWVJFT0ZPQUNZeVA2QjZ4cFZienZod1NaZzRzK1Zkcjl6NFYyVWI4Wld2TFA3c3JoMDdGajUrNFBCaEFJQXZmeG0rOHBYYmI3dHQ2OUdqRDl4MTEyV0hEbXc5OXNmaXB6M2Q5QTJNZ2lBYUJrRmhBUE5jQlR2LzVqRno0Vm1PQlRaS2R1Z0ZjVnh6ZVlwa3ZQZGRWTWdkRGRiVzE1SWd4dFRxSVcyWDdrS3pBRURWNkFnTUlSQVJNNjgrSHpnS1NkSHVSRWdjVjJ0Rk1NdVdUWk5waWVYUEVuQnpibi81aWY2ZXNuNSsyK2R2K3Y2M1doT3hjRUU4alZabUpFcTZYTFZUNUhDcFI0bEIwb0hnZGFWbjZ1NzBlTVZXK2E2ZUYxK0VIVHZnOE55QmROSEV4R3VuVGwwOE1RRUFyNTA2ZGRIRXhPc1hYUVFQUExEMXR0dW1Ybjk5YXNlT0hTKytlUGpxM2ErVXVHWWdkQlpoYmRRbzBBSXc0c3pzQmJWa0tvcmNzeC81N0tCYlZFQ0FJUlQxQXNxR1RJNFF4VFhuSEFsQzIyZm9reTRuL2xlVFNHK3VTUlpGTUJMbktPcHkxWUlESkJ0MWswc3ZlR2RzRkVKbXpNQjZaR2RuWi9NOE9zYWF3QUFBRFM1SlJFRlVielFhMXRxMTFJTUo0TXpLQ3hrUUFkRmJTMVdhcm0wQTArbFphQllVUVpFcy85TmptOTVwckVkT2hqcEVGNzF1ZnJzdDQrV3F5WjF1a28wbmhIcCtBL0hlRjdaaDBYbk5SZ3loZ3N1Nmg1bUdnQU8xMnV2ODZ3Q3ZBenk1NU90SEFZNTIvMndHREcrMFBpZkp2WTFqQW5Qd3c3Kzk3TG1MNitsc21rMys0cy92SEhTN2lvbEk0ZG84UTJMU0RibkFOb29pYVRrUEhGRUVIang3VzJJNmZLODQ3Nk9lZGtDSytGNE55V1BjREhrT1JNeE1YV1hnTmQxTjdldDBGbnFmSWhvT1lkbnFzZjNESElMTEl3YUxHSXhwdFZwYnRoUlZRRjZSb0U5NWxqbGEvazB6QkFDUk5jS2VpNFpjMXhPeWNDZ3VkMlpyeVJ1VDdYZE5sUnppWDVQelRway9ibTFSSEhIUmtvczRzajNPMEl4N04vM29YdTg5eE9mY0d3a3c1bnFnb2MxUVZrMFZPbWJVUU9ELzM5Njk5dGgxblhVQWY5YmFlNSs1eFVrY2t3U2p0b1FVQ1pHVzNseUJSRlNhU0ZUOUFzUXFSVnpLbTRwUFFiNElpa29rRUJNK0FJSVhUcU1LMUlKcFNhSytxS2lhZ3RvUTNOaDE0cm1kcy9kYXZCam5WandlZTd6SDNwUHorOG12Zk02c3ZlMXo3UE0vejFycldaRk9SUDdMdFE2THZ1bmF0bHVwYlJ1clY4cnVjUG5NMXRuL2V1aEhuL252RTdYa3NRNURXc3puM1FHbGtjVmljZXJTNWIzMXUvR3BjQnpTZWhjUmRWRkxMVG5uMmc5cFhuYUcrVkNIbENPbEpqZE4xM1c1eWZ0clh1c0hGcy9lL0V0Ui9jQU0rUWRuUm9laDcrcGVHZXB0TlFjWkltNFNrVksvR1BIQWhHWTJHNFloRFVQczdPdzFUVGZyOGdGN1hmZjJkaGVMK1dvN3kxMlhtK1lXMjd5L1g5dXVEbjBmdFE0N095WG43bmg2eXBSUzV2TzlNcFJTU2s0bHQ5RTJxem5sSVpXVlNLc1BQSERubCtqclhoNVdIdDdZZUtzdStsS2psdXVMYWxLS2xLSkdSR2x5VzJyNXQ4K1AzMVR2eUZKS3B6WTJ0b2VoSGhEQis3NXZJNVVtYjkrWFgvankzYmp6Rjg2Zi85eTMveW8xZTJtckxTdjVvSFVGN1pEU3JNMGpGYi9mMVVSZVg3dHZ1K3d1OXBjZWZmQ2JWWTZVVXhjclEreE5xN1BQaDU0SXVQUlNOSkdHOFZKZ21aZVZtbk91ZmJNUkVYbm9aeXZ0enZ2K1lUZW5QdkE5cnoyNFpkcmJPN05IWXV2RnAzNzg4VmNlM2JpeWtwc2NrVXF0cWV4RlhzMnJ1ei82emI4WTY3YnZtbHBUMTdiUjk3RzlIVzBidGNhcDJOOEJFcEVpOXBwNU56end3TXJWcS9mNlR1OUk2cTUvd05TdCthSU1PVkxaLzhCT2tTSnFyYlhlNEFEREd2WGdGRmgvY1lYayszYlcxRnB5MUpLYVhPYnBsanV3bEYvc2liQjEvUzRpSXU2TE1rUnUwMkZycDI1TDB6VDEyclUrcFRaRkxiV20vVC9Cejk5NVBFV2tpUHR6em0zVDltVm9GOUVjdGE3WnRHMi92VjFUeWluZGR1bnhGdFJhU3luN3IwQktFU2xxaVQ1MjE5WWVIUEVxRnovLzlhY3Z2dkNUQjMvK2c4ZC9jdTdpdWYzZm5NMTNYdC9iYWoveWFFVDg1NjkvNTlkK2VQYmhxMk5lOU02dHpHYXozZVlqVnpjdVBSeUw3dTByaTFQdC9QcVc4TzdNem14K2VxVisvNjA0MHcvcjM3MkxYVXRUMDVmZFhHZDU2RGZhMlc0M2I2TnZkOXBZdTNibGpUT25QeHJ4K3Q3V3FUYW5JeXhYT014S054djY5bFQvc2JkUC9lRGl1ZGNqL2pJaUhuL2xIKzU3NjQzMXZmelJOMWYvNS9SaWE3MzUzbE5mSGZlNjNOd0pYSERFcUQ1OTRSdVJvcmJqRlFMbmtXcHRjMXR6VXlNM0thTG1HczMrckVocVMycEw2cTVmTHBXU2FybmhOODdGa0p1aFhIN28yckN4ODlwalYwOVVuZTl3OWNxVjJQOUk3bEswTmJxSXFCRWxva1lNS1MxbE41OTc0WDB0OS9wM2ZrVkVHNUVqWmtkb1luTDdWOStOR0NMMnZ4ZWxpQzZpaVZnWjk5TEhkT2pDY2YvOXZLYytlL2FuWjlkMjEwNWZPWjNMc0xmWVczUnBiNlhkZm1SN1B0dTUvTkJQSnZqL3d6T2JtMXZyc2VnV2I2M0V2SlRGa0hJcGFUYTBiWjdscldhbzdYRHB4YWVmdmZzMzlzU0Z6ZHowdVVaVG9wWWNmYi9iUnBkajN1YXVsbXZEWXRTOXdPOTU2c0tGdmRtYmI1NjVldW1SaDYrY2ZqemlFeEVSS1QyenVibi9oQmRHT2hPUFd5Y0NMcnRQdi9pTmlERWo0TEJiYzlSWGZ2L1B4eG9RQUJpZDF0QkxiK3hkZEUwTUo2NmhDUUFzR3hGdzZkVjM1cjVHa2o0RWJRWUI0TVBPZHBDbE4zN0Jya21xZ0FBd2JTSWdvOHNtZ2dGZzRrd0VMNzFqbWJVMUZRd0FreVlDTHJzYUk5ZUNzd0FJQUpNbkFpNjlGUFd3STJ0dlMwMkhIZkVBQU54ckl1RFNxMlBudFRTaHc5b0JnQnV5SFlTeGxXUTdDQUJNbkNvZ3g4Rk1NQUJNbWdnSUFMQjBSRUJHbHJQalFRQmc2a1JBeGljQ0FzREVpWUJMVDF3RGdPVWpBaTY5TlBxK2NLRVNBS1pPQkZ4NngvSVdrQUlCWU5KRVFBQ0FwU01DTWphSEJBUEE1SW1BQUFCTFJ3UmtaS2txQXdMQTFJbUFTNitPSDllU0NBZ0EweVlDa2xKdDd2VTlBQUIzbFFpNDdJNm5ZbGVQWVV3QVlEUWlJR1BMMVR3d0FFeWNDTWd4RUFFQllOcEVRQUNBcFNNQ2Nnd3NCUVNBYVd2djlRM3dvVlB1OVEwQUFJZFJCVng2VmNrT0FKYU9DRWhOVmVFT0FKYUxDTGpzUnE4QjVrZzJCQVBBeEltQXk2NUc5R09QbVk3aDBEa0FZRVFpNExJYmZ5VmdpV3BMTUFCTW14M0JqS3ltSWdFQ3dNU3BBbklNekFNRHdMU0pnQUFBUzBjRTVCam9OUWdBMDJZdElHTXI4aDhBVEowcUlQcENBOERTRVFHWFhUcVd6b0FBd0tTSmdNc3VSYlVhQUFDV2pRaTQ3STdqSFdBeElBQk1uQUxReWZQVWhRdXZQZlphUkx6MjJJOGpQWHVQNytiL3FTblpFUXdBRXljQ25oam5Ybm8rUmRReWJOZlhIM3Z0c1JlZit1YkhYMzcwdnBlZWEybzB1Y2xObTVxMlJIem5kODdmNnp1TlNIcERBOENraVlBbndMbVhubzlhYXBTSTNEVE5mTDdWTnovNjFFc2ZyVG5ubkdwZmFobmEyZW9pWXJHWWYvYWw1NlBKMzMzeWorNzFYUU1BMDJVdDROUjk1c0p6cFE2UmNxcTFULzF1R3NwSzE3YzVtaWFsTkVRTWJaNDM2ZTNGOXU1aU94WWxtaHhSZi92YmYzY3ZiOW84TUFCTW15cmdkRDJ4dVpsV2Q0ZDVhV2ExTkl1aHlZY2V2anQwcFpaaGJiWSszOW43MUQ4K0Y3VjUrY3QvY3NobGFvbzBhbVFiWXVRQkFZQ3hxUUpPVjFyZHFidXJrZWFsUzBPKzFWY3FwYlM3MkNuOUltcFRtM2ppd3VaaFAxRlRiZTd3Vmo5NEI4VjJFQUNZT0JGd29qNzN6Yi9PcTlFOGVEbXR6NDd5ODZ0TjZ1WkRzNXBqZnZNbnBvanhXMFBiRFFJQTB5WUNUdEhudnZWOFNiVmJTV2w5OWNpRDFGbmJ4YlhjMUhQLzhqYzNmK0tSTHdFQW5GQWk0T1E4czdtNWZxM21uT2Q1dU1PaGFwZTdyaW56Y3U2ZkRwME9CZ0NXaUFnNE9aZnZHODYrdGRydmJPY3h1dXN0NXJ0NVBpdHR1Zk9oQUlBUERUdUNKMmZlbEIrZUhTS3RqRE5jazRlMXZpNE9qSURWeWowQVdENnFnTlB5eEt1YlA5L295M0NuVThEdnQ1aHYxNlo4OWx0L2U5QVRxc004QUdESmlJRFQwbTMxdzROcmk4WE9pR00yczdiSjNZRVB5MzhBc0h4RXdHbXBmVm0vdE51c0hYMGo4STJscUhYTXlpSUFjS0paQ3pndGVhOC9wSS9ma1pURmJxMnFmUURBZFNMZ3RKUW9LVVVkdXpvN3pQdGt3aGNBZUllSjRHbXBVWS9qY0xWY2RZQUdBTjZqQ2pnMXFkUTZlakJQVGFjR0NBQzhTd1NjbGxsS2ZkUFVzVXQyVGRjZFIzSHh4cktLSXdCTW5ZbmdhV2xUT2wxSzJkNGRkOWlVdTVUdjJtdWROWm9CZ0lrVEFhZGxyZGFyTlVxZWpUaG02ZXNRZXlrMU4zejBXQXAycW9BQU1HMGk0TFM4K1BUWCt0ek8xdFpISEhObE5vc2hYZnpDK1FNZVR6V04rVFpJZDIvS0dRQTRJaEZ3Y3BxdWk3S0lnMC8xdlMyNVJHeDFUZHk0QkJnUk1Xcit1ejdrNkNNQ0FLTVNBU2ZuZTA5K0pkVlltYTJYTWNwcE9UV2xtOGZpNEFnWWFmUmRRY3FBQURCeEl1QVV0VTEzK2RSRE9kM3Bpc0E4MUw2TjFOU0xYenBvRmpqcTJEVzdhaThJQUV5ZUNEaEYvL3JrVjY0OThNc3ZQL25WZnFjLzhpQk55dk16RzdWSi8vNkZQNzdwRTB0TmpnOEdnT1VpQWs3VUd4LzdyVjk5K2FXMXVIK3hjNVI4bG5Pek4xL04xNFpoL1pCU1lpbWxsakZuYm0wSEFZRHBFd0duS3FWZit0ODNWdWVMVDE0NUZjM2FiYTBMckgwTWZUUk5YM2FhNzMveXdDbmdmVU1wL1RCeUZUQ1ZjZmF5QUFESFJBU2Nyb3RmT3Y4clc5M1A3cS9yTzJXbE9WWDZmSGlCYmQ2WG9ZbVVvZ3l2L041WHYvLzBJZmt2SW5KSzQwYkF0T2l6Q0FnQTArYUF1RWw3NGZ6NWlEajN6My9mRDRzMnQwM2RHT3E4SCtaTmsyb1RFVFhWcUlzaFVvcmExR2dqcDFUNm10dFhuN3I1K3IvM3BJZ0gxdGJtMFVkRXRNMzF6U0ZEalZyalNGTzZiU245cXJsZ0FKZzBWY0FUNE9Mdi9zRi9mT0VQbTFncE1kUWFPZWNTK3hFdGFxblhUcDk5K1l0L2R1M0JSeU5LMU82VnA3LzI2aGR2TmY5RnhHclh6V2ZSUDdqKzhwTi9ldTNVSTFIamFNbHZiUy8veGsvWE4vTDYvTkVoTlNJZ0FFeWEvaDBuMzd1SjdhamRXSjU0ZGZQU3cwOUV4S1ZIUG5Ia1FaN1ozSHpyL3RpYnpiYzNocUhadmZqNXJ4OXRIQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFUcXovQXg4L1hmOTRKd1loQUFBQUFFbEZUa1N1UW1DQyIgaGVpZ2h0PSIzNzQiIHByZXNlcnZlQXNwZWN0UmF0aW89InhNaWRZTWlkIG1lZXQiLz48L2c+PC9nPjwvZz48L3N2Zz4=";

function LogoMark({ size = 32 }) {
  return (
    <img
      src={FAVICON_URL}
      alt="PendPay"
      style={{ width: size, height: size, objectFit: "contain", display: "block" }}
    />
  );
}

function LogoFull({ size = 32 }) {
  return (
    <img
      src={LOGO_URL}
      alt="PendPay"
      style={{ height: size, width: "auto", objectFit: "contain", display: "block" }}
    />
  );
}

function TopBar({ onBack, title, right, unreadCount = 0, onNotificationsClick }) {
  const C = useColors();
  // Logo + page title + action buttons collide on narrow screens — show logo only when there's no title.
  const showLogo = !onBack && !title;
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      padding: "18px 20px 14px",
      background: C.bg,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
        {onBack ? (
          <button onClick={onBack} style={{
            width: 34, height: 34, borderRadius: 12, flexShrink: 0,
            background: C.card, border: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}>{sv(IC.chevron_l, 16, C.textMid)}</button>
        ) : showLogo ? (
          <LogoFull size={80} />
        ) : null}
        {title && (
          <span style={{
            color: C.text, fontSize: 16, fontWeight: 800,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{title}</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
        {right}
        {!onBack && onNotificationsClick && (
          <button
            onClick={onNotificationsClick}
            aria-label="Notifications"
            style={{
              width: 42, height: 42, borderRadius: 14, background: C.card,
              border: `1px solid ${C.border}`, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              position: "relative",
            }}>
            {sv(IC.bell, 20, C.muted)}
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: 5, right: 5,
                minWidth: 18, height: 18, borderRadius: 9,
                background: C.lime, color: C.bg,
                fontSize: 9, fontWeight: 900,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 4px",
                border: `2px solid ${C.bg}`,
              }}>{unreadCount > 9 ? "9+" : unreadCount}</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/** Stable module-level sheet — do not define inside screen components (breaks input focus on re-render). */
function BottomSheet({ title, onClose, children, noPad = false }) {
  const C = useColors();
  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", border: `1px solid ${C.border}`, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px 0", flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 14px" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <p style={{ color: C.text, fontSize: 16, fontWeight: 900, margin: 0 }}>{title}</p>
            <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
          </div>
        </div>
        <div style={{ overflowY: "auto", padding: noPad ? "0" : "0 20px 40px", flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

function HomeScreen({ setScreen, openWalletModal, unreadCount, onNotificationsClick, activeScreen, onViewAllTransactions, onViewAllContacts, onContactSelect, onViewAllEvents, onEventSelect }) {
  const C = useColors();
  const [wallet, setWallet] = useState(null);
  const [profile, setProfile] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const loadWallet = useCallback(async () => {
    try {
      const w = await getWallet();
      setWallet(w);
      return w;
    } catch {
      setWallet(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [w, me, freq, upcoming] = await Promise.all([
          getWallet(),
          getMe(),
          getFrequentContacts().catch(() => ({ contacts: [] })),
          listEvents({ upcoming: true, limit: 5 }).catch(() => ({ events: [] })),
        ]);
        if (!cancelled) {
          setWallet(w);
          setProfile(me);
          setContacts(freq.contacts || []);
          setUpcomingEvents((upcoming.events || []).slice(0, 5));
        }
      } catch {
        if (!cancelled) {
          setWallet(null);
          setProfile(null);
          setContacts([]);
          setUpcomingEvents([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeScreen === "home") loadWallet();
  }, [activeScreen, loadWallet]);

  const balanceDisplay = wallet
    ? formatNairaFromKobo(wallet.availableBalance ?? wallet.available_balance)
    : "₦0.00";

  const displayName = (() => {
    if (!profile) return "there";
    const full = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
    return full || profile.tag || "there";
  })();

  const initials = (() => {
    if (!profile) return "∙∙";
    const a = profile.first_name?.[0] || "";
    const b = profile.last_name?.[0] || "";
    const both = (a + b).toUpperCase();
    return both || profile.tag?.slice(0, 2).toUpperCase() || "?";
  })();

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div style={{ paddingBottom: 0 }}>
      <TopBar unreadCount={unreadCount} onNotificationsClick={onNotificationsClick} />
      <div style={{ padding: "4px 20px 0" }}>
        {profile?.is_demo && (
          <div style={{
            marginBottom: 16, padding: "10px 12px", borderRadius: 12,
            background: C.lime + "14", border: `1px solid ${C.lime}55`,
          }}>
            <p style={{ color: C.lime, fontSize: 11, fontWeight: 800, margin: "0 0 2px", letterSpacing: "0.08em" }}>DEMO ACCOUNT</p>
            <p style={{ color: C.muted, fontSize: 12, margin: 0, lineHeight: 1.5 }}>
              Sample wallet, contracts, and tickets for client walkthroughs. Payments and edits are turned off.
            </p>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <p style={{ color: C.muted, fontSize: 12, marginBottom: 2 }}>{greeting}</p>
            <p style={{ color: C.text, fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>{displayName}</p>
          </div>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: C.forestMid, border: `2px solid ${C.lime}50`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, color: C.lime, fontWeight: 900,
          }}>{initials}</div>
        </div>

        <WavyCard style={{ marginBottom: 16 }}>
          <p style={{ color: C.muted, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 10 }}>PENDPAY WALLET</p>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
            <span style={{ color: C.text, fontSize: 40, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1 }}>{balanceDisplay}</span>
          </div>
          <p style={{ color: C.muted, fontSize: 11, marginBottom: wallet?.ticketSettlement?.pendingSettlementKobo ? 8 : 18 }}>Available balance</p>
          {wallet?.ticketSettlement?.pendingSettlementKobo ? (
            <p style={{ color: C.muted, fontSize: 12, marginBottom: 18 }} title="Pending ticket earnings are not withdrawable.">
              Ticket earnings {formatNairaFromKobo(wallet.ticketSettlement.earningsKobo)} · Pending {formatNairaFromKobo(wallet.ticketSettlement.pendingSettlementKobo)}
              {wallet.ticketSettlement.nextReleaseAt ? ` · Next release ${new Date(wallet.ticketSettlement.nextReleaseAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}` : ""}
            </p>
          ) : null}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
            {[
              { label: "Contract", icon: IC.send, id: "send" },
              { label: "Hub", icon: IC.hub, id: "deals" },
              { label: "Transfer", icon: IC.plus, walletModal: "transfer" },
              { label: "Withdraw", icon: IC.up_right, id: "wallet" },
            ].map(b => (
              <button
                key={b.label}
                type="button"
                disabled={b.disabled}
                onClick={() => !b.disabled && (b.walletModal ? openWalletModal(b.walletModal) : setScreen(b.id))}
                style={{
                background: C.forest, border: `1px solid ${C.forestBright}`,
                borderRadius: 14, padding: "12px 0",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 7,
                cursor: b.disabled ? "not-allowed" : "pointer",
                opacity: b.disabled ? 0.45 : 1,
              }}>
                {sv(b.icon, 16, b.disabled ? C.muted : C.lime)}
                <span style={{ color: b.disabled ? C.muted : C.textMid, fontSize: 10, fontWeight: 700, letterSpacing: "0.02em" }}>{b.label}</span>
              </button>
            ))}
          </div>
        </WavyCard>

        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 22, padding: "18px 18px 20px", marginBottom: 16,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <p style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>Favourite Contacts</p>
            <button type="button" onClick={onViewAllContacts} style={{ background: "none", border: "none", color: C.lime, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>View all</button>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={() => setScreen("send")} style={{
              width: 44, height: 44, borderRadius: "50%",
              background: C.forestMid, border: `1px dashed ${C.lime}60`,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>{sv(IC.plus, 16, C.lime)}</button>
            {contacts.map((c, i) => (
              <button key={c.tag} type="button" onClick={() => onContactSelect?.(c)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer" }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: c.color + "25", border: `1.5px solid ${c.color}60`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, color: c.color, fontWeight: 800,
                }}>{c.initials}</div>
                <span style={{ color: C.muted, fontSize: 10, maxWidth: 42, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name.split(" ")[0]}</span>
              </button>
            ))}
          </div>
        </div>

        <UpcomingEventsStrip
          events={upcomingEvents}
          onViewAll={onViewAllEvents}
          onEventSelect={onEventSelect}
        />

        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 22, padding: "18px 18px 4px", marginBottom: 16,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <p style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>Transactions</p>
            <button onClick={onViewAllTransactions} style={{ background: "none", border: "none", color: C.lime, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>View all</button>
          </div>
          <p style={{ color: C.muted, fontSize: 10, letterSpacing: "0.07em", fontWeight: 700, marginBottom: 10 }}>RECENT</p>
          <WalletTransactionList
            transactions={wallet?.transactions || []}
            limit={6}
            variant="home"
            emptyMessage="No wallet activity yet. Add funds or create a contract."
          />
        </div>
      </div>
    </div>
  );
}


function parseAiTerms(raw) {
  return resolveDealTerms(raw);
}

function paystackPublicKey(config) {
  return config?.paystackPublicKey || process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "";
}

async function completeDealPaystackCheckout({ dealId, config, userEmail, userPhone }) {
  const init = await initializeDealPayment(dealId);
  const publicKey = paystackPublicKey(config);
  if (!publicKey) throw new Error("Card payments are not configured yet");
  const response = await openPaystackCheckout({
    publicKey,
    email: userEmail || `${userPhone || "user"}@pendpay.app`,
    amountKobo: init.amountKobo,
    reference: init.reference,
    channels: init.channels,
  });
  return confirmDealPayment(dealId, response.reference);
}

async function completeDealSafeHavenCheckout({ dealId }) {
  const init = await initializeDealCheckout(dealId, "web");
  if (!init.checkoutConfig?.clientId) throw new Error("Bank checkout is not configured yet");
  const response = await openSafeHavenCheckout({
    checkoutConfig: init.checkoutConfig,
    referenceCode: init.referenceCode,
  });
  return confirmDealCheckout(dealId, response.referenceCode || init.referenceCode);
}

function InstantTransferOverlay({ dealId, instructions, onFunded, onClose }) {
  const C = useColors();
  const [checking, setChecking] = useState(false);
  const [checkNotice, setCheckNotice] = useState(null);

  const pollDealFunded = useCallback(async () => {
    if (!dealId) return false;
    try {
      const deal = await getDeal(dealId);
      if (deal.status === "pending_acceptance") {
        onFunded?.(deal);
        return true;
      }
    } catch { /* ignore poll errors */ }
    return false;
  }, [dealId, onFunded]);

  const confirmTransferSent = useCallback(async () => {
    if (!dealId) return;
    setChecking(true);
    setCheckNotice(null);
    try {
      const res = await checkDealInstantTransfer(dealId);
      if (res.funded) {
        onFunded?.(res.deal);
        return;
      }
      setCheckNotice(res.message || "Transfer not detected yet. Include your deal reference in the narration.");
    } catch (e) {
      setCheckNotice(e.message || "Could not check transfer");
    } finally {
      setChecking(false);
    }
  }, [dealId, onFunded]);

  useEffect(() => {
    if (!dealId) return undefined;
    pollDealFunded().catch(() => {});
    const id = setInterval(() => { pollDealFunded().catch(() => {}); }, 10000);
    return () => clearInterval(id);
  }, [dealId, pollDealFunded]);

  const amountKobo = instructions?.amountKobo ?? 0;

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: C.bg, zIndex: 450, overflowY: "auto" }}>
      <TopBar onBack={onClose} title="Instant transfer" />
      <div style={{ padding: "8px 20px 32px" }}>
        <div style={{ background: C.lime + "12", border: `1px solid ${C.lime}30`, borderRadius: 16, padding: "18px", marginBottom: 16, textAlign: "center" }}>
          <p style={{ color: C.muted, fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, margin: "0 0 8px" }}>TRANSFER EXACTLY</p>
          <p style={{ color: C.lime, fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
            {formatNairaFromKobo(amountKobo)}
          </p>
          <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>To your PendPay subaccount · include deal reference</p>
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px", marginBottom: 16 }}>
          {[
            ["Bank name", instructions?.bankName || "Safe Haven MFB"],
            ["Account number", instructions?.accountNumber || "Setting up…"],
            ["Account name", instructions?.accountName || "—"],
            ["Reference (narration)", instructions?.reference || "—"],
          ].map(([label, value], i, rows) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ color: C.muted, fontSize: 12 }}>{label}</span>
              <span style={{ color: label.includes("Reference") ? C.lime : C.text, fontSize: 12, fontWeight: label.includes("Reference") ? 900 : 700, textAlign: "right", maxWidth: "58%", wordBreak: "break-all" }}>{value}</span>
            </div>
          ))}
        </div>
        <div style={{ background: C.forest, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
          <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: "0 0 6px" }}>Checking for your transfer…</p>
          <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6, margin: 0 }}>
            {instructions?.note || "Transfer from any Nigerian bank using the details above. We confirm via Safe Haven and lock funds into escrow automatically — usually within a few minutes."}
          </p>
        </div>
        {checkNotice && (
          <p style={{ color: C.gold, fontSize: 12, fontWeight: 700, marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: C.gold + "12", border: `1px solid ${C.gold}35` }}>{checkNotice}</p>
        )}
        <button
          type="button"
          disabled={checking || !instructions?.accountNumber}
          onClick={confirmTransferSent}
          style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.lime, border: "none", color: C.bg, fontSize: 14, fontWeight: 900, cursor: checking ? "wait" : "pointer", marginBottom: 10, opacity: checking || !instructions?.accountNumber ? 0.75 : 1 }}
        >
          {checking ? "Checking transfer…" : "I've sent the transfer — check now"}
        </button>
        <button type="button" onClick={onClose} style={{ width: "100%", padding: "12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted, fontSize: 13, cursor: "pointer" }}>
          Continue later
        </button>
      </div>
    </div>
  );
}

async function completeWalletTopupCheckout({ amountNaira, paymentMethod, config, userEmail, userPhone }) {
  const init = await initializeWalletTopup(amountNaira, paymentMethod);
  const publicKey = paystackPublicKey(config);
  if (!publicKey) throw new Error("Card payments are not configured yet");
  const response = await openPaystackCheckout({
    publicKey,
    email: userEmail || `${userPhone || "user"}@pendpay.app`,
    amountKobo: init.amountKobo,
    reference: init.reference,
    channels: init.channels || (paymentMethod === "ussd" ? ["ussd"] : ["card"]),
  });
  return confirmWalletTopup(response.reference);
}

function dealStatusLabel(status, role) {
  const map = {
    pending_payment: "Pending",
    pending_acceptance: role === "sender" ? "Awaiting acceptance" : "Review required",
    active: "Active",
    completed: role === "receiver" ? "Paid to you" : "Completed",
    cancelled: "Cancelled",
    expired: "Expired",
    disputed: "Disputed",
  };
  return map[status] || status;
}

function resolveContractFileUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const apiBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";
  const origin = apiBase.replace(/\/api\/?$/, "");
  return `${origin}${url.startsWith("/") ? url : `/${url}`}`;
}

function ContractFileCard({ url, name, style }) {
  const C = useColors();
  const href = resolveContractFileUrl(url);
  if (!href) return null;
  const label = name || "Attached contract";
  const isPdf = /\.pdf$/i.test(label) || /\.pdf(\?|$)/i.test(href);
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
      padding: "16px", marginBottom: 16, ...style,
    }}>
      <p style={{ color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 10 }}>
        ATTACHED CONTRACT
      </p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "12px 14px", borderRadius: 12,
          background: C.surface, border: `1px solid ${C.lime}40`,
          textDecoration: "none",
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: C.lime + "18", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {sv("M14 2H6A2 2 0 004 4V20A2 2 0 006 22H18A2 2 0 0020 20V8L14 2ZM14 2V8H20", 18, C.lime)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {label}
          </p>
          <p style={{ color: C.muted, fontSize: 11, margin: "3px 0 0" }}>
            {isPdf ? "PDF document" : "Document"} · Tap to open
          </p>
        </div>
        <span style={{ color: C.lime, fontSize: 12, fontWeight: 800, flexShrink: 0 }}>Open →</span>
      </a>
    </div>
  );
}

function ReceiverScreen({ deal, onBack, onComplete, onOpenBankSetup }) {
  const C = useColors();
  const showToast = useToast();
  const raw = deal?.raw || {};
  const senderName = raw.senderName || deal?.name || "Sender";
  const amountNaira = Math.round(koboToNaira(raw.amount));
  const receiverFeeNaira = Math.round(koboToNaira(raw.receiverFee));
  const terms = resolveDealTerms(raw);
  const paymentSchedule = milestoneReceiveRows(raw, receiverFeeNaira);

  const [acceptedTerms, setAcceptedTerms] = useState({});
  const [queryInput, setQueryInput] = useState({});
  const [queryOpen, setQueryOpen] = useState(null);
  const [termQueries, setTermQueries] = useState([]);
  const [submittingQuery, setSubmittingQuery] = useState(null);
  const [termTexts, setTermTexts] = useState({});
  const [declined, setDeclined] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [activating, setActivating] = useState(false);
  const [rxStep, setRxStep] = useState(0); // 0=intro, 1=fee, 2=terms
  const [walletPreview, setWalletPreview] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setWalletLoading(true);
    getWallet()
      .then((w) => { if (!cancelled) setWalletPreview(w); })
      .catch(() => { if (!cancelled) setWalletPreview(null); })
      .finally(() => { if (!cancelled) setWalletLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const vaPending = isVirtualAccountPending(walletPreview, { loading: walletLoading });

  const bankSetupBanner = !walletLoading && vaPending ? (
    <div style={{
      padding: "14px 16px", borderRadius: 14, marginBottom: 16,
      background: C.gold + "14", border: `1px solid ${C.gold}40`,
    }}>
      <p style={{ color: C.gold, fontSize: 13, fontWeight: 800, margin: "0 0 6px" }}>Bank account required to get paid</p>
      <p style={{ color: C.muted, fontSize: 12, margin: "0 0 12px", lineHeight: 1.45 }}>
        You can accept this contract, but milestone payouts cannot be released until you finish BVN/NIN verification.
      </p>
      {onOpenBankSetup ? (
        <button
          type="button"
          onClick={onOpenBankSetup}
          style={{
            padding: "10px 14px", borderRadius: 12, border: "none", cursor: "pointer",
            background: C.gold, color: C.bg, fontSize: 12, fontWeight: 800,
          }}
        >
          Complete bank setup →
        </button>
      ) : null}
    </div>
  ) : null;

  useEffect(() => {
    const texts = Object.fromEntries(terms.map((t) => [t.id, t.text]));
    for (const q of termQueries) {
      if (q.status === "accepted" && q.responseText) texts[q.termId] = q.responseText;
    }
    setTermTexts(texts);
  }, [terms, termQueries]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getDealTermQueries(deal.id);
        if (!cancelled) setTermQueries(res.queries || []);
      } catch {
        if (!cancelled) setTermQueries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [deal.id]);

  useEffect(() => {
    if (!termQueries.some((q) => q.status === "pending")) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await getDealTermQueries(deal.id);
        setTermQueries(res.queries || []);
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(timer);
  }, [deal.id, termQueries]);

  const queriesByTermId = useMemo(() => {
    const map = {};
    for (const q of termQueries) {
      const prev = map[q.termId];
      if (!prev || new Date(q.createdAt) > new Date(prev.createdAt)) map[q.termId] = q;
    }
    return map;
  }, [termQueries]);

  const myHalf = receiverFeeNaira || Math.max(325, Math.round(amountNaira * 0.0075 / 2));
  const iGet = amountNaira - myHalf;
  const allTerms = terms;
  const allResolved = allTerms.every((t) => {
    const q = queriesByTermId[t.id];
    if (q?.status === "pending") return false;
    return acceptedTerms[t.id];
  });
  const acceptedCount = allTerms.filter((t) => acceptedTerms[t.id]).length;
  const hasQueries = allTerms.some((t) => queriesByTermId[t.id]?.status === "pending");

  const sendQuery = async (termId) => {
    const q = queryInput[termId];
    if (!q?.trim() || submittingQuery) return;
    setSubmittingQuery(termId);
    try {
      const res = await postDealTermQuery(deal.id, { termId, queryText: q.trim() });
      setTermQueries((prev) => [
        ...prev.filter((row) => !(row.termId === termId && row.status === "pending")),
        res.query,
      ]);
      setQueryInput((p) => ({ ...p, [termId]: "" }));
      setQueryOpen(null);
    } catch (e) {
      showToast(e.message || "Could not send query");
    } finally {
      setSubmittingQuery(null);
    }
  };

  const statusOf = (t) => {
    const q = queriesByTermId[t.id];
    if (q?.status === "pending") return "pending";
    if (q?.status === "accepted") return "updated";
    if (q?.status === "declined") return "declined";
    return acceptedTerms[t.id] ? "accepted" : "idle";
  };
  const statusStyle = (s) => ({
    declined: { bg: C.danger + "15", border: C.danger + "60", dot: C.danger },
    updated: { bg: C.info + "15", border: C.info + "60", dot: C.info },
    pending: { bg: C.gold + "12", border: C.gold + "50", dot: C.gold },
    accepted: { bg: C.lime + "12", border: C.lime + "60", dot: C.lime },
    idle: { bg: C.card, border: C.border, dot: C.muted },
  }[s]);

  const handleDecline = async () => {
    if (declining) return;
    setDeclining(true);
    try {
      await declineDeal(deal.id);
      setDeclined(true);
    } catch (e) {
      showToast(e.message || "Could not decline");
    } finally {
      setDeclining(false);
    }
  };

  const handleActivate = async () => {
    if (!allResolved || activating) return;
    setActivating(true);
    try {
      await acceptDeal(deal.id);
      onComplete?.("accepted");
    } catch (e) {
      showToast(e.message || "Could not accept");
    } finally {
      setActivating(false);
    }
  };

  if (declined) return (
    <div>
      <TopBar title="Deal declined" onBack={onBack} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: C.danger + "18", border: `2px solid ${C.danger}`,
          display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
        }}>{sv("M18 6L6 18M6 6L18 18", 32, C.danger, 2)}</div>
        <p style={{ color: C.text, fontSize: 20, fontWeight: 900, marginBottom: 8 }}>Deal declined</p>
        <p style={{ color: C.muted, fontSize: 14, marginBottom: 24 }}>You have declined this deal from {senderName}.</p>
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", textAlign: "left" }}>
            <p style={{ color: C.lime, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 4 }}>FUNDS RETURNED</p>
            <p style={{ color: C.text, fontSize: 13 }}>The sender's locked funds have been automatically returned to their account.</p>
          </div>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", textAlign: "left" }}>
            <p style={{ color: C.gold, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 4 }}>PLATFORM FEE RETAINED</p>
            <p style={{ color: C.text, fontSize: 13 }}>PendPay's fee is non-refundable. The fee covers the contract wallet service provided, regardless of deal outcome.</p>
          </div>
          <div style={{ background: C.forest, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", textAlign: "left" }}>
            <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>If you declined by mistake or want to renegotiate terms, ask the sender to resend the deal with updated terms.</p>
          </div>
        </div>
        <button onClick={() => { onComplete?.(); onBack?.(); }} style={{
          width: "100%", padding: "16px", background: C.card,
          border: `1px solid ${C.border}`, borderRadius: 14,
          color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer",
        }}>Close</button>
      </div>
    </div>
  );

  return (
    <div>
      <TopBar title={rxStep === 0 ? "Deal request" : rxStep === 1 ? "Your payment" : "Review terms"} onBack={rxStep > 0 ? () => setRxStep(rxStep - 1) : onBack} />
      <div style={{ height: 3, background: C.card }}>
        <div style={{ height: "100%", width: `${((rxStep + 1) / 3) * 100}%`, background: C.lime, transition: "width 0.3s", borderRadius: "0 2px 2px 0" }} />
      </div>

      <div style={{ padding: "20px 20px 120px" }}>

        {rxStep === 0 && (
          <div>
            {bankSetupBanner}
            <div style={{
              background: C.forest, border: `1px solid ${C.borderBright}`,
              borderRadius: 20, padding: "24px 20px", marginBottom: 20, textAlign: "center",
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: C.lime + "20", border: `2px solid ${C.lime}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px",
              }}>{sv(IC.lock, 24, C.lime)}</div>
              <p style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Deal request from</p>
              <p style={{ color: C.text, fontSize: 18, fontWeight: 900, marginBottom: 6 }}>{senderName}</p>
              <p style={{ color: C.lime, fontSize: 36, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 4 }}>₦{amountNaira.toLocaleString()}</p>
              <p style={{ color: C.muted, fontSize: 12 }}>is locked and waiting for you</p>
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
              {[
                { label: "What is PendPay?", val: "A secure contract wallet platform. The money is already locked — it cannot be touched until you approve the milestone.", color: C.lime },
                { label: "What do I need to do?", val: "Review the payment breakdown and contract terms, then accept or negotiate before the deal activates.", color: C.info },
                { label: "Am I committed now?", val: "No. You can decline this deal at any point before you tap Accept. No money moves until you agree.", color: C.gold },
                { label: "How long do I have?", val: "You have 48 hours to accept or decline. After that, the deal expires automatically and funds return to the sender.", color: "#C084FC" },
                ...(vaPending ? [{
                  label: "Can I get paid without bank setup?",
                  val: "No. Milestone releases go to your PendPay sub-account. Finish BVN/NIN setup before accepting if you want to get paid without delay.",
                  color: C.gold,
                }] : []),
                { label: "What is the inactivity fee?", val: "If the sender goes 7 days without any activity on an accepted deal, you automatically earn a 10% convenience fee — compensation for reserving your time.", color: C.lime },
              ].map((item, i, a) => (
                <div key={item.label} style={{ padding: "14px 16px", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <p style={{ color: item.color, fontSize: 11, fontWeight: 800, marginBottom: 4 }}>{item.label}</p>
                  <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.5 }}>{item.val}</p>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" onClick={handleDecline} disabled={declining} style={{
                flex: 1, padding: "16px", borderRadius: 14, cursor: "pointer",
                background: C.dangerDim, border: `1px solid ${C.danger}40`,
                color: C.danger, fontSize: 14, fontWeight: 800,
                opacity: declining ? 0.6 : 1,
              }}>{declining ? "Declining…" : "Decline"}</button>
              <button onClick={() => setRxStep(1)} style={{
                flex: 2, padding: "16px", borderRadius: 14, cursor: "pointer",
                background: C.lime, border: "none",
                color: C.bg, fontSize: 14, fontWeight: 900,
              }}>Review deal →</button>
            </div>
          </div>
        )}

        {rxStep === 1 && (
          <div>
            {bankSetupBanner}
            <p style={{ color: C.text, fontSize: 18, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 4 }}>Your payment breakdown</p>
            <p style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>This is exactly what you will receive and what you pay for the protection</p>

            <div style={{
              background: C.forest, border: `1px solid ${C.borderBright}`,
              borderRadius: 20, padding: "22px 20px", marginBottom: 16,
            }}>
              <div style={{ textAlign: "center", paddingBottom: 18, marginBottom: 18, borderBottom: `1px solid ${C.borderBright}` }}>
                <p style={{ color: C.muted, fontSize: 11, marginBottom: 6 }}>Deal agreed</p>
                <p style={{ color: C.text, fontSize: 38, fontWeight: 900, letterSpacing: "-0.03em" }}>₦{amountNaira.toLocaleString()}</p>
              </div>
              {[
                { label: "PendPay fee (your half)", val: `-₦${myHalf.toLocaleString()}`, sub: "1% total split equally — sender already paid their half", color: C.gold },
                { label: "You receive", val: `₦${iGet.toLocaleString()}`, sub: vaPending ? "paid to your PendPay account after bank setup + milestone approval" : "transferred to your bank on milestone approval", color: C.lime, big: true },
              ].map(r => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div>
                    <p style={{ color: C.muted, fontSize: 12 }}>{r.label}</p>
                    <p style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>{r.sub}</p>
                  </div>
                  <p style={{ color: r.color, fontSize: r.big ? 24 : 16, fontWeight: 900 }}>{r.val}</p>
                </div>
              ))}
            </div>

            <div style={{
              background: C.gold + "12", border: `1px solid ${C.gold}30`,
              borderRadius: 14, padding: "13px 16px", marginBottom: 20,
              display: "flex", alignItems: "flex-start", gap: 10,
            }}>
              {sv(IC.warn, 14, C.gold)}
              <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
                The sender also paid ₦{myHalf.toLocaleString()} on their end. The ₦{(myHalf * 2).toLocaleString()} total fee covers contract wallet protection for both of you. Neither party bears the full cost.
              </p>
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
              <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", padding: "12px 16px 8px" }}>PAYMENT SCHEDULE</p>
              {paymentSchedule.map((m, i, a) => (
                <div key={m.ms} style={{ padding: "12px 16px", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{m.ms}</p>
                    <p style={{ color: C.muted, fontSize: 11 }}>{m.when}</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ color: C.lime, fontSize: 14, fontWeight: 900 }}>₦{m.amt.toLocaleString("en-NG")}</p>
                    <p style={{ color: C.muted, fontSize: 10 }}>{m.pct}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setDeclined(true)} style={{
                flex: 1, padding: "16px", borderRadius: 14, cursor: "pointer",
                background: C.dangerDim, border: `1px solid ${C.danger}40`,
                color: C.danger, fontSize: 14, fontWeight: 800,
              }}>Decline</button>
              <button onClick={() => setRxStep(2)} style={{
                flex: 2, padding: "16px", borderRadius: 14, cursor: "pointer",
                background: C.lime, border: "none",
                color: C.bg, fontSize: 14, fontWeight: 900,
              }}>Review contract →</button>
            </div>
          </div>
        )}

        {rxStep === 2 && (
          <div>
            {bankSetupBanner}
            <p style={{ color: C.text, fontSize: 18, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 4 }}>Contract terms</p>
            <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Accept each term or propose a change. All terms must be resolved before the deal activates.</p>

            <ContractFileCard url={raw.contractFileUrl} name={raw.contractFileName} />

            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[
                { label: "Accepted", count: allTerms.filter(t => statusOf(t) === "accepted").length, color: C.lime },
                { label: "Queried", count: allTerms.filter(t => ["pending", "updated", "declined"].includes(statusOf(t))).length, color: C.gold },
                { label: "To review", count: allTerms.filter(t => statusOf(t) === "idle").length, color: C.muted },
              ].map(s => (
                <div key={s.label} style={{
                  flex: 1, background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: "10px 8px", textAlign: "center",
                }}>
                  <p style={{ color: s.color, fontSize: 18, fontWeight: 900 }}>{s.count}</p>
                  <p style={{ color: C.muted, fontSize: 10, fontWeight: 700 }}>{s.label}</p>
                </div>
              ))}
            </div>

            {terms.map((t) => {
              const status = statusOf(t);
              const ss = statusStyle(status);
              const isOpen = queryOpen === t.id;
              const q = queriesByTermId[t.id];
              return (
                <div key={t.id} style={{ marginBottom: 10 }}>
                  <div style={{ background: ss.bg, border: `1px solid ${ss.border}`, borderRadius: 14, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ width: 7, height: 7, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: ss.dot }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ color: status === "accepted" ? C.lime : status === "updated" ? C.info : C.text, fontSize: 13, lineHeight: 1.55, fontWeight: status === "accepted" || status === "updated" ? 600 : 400 }}>
                          {termTexts[t.id]}
                        </p>
                        {t.risk === "medium" && status === "idle" && (
                          <p style={{ color: C.gold, fontSize: 10, fontWeight: 800, marginTop: 4 }}>Review carefully — this affects your obligations</p>
                        )}
                        {status === "pending" && (
                          <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: C.gold + "18", border: `1px solid ${C.gold}40` }}>
                            <p style={{ color: C.gold, fontSize: 10, fontWeight: 800, marginBottom: 3 }}>YOUR QUERY — AWAITING SENDER</p>
                            <p style={{ color: C.textMid, fontSize: 12 }}>"{q?.queryText}"</p>
                          </div>
                        )}
                        {status === "updated" && q?.responseText && (
                          <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: C.info + "15", border: `1px solid ${C.info}40` }}>
                            <p style={{ color: C.info, fontSize: 10, fontWeight: 800, marginBottom: 3 }}>SENDER AGREED — TERM UPDATED</p>
                            <p style={{ color: C.textMid, fontSize: 12 }}>"{q.responseText}"</p>
                          </div>
                        )}
                        {status === "declined" && (
                          <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: C.danger + "15", border: `1px solid ${C.danger}40` }}>
                            <p style={{ color: C.danger, fontSize: 10, fontWeight: 800, marginBottom: 3 }}>SENDER DECLINED YOUR CHANGE</p>
                            <p style={{ color: C.muted, fontSize: 12 }}>Original term stands. Accept as-is, query again, or decline the whole deal below.</p>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                        {status !== "pending" && (
                          <button
                            onClick={() => setAcceptedTerms(p => ({ ...p, [t.id]: !acceptedTerms[t.id] }))}
                            style={{
                              width: 28, height: 28, borderRadius: 8, cursor: "pointer",
                              background: acceptedTerms[t.id] ? C.lime : C.surface,
                              border: `1.5px solid ${acceptedTerms[t.id] ? C.lime : C.border}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                            {acceptedTerms[t.id] ? sv(IC.check, 13, C.bg, 3) : null}
                          </button>
                        )}
                        {status !== "pending" && status !== "accepted" && (
                          <button
                            onClick={() => setQueryOpen(isOpen ? null : t.id)}
                            style={{
                              width: 28, height: 28, borderRadius: 8, cursor: "pointer",
                              background: isOpen ? C.gold + "30" : C.surface,
                              border: `1.5px solid ${isOpen ? C.gold : C.border}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                            {sv("M21 15A2 2 0 0119 17H7L3 21V5A2 2 0 015 3H19A2 2 0 0121 5V15Z", 13, isOpen ? C.gold : C.muted)}
                          </button>
                        )}
                        {status === "pending" && (
                          <div style={{ width: 28, height: 28, borderRadius: 8, background: C.gold + "20", border: `1px solid ${C.gold}40`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {sv("M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22", 12, C.gold)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ marginTop: 6, background: C.surface, border: `1px solid ${C.gold}60`, borderRadius: 14, padding: "14px 16px" }}>
                      <p style={{ color: C.gold, fontSize: 11, fontWeight: 800, marginBottom: 4 }}>PROPOSE A CHANGE</p>
                      <p style={{ color: C.muted, fontSize: 11, marginBottom: 10 }}>Be specific — the sender will see exactly what you write</p>
                      <textarea
                        placeholder={`e.g. "I need 60 days instead of 30 due to project complexity"`}
                        value={queryInput[t.id] || ""}
                        onChange={e => setQueryInput(p => ({ ...p, [t.id]: e.target.value }))}
                        style={{
                          width: "100%", padding: "11px 13px", borderRadius: 10, marginBottom: 10,
                          background: C.card, border: `1px solid ${C.border}`,
                          color: C.text, fontSize: 13, outline: "none",
                          resize: "none", height: 72, boxSizing: "border-box", lineHeight: 1.5,
                        }}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => setQueryOpen(null)} style={{ flex: 1, padding: "9px", borderRadius: 10, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                        <button onClick={() => sendQuery(t.id)} disabled={!queryInput[t.id]?.trim() || submittingQuery === t.id} style={{
                          flex: 2, padding: "9px", borderRadius: 10, border: "none",
                          background: queryInput[t.id]?.trim() && submittingQuery !== t.id ? C.gold : C.card,
                          color: queryInput[t.id]?.trim() && submittingQuery !== t.id ? C.bg : C.muted,
                          fontSize: 12, fontWeight: 900, cursor: "pointer",
                        }}>{submittingQuery === t.id ? "Sending…" : "Send to sender"}</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ padding: "13px 16px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, marginTop: 8, marginBottom: 14 }}>
              <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.65 }}>
                By accepting you confirm you have read all terms, agree to the milestone payment schedule, and authorise PendPay to release funds to you via our licensed banking partner on the sender's milestone approval.
                {vaPending ? " You understand payouts cannot complete until your PendPay bank account is set up." : ""}
              </p>
            </div>

            <button type="button" onClick={handleActivate} disabled={!allResolved || activating} style={{
              width: "100%", padding: "18px", marginBottom: 10,
              background: allResolved && !activating ? C.lime : C.card,
              border: `1px solid ${allResolved && !activating ? C.lime : C.border}`,
              borderRadius: 16,
              color: allResolved && !activating ? C.bg : C.muted,
              fontSize: 15, fontWeight: 900,
              cursor: allResolved && !activating ? "pointer" : "default",
              transition: "all 0.2s",
            }}>
              {activating
                ? "Activating…"
                : allResolved
                  ? (vaPending ? "Accept anyway (set up bank to get paid)" : "Activate Contract Wallet")
                  : hasQueries
                    ? `Query pending · ${acceptedCount}/${allTerms.length} accepted`
                    : `${acceptedCount}/${allTerms.length} terms accepted — review remaining`
              }
            </button>

            <button type="button" onClick={handleDecline} disabled={declining} style={{
              width: "100%", padding: "14px",
              background: "none", border: `1px solid ${C.danger}40`,
              borderRadius: 14, color: C.danger,
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              opacity: declining ? 0.6 : 1,
            }}>{declining ? "Declining…" : "Decline this deal"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function TermQueriesRespondModal({ deal, queries, onClose, onUpdated }) {
  const C = useColors();
  const raw = deal?.raw || {};
  const [terms, setTerms] = useState(() => resolveDealTerms(raw));
  const [localQueries, setLocalQueries] = useState(queries || []);
  const termsById = Object.fromEntries(terms.map((t) => [t.id, t]));
  const pending = (localQueries || []).filter((q) => q.status === "pending");
  const [respondingId, setRespondingId] = useState(null);
  const [responseText, setResponseText] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    setLocalQueries(queries || []);
  }, [queries]);

  const respond = async (queryId, action) => {
    setError("");
    setRespondingId(queryId);
    try {
      const result = await respondDealTermQuery(deal.id, queryId, {
        action,
        responseText: responseText[queryId] || undefined,
      });
      if (result.deal?.terms) setTerms(result.deal.terms);
      const refreshed = await getDealTermQueries(deal.id);
      setLocalQueries(refreshed.queries || []);
      await onUpdated?.();
      if (!(refreshed.queries || []).some((q) => q.status === "pending")) {
        onClose?.();
      }
    } catch (e) {
      setError(e.message || "Could not respond");
    } finally {
      setRespondingId(null);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, zIndex: 360, background: C.bg, overflowY: "auto" }}>
      <TopBar title="Term change requests" onBack={onClose} />
      <div style={{ padding: "16px 20px 120px" }}>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
          The receiver proposed changes to these terms. Accept to update the contract, or decline to keep the original wording.
        </p>
        {error && (
          <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{error}</p>
        )}
        {pending.length === 0 && (
          <p style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "32px 12px" }}>No pending term queries.</p>
        )}
        {pending.map((q) => {
          const original = termsById[q.termId]?.text || "Original term";
          return (
            <div key={q.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "16px", marginBottom: 12 }}>
              <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>ORIGINAL</p>
              <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.55, margin: "0 0 12px" }}>{original}</p>
              <p style={{ color: C.gold, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>RECEIVER PROPOSED</p>
              <p style={{ color: C.text, fontSize: 13, lineHeight: 1.55, margin: "0 0 12px" }}>{q.queryText}</p>
              <textarea
                placeholder="Optional: edit the accepted wording before approving"
                value={responseText[q.id] ?? q.queryText}
                onChange={(e) => setResponseText((p) => ({ ...p, [q.id]: e.target.value }))}
                style={{
                  width: "100%", padding: "11px 13px", borderRadius: 10, marginBottom: 12,
                  background: C.surface, border: `1px solid ${C.border}`,
                  color: C.text, fontSize: 13, outline: "none",
                  resize: "none", height: 72, boxSizing: "border-box", lineHeight: 1.5,
                }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => respond(q.id, "decline")} disabled={respondingId === q.id}
                  style={{ flex: 1, padding: "11px", borderRadius: 12, border: `1px solid ${C.danger}40`, background: C.danger + "10", color: C.danger, fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: respondingId === q.id ? 0.6 : 1 }}>
                  Decline
                </button>
                <button type="button" onClick={() => respond(q.id, "accept")} disabled={respondingId === q.id}
                  style={{ flex: 2, padding: "11px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 12, fontWeight: 900, cursor: "pointer", opacity: respondingId === q.id ? 0.6 : 1 }}>
                  {respondingId === q.id ? "Saving…" : "Accept change"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContractDetailModal({
  deal,
  onClose,
  onReview,
  onCancel,
  cancelling,
  onTermQueriesUpdated,
  onAction,
  onApprove,
  approving,
  milestoneStatus,
}) {
  const C = useColors();
  const raw = deal?.raw || {};
  const isSender = deal?.role === "sender";
  const isReceiver = deal?.role === "receiver";
  const [terms, setTerms] = useState(() => resolveDealTerms(raw));
  const [termQueries, setTermQueries] = useState([]);
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [respondingId, setRespondingId] = useState(null);
  const [responseText, setResponseText] = useState({});
  const [queryError, setQueryError] = useState("");
  const milestones = parseDealMilestonesList(raw);
  const status = raw.status;
  const milestoneToneColor = (tone) => ({
    success: C.lime,
    muted: C.muted,
    gold: C.gold,
    danger: C.danger,
    info: C.info,
  }[tone] || C.muted);
  const pendingAccept = status === "pending_acceptance";
  const pendingPayment = status === "pending_payment";
  const isActive = status === "active";
  const isDispute = status === "disputed";
  const ms = milestoneStatus || deriveInitialDealState(raw).milestoneStatus;
  const pendingAmountProposal = raw.pendingAmountProposal;
  const msAmt = deal?.msAmt || formatNairaCompactFromKobo(
    milestones[Number(raw.currentMilestone ?? raw.current_milestone ?? 0)]?.amount
  );
  const canRaiseDispute = isActive && !isDispute && typeof onAction === "function";
  const disputeAction = isReceiver ? "dispute_receiver" : "dispute_sender";
  const counterpart = isSender
    ? (raw.receiverName || raw.receiverTag || "Receiver")
    : (raw.senderName || raw.senderTag || "Sender");

  useEffect(() => {
    setTerms(resolveDealTerms(raw));
  }, [deal?.id, raw?.terms, raw?.contractText, raw?.aiTerms, raw?.ai_terms]);

  useEffect(() => {
    if (!deal?.id || !pendingAccept) return undefined;
    let cancelled = false;
    setQueriesLoading(true);
    (async () => {
      try {
        const res = await getDealTermQueries(deal.id);
        if (!cancelled) setTermQueries(res.queries || []);
      } catch {
        if (!cancelled) setTermQueries([]);
      } finally {
        if (!cancelled) setQueriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deal?.id, pendingAccept]);

  const queriesByTermId = useMemo(() => {
    const map = {};
    for (const q of termQueries) {
      const prev = map[q.termId];
      if (!prev || new Date(q.createdAt) > new Date(prev.createdAt)) map[q.termId] = q;
    }
    return map;
  }, [termQueries]);

  const pendingQueries = termQueries.filter((q) => q.status === "pending");

  const respondToQuery = async (queryId, action) => {
    setQueryError("");
    setRespondingId(queryId);
    try {
      const result = await respondDealTermQuery(deal.id, queryId, {
        action,
        responseText: responseText[queryId] || undefined,
      });
      if (result.deal?.terms) setTerms(result.deal.terms);
      const refreshed = await getDealTermQueries(deal.id);
      setTermQueries(refreshed.queries || []);
      await onTermQueriesUpdated?.();
    } catch (e) {
      setQueryError(e.message || "Could not respond");
    } finally {
      setRespondingId(null);
    }
  };

  const riskColor = (risk) => {
    if (risk === "high") return C.danger;
    if (risk === "medium") return C.gold;
    return C.lime;
  };

  const termQueryBadge = (termId) => {
    const q = queriesByTermId[termId];
    if (!q) return null;
    if (q.status === "pending") return { label: "CHANGE REQUESTED", color: C.gold };
    if (q.status === "accepted") return { label: "UPDATED", color: C.info };
    if (q.status === "declined") return { label: "DECLINED", color: C.muted };
    return null;
  };

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, zIndex: 350, background: C.bg, overflowY: "auto" }}>
      <TopBar title="Contract details" onBack={onClose} />
      <div style={{ padding: "16px 20px 120px" }}>
        <div style={{
          background: C.forest, border: `1px solid ${C.borderBright}`,
          borderRadius: 20, padding: "20px", marginBottom: 16,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <p style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>{raw.reference || "Contract"}</p>
              <p style={{ color: C.text, fontSize: 18, fontWeight: 900, margin: 0 }}>{counterpart}</p>
              <p style={{ color: C.muted, fontSize: 12, margin: "4px 0 0" }}>
                {isSender ? "You are paying" : "You are receiving"}
              </p>
            </div>
            <span style={{
              background: C.gold + "22", color: C.gold, fontSize: 10, fontWeight: 800,
              padding: "4px 10px", borderRadius: 20,
            }}>{dealStatusLabel(status, deal.role)}</span>
          </div>
          <p style={{ color: C.lime, fontSize: 32, fontWeight: 900, letterSpacing: "-0.03em", margin: 0 }}>
            {formatNairaCompactFromKobo(raw.amount)}
          </p>
          {pendingAccept && deal.expiresIn && (
            <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "10px 0 0" }}>
              {isReceiver ? `Respond within ${deal.expiresIn}` : `Receiver has ${deal.expiresIn} to respond`}
            </p>
          )}
          {pendingPayment && (
            <p style={{ color: C.gold, fontSize: 12, fontWeight: 700, margin: "10px 0 0" }}>
              {isReceiver
                ? "This contract is pending until the sender funds it. You'll be able to review once payment clears."
                : raw.splitFunding && raw.walletFundedKobo > 0
                  ? `₦${Math.round(Number(raw.walletFundedKobo) / 100).toLocaleString("en-NG")} from your wallet is reserved. Complete the remaining ${formatNairaCompactFromKobo(raw.externalDueKobo)} to activate this contract.`
                  : "Complete payment to fund escrow and activate this contract."}
            </p>
          )}
          {status === "disputed" && (
            <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "10px 0 0", lineHeight: 1.45 }}>
              Under dispute — funds frozen. Terms and milestones below are for reference.
            </p>
          )}
        </div>

        {raw.contractText && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "16px", marginBottom: 16 }}>
            <p style={{ color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 10 }}>CONTRACT TEXT</p>
            <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{raw.contractText}</p>
          </div>
        )}

        <ContractFileCard url={raw.contractFileUrl} name={raw.contractFileName} />

        {isSender && pendingAccept && pendingQueries.length > 0 && (
          <div style={{
            background: C.gold + "10", border: `1px solid ${C.gold}40`,
            borderRadius: 16, padding: "16px", marginBottom: 16,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              {sv(IC.warn, 14, C.gold)}
              <p style={{ color: C.gold, fontSize: 13, fontWeight: 800, margin: 0 }}>
                {pendingQueries.length} term change request{pendingQueries.length === 1 ? "" : "s"} from {counterpart}
              </p>
            </div>
            {queryError && (
              <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>{queryError}</p>
            )}
            {pendingQueries.map((q) => {
              const original = terms.find((t) => t.id === q.termId)?.text || "Original term";
              return (
                <div key={q.id} style={{
                  background: C.card, border: `1px solid ${C.gold}35`,
                  borderRadius: 14, padding: "14px", marginBottom: 10,
                }}>
                  <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>ORIGINAL</p>
                  <p style={{ color: C.textMid, fontSize: 12, lineHeight: 1.5, margin: "0 0 10px" }}>{original}</p>
                  <p style={{ color: C.gold, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>RECEIVER PROPOSED</p>
                  <p style={{ color: C.text, fontSize: 13, lineHeight: 1.5, margin: "0 0 10px", fontWeight: 600 }}>"{q.queryText}"</p>
                  <textarea
                    placeholder="Edit accepted wording (optional)"
                    value={responseText[q.id] ?? q.queryText}
                    onChange={(e) => setResponseText((p) => ({ ...p, [q.id]: e.target.value }))}
                    style={{
                      width: "100%", padding: "10px 12px", borderRadius: 10, marginBottom: 10,
                      background: C.surface, border: `1px solid ${C.border}`,
                      color: C.text, fontSize: 12, outline: "none",
                      resize: "none", height: 64, boxSizing: "border-box", lineHeight: 1.5,
                    }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={() => respondToQuery(q.id, "decline")} disabled={respondingId === q.id}
                      style={{ flex: 1, padding: "10px", borderRadius: 10, border: `1px solid ${C.danger}40`, background: C.danger + "10", color: C.danger, fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: respondingId === q.id ? 0.6 : 1 }}>
                      Decline
                    </button>
                    <button type="button" onClick={() => respondToQuery(q.id, "accept")} disabled={respondingId === q.id}
                      style={{ flex: 2, padding: "10px", borderRadius: 10, border: "none", background: C.lime, color: C.bg, fontSize: 12, fontWeight: 900, cursor: "pointer", opacity: respondingId === q.id ? 0.6 : 1 }}>
                      {respondingId === q.id ? "Saving…" : "Accept change"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {isSender && pendingAccept && queriesLoading && (
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 16, textAlign: "center" }}>Checking for term change requests…</p>
        )}

        {terms?.length > 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "16px", marginBottom: 16 }}>
            <p style={{ color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 12 }}>KEY TERMS</p>
            {terms.map((t) => {
              const q = queriesByTermId[t.id];
              const badge = termQueryBadge(t.id);
              const displayText = q?.status === "accepted" && q.responseText ? q.responseText : t.text;
              return (
                <div key={t.id} style={{
                  padding: "12px 0",
                  borderBottom: `1px solid ${C.border}`,
                }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: q?.status === "pending" ? C.gold : riskColor(t.risk),
                      marginTop: 5, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ color: C.text, fontSize: 13, lineHeight: 1.55, margin: 0 }}>{displayText}</p>
                      {q?.status === "pending" && isSender && (
                        <p style={{ color: C.gold, fontSize: 11, margin: "6px 0 0", lineHeight: 1.45 }}>
                          Receiver asked: "{q.queryText}"
                        </p>
                      )}
                      {q?.status === "pending" && isReceiver && (
                        <p style={{ color: C.gold, fontSize: 11, margin: "6px 0 0" }}>Your change request is awaiting sender response</p>
                      )}
                      {q?.status === "declined" && isReceiver && (
                        <p style={{ color: C.muted, fontSize: 11, margin: "6px 0 0" }}>Sender declined your change — original term applies</p>
                      )}
                    </div>
                    {badge ? (
                      <span style={{ color: badge.color, fontSize: 9, fontWeight: 800, whiteSpace: "nowrap" }}>{badge.label}</span>
                    ) : (t.risk === "medium" || t.risk === "high") ? (
                      <span style={{ color: C.gold, fontSize: 9, fontWeight: 800 }}>REVIEW</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {milestones.length > 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "16px", marginBottom: 20 }}>
            <p style={{ color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 12 }}>MILESTONES</p>
            {milestones.map((m, i) => {
              const msUi = deriveMilestoneUi(i, raw, deal?.role);
              return (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 0", borderBottom: i < milestones.length - 1 ? `1px solid ${C.border}` : "none",
                }}>
                  <div>
                    <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: 0 }}>{m.label || `Milestone ${i + 1}`}</p>
                    <p style={{ color: milestoneToneColor(msUi.tone), fontSize: 10, fontWeight: 700, margin: "2px 0 0" }}>
                      {msUi.label}
                    </p>
                  </div>
                  <p style={{ color: msUi.tone === "success" ? C.lime : C.textMid, fontSize: 14, fontWeight: 800, margin: 0 }}>
                    {formatNairaCompactFromKobo(m.amount)}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
          {isReceiver && pendingAccept && onReview && (
            <button type="button" onClick={onReview} style={{
              width: "100%", padding: "16px", borderRadius: 14, border: "none",
              background: C.lime, color: C.bg, fontSize: 15, fontWeight: 900,
              cursor: "pointer",
            }}>Review & accept contract →</button>
          )}

          {isSender && (pendingAccept || pendingPayment) && onCancel && (
            <button type="button" onClick={onCancel} disabled={cancelling} style={{
              width: "100%", padding: "14px", borderRadius: 14,
              border: `1px solid ${C.danger}40`, background: C.danger + "10",
              color: C.danger, fontSize: 14, fontWeight: 800, cursor: "pointer",
              opacity: cancelling ? 0.6 : 1,
            }}>{cancelling ? "Cancelling…" : "Cancel contract"}</button>
          )}

          {pendingAmountProposal && onAction && (
            <button type="button" onClick={() => onAction("scope_change")} style={{
              width: "100%", padding: "12px", borderRadius: 12, border: "none",
              background: C.gold, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer",
            }}>
              Review amount change ({formatNairaCompactFromKobo(pendingAmountProposal.oldAmountKobo)} → {formatNairaCompactFromKobo(pendingAmountProposal.newAmountKobo)})
            </button>
          )}

          {isActive && isReceiver && (ms === "in_progress" || ms === "awaiting_claim") && onAction && (
            <button type="button" onClick={() => onAction("claim")} style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none",
              background: C.lime, color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer",
            }}>
              Mark as done — claim {msAmt}
            </button>
          )}

          {isActive && isSender && ms === "claimed" && onApprove && (
            <button
              type="button"
              onClick={onApprove}
              disabled={approving}
              style={{
                width: "100%", padding: "14px", borderRadius: 14, border: "none",
                background: C.lime, color: C.bg, fontSize: 14, fontWeight: 900,
                cursor: approving ? "wait" : "pointer", opacity: approving ? 0.75 : 1,
              }}
            >
              {approving ? "Releasing…" : `Approve & release ${msAmt}`}
            </button>
          )}

          {isActive && isSender && ms === "claimed" && onAction && (
            <button type="button" onClick={() => onAction("partial_accept")} style={{
              width: "100%", padding: "12px", borderRadius: 12,
              border: `1px solid ${C.info}40`, background: C.info + "10",
              color: C.info, fontSize: 13, fontWeight: 800, cursor: "pointer",
            }}>
              Accept partial & renegotiate remainder
            </button>
          )}

          {isActive && isReceiver && ms === "claimed" && (
            <div style={{
              width: "100%", padding: "12px", borderRadius: 12,
              background: C.info + "10", border: `1px solid ${C.info}25`, textAlign: "center",
            }}>
              <p style={{ color: C.info, fontSize: 12, fontWeight: 700, margin: 0 }}>
                Awaiting sender approval · auto-releases in {AUTO_RELEASE_HOURS}hrs
              </p>
            </div>
          )}

          {isActive && ms === "partial_released" && onAction && (
            <button type="button" onClick={() => onAction("scope_change")} style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none",
              background: C.info, color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer",
            }}>
              {isSender ? "Renegotiate remaining scope →" : "Review renegotiation →"}
            </button>
          )}

          {isDispute && isSender && onAction && (
            <button type="button" onClick={() => onAction("release_anyway_confirm")} style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none",
              background: C.lime, color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer",
            }}>
              Release funds anyway
            </button>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {isActive && isSender && ms === "claimed" && onAction && (
              <button type="button" onClick={() => onAction("reject_milestone")} style={{
                flex: 1, minWidth: 120, padding: "12px", borderRadius: 12,
                border: `1px solid ${C.danger}40`, background: C.danger + "10",
                color: C.danger, fontSize: 13, fontWeight: 800, cursor: "pointer",
              }}>
                Reject
              </button>
            )}
            {canRaiseDispute && (
              <button type="button" onClick={() => onAction(disputeAction)} style={{
                flex: 1, minWidth: 120, padding: "12px", borderRadius: 12,
                border: `1px solid ${C.danger}40`, background: C.danger + "10",
                color: C.danger, fontSize: 13, fontWeight: 800, cursor: "pointer",
              }}>
                Dispute
              </button>
            )}
            {isDispute && onAction && (
              <>
                <button type="button" onClick={() => onAction("evidence")} style={{
                  flex: 1, minWidth: 120, padding: "12px", borderRadius: 12,
                  border: `1px solid ${C.gold}40`, background: C.gold + "12",
                  color: C.gold, fontSize: 13, fontWeight: 800, cursor: "pointer",
                }}>
                  {isSender ? "Submit evidence" : "Add evidence"}
                </button>
                <button type="button" onClick={() => onAction("arbitration_timeline")} style={{
                  flex: 1, minWidth: 120, padding: "12px", borderRadius: 12,
                  border: `1px solid ${C.border}`, background: "none",
                  color: C.muted, fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}>
                  Timeline
                </button>
              </>
            )}
            {isActive && !isDispute && onAction && (
              <button type="button" onClick={() => onAction("scope_chat")} style={{
                flex: 1, minWidth: 120, padding: "12px", borderRadius: 12,
                border: `1px solid ${C.border}`, background: "none",
                color: C.muted, fontSize: 13, fontWeight: 700, cursor: "pointer",
              }}>
                Scope change
              </button>
            )}
          </div>
        </div>

        {isSender && pendingAccept && !pendingPayment && (
          <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, marginTop: 12, textAlign: "center" }}>
            Funds are locked in escrow. The receiver must accept before work begins. You can cancel anytime before they accept.
          </p>
        )}
      </div>
    </div>
  );
}

function InviteLinkScreen({
  resolution,
  inviteUrl,
  loading,
  accepting,
  onSignUp,
  onSignIn,
  onAccept,
  onContinue,
  onSignOut,
  onCopyLink,
}) {
  const C = useColors();
  const showToast = useToast();
  const inv = resolution?.invitation;
  const scenario = resolution?.scenario;
  const inviter = inv?.inviterName || "Someone";
  const inviterTag = inv?.inviterTag ? `#${inv.inviterTag}` : null;

  const copyLink = async () => {
    const url = inviteUrl || resolution?.inviteUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      showToast?.("Link copied");
    } catch {
      onCopyLink?.(url);
      showToast?.("Copy the link manually");
    }
  };

  const contextLine = inv?.context === "contract"
    ? `${inviter} wants to send you a secure contract on PendPay.`
    : `${inviter} invited you to join PendPay.`;

  let title = "You're invited";
  let body = contextLine;
  let primaryLabel = "Create account";
  let primaryAction = onSignUp;
  let secondaryLabel = null;
  let secondaryAction = null;
  let tone = C.lime;

  if (loading) {
    title = "Checking invitation…";
    body = "One moment while we load your invite.";
  } else if (scenario === "accept") {
    title = "Accept invitation";
    body = `${inviter} invited you to PendPay. Accept to connect your account with theirs.`;
    primaryLabel = accepting ? "Accepting…" : "Accept invitation";
    primaryAction = onAccept;
    secondaryLabel = "Not now";
    secondaryAction = onContinue;
  } else if (scenario === "inviter") {
    title = "Your invite link";
    body = "Share this link with the person you invited. They can sign up and connect with you on PendPay.";
    primaryLabel = "Copy invite link";
    primaryAction = copyLink;
    secondaryLabel = "Continue to app";
    secondaryAction = onContinue;
  } else if (scenario === "wrong_account") {
    title = "Different account needed";
    body = "This invite was sent to a specific email or phone number. Sign out and create an account with those details, or sign in with the invited contact.";
    primaryLabel = "Sign out";
    primaryAction = onSignOut;
    secondaryLabel = "Continue with current account";
    secondaryAction = onContinue;
    tone = C.gold;
  } else if (scenario === "accepted_by_you") {
    title = "Already joined";
    body = `You already accepted ${inviter}'s invitation. You're all set on PendPay.`;
    primaryLabel = "Go to home";
    primaryAction = onContinue;
    tone = C.lime;
  } else if (scenario === "accepted_inviter_view") {
    title = "Invite accepted";
    body = "Someone used your invitation and joined PendPay. You can now send them contracts by #tag.";
    primaryLabel = "Go to home";
    primaryAction = onContinue;
  } else if (scenario === "accepted_other") {
    title = "Invite already used";
    body = "This invitation link has already been used. If you have an account, sign in instead.";
    primaryLabel = "Sign in";
    primaryAction = onSignIn;
    secondaryLabel = "Go to home";
    secondaryAction = onContinue;
    tone = C.muted;
  } else if (scenario === "expired") {
    title = "Invitation expired";
    body = `Ask ${inviter} to send you a fresh invite — this link is no longer valid.`;
    primaryLabel = "Continue";
    primaryAction = onContinue;
    tone = C.gold;
  } else if (scenario === "not_found") {
    title = "Invalid invitation";
    body = "This invite link isn't valid. Check the link or ask the sender to invite you again.";
    primaryLabel = "Continue";
    primaryAction = onContinue;
    tone = C.danger;
  } else if (scenario === "signup") {
    secondaryLabel = "Already have an account? Sign in";
    secondaryAction = onSignIn;
  }

  return (
    <div style={{
      position: "fixed", inset: 0, ...mobileShell, zIndex: 500, background: C.bg,
      display: "flex", flexDirection: "column", fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{ flex: 1, padding: "48px 24px 24px", display: "flex", flexDirection: "column" }}>
        <div style={{ marginBottom: 32 }}><LogoFull size={64} /></div>

        <div style={{
          background: C.forestMid, border: `1px solid ${C.borderBright}`,
          borderRadius: 20, padding: "22px 20px", marginBottom: 20,
        }}>
          <p style={{ color: tone, fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", margin: "0 0 10px" }}>
            {loading ? "INVITE" : scenario === "inviter" ? "YOUR INVITE" : "PENDPAY INVITE"}
          </p>
          <p style={{ color: C.text, fontSize: 22, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 10px", lineHeight: 1.25 }}>
            {title}
          </p>
          <p style={{ color: C.textMid, fontSize: 14, lineHeight: 1.65, margin: 0 }}>{body}</p>
        </div>

        {!loading && inv && scenario !== "not_found" && scenario !== "expired" && (
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
            padding: "16px 18px", marginBottom: 16,
          }}>
            <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", margin: "0 0 8px" }}>FROM</p>
            <p style={{ color: C.text, fontSize: 16, fontWeight: 800, margin: 0 }}>{inviter}</p>
            {inviterTag && (
              <p style={{ color: C.lime, fontSize: 13, fontWeight: 700, margin: "4px 0 0" }}>{inviterTag}</p>
            )}
            {inv.message && (
              <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.55, margin: "12px 0 0", fontStyle: "italic" }}>
                &ldquo;{inv.message}&rdquo;
              </p>
            )}
            {inv.suggestedTag && scenario === "signup" && (
              <p style={{ color: C.muted, fontSize: 12, margin: "12px 0 0", lineHeight: 1.5 }}>
                Suggested tag when you sign up: <span style={{ color: C.lime, fontWeight: 800 }}>#{inv.suggestedTag}</span>
              </p>
            )}
          </div>
        )}

        {!loading && (scenario === "inviter" || scenario === "signup") && (inviteUrl || resolution?.inviteUrl) && (
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: "12px 14px", marginBottom: 16,
          }}>
            <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", margin: "0 0 6px" }}>INVITE LINK</p>
            <p style={{ color: C.textMid, fontSize: 11, wordBreak: "break-all", margin: 0 }}>
              {inviteUrl || resolution?.inviteUrl}
            </p>
          </div>
        )}

        <div style={{ marginTop: "auto", paddingTop: 16 }}>
          <button
            type="button"
            disabled={loading || accepting}
            onClick={primaryAction}
            style={{
              width: "100%", padding: "17px", borderRadius: 16, border: "none",
              background: loading ? C.card : C.lime, color: loading ? C.muted : C.bg,
              fontSize: 15, fontWeight: 900, cursor: loading || accepting ? "default" : "pointer",
              opacity: accepting ? 0.7 : 1, marginBottom: secondaryLabel ? 12 : 0,
            }}
          >
            {primaryLabel}
          </button>
          {secondaryLabel && secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction}
              style={{
                width: "100%", padding: "14px", borderRadius: 14,
                border: `1px solid ${C.border}`, background: "none",
                color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InviteSheet({ onClose, context = "general", suggestedTag = "", defaultEmail = "", defaultPhone = "" }) {
  const C = useColors();
  const showToast = useToast();
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const contextCopy =
    context === "contract"
      ? "Invite them to join PendPay so you can send a secure contract."
      : "Share PendPay with friends, clients, or collaborators.";

  const handleSend = async () => {
    setError("");
    const emailTrim = email.trim();
    const phoneTrim = phone.trim();
    if (!emailTrim && !phoneTrim) {
      setError("Enter an email or phone number");
      return;
    }
    setSubmitting(true);
    try {
      const res = await sendInvitation({
        email: emailTrim || undefined,
        phone: phoneTrim ? formatPhoneForApi(phoneTrim) : undefined,
        context,
        message: message.trim() || undefined,
        suggestedTag: suggestedTag ? suggestedTag.replace(/^#/, "") : undefined,
      });
      setResult(res);
      showToast?.("Invitation sent");
    } catch (e) {
      setError(e.message || "Could not send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(result.inviteUrl);
      showToast?.("Link copied");
    } catch {
      showToast?.("Copy the link manually");
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, ...mobileShell, zIndex: 400,
      background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column", justifyContent: "flex-end",
    }}>
      <div style={{
        background: C.surface, borderRadius: "20px 20px 0 0", border: `1px solid ${C.border}`,
        maxHeight: "90vh", overflowY: "auto", padding: "16px 20px 40px",
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 16px" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <p style={{ color: C.text, fontSize: 16, fontWeight: 900, margin: 0 }}>Invite to PendPay</p>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, marginBottom: 20 }}>{contextCopy}</p>

        {!result ? (
          <>
            {suggestedTag && (
              <div style={{
                padding: "10px 14px", borderRadius: 12, marginBottom: 16,
                background: C.gold + "12", border: `1px solid ${C.gold}30`,
              }}>
                <p style={{ color: C.gold, fontSize: 12, fontWeight: 700, margin: 0 }}>
                  No user found for #{suggestedTag.replace(/^#/, "")} — invite them instead
                </p>
              </div>
            )}
            <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>EMAIL</p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="friend@email.com"
              autoCapitalize="none"
              style={{
                width: "100%", padding: "13px 14px", borderRadius: 12, marginBottom: 14,
                background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, outline: "none",
                boxSizing: "border-box",
              }}
            />
            <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>PHONE</p>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="08012345678"
              style={{
                width: "100%", padding: "13px 14px", borderRadius: 12, marginBottom: 14,
                background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, outline: "none",
                boxSizing: "border-box",
              }}
            />
            <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>PERSONAL NOTE (OPTIONAL)</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hey — join PendPay so we can do this deal safely…"
              rows={3}
              style={{
                width: "100%", padding: "13px 14px", borderRadius: 12, marginBottom: 16,
                background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, outline: "none",
                resize: "none", boxSizing: "border-box", lineHeight: 1.5,
              }}
            />
            {error && <p style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{error}</p>}
            <button type="button" onClick={handleSend} disabled={submitting} style={{
              width: "100%", padding: "16px", borderRadius: 14, border: "none",
              background: C.lime, color: C.bg, fontSize: 15, fontWeight: 900,
              cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.7 : 1,
            }}>
              {submitting ? "Sending…" : "Send invitation"}
            </button>
          </>
        ) : (
          <div>
            <div style={{
              padding: "16px", borderRadius: 14, marginBottom: 16,
              background: C.lime + "12", border: `1px solid ${C.lime}40`,
            }}>
              <p style={{ color: C.lime, fontSize: 14, fontWeight: 800, margin: "0 0 6px" }}>
                {result.delivered ? "Invitation sent!" : "Invite link ready"}
              </p>
              <p style={{ color: C.muted, fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                {result.delivered
                  ? "They'll receive an email or SMS with a link to sign up."
                  : "Delivery wasn't available — share the link below directly."}
              </p>
            </div>
            <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>SHARE LINK</p>
            <div style={{
              display: "flex", gap: 8, marginBottom: 20,
            }}>
              <input
                readOnly
                value={result.inviteUrl || ""}
                style={{
                  flex: 1, padding: "12px 14px", borderRadius: 12,
                  background: C.card, border: `1px solid ${C.border}`, color: C.textMid, fontSize: 12, outline: "none",
                }}
              />
              <button type="button" onClick={copyLink} style={{
                padding: "12px 16px", borderRadius: 12, border: "none",
                background: C.lime, color: C.bg, fontSize: 12, fontWeight: 900, cursor: "pointer", flexShrink: 0,
              }}>Copy</button>
            </div>
            <button type="button" onClick={onClose} style={{
              width: "100%", padding: "14px", borderRadius: 14,
              border: `1px solid ${C.border}`, background: "none",
              color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer",
            }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function TagSearch({ onSelect, onInviteNotFound, initialTag }) {
  const C = useColors();
  const [query, setQuery] = useState(initialTag ? String(initialTag).replace(/^[@#]/, "") : "");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const mapUsers = (users) =>
    (users || []).map((u) => ({
      tag: u.tag,
      name: u.name,
      initials: (u.name || u.tag || "?")
        .split(/\s+/)
        .map((x) => x[0])
        .join("")
        .slice(0, 2)
        .toUpperCase(),
      color: C.lime,
    }));

  const pick = useCallback((u) => {
    setSelected(u);
    setQuery(u.tag);
    setResults([]);
    setSearched(false);
    onSelect(u.name, u.tag);
  }, [onSelect]);

  const runSearch = useCallback(async (overrideQuery) => {
    const raw = String(overrideQuery ?? query).replace(/^[@#]/, "").trim();
    if (raw.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setSearching(true);
    setSelected(null);
    setSearched(false);
    let autoSelected = false;
    try {
      const users = mapUsers((await searchUsers(raw)).users);
      if (users.length === 1) {
        autoSelected = true;
        pick(users[0]);
        return;
      }
      setResults(users);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
      if (!autoSelected) setSearched(true);
    }
  }, [query, pick]);

  useEffect(() => {
    if (!initialTag) return;
    const t = String(initialTag).replace(/^[@#]/, "").trim();
    if (!t) return;
    setQuery(t);
    runSearch(t);
  }, [initialTag, runSearch]);

  const rawQuery = query.replace(/^[@#]/, "").trim();
  const canSearch = rawQuery.length >= 2 && !searching && !selected;
  const showNotFound = searched && !searching && results.length === 0 && !selected && rawQuery.length >= 2;

  return (
    <div style={{ position: "relative" }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSearch) runSearch();
        }}
      >
        <div style={{
          display: "flex", alignItems: "center",
          background: C.card, border: `1px solid ${selected ? C.lime + "60" : C.border}`,
          borderRadius: 12, padding: "0 8px 0 14px", gap: 8,
          transition: "border-color 0.2s",
        }}>
          <input
            value={query}
            onChange={(e) => {
              setSelected(null);
              setQuery(e.target.value);
              setResults([]);
              setSearched(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Go" || e.key === "Search") {
                e.preventDefault();
                if (canSearch) runSearch();
              }
            }}
            type="search"
            enterKeyHint="search"
            inputMode="search"
            placeholder="#tag, phone, or email"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            style={{
              flex: 1, padding: "13px 0", background: "none", border: "none",
              color: C.text, fontSize: 14, outline: "none",
            }}
          />
          {selected && <span style={{ color: C.lime, fontSize: 16, paddingRight: 6 }}>✓</span>}
          {searching && (
            <span style={{ color: C.muted, fontSize: 11, paddingRight: 6 }}>…</span>
          )}
          {!selected && !searching && (
            <button
              type="submit"
              disabled={!canSearch}
              aria-label="Search recipient"
              style={{
                flexShrink: 0, width: 36, height: 36, borderRadius: 10, border: "none",
                background: canSearch ? C.lime : C.border,
                color: canSearch ? C.bg : C.muted,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: canSearch ? "pointer" : "default",
                opacity: canSearch ? 1 : 0.55,
              }}
            >
              {sv(IC.enter, 16, canSearch ? C.bg : C.muted, 2.2)}
            </button>
          )}
        </div>
      </form>
      <p style={{ color: C.muted, fontSize: 11, margin: "8px 2px 0" }}>
        Type the full #tag, phone number, or email
      </p>

      {showNotFound && onInviteNotFound && (
        <div style={{
          marginTop: 10, padding: "14px 16px", borderRadius: 14,
          background: C.card, border: `1px solid ${C.border}`,
        }}>
          <p style={{ color: C.textMid, fontSize: 13, fontWeight: 700, margin: "0 0 4px" }}>
            {rawQuery} isn't on PendPay yet
          </p>
          <p style={{ color: C.muted, fontSize: 12, margin: "0 0 12px", lineHeight: 1.45 }}>
            Send them an invite to sign up — once they join, you can send a contract.
          </p>
          <button type="button" onClick={() => onInviteNotFound(rawQuery)} style={{
            width: "100%", padding: "12px", borderRadius: 12, border: "none",
            background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer",
          }}>Invite {rawQuery} →</button>
        </div>
      )}

      {showNotFound && !onInviteNotFound && (
        <p style={{ color: C.danger, fontSize: 12, margin: "10px 2px 0" }}>
          No PendPay user found for “{rawQuery}”. Check the #tag, phone, or email and try again.
        </p>
      )}

      {searched && results.length > 0 && (
        <div style={{
          marginTop: 10,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}>
          {results.map((u, i) => (
            <button key={u.tag} type="button" onClick={() => pick(u)} style={{
              width: "100%", padding: "12px 14px", background: "none",
              border: "none", borderBottom: i < results.length - 1 ? `1px solid ${C.border}` : "none",
              display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
              textAlign: "left",
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                background: u.color + "22", border: `1px solid ${u.color}50`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, color: u.color, fontWeight: 900,
              }}>{u.initials}</div>
              <div style={{ flex: 1 }}>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: 0 }}>{u.name}</p>
                <p style={{ color: C.lime, fontSize: 11, margin: 0 }}>#{u.tag}</p>
              </div>
              <span style={{ color: C.muted, fontSize: 13 }}>›</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div style={{
          marginTop: 8, padding: "10px 14px", borderRadius: 10,
          background: C.lime + "12", border: `1px solid ${C.lime}30`,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: selected.color + "22", border: `1px solid ${selected.color}50`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, color: selected.color, fontWeight: 900, flexShrink: 0,
          }}>{selected.initials}</div>
          <div>
            <p style={{ color: C.lime, fontSize: 12, fontWeight: 800, margin: 0 }}>{selected.name}</p>
            <p style={{ color: C.muted, fontSize: 10, margin: 0 }}>Sending to PendPay wallet · instant</p>
          </div>
        </div>
      )}
    </div>
  );
}


function BankSearchPicker({ banks, selected, onSelect, label = "Select bank" }) {
  const C = useColors();
  const [query, setQuery] = useState("");

  const featuredBanks = useMemo(
    () => banks.filter((b) => b.preferred).slice(0, 4),
    [banks]
  );

  const visibleBanks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const popular = banks.filter((b) => !b.preferred).slice(0, 8);
      return [...featuredBanks, ...popular.filter((b) => !featuredBanks.some((f) => f.code === b.code))];
    }
    return banks.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        String(b.code || "").includes(q)
    );
  }, [banks, query, featuredBanks]);

  const showSearchHint = !query.trim() && banks.length > visibleBanks.length;

  return (
    <div>
      {label ? (
        <p style={{ color: C.text, fontSize: 13, fontWeight: 800, marginBottom: 10 }}>{label}</p>
      ) : null}
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search bank name…"
        autoCapitalize="none"
        autoCorrect="off"
        style={{
          width: "100%", padding: "12px 14px", borderRadius: 12, marginBottom: 12,
          background: C.card, border: `1px solid ${C.border}`, color: C.text,
          fontSize: 14, outline: "none", boxSizing: "border-box",
        }}
      />
      {query.trim() && visibleBanks.length === 0 && (
        <p style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>No banks match your search.</p>
      )}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8,
        maxHeight: query.trim() ? 260 : 220, overflowY: "auto",
      }}>
        {visibleBanks.map((b) => (
          <button
            key={`${b.code}-${b.id}`}
            type="button"
            onClick={() => onSelect(b)}
            style={{
              padding: "10px", borderRadius: 12, cursor: "pointer",
              background: selected?.code === b.code ? C.lime + "18" : C.card,
              border: `1px solid ${selected?.code === b.code ? C.lime + "60" : b.preferred ? C.info + "60" : C.border}`,
              display: "flex", alignItems: "center", gap: 8, textAlign: "left",
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: b.color, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <span style={{
                fontSize: 11, fontWeight: 600,
                color: selected?.code === b.code ? C.lime : C.textMid,
                display: "block", lineHeight: 1.35,
              }}>{b.name}</span>
              {b.preferred && (
                <span style={{ fontSize: 9, color: C.info, fontWeight: 800 }}>DIRECT INTEGRATION</span>
              )}
            </div>
          </button>
        ))}
      </div>
      {showSearchHint && (
        <p style={{ color: C.muted, fontSize: 11, margin: "0 0 12px", lineHeight: 1.45 }}>
          Can't find your bank? Search above — we list all banks supported for withdrawals.
        </p>
      )}
    </div>
  );
}


function buildMilestoneAmountsNaira(amountStr, msList, minContractNaira = 250) {
  const total = parseFloat(amountStr || 0);
  if (!total || total < minContractNaira) {
    throw new Error(`Amount must be at least ₦${minContractNaira.toLocaleString("en-NG")}`);
  }
  const rows = msList.map((m) => ({
    label: m.label || "Milestone",
    amount: Math.max(1, Math.round((total * (Number(m.pct) || 0)) / 100)),
  }));
  const sum = rows.reduce((s, r) => s + r.amount, 0);
  const diff = Math.round(total) - sum;
  if (rows.length) rows[rows.length - 1].amount += diff;
  return rows;
}

function SendScreen({ setScreen, openWalletModal, pendingSendContact, clearPendingSendContact }) {
  const C = useColors();
  const { isDark } = useTheme();
  const { config } = useAppConfig();
  const tiers = config?.tiers || [];
  const contractTemplates = config?.contractTemplates || [];
  const minPlatformFee = config?.fees?.platformMinFeeNaira ?? 650;
  const minContractNaira = config?.fees?.minContractNaira ?? 250;
  const [quickContacts, setQuickContacts] = useState([]);
  const [step, setStep] = useState(0);
  const [amount, setAmount] = useState("");
  const [recipient, setRecipient] = useState("");
  const [recipientTag, setRecipientTag] = useState("");
  const [verified, setVerified] = useState(false);
  const [desc, setDesc] = useState("");
  const [milestones, setMilestones] = useState([
    { label: "Upfront deposit", pct: 50, dueDate: "" },
    { label: "On delivery", pct: 50, dueDate: "" },
  ]);
  const [msPreset, setMsPreset] = useState("half");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const PRESETS = {
    full: [{ label: "Full payment on completion", pct: 100, dueDate: "" }],
    half: [{ label: "Upfront deposit", pct: 50, dueDate: "" }, { label: "On completion", pct: 50, dueDate: "" }],
    third: [{ label: "To start", pct: 34, dueDate: "" }, { label: "Midway", pct: 33, dueDate: "" }, { label: "On completion", pct: 33, dueDate: "" }],
  };

  const addMilestone = () => {
    if (milestones.length >= 6) return;
    const n = milestones.length + 1;
    const even = Math.floor(100 / n);
    setMilestones([...milestones.map(m => ({ ...m, pct: even })), { label: `Milestone ${n}`, pct: 100 - even * (n - 1), dueDate: "" }]);
  };
  const removeMilestone = (idx) => {
    if (milestones.length <= 1) return;
    const next = milestones.filter((_, i) => i !== idx);
    const diff = 100 - next.reduce((s, m) => s + m.pct, 0);
    if (next.length) next[next.length - 1].pct += diff;
    setMilestones(next);
  };
  const updateMsPct = (idx, raw) => {
    const pct = Math.min(Math.max(raw, 5), 95);
    const ms = milestones.map((m, i) => ({ ...m }));
    ms[idx].pct = pct;
    const others = ms.filter((_, i) => i !== idx);
    const leftover = 100 - pct;
    const oldSum = others.reduce((s, m) => s + m.pct, 0);
    others.forEach(m => { m.pct = oldSum > 0 ? Math.round(m.pct / oldSum * leftover) : Math.round(leftover / others.length); });
    ms[ms.length - 1].pct += 100 - ms.reduce((s, m) => s + m.pct, 0);
    setMilestones(ms);
  };
  const updateMsAmt = (idx, nairaStr) => {
    const naira = parseFloat(nairaStr) || 0;
    const total = parseFloat(amount || 0);
    if (!total) return;
    updateMsPct(idx, Math.round((naira / total) * 100));
  };
  const [contractText, setContractText] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [showChips, setShowChips] = useState(false);
  const [activeCat, setActiveCat] = useState("");
  const [contractFile, setContractFile] = useState(null);
  const [aiTerms, setAiTerms] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [submittingDeal, setSubmittingDeal] = useState(false);
  const [dealError, setDealError] = useState(null);
  const [createdRef, setCreatedRef] = useState("");
  const [createdDealId, setCreatedDealId] = useState("");
  const [receiptMode, setReceiptMode] = useState("funded"); // funded | awaiting_payment
  const [lastPaymentMethod, setLastPaymentMethod] = useState("bank_transfer");
  const [selectedFundingMethod, setSelectedFundingMethod] = useState("bank_transfer");
  const [fundingBreakdown, setFundingBreakdown] = useState(null);
  const [instantTransferInstructions, setInstantTransferInstructions] = useState(null);
  const [showInstantTransfer, setShowInstantTransfer] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [walletPreview, setWalletPreview] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [userTier, setUserTier] = useState(1);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSuggestedTag, setInviteSuggestedTag] = useState("");

  useEffect(() => {
    if (contractTemplates.length && !activeCat) {
      setActiveCat(contractTemplates[0].cat);
    }
  }, [contractTemplates, activeCat]);

  useEffect(() => {
    let cancelled = false;
    setWalletLoading(true);
    (async () => {
      try {
        const [w, me, freq] = await Promise.all([
          getWallet(),
          getMe(),
          getFrequentContacts().catch(() => ({ contacts: [] })),
        ]);
        if (!cancelled) {
          setWalletPreview(w);
          setUserTier(me?.tier ?? 1);
          setUserEmail(me?.email || "");
          setUserPhone(me?.phone || "");
          setQuickContacts(freq.contacts || []);
        }
      } catch {
        if (!cancelled) {
          setWalletPreview(null);
          setUserTier(1);
          setQuickContacts([]);
        }
      } finally {
        if (!cancelled) setWalletLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submitCreateDeal = async (paymentMethod) => {
    setDealError(null);
    try {
      if (isVirtualAccountPending(walletPreview, { loading: walletLoading })) {
        throw new Error("Complete bank account setup before creating a contract.");
      }
      const total = parseFloat(amount || 0);
      if (!contractText?.trim()) throw new Error("Add a contract description first.");
      if (!verified || !recipient || !recipientTag) throw new Error("Find and select a recipient first.");
      const milestonesPayload = buildMilestoneAmountsNaira(amount, milestones, minContractNaira);
      const tag = String(recipientTag || "").replace(/^#/, "").toLowerCase().trim();
      const totalToFund = senderTotal(amount);
      const walletApplied = Math.min(availNaira ?? 0, totalToFund);
      const externalRemaining = Math.max(0, totalToFund - walletApplied);
      const resolvedMethod = externalRemaining <= 0 ? "wallet" : paymentMethod;

      const body = {
        amountNaira: Math.round(total),
        milestones: milestonesPayload,
        contractText: contractText.trim(),
        paymentMethod: resolvedMethod,
        receiverTag: tag,
      };
      setSubmittingDeal(true);
      setLastPaymentMethod(resolvedMethod);
      const res = await createDeal(body, contractFile);
      const deal = res.deal || {};
      setCreatedRef(deal.reference || "");
      setCreatedDealId(deal.id || "");
      setFundingBreakdown(res.funding || null);

      if (res.funding?.fullyFundedFromWallet || deal.status === "pending_acceptance") {
        setReceiptMode("funded");
        setShowReceipt(true);
        return;
      }

      if (resolvedMethod === "card" || resolvedMethod === "ussd") {
        try {
          await completeDealPaystackCheckout({
            dealId: deal.id,
            config,
            userEmail,
            userPhone,
          });
          setReceiptMode("funded");
          setShowReceipt(true);
        } catch (e) {
          if (e.message === "Payment cancelled") {
            setReceiptMode("awaiting_payment");
            setShowReceipt(true);
          } else {
            throw e;
          }
        }
        return;
      }

      if (resolvedMethod === "bank_transfer") {
        try {
          await completeDealSafeHavenCheckout({ dealId: deal.id });
          setReceiptMode("funded");
          setShowReceipt(true);
        } catch (e) {
          if (e.message === "Payment cancelled") {
            setReceiptMode("awaiting_payment");
            setShowReceipt(true);
          } else {
            throw e;
          }
        }
        return;
      }

      if (resolvedMethod === "instant_transfer") {
        setInstantTransferInstructions(res.fundingInstructions || null);
        setShowInstantTransfer(true);
        return;
      }

      setReceiptMode("awaiting_payment");
      setShowReceipt(true);
    } catch (e) {
      setDealError(e.message || "Could not create contract");
    } finally {
      setSubmittingDeal(false);
    }
  };

  const totalFee = amt => {
    const raw = parseFloat(amt || 0) * 0.01;
    return Math.max(minPlatformFee, Math.round(raw));
  };
  const senderFee = amt => Math.round(totalFee(amt) / 2);
  const receiverFee = amt => totalFee(amt) - senderFee(amt);
  const senderTotal = amt => Math.round(parseFloat(amt || 0)) + senderFee(amt);
  const receiverGets = amt => Math.round(parseFloat(amt || 0)) - receiverFee(amt);
  /** True when ₦650 floor applies instead of straight 1%. */
  const usesMinPlatformFee = amt => Math.round(parseFloat(amt || 0) * 0.01) < minPlatformFee;
  const platformFeeBasisLabel = amt =>
    usesMinPlatformFee(amt)
      ? `₦${minPlatformFee.toLocaleString("en-NG")} minimum · split 50/50`
      : "1% of contract · split 50/50";

  const numpad = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "DEL"];

  const pressKey = k => {
    if (k === "DEL") { setAmount(a => a.slice(0, -1)); return; }
    if (k === "." && amount.includes(".")) return;
    if (amount.length >= 12) return;
    setAmount(a => a + k);
  };

  const selectRecipient = (name, tagVal) => {
    setRecipient(name);
    setRecipientTag(tagVal);
    setVerified(true);
  };

  useEffect(() => {
    if (!pendingSendContact?.tag) return;
    setRecipient(pendingSendContact.name || pendingSendContact.tag);
    setRecipientTag(pendingSendContact.tag);
    setVerified(true);
    clearPendingSendContact?.();
  }, [pendingSendContact, clearPendingSendContact]);

  const availNaira = walletPreview
    ? (walletPreview.availableBalance ?? walletPreview.available_balance ?? 0) / 100
    : null;
  const vaPending = isVirtualAccountPending(walletPreview, { loading: walletLoading });
  const contractBlocked = walletLoading || vaPending;
  const totalToFundNaira = senderTotal(amount);
  const walletAppliedNaira = Math.min(availNaira ?? 0, totalToFundNaira);
  const externalRemainingNaira = Math.max(0, totalToFundNaira - walletAppliedNaira);
  const receiptWalletAppliedNaira = fundingBreakdown?.walletFundedKobo != null
    ? fundingBreakdown.walletFundedKobo / 100
    : walletAppliedNaira;
  const receiptExternalDueNaira = fundingBreakdown?.externalDueKobo != null
    ? fundingBreakdown.externalDueKobo / 100
    : externalRemainingNaira;

  return (
    <div style={{ paddingBottom: 0 }}>
      <TopBar onBack={() => step > 0 ? setStep(s => s - 1) : setScreen("home")} title="Create Contract" />

      <div style={{ height: 3, background: C.card }}>
        <div style={{ height: "100%", width: `${((step + 1) / 5) * 100}%`, background: C.lime, transition: "width 0.35s", borderRadius: "0 2px 2px 0" }} />
      </div>

      {!walletLoading && vaPending && (
        <div style={{
          margin: "12px 20px 0", padding: "14px 16px", borderRadius: 14,
          background: C.gold + "14", border: `1px solid ${C.gold}40`,
        }}>
          <p style={{ color: C.gold, fontSize: 13, fontWeight: 800, margin: 0 }}>Bank account required</p>
          <p style={{ color: C.muted, fontSize: 12, margin: "6px 0 12px", lineHeight: 1.45 }}>
            Finish BVN/NIN setup to get your PendPay sub-account before creating a contract.
          </p>
          <button
            type="button"
            onClick={() => setScreen("profile")}
            style={{
              padding: "10px 14px", borderRadius: 12, border: "none", cursor: "pointer",
              background: C.gold, color: C.bg, fontSize: 12, fontWeight: 800,
            }}
          >
            Complete bank setup →
          </button>
        </div>
      )}

      {step === 0 && (
        <div style={{ padding: "24px 20px" }}>
          {/* Tag search */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ color: C.text, fontSize: 15, fontWeight: 800, marginBottom: 10 }}>Find recipient</p>
            <TagSearch
              onSelect={selectRecipient}
              onInviteNotFound={(tag) => { setInviteSuggestedTag(tag); setShowInvite(true); }}
            />
            <button type="button" onClick={() => { setInviteSuggestedTag(""); setShowInvite(true); }} style={{
              marginTop: 10, background: "none", border: "none", padding: 0,
              color: C.lime, fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>Invite someone to PendPay →</button>
          </div>

          <p style={{ color: C.text, fontSize: 15, fontWeight: 800, marginBottom: 12 }}>Quick Send</p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 24 }}>
            {quickContacts.slice(0, 4).map((u) => (
              <button key={u.tag} onClick={() => {
                if (contractBlocked) return;
                selectRecipient(u.name, u.tag);
                setStep(1);
              }} style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                background: "none", border: "none", cursor: contractBlocked ? "not-allowed" : "pointer", padding: 0,
                opacity: contractBlocked ? 0.45 : 1,
              }}>
                <div style={{
                  width: 46, height: 46, borderRadius: "50%",
                  background: u.color + "20", border: `1.5px solid ${u.color}50`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, color: u.color, fontWeight: 800,
                }}>{u.initials}</div>
                <span style={{ color: C.muted, fontSize: 10, maxWidth: 46, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(u.name || u.tag || "").split(" ")[0]}</span>
              </button>
            ))}
          </div>

          <button onClick={() => setStep(1)} disabled={!verified || contractBlocked} style={{
            width: "100%", padding: "17px", background: C.lime, border: "none",
            borderRadius: 16, color: C.bg, fontSize: 15, fontWeight: 900, cursor: "pointer",
            opacity: !verified || contractBlocked ? 0.3 : 1,
          }}>Continue</button>
          <p style={{ color: C.muted, fontSize: 11, textAlign: "center", marginTop: 10 }}>
            {(() => {
              const t = getTierMeta(tiers, userTier, C);
              const canUpgrade = tiers.some((tier) => tier.id > t.id);
              return (
                <>
                  {t.name} · max deal ₦{t.maxDeal.toLocaleString("en-NG")}
                  {canUpgrade ? (
                    <>
                      {" · "}
                      <button
                        type="button"
                        onClick={() => openWalletModal?.("tier")}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          color: C.gold,
                          fontWeight: 700,
                          fontSize: "inherit",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        upgrade for higher limits
                      </button>
                    </>
                  ) : null}
                </>
              );
            })()}
          </p>
        </div>
      )}

      {step === 1 && (
        <div>
          <div style={{
            background: C.forestMid, margin: "20px 20px 0",
            border: `1px solid ${C.borderBright}`,
            borderRadius: 24, padding: "32px 24px 28px", textAlign: "center",
          }}>
            <p style={{ color: C.muted, fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, marginBottom: 16 }}>AMOUNT</p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ color: C.muted, fontSize: 34, fontWeight: 300 }}>₦</span>
              <span style={{
                color: amount ? C.text : C.muted,
                fontSize: 52, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1,
                minWidth: 10,
              }}>{amount || "0"}</span>
            </div>
            <p style={{ color: C.lime, fontSize: 12, fontWeight: 700 }}>
              Minimum contract: ₦{minContractNaira.toLocaleString("en-NG")} · Transfer limit: ₦10,000,000
            </p>
            {amount && (
              <p style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>
                You lock ₦{senderTotal(amount).toLocaleString()} · recipient gets ₦{receiverGets(amount).toLocaleString()}
                <span style={{ display: "block", marginTop: 4, color: C.textMid }}>
                  Platform fee ₦{totalFee(amount).toLocaleString()} ({platformFeeBasisLabel(amount)}) — your half ₦{senderFee(amount).toLocaleString()} due now
                </span>
              </p>
            )}
          </div>

          <div style={{ padding: "20px 20px 0" }}>
            <input type="range" min={1} max={10000000} step={50000}
              value={parseInt(amount || 0)}
              onChange={e => setAmount(String(e.target.value))}
              style={{ width: "100%", accentColor: C.lime, marginBottom: 20 }} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
              {numpad.map(k => (
                <button key={k} onClick={() => pressKey(k)} style={{
                  height: 62, borderRadius: 18, border: "none", cursor: "pointer",
                  background: k === "DEL" ? C.danger + "18" : C.card,
                  border: `1px solid ${k === "DEL" ? C.danger + "40" : C.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {k === "DEL"
                    ? sv(IC.del, 18, C.danger)
                    : <span style={{ color: C.text, fontSize: 22, fontWeight: 700 }}>{k}</span>
                  }
                </button>
              ))}
            </div>

            <button onClick={() => setStep(2)} disabled={!amount || parseFloat(amount) < minContractNaira || contractBlocked} style={{
              width: "100%", padding: "17px", background: C.lime, border: "none",
              borderRadius: 16, color: C.bg, fontSize: 15, fontWeight: 900, cursor: "pointer",
              opacity: (!amount || parseFloat(amount) < minContractNaira || contractBlocked) ? 0.3 : 1,
            }}>Hold To Send</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div style={{ padding: "20px 20px 0" }}>
          <p style={{ color: C.text, fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 4 }}>Structure your deal</p>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>How should the payment be released?</p>

          {/* ── PAYMENT PRESET SELECTOR ─────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {[
              { id: "full", label: "When the job is done", sub: `Full ₦${parseInt(amount || 0).toLocaleString()} released on your approval` },
              { id: "half", label: "Half now, half when done", sub: `₦${Math.round(parseFloat(amount || 0) * 0.5).toLocaleString()} upfront · ₦${Math.round(parseFloat(amount || 0) * 0.5).toLocaleString()} on completion` },
              { id: "third", label: "In three equal stages", sub: "⅓ to start · ⅓ midway · ⅓ on completion" },
              { id: "custom", label: "Custom", sub: "I'll set my own amounts and stages" },
            ].map(opt => (
              <button key={opt.id} onClick={() => {
                setMsPreset(opt.id);
                if (opt.id !== "custom") setMilestones(PRESETS[opt.id] || PRESETS.half);
                if (opt.id === "custom") setShowAdvanced(true);
              }} style={{
                width: "100%", padding: "14px 16px", borderRadius: 14, cursor: "pointer",
                background: msPreset === opt.id ? C.lime + "15" : C.card,
                border: `2px solid ${msPreset === opt.id ? C.lime : C.border}`,
                display: "flex", alignItems: "center", gap: 14, textAlign: "left",
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  border: `2px solid ${msPreset === opt.id ? C.lime : C.border}`,
                  background: msPreset === opt.id ? C.lime : "none",
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  {msPreset === opt.id && <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.bg }} />}
                </div>
                <div>
                  <p style={{ color: msPreset === opt.id ? C.lime : C.text, fontSize: 14, fontWeight: 800, margin: 0 }}>{opt.label}</p>
                  <p style={{ color: C.muted, fontSize: 12, margin: "2px 0 0" }}>{opt.sub}</p>
                </div>
              </button>
            ))}
          </div>

          {/* ── ADVANCED: show full builder when custom OR user expands ── */}
          {(msPreset === "custom" || showAdvanced) ? (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: "16px", marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: 0 }}>Payment stages</p>
                {msPreset !== "custom" && (
                  <button onClick={() => setShowAdvanced(false)} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer" }}>Done</button>
                )}
              </div>
              {milestones.map((m, i) => {
                const col = [C.gold, C.lime, C.info, "#FF8C60", "#C084FC", "#FF5252"][i] || C.lime;
                const msAmt = Math.round(parseFloat(amount || 0) * m.pct / 100);
                return (
                  <div key={i} style={{ marginBottom: i < milestones.length - 1 ? 16 : 0, paddingBottom: i < milestones.length - 1 ? 16 : 0, borderBottom: i < milestones.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: col, flexShrink: 0 }} />
                      <input value={m.label} onChange={e => { const ms = [...milestones]; ms[i] = { ...ms[i], label: e.target.value }; setMilestones(ms); }}
                        style={{ flex: 1, padding: "8px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, outline: "none" }} />
                      <div style={{ display: "flex", alignItems: "center", background: C.surface, border: `1px solid ${col}50`, borderRadius: 8, padding: "6px 10px", minWidth: 90 }}>
                        <span style={{ color: C.muted, fontSize: 12 }}>₦</span>
                        <input type="number" value={msAmt || ""} onChange={e => updateMsAmt(i, e.target.value)}
                          style={{ width: 60, background: "none", border: "none", outline: "none", color: col, fontSize: 13, fontWeight: 800 }} />
                      </div>
                      {milestones.length > 1 && (
                        <button onClick={() => removeMilestone(i)} style={{ background: "none", border: "none", color: C.muted, fontSize: 18, cursor: "pointer", lineHeight: 1, padding: "0 4px" }}>✕</button>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <input type="range" min={5} max={95} step={1} value={m.pct}
                        onChange={e => updateMsPct(i, parseInt(e.target.value))}
                        style={{ flex: 1, accentColor: col }} />
                      <span style={{ color: col, fontSize: 13, fontWeight: 900, minWidth: 36, textAlign: "right" }}>{m.pct}%</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: C.muted, fontSize: 11, whiteSpace: "nowrap" }}>Due by</span>
                      <input type="date" value={m.dueDate} min={new Date().toISOString().split("T")[0]}
                        onChange={e => { const ms = [...milestones]; ms[i] = { ...ms[i], dueDate: e.target.value }; setMilestones(ms); }}
                        style={{ flex: 1, padding: "7px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${m.dueDate ? col + "50" : C.border}`, color: m.dueDate ? C.text : C.muted, fontSize: 12, outline: "none", colorScheme: isDark ? "dark" : "light" }} />
                      {m.dueDate && <button onClick={() => { const ms = [...milestones]; ms[i] = { ...ms[i], dueDate: "" }; setMilestones(ms); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 14, cursor: "pointer", padding: 0 }}>✕</button>}
                    </div>
                  </div>
                );
              })}
              {milestones.length < 6 && (
                <button onClick={addMilestone} style={{ width: "100%", marginTop: 14, padding: "10px", borderRadius: 10, background: "none", border: `1px dashed ${C.border}`, color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Add stage</button>
              )}
            </div>
          ) : msPreset && msPreset !== "custom" && (
            <button onClick={() => setShowAdvanced(true)} style={{
              background: "none", border: "none", color: C.lime, fontSize: 12, fontWeight: 700,
              cursor: "pointer", marginBottom: 16, padding: 0, display: "block",
            }}>Customise stages, amounts & due dates →</button>
          )}

          {/* ── CONTRACT DETAILS ─────────────────────────────────── */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginBottom: 16 }}>
            <p style={{ color: C.text, fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Describe the deal</p>
            <p style={{ color: C.muted, fontSize: 12, marginBottom: 14 }}>The receiver must accept these terms before the deal starts. Tap to add conditions.</p>

            {/* ── TEXTAREA FIRST ─── */}
            <textarea
              placeholder="Describe what's being bought or done, when it should happen, and what both sides agree to..."
              value={contractText}
              onChange={e => setContractText(e.target.value)}
              style={{
                width: "100%", padding: "13px 14px", borderRadius: 14,
                marginBottom: 8,
                background: C.surface,
                border: `2px solid ${!contractText ? C.danger + "40" : C.lime + "55"}`,
                color: C.text, fontSize: 13, outline: "none", resize: "none",
                height: 90, boxSizing: "border-box", lineHeight: 1.6,
              }}
            />
            {!contractText && (
              <p style={{ color: C.danger, fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
                ⚠ Required — describe the deal or use quick adds below
              </p>
            )}

            {/* ── EXPANDABLE: Quick adds ─── */}
            <button onClick={() => setShowChips(s => !s)} style={{
              width: "100%", padding: "11px 14px", borderRadius: 12, cursor: "pointer", marginBottom: 8,
              background: "none", border: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14 }}>⚡</span>
                <span style={{ color: C.textMid, fontSize: 12, fontWeight: 700 }}>Use quick adds</span>
              </div>
              <span style={{ color: C.muted, fontSize: 12, display: "inline-block", transform: showChips ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
            </button>

            {showChips && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px", marginBottom: 8 }}>
                {[
                  {
                    label: "🛵 Same-day / Meetup", chips: [
                      "Pay on delivery — funds release when buyer confirms receipt",
                      "Meetup exchange — funds release when both parties confirm",
                      "Item must match description or deal is cancelled",
                      "No refund once buyer confirms item received",
                      "Seller must dispatch same day",
                      "Buyer has 1 hour at meetup to inspect before confirming",
                    ]
                  },
                  {
                    label: "📦 Delivery & Services", chips: [
                      "Deliver within 24 hours",
                      "Deliver within 3 days",
                      "Deliver within 7 days",
                      "Deliver within 14 days",
                      "Deliver within 30 days",
                      "Work starts after deposit is released",
                      "Client must approve before final payment",
                    ]
                  },
                  {
                    label: "🔧 Work Conditions", chips: [
                      "3 revision rounds included",
                      "No revisions after approval",
                      "Materials supplied by client",
                      "Materials supplied by worker",
                      "Late delivery penalty applies",
                      "50% fee if client cancels after work starts",
                      "Full refund if seller cancels",
                    ]
                  },
                ].map(group => (
                  <div key={group.label} style={{ marginBottom: 12 }}>
                    <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>{group.label}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                      {group.chips.map(chip => {
                        const active = contractText.includes(chip);
                        return (
                          <button key={chip} onClick={() => {
                            if (active) {
                              setContractText(t => t.replace(`
• ${chip}`, "").replace(`• ${chip}
`, "").replace(`• ${chip}`, "").trim());
                            } else {
                              setContractText(t => t ? `${t}
• ${chip}` : `• ${chip}`);
                            }
                          }} style={{
                            padding: "6px 11px", borderRadius: 20, cursor: "pointer", fontSize: 11, fontWeight: 600,
                            background: active ? C.lime + "22" : C.surface,
                            border: `1px solid ${active ? C.lime + "60" : C.border}`,
                            color: active ? C.lime : C.muted,
                          }}>
                            {active ? "✓ " : ""}{chip}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}


            {/* ── EXPANDABLE EXTRAS ─── */}
            <div style={{ marginBottom: 20 }}>

              {/* Full contract templates — expandable */}
              <button onClick={() => setShowTemplates(s => !s)} style={{
                width: "100%", padding: "11px 14px", borderRadius: 12, cursor: "pointer", marginBottom: 8,
                background: "none", border: `1px solid ${C.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>📄</span>
                  <span style={{ color: C.textMid, fontSize: 12, fontWeight: 700 }}>Use a full contract template</span>
                </div>
                <span style={{ color: C.muted, fontSize: 12, transform: showTemplates ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
              </button>

              {showTemplates && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 8, overflow: "hidden" }}>
                  <HorizontalScroll innerStyle={{ borderBottom: `1px solid ${C.border}`, padding: "4px 4px 0" }}>
                    {contractTemplates.map(cat => (
                      <button key={cat.cat} onClick={() => setActiveCat(cat.cat)} style={{
                        padding: "8px 12px", background: activeCat === cat.cat ? C.lime + "18" : "none",
                        border: "none", borderBottom: `2px solid ${activeCat === cat.cat ? C.lime : "transparent"}`,
                        color: activeCat === cat.cat ? C.lime : C.muted, fontSize: 14, cursor: "pointer", whiteSpace: "nowrap"
                      }}>
                        {cat.icon}
                      </button>
                    ))}
                  </HorizontalScroll>
                  <div style={{ padding: "8px" }}>
                    {(contractTemplates.find((c) => c.cat === activeCat)?.templates || []).map((t) => (
                      <button key={t.name} onClick={() => { setContractText(t.text); setShowTemplates(false); }} style={{
                        width: "100%", padding: "10px 12px", borderRadius: 10, marginBottom: 4,
                        background: C.surface, border: `1px solid ${C.border}`, textAlign: "left", cursor: "pointer",
                        display: "flex", justifyContent: "space-between", alignItems: "center"
                      }}>
                        <span style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{t.name}</span>
                        <span style={{ color: C.lime, fontSize: 12 }}>Use →</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Attach file — expandable */}
              <button onClick={() => setShowAttach(s => !s)} style={{
                width: "100%", padding: "11px 14px", borderRadius: 12, cursor: "pointer",
                background: contractFile ? C.lime + "10" : "none",
                border: `1px solid ${contractFile ? C.lime + "40" : C.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>📎</span>
                  <span style={{ color: contractFile ? C.lime : C.textMid, fontSize: 12, fontWeight: 700 }}>
                    {contractFile ? contractFile.name : "Attach a contract file (PDF / DOCX)"}
                  </span>
                </div>
                {contractFile
                  ? <button onClick={e => { e.stopPropagation(); setContractFile(null); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 14, cursor: "pointer" }}>✕</button>
                  : <span style={{ color: C.muted, fontSize: 12 }}>⌄</span>
                }
              </button>

              {showAttach && !contractFile && (
                <label style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "13px 16px", borderRadius: 12, cursor: "pointer", marginTop: 8,
                  background: C.card, border: `2px dashed ${C.border}`,
                }}>
                  {sv("M14 2H6A2 2 0 004 4V20A2 2 0 006 22H18A2 2 0 0020 20V8L14 2ZM14 2V8H20", 18, C.muted)}
                  <p style={{ color: C.muted, fontSize: 12 }}>Tap to choose a file — AI will extract the key terms</p>
                  <input type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }}
                    onChange={e => { setContractFile(e.target.files[0]); setShowAttach(false); }} />
                </label>
              )}
            </div>
          </div>

          <button onClick={async () => {
            setAnalyzing(true);
            try {
              const { terms } = await analyzeContract(contractText || "");
              if (terms && Array.isArray(terms) && terms.length) {
                setAiTerms(terms);
              } else {
                throw new Error("No AI terms");
              }
              setStep(3);
            } catch (e) {
              setAiTerms(deriveTermsFromContractText(contractText));
              setStep(3);
            } finally {
              setAnalyzing(false);
            }
          }} style={{
            width: "100%", padding: "17px", background: C.lime, border: "none",
            borderRadius: 16, color: C.bg, fontSize: 15, fontWeight: 900, cursor: "pointer",
            opacity: analyzing || !contractText ? 0.4 : 1,
          }}>
            {analyzing ? "Reviewing your deal···" : "Analyse & Continue →"}
          </button>
        </div>
      )}

      {step === 3 && aiTerms && (() => {
        // editingTerm tracks which term is being edited inline
        return (
          <div style={{ padding: "24px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.lime }} />
              <p style={{ color: C.lime, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em" }}>AI CONTRACT REVIEW</p>
            </div>
            <p style={{ color: C.text, fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 4 }}>Review your terms</p>
            <p style={{ color: C.muted, fontSize: 13, marginBottom: 6 }}>AI extracted these from your description. <span style={{ color: C.lime, fontWeight: 700 }}>Tap any term to edit it</span> before the receiver sees it.</p>
            <p style={{ color: C.muted, fontSize: 12, marginBottom: 20 }}>Sending to: <span style={{ color: C.textMid, fontWeight: 700 }}>{recipient}</span></p>

            {aiTerms.map((t, i) => (
              <div key={t.id} style={{
                background: C.card, border: `1px solid ${t.risk === "high" ? C.danger + "50" : t.risk === "medium" ? C.gold + "50" : C.border}`,
                borderRadius: 14, marginBottom: 10, overflow: "hidden",
              }}>
                {editingTerm === t.id ? (
                  /* Edit mode */
                  <div style={{ padding: "14px 16px" }}>
                    <textarea
                      defaultValue={t.text}
                      autoFocus
                      onBlur={e => {
                        setAiTerms(terms => terms.map((x, j) => j === i ? { ...x, text: e.target.value } : x));
                        setEditingTerm(null);
                      }}
                      style={{
                        width: "100%", padding: "10px", borderRadius: 10, marginBottom: 8,
                        background: C.surface, border: `1px solid ${C.lime}60`,
                        color: C.text, fontSize: 13, outline: "none", resize: "none",
                        height: 80, boxSizing: "border-box", lineHeight: 1.6,
                      }}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      {["low", "medium", "high"].map(r => (
                        <button key={r} onClick={() => setAiTerms(terms => terms.map((x, j) => j === i ? { ...x, risk: r } : x))} style={{
                          flex: 1, padding: "6px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 800,
                          background: t.risk === r ? (r === "high" ? C.danger : r === "medium" ? C.gold : C.lime) + "25" : C.surface,
                          border: `1px solid ${t.risk === r ? (r === "high" ? C.danger : r === "medium" ? C.gold : C.lime) + "60" : C.border}`,
                          color: r === "high" ? C.danger : r === "medium" ? C.gold : C.lime,
                        }}>{r.toUpperCase()}</button>
                      ))}
                      <button onClick={() => { setAiTerms(terms => terms.filter((_, j) => j !== i)); setEditingTerm(null); }} style={{
                        padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 800,
                        background: C.danger + "12", border: `1px solid ${C.danger}30`, color: C.danger,
                      }}>Delete</button>
                    </div>
                  </div>
                ) : (
                  /* View mode — tap to edit */
                  <button onClick={() => setEditingTerm(t.id)} style={{
                    width: "100%", padding: "14px 16px", background: "none", border: "none",
                    display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", textAlign: "left",
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                      background: t.risk === "high" ? C.danger : t.risk === "medium" ? C.gold : C.lime,
                    }} />
                    <p style={{ flex: 1, color: C.text, fontSize: 13, lineHeight: 1.5, margin: 0 }}>{t.text}</p>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 10,
                        background: t.risk === "high" ? C.danger + "20" : t.risk === "medium" ? C.gold + "20" : C.lime + "15",
                        color: t.risk === "high" ? C.danger : t.risk === "medium" ? C.gold : C.lime,
                      }}>{t.risk === "high" ? "CHECK" : t.risk === "medium" ? "REVIEW" : "CLEAR"}</span>
                      <span style={{ color: C.muted, fontSize: 10 }}>Tap to edit</span>
                    </div>
                  </button>
                )}
              </div>
            ))}

            {/* Add a term manually */}
            <button onClick={() => {
              const newTerm = { id: `manual_${Date.now()}`, text: "", risk: "low" };
              setAiTerms(t => [...t, newTerm]);
              setEditingTerm(newTerm.id);
            }} style={{
              width: "100%", padding: "11px", borderRadius: 12, marginBottom: 16, cursor: "pointer",
              background: "none", border: `1px dashed ${C.border}`, color: C.muted,
              fontSize: 12, fontWeight: 700,
            }}>+ Add a term manually</button>

            <div style={{ padding: "13px 16px", borderRadius: 12, background: C.info + "12", border: `1px solid ${C.info}30`, marginBottom: 22, display: "flex", alignItems: "flex-start", gap: 10 }}>
              {sv(IC.shield, 14, C.info)}
              <p style={{ color: C.muted, fontSize: 12 }}>The receiver must accept all of these before the deal starts. Any terms marked <span style={{ color: C.gold, fontWeight: 700 }}>REVIEW</span> need their explicit acknowledgement.</p>
            </div>

            <button onClick={() => setStep(4)} disabled={contractBlocked} style={{
              width: "100%", padding: "17px", background: C.lime, border: "none",
              borderRadius: 16, color: C.bg, fontSize: 15, fontWeight: 900, cursor: "pointer",
              opacity: contractBlocked ? 0.3 : 1,
            }}>{vaPending ? "Complete bank setup first" : "Confirm deal →"}</button>
          </div>
        );
      })()}

      {step === 4 && (
        <div style={{ padding: "24px 20px" }}>
          <p style={{ color: C.text, fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 4 }}>Review & confirm</p>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>Check all terms before locking funds</p>

          <div style={{
            background: C.forestMid, border: `1px solid ${C.borderBright}`,
            borderRadius: 22, padding: "20px", marginBottom: 14,
          }}>
            <div style={{ textAlign: "center", marginBottom: 20, paddingBottom: 18, borderBottom: `1px solid ${C.border}` }}>
              <p style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Sending to {recipient}</p>
              <p style={{ color: C.lime, fontSize: 38, fontWeight: 900, letterSpacing: "-0.03em" }}>₦{parseInt(amount || 0).toLocaleString()}</p>
              <p style={{ color: C.muted, fontSize: 12 }}>#{String(recipientTag || "").replace(/^#/, "")}</p>
            </div>
            {[
              ["Contract value", `₦${parseInt(amount || 0).toLocaleString()}`],
              ["Platform fee (total)", `₦${totalFee(amount).toLocaleString()} · ${platformFeeBasisLabel(amount)}`],
              ["Your half (due now)", `₦${senderFee(amount).toLocaleString()}`],
              ["Total you pay to activate", `₦${senderTotal(amount).toLocaleString()}`],
              ["Receiver's half", `₦${receiverFee(amount).toLocaleString()} deducted at release`],
              ["Receiver gets", `₦${receiverGets(amount || 0).toLocaleString()}`],
              ["Milestones", `${milestones.length} stage${milestones.length !== 1 ? "s" : ""}`],
            ].map(([l, v], i, a) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <span style={{ color: C.muted, fontSize: 12, flex: 1, paddingRight: 8 }}>{l}</span>
                <span style={{
                  color: l === "Total you pay to activate" ? C.lime
                    : l === "Receiver gets" ? C.textMid
                      : l.includes("Receiver") ? C.muted
                        : C.text,
                  fontSize: 13, fontWeight: 700, textAlign: "right",
                }}>{v}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: "13px 16px", borderRadius: 14, background: C.lime + "10", border: `1px solid ${C.lime}30`, marginBottom: 22, display: "flex", alignItems: "flex-start", gap: 10 }}>
            {sv(IC.shield, 15, C.lime)}
            <div>
              <p style={{ color: C.lime, fontSize: 12, fontWeight: 800 }}>Secured via licensed banking partner</p>
              <p style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>Funds are secured via Wema ALAT / Safe Haven. Bank transfers lock straight into this contract when you include your deal reference and not your available balance. Releases only on your milestone approval.</p>
            </div>
          </div>

          {/* Wallet-first summary */}
          <div style={{ padding: "14px 16px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, marginBottom: 12 }}>
            <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", margin: "0 0 10px" }}>FUNDING BREAKDOWN</p>
            {[
              ["Total to fund", `₦${totalToFundNaira.toLocaleString("en-NG")}`],
              ["From your PendPay wallet", walletAppliedNaira > 0 ? `₦${Math.round(walletAppliedNaira).toLocaleString("en-NG")}` : "₦0"],
              ["Remaining to pay", externalRemainingNaira > 0 ? `₦${Math.round(externalRemainingNaira).toLocaleString("en-NG")}` : "₦0 · fully covered"],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span style={{ color: C.muted, fontSize: 12 }}>{label}</span>
                <span style={{ color: label === "Remaining to pay" && externalRemainingNaira > 0 ? C.lime : C.text, fontSize: 12, fontWeight: 700 }}>{value}</span>
              </div>
            ))}
          </div>

          {externalRemainingNaira > 0 && (
            <>
              <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 12 }}>PAY THE REMAINDER VIA</p>

              {dealError && (
                <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{dealError}</p>
              )}

              {[
                { method: "card", icon: "💳", title: "Debit / credit card", sub: "Verve, Mastercard, Visa · 1.5% card fee applies · Paystack" },
                { method: "bank_transfer", icon: "🏦", title: "Bank transfer", sub: "Pay from any Nigerian Bank account - No extra fee." },
                { method: "ussd", icon: "📞", title: "USSD / mobile money", sub: "*737#, *901#, OPay, Moniepoint, PalmPay · Paystack" },
              ].map(({ method, icon, title, sub }) => {
                const selected = selectedFundingMethod === method;
                return (
                  <button
                    key={method}
                    type="button"
                    disabled={submittingDeal}
                    onClick={() => setSelectedFundingMethod(method)}
                    style={{
                      width: "100%", padding: "14px 16px",
                      background: selected ? C.lime + "14" : C.card,
                      border: `1px solid ${selected ? C.lime : C.border}`,
                      borderRadius: 14, cursor: submittingDeal ? "wait" : "pointer",
                      display: "flex", alignItems: "center", gap: 12,
                      marginBottom: 8, textAlign: "left",
                      opacity: submittingDeal ? 0.7 : 1,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{icon}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: 0 }}>{title}</p>
                      <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{sub}</p>
                    </div>
                    <span style={{ color: selected ? C.lime : C.muted, fontSize: 16 }}>{selected ? "●" : "›"}</span>
                  </button>
                );
              })}

              <button
                type="button"
                disabled={submittingDeal || contractBlocked}
                onClick={() => submitCreateDeal(selectedFundingMethod)}
                style={{
                  width: "100%", padding: "16px", background: C.lime, border: "none",
                  borderRadius: 14, color: C.bg, fontSize: 15, fontWeight: 900,
                  cursor: submittingDeal || contractBlocked ? "wait" : "pointer", marginBottom: 12,
                  opacity: submittingDeal || contractBlocked ? 0.75 : 1,
                }}
              >
                {submittingDeal
                  ? "Processing…"
                  : vaPending
                    ? "Complete bank setup first"
                  : selectedFundingMethod === "instant_transfer"
                    ? `Continue · transfer ₦${Math.round(externalRemainingNaira).toLocaleString("en-NG")} →`
                    : selectedFundingMethod === "bank_transfer"
                    ? `Continue · transfer ₦${Math.round(externalRemainingNaira).toLocaleString("en-NG")} →`
                    : selectedFundingMethod === "card"
                      ? `Pay ₦${Math.round(externalRemainingNaira).toLocaleString("en-NG")} with card →`
                      : `Pay ₦${Math.round(externalRemainingNaira).toLocaleString("en-NG")} with USSD →`}
              </button>
            </>
          )}

          {externalRemainingNaira <= 0 && (
            <>
              {dealError && (
                <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{dealError}</p>
              )}
              <button
                type="button"
                disabled={submittingDeal || contractBlocked}
                onClick={() => submitCreateDeal("wallet")}
                style={{
                  width: "100%", padding: "16px", background: C.lime, border: "none",
                  borderRadius: 14, color: C.bg, fontSize: 15, fontWeight: 900,
                  cursor: submittingDeal || contractBlocked ? "wait" : "pointer", marginBottom: 12,
                  opacity: submittingDeal || contractBlocked ? 0.75 : 1,
                }}
              >
                {submittingDeal ? "Processing…" : vaPending ? "Complete bank setup first" : "Fund from wallet →"}
              </button>
            </>
          )}

          <p style={{ color: C.muted, fontSize: 11, textAlign: "center", marginBottom: 4 }}>
            Money goes <span style={{ color: C.lime, fontWeight: 700 }}>directly into the contract wallet</span>
          </p>
        </div>
      )}
      {/* ── DEAL RECEIPT ───────────────────────────────────────── */}
      {showReceipt && (
        <div style={{ position: "fixed", inset: 0, ...mobileShell, background: C.bg, zIndex: 400, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%",
            background: receiptMode === "funded" ? C.lime + "20" : C.gold + "18",
            border: `2px solid ${receiptMode === "funded" ? C.lime : C.gold}`,
            display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
          }}>
            {sv(receiptMode === "funded" ? IC.check : IC.lock, 32, receiptMode === "funded" ? C.lime : C.gold, 2.5)}
          </div>
          <p style={{ color: C.text, fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6, textAlign: "center" }}>
            {receiptMode === "funded" ? "Contract funded" : "Contract pending"}
          </p>
          <p style={{ color: C.muted, fontSize: 14, textAlign: "center", marginBottom: 28, lineHeight: 1.5 }}>
            {receiptMode === "funded"
              ? `₦${senderTotal(amount || 0).toLocaleString()} is locked in escrow. We've notified the receiver.`
              : lastPaymentMethod === "instant_transfer"
                ? receiptWalletAppliedNaira > 0
                  ? `₦${Math.round(receiptWalletAppliedNaira).toLocaleString("en-NG")} from your wallet is reserved. Transfer the remaining ₦${Math.round(receiptExternalDueNaira).toLocaleString("en-NG")} to your PendPay subaccount.`
                  : "Transfer to your PendPay subaccount to fund this contract. Include your deal reference in the narration."
                : lastPaymentMethod === "bank_transfer"
                ? receiptWalletAppliedNaira > 0
                  ? `₦${Math.round(receiptWalletAppliedNaira).toLocaleString("en-NG")} from your wallet is reserved. Pay the remaining ₦${Math.round(receiptExternalDueNaira).toLocaleString("en-NG")} via bank checkout.`
                  : "Pay with your bank to fund this contract. The receiver can review once payment clears."
                : receiptWalletAppliedNaira > 0
                  ? `₦${Math.round(receiptWalletAppliedNaira).toLocaleString("en-NG")} from your wallet is reserved. Complete the remaining ₦${Math.round(receiptExternalDueNaira).toLocaleString("en-NG")} payment.`
                  : "Complete payment from Contracts to send this to the receiver."}
          </p>
          <div style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: "18px", marginBottom: 24 }}>
            {[
              ["Contract value", `₦${parseInt(amount || 0).toLocaleString()}`],
              ["Platform fee (total)", `₦${totalFee(amount || 0).toLocaleString()} · ${platformFeeBasisLabel(amount || 0)}`],
              ["Your half", `₦${senderFee(amount || 0).toLocaleString()}`],
              ["Total to fund", `₦${senderTotal(amount || 0).toLocaleString()}`],
              ...(receiptWalletAppliedNaira > 0 ? [["From wallet", `₦${Math.round(receiptWalletAppliedNaira).toLocaleString("en-NG")}`]] : []),
              ...(receiptMode === "awaiting_payment" && receiptExternalDueNaira > 0 ? [["Remaining to pay", `₦${Math.round(receiptExternalDueNaira).toLocaleString("en-NG")}`]] : []),
              ["Receiver", recipient || "—"],
              ["Milestones", `${milestones.length} stage${milestones.length !== 1 ? "s" : ""}`],
              ["Reference", createdRef || "—"],
              ["Payment", receiptMode === "funded" ? "Confirmed" : lastPaymentMethod === "instant_transfer" ? "Instant transfer · pending" : lastPaymentMethod === "bank_transfer" ? "Bank transfer" : lastPaymentMethod === "card" ? "Card · pending" : "USSD · pending"],
              ["Status", receiptMode === "funded" ? "Funded" : "Pending"],
              ...(receiptMode === "funded" ? [["Funded on", new Date().toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })]] : []),
            ].map(([l, v], i, a) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <span style={{ color: C.muted, fontSize: 12 }}>{l}</span>
                <span style={{ color: l === "Reference" ? C.lime : l === "Total to fund" ? C.text : C.textMid, fontSize: 12, fontWeight: l === "Reference" || l === "Total to fund" ? 900 : 600 }}>{v}</span>
              </div>
            ))}
          </div>
            {receiptMode === "awaiting_payment" && lastPaymentMethod === "instant_transfer" && createdDealId && (
              <button type="button" disabled={submittingDeal} onClick={() => {
                setShowReceipt(false);
                setShowInstantTransfer(true);
              }} style={{ width: "100%", padding: "16px", borderRadius: 16, background: C.lime, border: "none", color: C.bg, fontSize: 15, fontWeight: 900, cursor: "pointer", marginBottom: 12 }}>
                View transfer details →
              </button>
            )}
            {receiptMode === "awaiting_payment" && lastPaymentMethod === "bank_transfer" && createdDealId && (
              <button type="button" disabled={submittingDeal} onClick={async () => {
                setDealError(null);
                setSubmittingDeal(true);
                try {
                  await completeDealSafeHavenCheckout({ dealId: createdDealId });
                  setReceiptMode("funded");
                } catch (e) {
                  if (e.message !== "Payment cancelled") setDealError(e.message || "Payment failed");
                } finally {
                  setSubmittingDeal(false);
                }
              }} style={{ width: "100%", padding: "16px", borderRadius: 16, background: C.lime, border: "none", color: C.bg, fontSize: 15, fontWeight: 900, cursor: "pointer", marginBottom: 12 }}>
                {submittingDeal ? "Opening checkout…" : "Complete payment now →"}
              </button>
            )}
          {receiptMode === "awaiting_payment" && (lastPaymentMethod === "card" || lastPaymentMethod === "ussd") && createdDealId && (
            <button type="button" disabled={submittingDeal} onClick={async () => {
              setDealError(null);
              setSubmittingDeal(true);
              try {
                await completeDealPaystackCheckout({ dealId: createdDealId, config, userEmail, userPhone });
                setReceiptMode("funded");
              } catch (e) {
                if (e.message !== "Payment cancelled") setDealError(e.message || "Payment failed");
              } finally {
                setSubmittingDeal(false);
              }
            }} style={{ width: "100%", padding: "16px", borderRadius: 16, background: C.lime, border: "none", color: C.bg, fontSize: 15, fontWeight: 900, cursor: "pointer", marginBottom: 12 }}>
              {submittingDeal ? "Opening checkout…" : "Complete payment now →"}
            </button>
          )}
          {dealError && (
            <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12, textAlign: "center" }}>{dealError}</p>
          )}
          <button onClick={() => { setShowReceipt(false); setScreen("deals"); }} style={{ width: "100%", padding: "16px", borderRadius: 16, background: receiptMode === "funded" ? C.lime : C.card, border: receiptMode === "funded" ? "none" : `1px solid ${C.border}`, color: receiptMode === "funded" ? C.bg : C.text, fontSize: 15, fontWeight: 900, cursor: "pointer", marginBottom: 12 }}>
            {receiptMode === "funded" ? "View in Contracts" : "Go to Contracts"}
          </button>
          <button onClick={() => { setShowReceipt(false); setStep(0); setAmount(""); setRecipient(""); setBank(null); setAcct(""); setVerified(false); }} style={{ width: "100%", padding: "14px", borderRadius: 16, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 14, cursor: "pointer" }}>Create another deal</button>
        </div>
      )}
      {showInstantTransfer && createdDealId && (
        <InstantTransferOverlay
          dealId={createdDealId}
          instructions={instantTransferInstructions}
          onFunded={() => {
            setShowInstantTransfer(false);
            setReceiptMode("funded");
            setShowReceipt(true);
          }}
          onClose={() => {
            setShowInstantTransfer(false);
            setReceiptMode("awaiting_payment");
            setShowReceipt(true);
          }}
        />
      )}
      {showInvite && (
        <InviteSheet
          context="contract"
          suggestedTag={inviteSuggestedTag}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}

function PartialAcceptModal({ deal, onClose, onConfirm }) {
  const C = useColors();
  const showToast = useToast();
  const [partialAmt, setPartialAmt] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const msAmtKobo = (() => {
    const milestones = parseDealMilestonesList(deal.raw);
    const curIdx = Number(deal.raw?.currentMilestone ?? deal.raw?.current_milestone ?? 0);
    const currentMs = milestones[curIdx];
    if (currentMs?.amount != null) return Number(currentMs.amount);
    return Number(deal.raw?.amount) || 0;
  })();
  const msAmt = Math.round(msAmtKobo / 100);

  const handleConfirm = async () => {
    const naira = parseInt(partialAmt, 10);
    if (!naira || naira <= 0 || naira >= msAmt) return;
    setSubmitting(true);
    try {
      await onConfirm(naira, note.trim());
    } catch (e) {
      showToast(e.message || "Could not release partial amount");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: `1px solid ${C.border}` }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
        <p style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: 4 }}>Accept partial milestone</p>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Release part of this milestone now. The remainder stays locked and you renegotiate scope with the receiver.</p>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
          {[["Full milestone value", `₦${msAmt.toLocaleString()}`]].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: C.muted, fontSize: 12 }}>{l}</span>
              <span style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
        <p style={{ color: C.text, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Amount to release now</p>
        <div style={{ display: "flex", alignItems: "center", background: C.card, border: `1px solid ${C.lime}50`, borderRadius: 12, padding: "10px 14px", marginBottom: 8 }}>
          <span style={{ color: C.muted, fontSize: 14 }}>₦</span>
          <input type="number" value={partialAmt} onChange={e => setPartialAmt(e.target.value)}
            placeholder={`Max ₦${msAmt.toLocaleString()}`}
            style={{ flex: 1, background: "none", border: "none", color: C.text, fontSize: 16, fontWeight: 800, outline: "none", marginLeft: 4 }} />
        </div>
        {partialAmt && parseInt(partialAmt) > 0 && parseInt(partialAmt) < msAmt && (
          <div style={{ background: C.info + "12", border: `1px solid ${C.info}30`, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
            <p style={{ color: C.info, fontSize: 11, fontWeight: 700, margin: 0 }}>
              ₦{(msAmt - parseInt(partialAmt)).toLocaleString()} remains locked · opens scope renegotiation with receiver
            </p>
          </div>
        )}
        <input value={note} onChange={e => setNote(e.target.value)}
          placeholder="Reason for partial release (optional)..."
          style={{ width: "100%", padding: "11px 13px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 12, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} disabled={submitting} style={{ flex: 1, padding: "14px", borderRadius: 12, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !partialAmt || parseInt(partialAmt, 10) <= 0 || parseInt(partialAmt, 10) >= msAmt}
            style={{
              flex: 2, padding: "14px", borderRadius: 12, border: "none",
              background: partialAmt && parseInt(partialAmt, 10) > 0 && parseInt(partialAmt, 10) < msAmt ? C.lime : C.card,
              color: partialAmt && parseInt(partialAmt, 10) > 0 && parseInt(partialAmt, 10) < msAmt ? C.bg : C.muted,
              fontSize: 13, fontWeight: 900, cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.75 : 1
            }}>
            {submitting ? "Releasing…" : `Release ₦${partialAmt ? parseInt(partialAmt, 10).toLocaleString() : "—"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function DisputeModal({ deal, evidenceInput, setEvidenceInput, onClose, onSubmit }) {
  const C = useColors();
  const [dispatchFiles, setDispatchFiles] = useState([]); // File[]
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const files = Array.from(e.target.files);
    setDispatchFiles((a) => [...a, ...files]);
    e.target.value = "";
  };

  const isEvidence = deal.action === "evidence";
  const isReceiverDispute = deal.action === "dispute_receiver";

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: `1px solid ${C.border}` }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
        <p style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: 4 }}>
          {isEvidence ? "Submit evidence" : "Raise a dispute"}
        </p>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 14 }}>
          {isReceiverDispute
            ? "If the sender is withholding funds unfairly, explain your situation. PendPay can force-release within 7 days."
            : isEvidence
              ? "Upload documents, screenshots, or any proof to support your case."
              : "If work was not completed as agreed, explain what is missing."
          }
        </p>
        <div style={{ background: C.danger + "12", border: `1px solid ${C.danger}30`, borderRadius: 12, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          {sv(IC.lock, 12, C.danger)}
          <p style={{ color: C.danger, fontSize: 11, fontWeight: 800, margin: 0 }}>Funds frozen · neither party can withdraw during arbitration</p>
        </div>
        <textarea
          placeholder={isReceiverDispute
            ? "Describe what you delivered and why funds should be released..."
            : "Describe what was missing or not delivered as agreed..."
          }
          value={evidenceInput}
          onChange={e => setEvidenceInput(e.target.value)}
          style={{
            width: "100%", padding: "13px", borderRadius: 12, marginBottom: 10,
            background: C.card, border: `1px solid ${C.border}`,
            color: C.text, fontSize: 13, outline: "none", resize: "none",
            height: 80, boxSizing: "border-box", lineHeight: 1.5,
          }}
        />
        {/* File attachments */}
        <input type="file" ref={fileRef} onChange={handleFile} multiple style={{ display: "none" }} />
        {dispatchFiles.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {dispatchFiles.map((f, i) => (
              <div key={`${f.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 5, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "4px 10px" }}>
                <span style={{ fontSize: 12 }}>📎</span>
                <span style={{ color: C.textMid, fontSize: 11, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                <span style={{ color: C.muted, fontSize: 10 }}>{(f.size / 1024).toFixed(0)}KB</span>
                <button type="button" onClick={() => setDispatchFiles((a) => a.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", padding: 0 }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => fileRef.current?.click()} style={{
          width: "100%", padding: "11px", borderRadius: 12, marginBottom: 14,
          background: "none", border: `1px dashed ${C.border}`,
          color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          {sv("M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48", 14, C.muted, 1.5)}
          Attach documents or screenshots
        </button>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" onClick={onClose} disabled={busy} style={{ flex: 1, padding: "14px", borderRadius: 12, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button
            type="button"
            onClick={async () => {
              const canSubmit = evidenceInput.trim().length > 0 || dispatchFiles.length > 0;
              if (!canSubmit) return;
              setBusy(true);
              try {
                await Promise.resolve(onSubmit(evidenceInput, dispatchFiles));
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy || (!evidenceInput.trim() && dispatchFiles.length === 0)}
            style={{
              flex: 2, padding: "14px", borderRadius: 12, border: "none",
              background: (evidenceInput.trim() || dispatchFiles.length > 0) && !busy ? C.danger : C.card,
              color: (evidenceInput.trim() || dispatchFiles.length > 0) && !busy ? C.text : C.muted,
              fontSize: 13, fontWeight: 900, cursor: busy ? "wait" : "pointer",
            }}
          >{busy ? "Please wait…" : isEvidence ? "Submit evidence" : "Submit dispute"}</button>
        </div>
      </div>
    </div>
  );
}

function CancelContractModal({ deal, onClose, onConfirm, cancelling, error }) {
  const C = useColors();
  const raw = deal?.raw || {};
  const counterpart = raw.receiverName || raw.receiverTag || deal?.name || deal?.counterpart || "the receiver";
  const amount = deal?.amt || formatNairaCompactFromKobo(raw.amount);

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: `1px solid ${C.border}` }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
        <div style={{
          width: 48, height: 48, borderRadius: 14, background: C.danger + "18",
          border: `1px solid ${C.danger}40`, display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 16,
        }}>
          {sv(IC.warn, 22, C.danger, 2)}
        </div>
        <p style={{ color: C.text, fontSize: 18, fontWeight: 900, marginBottom: 8 }}>Cancel this contract?</p>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
          Your locked funds for <span style={{ color: C.text, fontWeight: 700 }}>{counterpart}</span>
          {amount ? <> ({amount})</> : null} will be returned to your wallet. This cannot be undone.
        </p>
        {error && (
          <p style={{ color: C.danger, fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>{error}</p>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" onClick={onClose} disabled={cancelling} style={{
            flex: 1, padding: "14px", borderRadius: 12, background: "none",
            border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 700,
            cursor: cancelling ? "not-allowed" : "pointer", opacity: cancelling ? 0.6 : 1,
          }}>Keep contract</button>
          <button type="button" onClick={onConfirm} disabled={cancelling} style={{
            flex: 1, padding: "14px", borderRadius: 12, border: "none",
            background: cancelling ? C.card : C.danger,
            color: cancelling ? C.muted : C.text,
            fontSize: 13, fontWeight: 900, cursor: cancelling ? "not-allowed" : "pointer",
          }}>{cancelling ? "Cancelling…" : "Yes, cancel"}</button>
        </div>
      </div>
    </div>
  );
}

function ReleaseAnywayModal({ deal, onClose, onConfirm, releasing }) {
  const C = useColors();
  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: `1px solid ${C.border}` }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
        <p style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: 8 }}>Release payment anyway?</p>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>
          This closes the dispute and releases <span style={{ color: C.text, fontWeight: 700 }}>{deal.msAmt || deal.amt}</span> to{" "}
          <span style={{ color: C.text, fontWeight: 700 }}>{deal.name}</span>. This cannot be undone.
        </p>
        <div style={{ background: C.lime + "12", border: `1px solid ${C.lime}30`, borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: 0 }}>Dispute will be marked resolved in the receiver&apos;s favour</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" onClick={onClose} disabled={releasing} style={{ flex: 1, padding: "14px", borderRadius: 12, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button type="button" onClick={onConfirm} disabled={releasing} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "none", background: releasing ? C.card : C.lime, color: releasing ? C.muted : C.bg, fontSize: 13, fontWeight: 900, cursor: releasing ? "wait" : "pointer" }}>
            {releasing ? "Releasing…" : "Yes, release funds"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectionModal({ deal, onClose, onSubmit }) {
  const C = useColors();
  const [reason, setReason] = useState("");
  const cats = ["Work not completed as agreed", "Quality below standard", "Wrong deliverable submitted", "Deadline not met", "Other"];
  const [selected, setSelected] = useState(null);
  const fullReason = selected === "Other" ? reason : selected ? `${selected}${reason ? ": " + reason : ""}` : reason;
  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: `1px solid ${C.border}` }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
        <p style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: 4 }}>Reject milestone</p>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>You must provide a reason. This is recorded and shared with the worker and PendPay arbitration.</p>
        <p style={{ color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 10 }}>SELECT A REASON</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {cats.map(c => (
            <button key={c} onClick={() => setSelected(c)} style={{
              padding: "11px 14px", borderRadius: 12, textAlign: "left", cursor: "pointer",
              background: selected === c ? C.danger + "20" : C.card,
              border: `1px solid ${selected === c ? C.danger + "80" : C.border}`,
              color: selected === c ? C.danger : C.textMid, fontSize: 13, fontWeight: selected === c ? 800 : 500,
            }}>{c}</button>
          ))}
        </div>
        <p style={{ color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>ADDITIONAL DETAILS {selected && selected !== "Other" ? "(optional)" : "(required)"}</p>
        <textarea
          placeholder="Describe exactly what was missing or incorrect..."
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={{ width: "100%", padding: "13px", borderRadius: 12, marginBottom: 14, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, outline: "none", resize: "none", height: 80, boxSizing: "border-box", lineHeight: 1.5 }}
        />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "14px", borderRadius: 12, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={() => fullReason.trim() && onSubmit(fullReason)} disabled={!fullReason.trim()} style={{
            flex: 2, padding: "14px", borderRadius: 12, border: "none",
            background: fullReason.trim() ? C.danger : C.card,
            color: fullReason.trim() ? C.text : C.muted,
            fontSize: 13, fontWeight: 900, cursor: "pointer",
          }}>Submit rejection</button>
        </div>
      </div>
    </div>
  );
}

const SCOPE_CHAT_SYSTEM = { id: "sys", from: "system", text: "Scope changes only. Archived for 90 days after final disbursement." };

function ScopeChatModal({ deal, onClose, onDealUpdated, onRaiseDispute }) {
  const C = useColors();
  const showToast = useToast();
  const [msgs, setMsgs] = useState([SCOPE_CHAT_SYSTEM]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]); // {name, size}
  const [showMonetary, setShowMonetary] = useState(false);
  const [newAmt, setNewAmt] = useState("");
  const [amtReason, setAmtReason] = useState("");
  const [myId, setMyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postError, setPostError] = useState("");
  const [sending, setSending] = useState(false);
  const [responding, setResponding] = useState(null);
  const [monetaryAutoOpened, setMonetaryAutoOpened] = useState(false);

  const fileRef = useRef(null);
  const isReceiver = deal.role === "receiver";
  const isSender = deal.role === "sender";

  const scopeOldKobo = (() => {
    const milestones = parseDealMilestonesList(deal.raw);
    const curIdx = Number(deal.raw?.currentMilestone ?? deal.raw?.current_milestone ?? 0);
    const currentMs = milestones[curIdx];
    if (currentMs?.amount != null) return Number(currentMs.amount);
    return Number(deal.raw?.amount) || 0;
  })();
  const scopeOldNaira = Math.round(scopeOldKobo / 100);
  const isPartialRenegotiation = deal.raw?.partialRelease?.remainderKobo != null
    || parseDealMilestonesList(deal.raw)[Number(deal.raw?.currentMilestone ?? 0)]?.status === "partial_released";
  const partialReleasedAmt = deal.raw?.partialRelease?.releasedKobo != null
    ? formatNairaCompactFromKobo(deal.raw.partialRelease.releasedKobo)
    : null;

  const mapScopeRowToUi = useCallback((m, uid) => {
    let ap = m.amountProposal;
    if (typeof ap === "string") {
      try { ap = JSON.parse(ap); } catch { ap = null; }
    }
    if (ap && typeof ap === "object" && (ap.oldAmountKobo != null || ap.newAmountKobo != null)) {
      return {
        id: m.id,
        senderId: m.senderId,
        from: "amount_proposal",
        oldAmt: formatNairaCompactFromKobo(ap.oldAmountKobo ?? 0),
        newAmt: formatNairaCompactFromKobo(ap.newAmountKobo ?? 0),
        reason: ap.reason,
        status: ap.status || "pending",
      };
    }
    let att = m.attachments;
    if (typeof att === "string") {
      try { att = JSON.parse(att); } catch { att = []; }
    }
    return {
      id: m.id,
      from: m.senderId === uid ? "you" : "other",
      text: m.message || "",
      attachments: Array.isArray(att) ? att : [],
    };
  }, []);

  const respondProposal = async (messageId, action) => {
    setResponding(messageId);
    setPostError("");
    try {
      await respondAmountProposal(deal.id, messageId, action);
      const me = await getMe();
      setMyId(me?.id);
      const data = await getScopeMessages(deal.id);
      const mapped = (data.messages || []).map((row) => mapScopeRowToUi(row, me?.id));
      setMsgs([SCOPE_CHAT_SYSTEM, ...mapped]);
      if (action === "accept" && typeof onDealUpdated === "function") await onDealUpdated();
      showToast(
        action === "accept" ? "Amount change accepted — contract updated" : "Amount change declined",
        action === "accept" ? "success" : "info"
      );
    } catch (e) {
      setPostError(e.message || "Could not update proposal");
      showToast(e.message || "Could not update proposal");
    } finally {
      setResponding(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPostError("");
      try {
        const [me, data] = await Promise.all([getMe(), getScopeMessages(deal.id)]);
        if (cancelled) return;
        setMyId(me?.id);
        const mapped = (data.messages || []).map((row) => mapScopeRowToUi(row, me?.id));
        setMsgs([SCOPE_CHAT_SYSTEM, ...mapped]);
      } catch (e) {
        if (!cancelled) setPostError(e.message || "Could not load scope chat");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deal.id, mapScopeRowToUi]);

  const inboundPendingProposal = (() => {
    const fromMsg = [...msgs].reverse().find(
      (m) => m.from === "amount_proposal" && m.status === "pending" && m.senderId && m.senderId !== myId
    );
    if (fromMsg) return fromMsg;
    const api = deal.raw?.pendingAmountProposal;
    if (api?.messageId && myId && api.proposerId !== myId) {
      return {
        id: api.messageId,
        oldAmt: formatNairaCompactFromKobo(api.oldAmountKobo),
        newAmt: formatNairaCompactFromKobo(api.newAmountKobo),
        reason: api.reason,
        status: "pending",
        senderId: api.proposerId,
        proposerName: api.proposerName,
        from: "amount_proposal",
      };
    }
    return null;
  })();

  useEffect(() => {
    if (loading || !myId || monetaryAutoOpened) return;
    if (isPartialRenegotiation && isReceiver && !inboundPendingProposal) {
      setShowMonetary(true);
      setMonetaryAutoOpened(true);
    }
  }, [loading, myId, monetaryAutoOpened, isPartialRenegotiation, isReceiver, inboundPendingProposal]);

  const handleFile = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(a => [...a, ...files.map(f => ({ name: f.name, size: (f.size / 1024).toFixed(0) + "KB" }))]);
    e.target.value = "";
  };

  const removeAttachment = (i) => setAttachments(a => a.filter((_, j) => j !== i));

  const send = async () => {
    if (!input.trim() && attachments.length === 0) return;
    setSending(true);
    setPostError("");
    try {
      const { message } = await postScopeMessage(deal.id, {
        message: input.trim(),
        attachments: attachments.map((a) => ({ name: a.name, size: a.size })),
      });
      const ui = mapScopeRowToUi(message, myId);
      setMsgs((m) => [...m, ui]);
      setInput("");
      setAttachments([]);
    } catch (e) {
      setPostError(e.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const proposalCardLabel = isPartialRenegotiation ? "REMAINDER PROPOSAL" : "AMOUNT CHANGE PROPOSAL";

  const proposeAmount = async () => {
    if (!newAmt || !amtReason.trim()) return;
    const newNaira = parseInt(newAmt, 10);
    const oldKobo = scopeOldKobo;
    const newKobo = Math.round(newNaira * 100);
    setSending(true);
    setPostError("");
    try {
      const { message } = await postScopeMessage(deal.id, {
        amountProposal: {
          oldAmountKobo: oldKobo,
          newAmountKobo: newKobo,
          reason: amtReason.trim(),
        },
      });
      const ui = mapScopeRowToUi(message, myId);
      setMsgs((m) => [...m, ui]);
      setShowMonetary(false);
      setNewAmt("");
      setAmtReason("");
      showToast("Proposal sent — waiting for the other party to respond", "success");
    } catch (e) {
      setPostError(e.message || "Could not send proposal");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", border: `1px solid ${C.border}`, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 14px" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <p style={{ color: C.text, fontSize: 15, fontWeight: 900, margin: 0 }}>Scope renegotiation</p>
              <p style={{ color: C.muted, fontSize: 11, margin: 0 }}>{deal.name} · 90-day archive</p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => setShowMonetary(!showMonetary)} style={{
                padding: "6px 12px", borderRadius: 10, cursor: "pointer", fontSize: 11, fontWeight: 800,
                background: showMonetary ? C.gold + "30" : C.card,
                border: `1px solid ${showMonetary ? C.gold + "60" : C.border}`,
                color: showMonetary ? C.gold : C.muted,
              }}>₦ Amount</button>
              <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
          </div>
        </div>

        {/* Partial renegotiation — what to do next */}
        {isPartialRenegotiation && (
          <div style={{ padding: "12px 20px", background: C.info + "12", borderBottom: `1px solid ${C.info}30` }}>
            <p style={{ color: C.info, fontSize: 12, fontWeight: 800, margin: "0 0 4px" }}>
              {isReceiver ? "Partial payment received — agree on the remainder" : "Renegotiate the locked remainder"}
            </p>
            <p style={{ color: C.muted, fontSize: 11, margin: 0, lineHeight: 1.45 }}>
              {partialReleasedAmt && isReceiver ? `${partialReleasedAmt} already paid. ` : ""}
              {isReceiver
                ? `Propose what you should receive for the remaining work (${formatNairaCompactFromKobo(scopeOldKobo)} locked). The sender must accept before you can claim again.`
                : `Review the receiver's proposal below, or propose a remainder amount. Accepting updates the locked funds.`}
              {typeof onRaiseDispute === "function" && deal.raw?.status === "active" && (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={onRaiseDispute}
                    style={{
                      background: "none", border: "none", padding: 0, margin: 0,
                      color: C.danger, fontSize: 11, fontWeight: 800, cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Raise a dispute instead
                  </button>
                </>
              )}
            </p>
          </div>
        )}

        {/* Sticky Accept / Decline when a proposal is waiting on you */}
        {inboundPendingProposal && (
          <div style={{ padding: "14px 20px", background: C.gold + "18", borderBottom: `1px solid ${C.gold}40` }}>
            <p style={{ color: C.gold, fontSize: 11, fontWeight: 800, margin: "0 0 8px" }}>
              {inboundPendingProposal.proposerName || deal.name} PROPOSED A {isPartialRenegotiation ? "REMAINDER" : "NEW AMOUNT"}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: inboundPendingProposal.reason ? 8 : 10 }}>
              <span style={{ color: C.muted, fontSize: 14, textDecoration: "line-through" }}>{inboundPendingProposal.oldAmt}</span>
              <span style={{ color: C.muted }}>→</span>
              <span style={{ color: C.text, fontSize: 16, fontWeight: 900 }}>{inboundPendingProposal.newAmt}</span>
            </div>
            {inboundPendingProposal.reason && (
              <p style={{ color: C.textMid, fontSize: 12, margin: "0 0 10px", lineHeight: 1.45 }}>{inboundPendingProposal.reason}</p>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                disabled={responding === inboundPendingProposal.id}
                onClick={() => respondProposal(inboundPendingProposal.id, "accept")}
                style={{
                  flex: 1, padding: "11px", borderRadius: 10, border: "none",
                  background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900,
                  cursor: responding === inboundPendingProposal.id ? "wait" : "pointer",
                  opacity: responding === inboundPendingProposal.id ? 0.7 : 1,
                }}
              >
                {responding === inboundPendingProposal.id ? "…" : "Accept"}
              </button>
              <button
                type="button"
                disabled={responding === inboundPendingProposal.id}
                onClick={() => respondProposal(inboundPendingProposal.id, "decline")}
                style={{
                  flex: 1, padding: "11px", borderRadius: 10, border: `1px solid ${C.danger}50`,
                  background: C.danger + "15", color: C.danger, fontSize: 13, fontWeight: 800,
                  cursor: responding === inboundPendingProposal.id ? "wait" : "pointer",
                  opacity: responding === inboundPendingProposal.id ? 0.7 : 1,
                }}
              >
                Decline
              </button>
            </div>
          </div>
        )}

        {/* Monetary change panel */}
        {showMonetary && (
          <div style={{ padding: "14px 20px", background: C.gold + "10", borderBottom: `1px solid ${C.gold}30` }}>
            <p style={{ color: C.gold, fontSize: 12, fontWeight: 800, marginBottom: 10 }}>
              {isPartialRenegotiation ? "PROPOSE NEW REMAINDER AMOUNT" : "PROPOSE AMOUNT CHANGE"}
            </p>
            {isPartialRenegotiation && (
              <p style={{ color: C.muted, fontSize: 11, margin: "0 0 10px", lineHeight: 1.45 }}>
                Current locked remainder for this milestone: {formatNairaCompactFromKobo(scopeOldKobo)}
              </p>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
              <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 12px" }}>
                <p style={{ color: C.muted, fontSize: 10, marginBottom: 2 }}>CURRENT</p>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 800 }}>
                  {isPartialRenegotiation ? formatNairaCompactFromKobo(scopeOldKobo) : deal.amt}
                </p>
              </div>
              <span style={{ color: C.muted, fontSize: 16 }}>→</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", background: C.card, border: `1px solid ${C.gold}50`, borderRadius: 10, padding: "8px 12px" }}>
                  <span style={{ color: C.muted, fontSize: 13 }}>₦</span>
                  <input
                    type="number"
                    value={newAmt}
                    onChange={e => setNewAmt(e.target.value)}
                    placeholder="New amount"
                    style={{ flex: 1, background: "none", border: "none", color: C.text, fontSize: 13, fontWeight: 800, outline: "none", marginLeft: 4 }}
                  />
                </div>
              </div>
            </div>
            <input
              value={amtReason}
              onChange={e => setAmtReason(e.target.value)}
              placeholder="Reason for change (required)..."
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box", marginBottom: 10 }}
            />
            {newAmt && parseInt(newAmt, 10) !== scopeOldNaira && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 12px", marginBottom: 10 }}>
                <p style={{ color: C.muted, fontSize: 11 }}>
                  {parseInt(newAmt, 10) > scopeOldNaira
                    ? `↑ Sender tops up ₦${(parseInt(newAmt, 10) - scopeOldNaira).toLocaleString()} into contract wallet on acceptance`
                    : `↓ ₦${(scopeOldNaira - parseInt(newAmt, 10)).toLocaleString()} returned to sender on acceptance`
                  }
                </p>
              </div>
            )}
            <button onClick={proposeAmount} disabled={sending || !newAmt || !amtReason.trim()} style={{
              width: "100%", padding: "10px", borderRadius: 10, border: "none", cursor: "pointer",
              background: newAmt && amtReason.trim() && !sending ? C.gold : C.card,
              color: newAmt && amtReason.trim() && !sending ? C.bg : C.muted,
              fontSize: 13, fontWeight: 900,
            }}>{sending ? "Sending…" : "Send proposal"}</button>
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
          {postError && (
            <p style={{ color: C.danger, fontSize: 12, textAlign: "center", margin: 0 }}>{postError}</p>
          )}
          {loading && (
            <p style={{ color: C.muted, fontSize: 12, textAlign: "center" }}>Loading conversation…</p>
          )}
          {msgs.map((m, i) => (
            <div key={m.id || i} style={{
              alignSelf: m.from === "system" ? "center" : m.from === "amount_proposal" ? "center" : m.from === "you" ? "flex-end" : "flex-start",
              maxWidth: m.from === "system" || m.from === "amount_proposal" ? "100%" : "80%",
            }}>
              {m.from === "system" && (
                <p style={{ color: C.muted, fontSize: 11, textAlign: "center", background: C.card, padding: "7px 12px", borderRadius: 10, border: `1px solid ${C.border}`, margin: 0 }}>{m.text}</p>
              )}
              {m.from === "amount_proposal" && (
                <div style={{ background: C.gold + "15", border: `1px solid ${C.gold}40`, borderRadius: 14, padding: "12px 14px", width: "100%" }}>
                  <p style={{ color: C.gold, fontSize: 11, fontWeight: 800, marginBottom: 6 }}>
                    {proposalCardLabel} — {(m.status || "pending").toUpperCase()}
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: m.reason ? 6 : 8 }}>
                    <span style={{ color: C.muted, fontSize: 13, textDecoration: "line-through" }}>{m.oldAmt}</span>
                    <span style={{ color: C.muted }}>→</span>
                    <span style={{ color: C.text, fontSize: 15, fontWeight: 900 }}>{m.newAmt}</span>
                  </div>
                  {m.reason && (
                    <p style={{ color: C.textMid, fontSize: 12, marginBottom: 8, lineHeight: 1.45 }}>{m.reason}</p>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    {m.status === "pending" && myId && m.senderId && m.senderId !== myId ? (
                      <>
                        <button
                          type="button"
                          disabled={responding === m.id}
                          onClick={() => respondProposal(m.id, "accept")}
                          style={{
                            flex: 1, padding: "8px", borderRadius: 10, border: `1px solid ${C.lime}50`,
                            background: C.lime + "18", color: C.lime, fontSize: 12, fontWeight: 800,
                            cursor: responding === m.id ? "wait" : "pointer", opacity: responding === m.id ? 0.6 : 1,
                          }}
                        >
                          {responding === m.id ? "…" : "Accept"}
                        </button>
                        <button
                          type="button"
                          disabled={responding === m.id}
                          onClick={() => respondProposal(m.id, "decline")}
                          style={{
                            flex: 1, padding: "8px", borderRadius: 10, border: `1px solid ${C.danger}40`,
                            background: C.danger + "10", color: C.danger, fontSize: 12, fontWeight: 800,
                            cursor: responding === m.id ? "wait" : "pointer", opacity: responding === m.id ? 0.6 : 1,
                          }}
                        >
                          Decline
                        </button>
                      </>
                    ) : (
                      <p style={{ color: C.muted, fontSize: 11, margin: 0, textAlign: "center", width: "100%" }}>
                        {m.status === "pending" && myId && m.senderId === myId
                          ? "Waiting for the other party to accept or decline"
                          : m.status === "declined" && isPartialRenegotiation
                            ? "Declined — send a new remainder proposal above"
                            : m.status !== "pending"
                              ? `Proposal ${m.status}`
                              : ""}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {m.from !== "system" && m.from !== "amount_proposal" && (
                <div style={{
                  background: m.from === "you" ? C.lime + "20" : C.card,
                  border: `1px solid ${m.from === "you" ? C.lime + "40" : C.border}`,
                  borderRadius: 14, padding: "10px 14px",
                }}>
                  <p style={{ color: C.text, fontSize: 13, margin: 0 }}>{m.text}</p>
                  {m.attachments && m.attachments.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                      {m.attachments.map((a, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, background: C.card, borderRadius: 8, padding: "5px 8px", border: `1px solid ${C.border}` }}>
                          <span style={{ fontSize: 14 }}>📎</span>
                          <p style={{ color: C.textMid, fontSize: 11, margin: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</p>
                          <span style={{ color: C.muted, fontSize: 10 }}>{a.size}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Attachment preview */}
        {attachments.length > 0 && (
          <div style={{ padding: "8px 20px 0", display: "flex", flexWrap: "wrap", gap: 6 }}>
            {attachments.map((a, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "4px 8px" }}>
                <span style={{ fontSize: 12 }}>📎</span>
                <span style={{ color: C.textMid, fontSize: 11, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                <button onClick={() => removeAttachment(i)} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Input row */}
        <div style={{ padding: "10px 20px 32px", borderTop: `1px solid ${C.border}` }}>
          {isPartialRenegotiation && !showMonetary && !inboundPendingProposal && (
            <button
              type="button"
              onClick={() => setShowMonetary(true)}
              style={{
                width: "100%", padding: "12px", borderRadius: 12, border: "none", marginBottom: 10,
                background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer",
              }}
            >
              {isReceiver
                ? `Propose remainder amount (locked: ${formatNairaCompactFromKobo(scopeOldKobo)})`
                : `Propose remainder amount`}
            </button>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <input type="file" ref={fileRef} onChange={handleFile} multiple style={{ display: "none" }} />
            <button onClick={() => fileRef.current?.click()} style={{
              width: 40, height: 40, borderRadius: 12, background: C.card, border: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
            }}>
              {sv("M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48", 16, C.muted, 1.5)}
            </button>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !sending && send()}
              placeholder={isPartialRenegotiation ? "Discuss scope changes…" : "Propose a change..."}
              style={{ flex: 1, padding: "11px 14px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, outline: "none" }}
            />
            <button onClick={send} disabled={sending || (!input.trim() && attachments.length === 0)} style={{
              width: 40, height: 40, borderRadius: 12, border: "none", flexShrink: 0,
              background: !sending && (input.trim() || attachments.length > 0) ? C.lime : C.card,
              color: !sending && (input.trim() || attachments.length > 0) ? C.bg : C.muted,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
              {sv("M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z", 15, !sending && (input.trim() || attachments.length > 0) ? C.bg : C.muted, 1.5)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ArbitrationTimelineModal({ deal, onClose }) {
  const C = useColors();
  const dispute = deal?.raw?.dispute || deal?.dispute || null;
  const timeline = buildArbitrationTimeline(dispute);
  const counterpart = deal?.name || "Counterparty";

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: `1px solid ${C.border}` }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <p style={{ color: C.text, fontSize: 16, fontWeight: 900, margin: 0 }}>Arbitration timeline</p>
            <p style={{ color: C.muted, fontSize: 12, margin: "4px 0 0" }}>
              {counterpart} · {timeline.headline}
            </p>
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ background: C.danger + "12", border: `1px solid ${C.danger}30`, borderRadius: 12, padding: "10px 14px", marginBottom: 12 }}>
          <p style={{ color: C.danger, fontSize: 11, fontWeight: 800, margin: 0 }}>
            {timeline.isResolved
              ? "DISPUTE CLOSED · FUNDS SETTLED PER RULING"
              : "FUNDS FROZEN · NEITHER PARTY CAN WITHDRAW"}
          </p>
        </div>
        {timeline.currentLabel && !timeline.isResolved && (
          <p style={{ color: C.textMid || C.muted, fontSize: 12, margin: "0 0 16px" }}>
            Current stage: <span style={{ color: C.text, fontWeight: 700 }}>{timeline.currentLabel}</span>
            {" · "}target resolve within {timeline.targetDays} days
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {timeline.steps.map((e, i) => {
            const done = e.state === "done";
            const current = e.state === "current";
            const accent = done || current ? C.lime : C.border;
            return (
              <div key={e.id} style={{ display: "flex", gap: 14, paddingBottom: i < timeline.steps.length - 1 ? 20 : 0 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    background: done ? C.lime : current ? C.lime + "22" : C.card,
                    border: `2px solid ${accent}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {done
                      ? sv(IC.check, 14, C.bg, 2.5)
                      : <div style={{ width: 8, height: 8, borderRadius: "50%", background: current ? C.lime : C.border }} />}
                  </div>
                  {i < timeline.steps.length - 1 && (
                    <div style={{ width: 2, flex: 1, background: done ? C.lime + "40" : C.border, marginTop: 4 }} />
                  )}
                </div>
                <div style={{ paddingBottom: 4 }}>
                  <p style={{
                    color: done || current ? C.text : C.muted,
                    fontSize: 13,
                    fontWeight: done || current ? 800 : 500,
                    marginBottom: 2,
                    marginTop: 0,
                  }}>
                    {e.label}
                    {current ? " · now" : ""}
                  </p>
                  <p style={{ color: C.muted, fontSize: 11, margin: 0 }}>{e.sub}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function mapApiDealToUi(d) {
  let milestones = d.milestones;
  if (typeof milestones === "string") {
    try { milestones = JSON.parse(milestones); } catch { milestones = []; }
  }
  if (!Array.isArray(milestones)) milestones = [];
  const totalMs = milestones.length;
  const cur = d.currentMilestone != null ? Number(d.currentMilestone) : 0;
  const msLabel = totalMs
    ? (d.status === "completed" ? `${totalMs} of ${totalMs}` : `${Math.min(cur + 1, totalMs)} of ${totalMs}`)
    : "—";

  const counterpart =
    d.source === "checkout" && d.merchant?.displayName
      ? d.merchant.displayName
      : d.role === "sender"
        ? (d.receiverName || d.receiverTag || "Receiver")
        : (d.senderName || d.senderTag || "Sender");

  let uiStatus = "escrow";
  if (d.status === "completed") uiStatus = "released";
  else if (d.status === "disputed") uiStatus = "dispute";
  else if (d.status === "pending_acceptance" || d.status === "pending_payment") uiStatus = "pending";
  else if (d.status === "cancelled" || d.status === "expired") uiStatus = "closed";

  const pendingAccept = d.status === "pending_acceptance";
  const pendingPayment = d.status === "pending_payment";

  let expiresIn = "";
  if (pendingAccept && d.expiresAt) {
    const h = Math.ceil((new Date(d.expiresAt) - Date.now()) / 3600000);
    expiresIn = h > 0 ? `${h}hrs` : "Soon";
  }

  const dateStr = d.createdAt
    ? new Date(d.createdAt).toLocaleDateString("en-NG", { month: "short", day: "numeric" })
    : "";

  const msIndex = totalMs ? Math.min(Math.max(cur, 0), totalMs - 1) : 0;
  const currentMs = milestones[msIndex];
  const msAmt = currentMs?.amount != null
    ? formatNairaCompactFromKobo(currentMs.amount)
    : formatNairaCompactFromKobo(d.amount);

  const paymentDueKobo = d.externalDueKobo != null ? Number(d.externalDueKobo) : null;
  const walletFundedKobo = d.walletFundedKobo != null ? Number(d.walletFundedKobo) : 0;

  return {
    id: d.id,
    name: counterpart,
    amt: formatNairaCompactFromKobo(d.amount),
    paymentDue: paymentDueKobo != null ? formatNairaCompactFromKobo(paymentDueKobo) : null,
    walletFunded: walletFundedKobo > 0 ? formatNairaCompactFromKobo(walletFundedKobo) : null,
    splitFunding: Boolean(d.splitFunding),
    status: uiStatus,
    cat: d.source === "checkout" ? "Merchant order" : "Contract",
    date: dateStr,
    ms: msLabel,
    role: d.role,
    msAmt,
    pendingAccept,
    pendingPayment,
    expiresIn,
    statusLabel: dealStatusLabel(d.status, d.role),
    merchantLogo: d.merchant?.logoUrl || null,
    raw: d,
  };
}

function deriveInitialDealState(raw) {
  const st = raw.status;
  if (st === "disputed") return { milestoneStatus: "disputed", evidence: "" };
  if (st === "completed") return { milestoneStatus: "complete" };

  const ev = raw.currentMilestoneEvent;
  const milestones = parseDealMilestonesList(raw);
  const curIdx = Number(raw.currentMilestone ?? raw.current_milestone ?? 0);
  const currentMs = milestones[curIdx];

  if (currentMs?.status === "partial_released") {
    return {
      milestoneStatus: "partial_released",
      partialReleasedKobo: currentMs.partialReleasedKobo ?? ev?.amount,
      remainderKobo: currentMs.amount,
    };
  }
  if (ev?.type === "claimed") {
    return { milestoneStatus: "claimed", claimNote: ev.note || "" };
  }
  if (ev?.type === "rejected") return { milestoneStatus: "in_progress" };

  return { milestoneStatus: "in_progress" };
}

/** Local-only UI states that the API does not track — preserve across refresh. */
const LOCAL_MILESTONE_STATUSES = new Set(["early_release_requested"]);

function mergeDealState(prevLocal, fromApi) {
  if (LOCAL_MILESTONE_STATUSES.has(prevLocal.milestoneStatus)) {
    return { ...fromApi, ...prevLocal };
  }
  // Server-derived milestone phase wins (e.g. after approve advances current_milestone).
  return { ...prevLocal, ...fromApi };
}

function fmtCompactKobo(kobo) {
  const n = (Number(kobo) || 0) / 100;
  if (n >= 1e6) return `₦${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `₦${Math.round(n / 1e3)}K`;
  return `₦${Math.round(n).toLocaleString("en-NG")}`;
}

function statsFromAnalytics(analytics = {}) {
  const moneyInKobo = Number(analytics.moneyInKobo) || 0;
  const releasedOutKobo = Number(analytics.releasedOutKobo) || 0;
  const awaitingPaymentKobo = Number(analytics.awaitingPaymentKobo) || 0;
  const inContractKobo = Number(analytics.inContractKobo) || 0;
  const awaitingOnly = awaitingPaymentKobo > 0 && inContractKobo === 0;
  return {
    moneyInKobo,
    releasedOutKobo,
    awaitingPaymentKobo,
    inContractKobo,
    income: fmtCompactKobo(moneyInKobo),
    released: fmtCompactKobo(releasedOutKobo),
    middleLabel: awaitingOnly ? "Pending" : "In contract",
    middleSub: awaitingOnly ? "awaiting funding" : "funded / open",
    middleValue: fmtCompactKobo(awaitingOnly ? awaitingPaymentKobo : inContractKobo),
    middleColorKey: awaitingOnly ? "gold" : "info",
    deals: Number(analytics.dealsCount) || 0,
    rate: analytics.successRate || "—",
  };
}

/** Wallet analytics: period toggle, money stats, success rate, contract flow chart. */
function DealAnalyticsPanel({ analyticsByPeriod = {}, period, setPeriod }) {
  const C = useColors();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const chartData = useMemo(() => {
    const row = analyticsByPeriod[period] || {};
    return {
      labels: row.labels || [],
      income: row.income || [],
      outflow: row.outflow || row.escrow || [],
    };
  }, [analyticsByPeriod, period]);

  const st = useMemo(
    () => statsFromAnalytics(analyticsByPeriod[period] || {}),
    [analyticsByPeriod, period]
  );
  const flowTotal = Math.max(
    1,
    st.moneyInKobo + st.releasedOutKobo + st.inContractKobo + st.awaitingPaymentKobo
  );
  const inPct = Math.min(100, Math.round((st.moneyInKobo / flowTotal) * 100));
  const middleColor = st.middleColorKey === "gold" ? C.gold : C.info;

  useEffect(() => {
    if (!chartRef.current) return;
    if (typeof window === "undefined") return;

    const buildChart = () => {
      if (!window.Chart) return;
      if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null; }
      const d = chartData;
      if (!d.labels?.length) return;
      chartInstance.current = new window.Chart(chartRef.current, {
        type: "line",
        data: {
          labels: d.labels,
          datasets: [
            {
              label: "Money in (you received)",
              data: d.income,
              borderColor: "#2EC4A0",
              backgroundColor: "rgba(46,196,160,0.08)",
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: "#2EC4A0",
              tension: 0.4,
              fill: true,
            },
            {
              label: "Money out (you sent)",
              data: d.outflow,
              borderColor: "#F0D060",
              backgroundColor: "rgba(240,208,96,0.05)",
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: "#F0D060",
              tension: 0.4,
              fill: false,
              borderDash: [5, 3],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#1C2A1C",
              borderColor: "#253225",
              borderWidth: 1,
              titleColor: "#5A7050",
              bodyColor: "#EEF5E0",
              padding: 10,
              callbacks: {
                label: (ctx) => {
                  const v = ctx.raw;
                  const formatted = v >= 1000000
                    ? "₦" + (v / 1000000).toFixed(1) + "M"
                    : "₦" + Math.round(v / 1000) + "K";
                  return " " + ctx.dataset.label + ": " + formatted;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { color: "rgba(37,50,37,0.6)", drawBorder: false },
              ticks: { color: "#5A7050", font: { size: 10, weight: "600" }, maxRotation: 0 },
              border: { display: false },
            },
            y: {
              grid: { color: "rgba(37,50,37,0.6)", drawBorder: false },
              ticks: {
                color: "#5A7050", font: { size: 10 },
                callback: (v) => (v >= 1000000 ? "₦" + (v / 1000000).toFixed(1) + "M" : "₦" + Math.round(v / 1000) + "K"),
                maxTicksLimit: 5,
              },
              border: { display: false },
            },
          },
        },
      });
    };

    if (window.Chart) {
      buildChart();
    } else {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
      s.onload = buildChart;
      document.head.appendChild(s);
    }
    return () => { if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null; } };
  }, [period, chartData]);

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: "flex", background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: 4, marginBottom: 18,
      }}>
        {["Week", "Month", "Year"].map((p) => (
          <button key={p} type="button" onClick={() => setPeriod(p)} style={{
            flex: 1, padding: "8px 0", borderRadius: 10, border: "none", cursor: "pointer",
            background: period === p ? C.lime : "transparent",
            color: period === p ? C.bg : C.muted,
            fontSize: 12, fontWeight: 800, transition: "all 0.2s",
          }}>{p}</button>
        ))}
      </div>

      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14,
      }}>
        {[
          { label: "Money in", sub: "paid to you, cleared", value: st.income, color: C.lime },
          {
            label: st.middleLabel,
            sub: st.middleSub,
            value: st.middleValue,
            color: middleColor,
          },
          { label: "Released", sub: "you sent, settled", value: st.released, color: C.gold },
        ].map((s) => (
          <div key={s.label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 14, padding: "12px 10px", minWidth: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
              <span style={{ color: C.muted, fontSize: 8, fontWeight: 800, letterSpacing: "0.04em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.label.toUpperCase()}</span>
            </div>
            <p style={{ color: C.muted, fontSize: 8, marginBottom: 6, lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{s.sub}</p>
            <p style={{ color: s.color, fontSize: 16, fontWeight: 900, letterSpacing: "-0.02em", margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ height: 4, borderRadius: 4, background: C.border, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${inPct}%`, background: C.lime, borderRadius: 4, transition: "width 0.3s" }} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Contracts", value: st.deals },
          { label: "Success rate", value: st.rate },
        ].map((s) => (
          <div key={s.label} style={{
            background: C.forestMid, border: `1px solid ${C.borderBright}`,
            borderRadius: 14, padding: "12px 14px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ color: C.muted, fontSize: 12 }}>{s.label}</span>
            <span style={{ color: C.text, fontSize: 16, fontWeight: 900 }}>{s.value}</span>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 20, padding: "18px 16px 14px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: 0 }}>Contract flow</p>
          <div style={{ display: "flex", gap: 14 }}>
            {[{ color: C.lime, label: "Money in" }, { color: C.gold, label: "Money out", dashed: true }].map((l) => (
              <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{
                  width: 18, height: 2,
                  background: l.dashed ? "transparent" : l.color,
                  borderTop: l.dashed ? `2px dashed ${l.color}` : "none",
                  borderRadius: 2,
                }} />
                <span style={{ color: C.muted, fontSize: 10, fontWeight: 700 }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: "relative", width: "100%", height: 160 }}>
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  );
}

function DealsScreen({ setScreen, selectedDealId, onDealSelected, pendingDealAction, clearPendingDealAction, unreadCount, onNotificationsClick }) {
  const C = useColors();
  const showToast = useToast();
  const { config } = useAppConfig();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [dealStates, setDealStates] = useState({});
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [activeDeal, setActiveDeal] = useState(null);
  const [evidenceInput, setEvidenceInput] = useState("");
  const [claimNote, setClaimNote] = useState("");
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelConfirmDeal, setCancelConfirmDeal] = useState(null);
  const [cancelError, setCancelError] = useState("");
  const [paymentCompletingId, setPaymentCompletingId] = useState(null);
  const [paymentError, setPaymentError] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [fundingWallet, setFundingWallet] = useState(null);
  const [termQueriesByDeal, setTermQueriesByDeal] = useState({});
  const [fundingWalletLoading, setFundingWalletLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [releasingDespiteDisputeId, setReleasingDespiteDisputeId] = useState(null);
  const [instantTransferDeal, setInstantTransferDeal] = useState(null);
  const [instantTransferInstructions, setInstantTransferInstructions] = useState(null);

  const loadFundingWallet = useCallback(async () => {
    setFundingWalletLoading(true);
    try {
      const w = await getWallet();
      setFundingWallet(w);
      return w;
    } catch {
      setFundingWallet(null);
      return null;
    } finally {
      setFundingWalletLoading(false);
    }
  }, []);

  const requestCancelContract = (dealId) => {
    const deal = deals.find((d) => d.id === dealId) || (activeDeal?.id === dealId ? activeDeal : null);
    setCancelError("");
    setCancelConfirmDeal(deal || { id: dealId });
  };

  const handleCancelContract = async () => {
    if (!cancelConfirmDeal?.id) return;
    const dealId = cancelConfirmDeal.id;
    setCancellingId(dealId);
    setCancelError("");
    try {
      await cancelDeal(dealId);
      setCancelConfirmDeal(null);
      setActiveDeal(null);
      await loadDeals();
    } catch (e) {
      setCancelError(e.message || "Could not cancel contract");
    } finally {
      setCancellingId(null);
    }
  };

  const loadTermQueriesForDeals = useCallback(async (rows) => {
    const pending = rows.filter((d) => d.pendingAccept);
    if (!pending.length) {
      setTermQueriesByDeal({});
      return;
    }
    const map = {};
    await Promise.all(
      pending.map(async (d) => {
        try {
          const res = await getDealTermQueries(d.id);
          map[d.id] = res.queries || [];
        } catch {
          map[d.id] = [];
        }
      })
    );
    setTermQueriesByDeal(map);
  }, []);

  const loadDeals = useCallback(async () => {
    setDealsLoading(true);
    try {
      const params = { limit: 100 };
      const q = query.trim();
      if (q) params.q = q;
      if (roleFilter === "sender" || roleFilter === "receiver") params.role = roleFilter;
      const res = await getDeals(params);
      const mapped = (res.deals || []).map(mapApiDealToUi);
      setDeals(mapped);
      setDealStates((prev) => {
        const ds = {};
        mapped.forEach((row) => {
          const fromApi = deriveInitialDealState(row.raw);
          const prevLocal = prev[row.id] || {};
          ds[row.id] = mergeDealState(prevLocal, fromApi);
        });
        return ds;
      });
      await loadTermQueriesForDeals(mapped);
      return mapped;
    } catch (e) {
      console.error(e);
      setDeals([]);
      setTermQueriesByDeal({});
      return [];
    } finally {
      setDealsLoading(false);
    }
  }, [loadTermQueriesForDeals, query, roleFilter]);

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  useEffect(() => {
    loadFundingWallet();
  }, [loadFundingWallet]);

  useEffect(() => {
    const needsAccount = deals.some(
      (d) => d.pendingPayment && ["bank_transfer", "instant_transfer"].includes(d.raw?.paymentMethod)
    );
    const vaPending = isVirtualAccountPending(fundingWallet, { loading: fundingWalletLoading });
    if (!needsAccount || !vaPending) return undefined;
    const id = setInterval(() => { loadFundingWallet(); }, 15000);
    return () => clearInterval(id);
  }, [deals, fundingWallet, fundingWalletLoading, loadFundingWallet]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMe();
        if (!cancelled) {
          setUserEmail(me?.email || "");
          setUserPhone(me?.phone || "");
        }
      } catch { /* optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCompletePayment = async (dealId, paymentMethod) => {
    setPaymentError("");
    setPaymentCompletingId(dealId);
    try {
      if (paymentMethod === "instant_transfer") {
        const deal = deals.find((d) => d.id === dealId);
        const wallet = fundingWallet || await loadFundingWallet();
        const externalDueKobo = deal?.raw?.externalDueKobo ?? deal?.raw?.external_due_kobo;
        setInstantTransferInstructions({
          accountNumber: resolveWalletAccountNumber(wallet),
          bankName: resolveWalletBankName(wallet),
          accountName: wallet?.accountName || wallet?.account_name || "—",
          amountKobo: externalDueKobo,
          reference: deal?.raw?.reference,
          narrationHint: deal?.raw?.reference,
          note: "Transfer the exact amount to your PendPay subaccount and include the deal reference in the narration.",
        });
        setInstantTransferDeal(dealId);
        return;
      }
      if (paymentMethod === "bank_transfer") {
        await completeDealSafeHavenCheckout({ dealId });
      } else {
        await completeDealPaystackCheckout({ dealId, config, userEmail, userPhone });
      }
      await loadDeals();
    } catch (e) {
      if (e.message !== "Payment cancelled") {
        setPaymentError(e.message || "Payment failed");
      }
    } finally {
      setPaymentCompletingId(null);
    }
  };

  useEffect(() => {
    if (!selectedDealId || dealsLoading || !deals.length) return;
    if (pendingDealAction?.dealId === selectedDealId) return;
    const row = deals.find((d) => d.id === selectedDealId || d.raw?.id === selectedDealId);
    if (row) {
      if (row.pendingAccept && row.role === "receiver") {
        setActiveDeal({ ...row, action: "review_contract" });
      } else {
        setActiveDeal({ ...row, action: "view_contract" });
      }
      onDealSelected?.();
    }
  }, [selectedDealId, deals, dealsLoading, onDealSelected, pendingDealAction?.dealId]);

  useEffect(() => {
    if (!pendingDealAction?.dealId || dealsLoading || !deals.length) return;
    const row = deals.find((d) => d.id === pendingDealAction.dealId || d.raw?.id === pendingDealAction.dealId);
    if (row) {
      setActiveDeal({ ...row, action: pendingDealAction.action || "scope_change" });
      clearPendingDealAction?.();
      onDealSelected?.();
    }
  }, [pendingDealAction, deals, dealsLoading, clearPendingDealAction, onDealSelected]);

  const sc = (d) => {
    const ms = dealStates[d.id]?.milestoneStatus;
    if (d.status === "dispute") return C.danger;
    if (d.status === "closed") return C.muted;
    if (d.status === "pending") return C.gold;
    if (d.status === "released") return C.lime;
    if (ms === "claimed") return C.info;
    if (ms === "awaiting_release") return C.gold;
    return C.gold;
  };
  const sl = (d) => {
    const ms = dealStates[d.id]?.milestoneStatus;
    if (d.status === "closed") return d.statusLabel || "Closed";
    if (d.pendingPayment) return d.statusLabel || "Pending";
    if (d.status === "pending") return d.statusLabel || "Pending";
    if (d.status === "dispute") return d.role === "receiver" ? "Disputed — awaiting arbitration" : "Dispute open";
    if (d.status === "released") return d.role === "receiver" ? "Paid to you" : "You paid";
    if (ms === "claimed") return "Claim sent — awaiting sender";
    if (ms === "awaiting_release") return "Sender approved — releasing";
    return d.role === "receiver" ? "Awaiting your claim" : "Awaiting your release";
  };
  const filtered = filter === "all" ? deals : deals.filter(d => d.status === filter);
  const hasActiveFilters = Boolean(query.trim()) || filter !== "all" || roleFilter !== "all";

  const chipStyle = (active) => ({
    flex: "0 0 auto",
    boxSizing: "border-box",
    height: 34,
    padding: "0 14px",
    borderRadius: 999,
    border: `1.5px solid ${active ? C.lime : C.border}`,
    background: active ? `${C.lime}18` : C.card,
    color: active ? C.lime : C.textMid || C.text,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  });

  const filterSectionLabel = {
    display: "block",
    color: C.muted,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 8,
  };

  const chipScroller = {
    display: "flex",
    flexWrap: "nowrap",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 2,
    marginBottom: 14,
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  };

  const searchInputStyle = {
    flex: 1,
    minWidth: 0,
    boxSizing: "border-box",
    height: 48,
    padding: "0 14px",
    borderRadius: 14,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.text,
    fontSize: 14,
    fontWeight: 600,
    outline: "none",
  };

  const STATUS_CHIPS = [
    ["all", "All"],
    ["pending", "Pending"],
    ["escrow", "Active"],
    ["released", "Completed"],
    ["dispute", "Disputes"],
    ["closed", "Closed"],
  ];
  const ROLE_CHIPS = [
    ["all", "All"],
    ["sender", "You fund"],
    ["receiver", "You deliver"],
  ];

  return (
    <div style={{ paddingBottom: 0 }}>
      <TopBar title="Contracts" unreadCount={unreadCount} onNotificationsClick={onNotificationsClick} right={
        <button onClick={() => setScreen("send")} style={{
          width: 34, height: 34, borderRadius: 12, background: C.lime,
          border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
        }}>{sv(IC.plus, 16, C.bg, 2.5)}</button>
      } />

      <div style={{ padding: "16px 20px 0" }}>
        {dealsLoading && (
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>Loading contracts…</p>
        )}

        {paymentError && (
          <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{paymentError}</p>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadDeals()}
            placeholder="Search name, @tag, or ref"
            aria-label="Search contracts"
            style={searchInputStyle}
          />
          <button
            type="button"
            onClick={loadDeals}
            aria-label="Search"
            style={{
              flex: "0 0 auto",
              width: 48,
              height: 48,
              borderRadius: 14,
              border: `1px solid ${C.border}`,
              background: C.card,
              color: C.lime,
              cursor: "pointer",
              fontSize: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ⌕
          </button>
        </div>

        <div style={{ marginBottom: 4 }}>
          <span style={filterSectionLabel}>Status</span>
          <div style={chipScroller}>
            {STATUS_CHIPS.map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setFilter(v)}
                style={chipStyle(filter === v)}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <span style={filterSectionLabel}>Role</span>
          <div style={{ ...chipScroller, marginBottom: 16 }}>
            {ROLE_CHIPS.map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setRoleFilter(v)}
                style={chipStyle(roleFilter === v)}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Transaction list */}
        {!dealsLoading && filtered.length === 0 && (
          <p style={{ color: C.muted, fontSize: 13, padding: "24px 8px", textAlign: "center" }}>
            {hasActiveFilters
              ? "No contracts match your search or filters."
              : "No contracts yet. Create one with the + button above."}
          </p>
        )}
        {filtered.map((d, i) => {
          const ms = dealStates[d.id]?.milestoneStatus;
          const partialRelease = d.raw?.partialRelease;
          const partialReleasedAmt = partialRelease?.releasedKobo != null
            ? formatNairaCompactFromKobo(partialRelease.releasedKobo)
            : (dealStates[d.id]?.partialAmt || dealStates[d.id]?.partialPaid);
          const remainderLockedAmt = partialRelease?.remainderKobo != null
            ? formatNairaCompactFromKobo(partialRelease.remainderKobo)
            : null;
          const color = sc(d);
          const label = sl(d);
          const isSender = d.role === "sender";
          const isReceiver = d.role === "receiver";
          const rawSt = d.raw?.status;
          const isPending = d.status === "pending";
          const isActive = d.status === "escrow" && rawSt === "active";
          const isClosed = d.status === "closed";
          const isDispute = d.status === "dispute";
          const canRaiseDispute = isActive && !isDispute;
          const disputeAction = isReceiver ? "dispute_receiver" : "dispute_sender";
          const isClaimed = ms === "claimed";

          // Status pill text — short and clean
          const statusPill = () => {
            if (isDispute) return { text: "Disputed", color: C.danger };
            if (isClosed) return { text: rawSt === "expired" ? "Expired" : "Cancelled", color: C.muted };
            if (isPending && d.pendingPayment) return { text: "Pending", color: C.gold };
            if (isPending && d.pendingAccept) return { text: isReceiver ? "Review" : "Awaiting acceptance", color: C.gold };
            if (d.status === "released") return { text: isSender ? "Released" : "Received", color: C.lime };
            if (isClaimed) return { text: "Claimed", color: C.info };
            if (ms === "partial_released") return { text: "Renegotiating", color: C.info };
            if (d.raw?.pendingAmountProposal) return { text: "Scope change", color: C.gold };
            if (ms === "awaiting_claim") return { text: "Active", color: C.gold };
            return { text: "Active", color: C.gold };
          };
          const pill = statusPill();
          const pendingTermQueries = (termQueriesByDeal[d.id] || []).filter((q) => q.status === "pending");
          const pendingAmountProposal = d.raw?.pendingAmountProposal;

          // One-line context beneath the name
          const contextLine = () => {
            if (pendingAmountProposal) {
              return `${pendingAmountProposal.proposerName} proposed ${formatNairaCompactFromKobo(pendingAmountProposal.oldAmountKobo)} → ${formatNairaCompactFromKobo(pendingAmountProposal.newAmountKobo)}`;
            }
            if (isDispute) return isReceiver ? "Under arbitration" : "Dispute open";
            if (d.status === "released") return `Completed · ${d.date}`;
            if (isClaimed) return `Claimed · auto-release ${AUTO_RELEASE_HOURS}hrs`;
            if (isPending && d.pendingPayment) return `Pending · ${d.date} · MS ${d.ms}`;
            return `${d.cat} · ${d.date} · MS ${d.ms}`;
          };

          return (
            <div key={d.id} style={{ marginBottom: 12 }}>
              <div style={{
                background: C.card, borderRadius: 18,
                border: `1px solid ${C.border}`,
                overflow: "hidden",
              }}>
                {/* Colour accent strip top */}
                <div style={{ height: 3, background: color, opacity: 0.7 }} />

                {/* Header row */}
                <div style={{ padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 12 }}>
                  {/* Role icon — clearly sender vs receiver */}
                  <div style={{
                    width: 42, height: 42, borderRadius: 13, flexShrink: 0,
                    background: color + "18", border: `1px solid ${color}30`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {sv(isDispute ? IC.warn : d.status === "released" ? IC.check : isClaimed ? IC.refresh : IC.lock, 17, color)}
                  </div>

                  {/* Name + role context */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      color: C.text, fontSize: 14, fontWeight: 800, margin: "0 0 1px",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                    }}>{d.name}</p>
                    <p style={{ color: C.muted, fontSize: 11, margin: 0 }}>
                      {isSender
                        ? <span>You're paying · {d.cat}</span>
                        : <span>You're getting paid · {d.cat}</span>
                      }
                    </p>
                    <p style={{ color: C.muted, fontSize: 10, margin: "1px 0 0" }}>{d.date} · MS {d.ms}</p>
                  </div>

                  {/* Amount + status */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <p style={{ color: C.text, fontSize: 15, fontWeight: 900, margin: "0 0 4px" }}>{d.amt}</p>
                    <span style={{
                      background: pill.color + "22", color: pill.color,
                      fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 20,
                      whiteSpace: "nowrap",
                    }}>{pill.text}</span>
                  </div>
                </div>

                {/* ── CALM STATE — work in progress, nothing needed ── */}
                {isActive && ms === "in_progress" && (
                  <div style={{
                    margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12,
                    background: C.lime + "08", border: `1px solid ${C.lime}20`,
                    display: "flex", alignItems: "center", gap: 8
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.lime, flexShrink: 0 }} />
                    <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: 0 }}>
                      {isSender ? "In progress · waiting for work to be completed" : "In progress · complete the work, then claim payment"}
                    </p>
                  </div>
                )}

                {/* ── ATTENTION STATES — only shown when action needed ── */}
                {isActive && ms === "claimed" && isSender && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.gold + "15", border: `1px solid ${C.gold}40`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.refresh, 12, C.gold)}
                    <div>
                      <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, margin: 0 }}>Work claimed — approve or reject payment</p>
                      {d.raw?.currentMilestoneEvent?.note && (
                        <p style={{ color: C.muted, fontSize: 10, margin: "3px 0 0" }}>"{d.raw.currentMilestoneEvent.note}"</p>
                      )}
                    </div>
                  </div>
                )}
                {isActive && ms === "awaiting_claim" && isReceiver && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.lime + "15", border: `1px solid ${C.lime}30`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.lock, 12, C.lime)}
                    <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: 0 }}>Your payment is ready — claim your milestone</p>
                  </div>
                )}
                {isPending && isReceiver && d.pendingAccept && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}30`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.warn, 12, C.danger)}
                    <p style={{ color: C.danger, fontSize: 11, fontWeight: 700, margin: 0 }}>Expires in {d.expiresIn} — review and accept or decline</p>
                  </div>
                )}
                {isPending && isReceiver && d.pendingPayment && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.gold + "10", border: `1px solid ${C.gold}30`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.lock, 12, C.gold)}
                    <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, margin: 0 }}>Pending contract — waiting for {d.name} to fund before you can review</p>
                  </div>
                )}
                {isPending && isSender && d.pendingPayment && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.gold + "12", border: `1px solid ${C.gold}35`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.lock, 12, C.gold)}
                    <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, margin: 0 }}>
                      {d.raw?.paymentMethod === "bank_transfer"
                        ? `Pending — pay ${d.paymentDue || d.amt} via bank checkout to activate`
                        : "Pending — complete payment to activate this contract"}
                    </p>
                  </div>
                )}
                {isPending && isSender && d.pendingAccept && pendingTermQueries.length > 0 && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.gold + "15", border: `1px solid ${C.gold}40`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.warn, 12, C.gold)}
                    <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, margin: 0 }}>
                      {pendingTermQueries.length} term change request{pendingTermQueries.length === 1 ? "" : "s"} waiting for your response
                    </p>
                  </div>
                )}
                {isPending && isSender && d.pendingAccept && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.gold + "12", border: `1px solid ${C.gold}30`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.refresh, 12, C.gold)}
                    <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, margin: 0 }}>Waiting for {d.name} to accept · expires in {d.expiresIn}</p>
                  </div>
                )}
                {pendingAmountProposal && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.gold + "15", border: `1px solid ${C.gold}40`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.warn, 12, C.gold)}
                    <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, margin: 0 }}>
                      {pendingAmountProposal.proposerName} proposed changing the amount to {formatNairaCompactFromKobo(pendingAmountProposal.newAmountKobo)} — review in scope chat
                    </p>
                  </div>
                )}
                {isActive && ms === "claimed" && isReceiver && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.info + "12", border: `1px solid ${C.info}30`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.refresh, 12, C.info)}
                    <p style={{ color: C.info, fontSize: 11, fontWeight: 700, margin: 0 }}>Claim sent · auto-releases in {AUTO_RELEASE_HOURS}hrs if no response</p>
                  </div>
                )}
                {isActive && isSender && d.inactivityWarning && ms === "in_progress" && (
                  <div style={{ margin: "6px 16px 0", padding: "8px 12px", borderRadius: 12, background: C.gold + "12", border: `1px solid ${C.gold}25`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.warn, 11, C.gold)}
                    <p style={{ color: C.gold, fontSize: 10, fontWeight: 700, margin: 0 }}>Day {d.inactiveDays} of 7 — receiver earns 10% fee if no activity by day 7</p>
                  </div>
                )}
                {/* ── EARLY RELEASE REQUESTED — receiver asked for early pay ── */}
                {isActive && isReceiver && ms === "early_release_requested" && (
                  <div style={{
                    margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12,
                    background: C.lime + "12", border: `1px solid ${C.lime}30`,
                    display: "flex", alignItems: "center", gap: 8
                  }}>
                    {sv(IC.refresh, 12, C.lime)}
                    <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: 0 }}>
                      Early payment requested — waiting for sender to approve
                    </p>
                  </div>
                )}
                {isActive && isSender && ms === "early_release_requested" && (
                  <div style={{
                    margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12,
                    background: C.gold + "15", border: `1px solid ${C.gold}40`,
                    display: "flex", alignItems: "center", gap: 8
                  }}>
                    {sv(IC.refresh, 12, C.gold)}
                    <div>
                      <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, margin: 0 }}>Receiver says the job is done early</p>
                      <p style={{ color: C.muted, fontSize: 10, margin: "2px 0 0" }}>Review and release payment, or ask for more details</p>
                    </div>
                  </div>
                )}

                {/* ── PARTIAL RELEASED — renegotiating remainder ── */}
                {isActive && ms === "partial_released" && (
                  <div style={{
                    margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12,
                    background: C.info + "12", border: `1px solid ${C.info}30`,
                    display: "flex", alignItems: "center", gap: 8
                  }}>
                    {sv(IC.lock, 12, C.info)}
                    <div>
                      <p style={{ color: C.info, fontSize: 11, fontWeight: 700, margin: 0 }}>
                        {isSender
                          ? `${partialReleasedAmt || "—"} released · ${remainderLockedAmt || "—"} locked pending renegotiation`
                          : `${partialReleasedAmt || "—"} received · ${remainderLockedAmt || "—"} remainder being renegotiated`}
                      </p>
                      <p style={{ color: C.muted, fontSize: 10, margin: "2px 0 0" }}>
                        {isSender ? "Agree new scope via scope chat" : "Propose or accept a new remainder amount"}
                      </p>
                    </div>
                  </div>
                )}

                {/* ── AWAITING CLAIM — sender waiting for receiver to claim ── */}
                {isActive && isSender && ms === "awaiting_claim" && (
                  <div style={{
                    margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12,
                    background: C.card, border: `1px solid ${C.border}`,
                    display: "flex", alignItems: "center", gap: 8
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.muted, flexShrink: 0 }} />
                    <p style={{ color: C.muted, fontSize: 11, margin: 0 }}>Waiting for receiver to claim their milestone payment</p>
                  </div>
                )}

                {isDispute && (
                  <div style={{ margin: "10px 16px 0", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}30`, display: "flex", alignItems: "center", gap: 8 }}>
                    {sv(IC.warn, 12, C.danger)}
                    <p style={{ color: C.danger, fontSize: 11, fontWeight: 700, margin: 0 }}>
                      {isReceiver ? "Under PendPay arbitration" : "Dispute open · submit evidence"}
                    </p>
                  </div>
                )}

                {/* ── ACTION BUTTONS ── */}
                <div style={{ padding: "12px 16px 14px", display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>

                  {/* IN PROGRESS — sender can release early */}
                  {isActive && isSender && ms === "in_progress" && (
                    <button onClick={() => setDealStates(p => ({ ...p, [d.id]: { ...p[d.id], milestoneStatus: "complete" } }))}
                      style={{ width: "100%", padding: "11px", borderRadius: 12, border: `1px solid ${C.lime}40`, background: C.lime + "12", color: C.lime, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                      Release early
                    </button>
                  )}

                  {/* IN PROGRESS — receiver can claim when done */}
                  {isActive && isReceiver && ms === "in_progress" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <button type="button" onClick={() => setActiveDeal({ ...d, action: "claim" })}
                        style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
                        Mark as done — claim {d.msAmt}
                      </button>
                    </div>
                  )}

                  {/* PENDING — receiver reviews full contract flow */}
                  {isPending && isReceiver && d.pendingAccept && (
                    <button type="button" onClick={() => setActiveDeal({ ...d, action: "review_contract" })}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
                      Review contract →
                    </button>
                  )}

                  {isPending && isSender && d.pendingAccept && pendingTermQueries.length > 0 && (
                    <button type="button" onClick={() => setActiveDeal({ ...d, action: "term_queries" })}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.gold, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
                      Respond to term change{pendingTermQueries.length > 1 ? "s" : ""} ({pendingTermQueries.length})
                    </button>
                  )}

                  {pendingAmountProposal && (
                    <button type="button" onClick={() => setActiveDeal({ ...d, action: "scope_change" })}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.gold, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
                      Review amount change ({formatNairaCompactFromKobo(pendingAmountProposal.oldAmountKobo)} → {formatNairaCompactFromKobo(pendingAmountProposal.newAmountKobo)})
                    </button>
                  )}

                  {/* PENDING PAYMENT — receiver can preview only */}
                  {isPending && isReceiver && d.pendingPayment && (
                    <button type="button" onClick={() => setActiveDeal({ ...d, action: "view_contract" })}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.textMid, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                      Preview pending contract
                    </button>
                  )}

                  {/* PENDING PAYMENT — sender completes card/USSD checkout */}
                  {isPending && isSender && d.pendingPayment && ["card", "ussd"].includes(d.raw?.paymentMethod) && (
                    <button type="button" onClick={() => handleCompletePayment(d.id, d.raw?.paymentMethod)} disabled={paymentCompletingId === d.id}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer", opacity: paymentCompletingId === d.id ? 0.7 : 1 }}>
                      {paymentCompletingId === d.id ? "Opening checkout…" : `Complete payment · ${d.paymentDue || d.amt}`}
                    </button>
                  )}

                  {/* PENDING PAYMENT — sender completes bank checkout */}
                  {isPending && isSender && d.pendingPayment && d.raw?.paymentMethod === "bank_transfer" && (
                    <button type="button" onClick={() => handleCompletePayment(d.id, "bank_transfer")} disabled={paymentCompletingId === d.id}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer", opacity: paymentCompletingId === d.id ? 0.7 : 1 }}>
                      {paymentCompletingId === d.id ? "Opening checkout…" : `Pay with bank · ${d.paymentDue || d.amt}`}
                    </button>
                  )}

                  {/* PENDING PAYMENT — sender completes instant transfer */}
                  {isPending && isSender && d.pendingPayment && d.raw?.paymentMethod === "instant_transfer" && (
                    <button type="button" onClick={() => handleCompletePayment(d.id, "instant_transfer")} disabled={paymentCompletingId === d.id}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer", opacity: paymentCompletingId === d.id ? 0.7 : 1 }}>
                      {paymentCompletingId === d.id ? "Loading…" : `Complete instant transfer · ${d.paymentDue || d.amt}`}
                    </button>
                  )}

                  {/* PENDING — sender can view or cancel */}
                  {isPending && isSender && (d.pendingAccept || d.pendingPayment) && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" onClick={() => setActiveDeal({ ...d, action: "view_contract" })}
                        style={{ flex: 2, padding: "11px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                        View contract
                      </button>
                      <button type="button" onClick={() => requestCancelContract(d.id)} disabled={cancellingId === d.id}
                        style={{ flex: 1, padding: "11px", borderRadius: 12, border: `1px solid ${C.danger}40`, background: C.danger + "10", color: C.danger, fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: cancellingId === d.id ? 0.6 : 1 }}>
                        {cancellingId === d.id ? "…" : "Cancel"}
                      </button>
                    </div>
                  )}

                  {/* CLAIMED — sender approves or rejects */}
                  {isActive && isSender && ms === "claimed" && (
                    <button type="button" onClick={async () => {
                      if (approvingId) return;
                      setApprovingId(d.id);
                      try {
                        const res = await approveMilestone(d.id);
                        setDealStates((p) => ({
                          ...p,
                          [d.id]: {
                            ...p[d.id],
                            milestoneStatus: res.completed ? "complete" : "in_progress",
                          },
                        }));
                        await loadDeals();
                        showToast(
                          res.completed ? "Contract completed — all funds released" : "Milestone approved and released",
                          "success"
                        );
                      } catch (e) {
                        showToast(e.message || "Could not approve");
                      } finally {
                        setApprovingId(null);
                      }
                    }}
                      disabled={approvingId === d.id}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: approvingId === d.id ? "wait" : "pointer", opacity: approvingId === d.id ? 0.75 : 1 }}>
                      {approvingId === d.id ? "Releasing…" : `Approve & release ${d.msAmt}`}
                    </button>
                  )}
                  {isActive && isSender && ms === "claimed" && (
                    <button onClick={() => setActiveDeal({ ...d, action: "partial_accept" })}
                      style={{ width: "100%", padding: "10px", borderRadius: 12, border: `1px solid ${C.info}40`, background: C.info + "10", color: C.info, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                      Accept partial & renegotiate remainder
                    </button>
                  )}

                  {/* AWAITING CLAIM — receiver claims */}
                  {isActive && isReceiver && ms === "awaiting_claim" && (
                    <button onClick={() => setActiveDeal({ ...d, action: "claim" })}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
                      Claim ₦{d.msAmt}
                    </button>
                  )}
                  {isActive && isReceiver && ms === "claimed" && (
                    <div style={{ width: "100%", padding: "12px", borderRadius: 12, background: C.info + "10", border: `1px solid ${C.info}25`, textAlign: "center" }}>
                      <p style={{ color: C.info, fontSize: 12, fontWeight: 700, margin: 0 }}>Awaiting sender approval · auto-releases in {AUTO_RELEASE_HOURS}hrs</p>
                    </div>
                  )}

                  {/* EARLY RELEASE — receiver requested, sender approves */}
                  {isActive && isSender && ms === "early_release_requested" && (
                    <button onClick={() => setDealStates(p => ({ ...p, [d.id]: { ...p[d.id], milestoneStatus: "complete" } }))}
                      style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
                      Approve early release · pay ₦{d.msAmt}
                    </button>
                  )}
                  {isActive && isSender && ms === "early_release_requested" && (
                    <button onClick={() => setActiveDeal({ ...d, action: "scope_change" })}
                      style={{ width: "100%", padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      Ask for more details first
                    </button>
                  )}
                  {isActive && isReceiver && ms === "early_release_requested" && (
                    <div style={{ width: "100%", padding: "12px", borderRadius: 12, background: C.lime + "08", border: `1px solid ${C.lime}20`, textAlign: "center" }}>
                      <p style={{ color: C.lime, fontSize: 12, fontWeight: 700, margin: 0 }}>Request sent — waiting for sender to approve</p>
                    </div>
                  )}

                  {/* EARLY RELEASE REQUEST — receiver initiates from in-progress */}
                  {isActive && isReceiver && ms === "in_progress" && (
                    <button onClick={() => setDealStates(p => ({ ...p, [d.id]: { ...p[d.id], milestoneStatus: "early_release_requested" } }))}
                      style={{ width: "100%", padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      Finished early — request payment now
                    </button>
                  )}

                  {/* PARTIAL RELEASED — both parties open scope chat */}
                  {isActive && ms === "partial_released" && (
                    <button onClick={() => setActiveDeal({ ...d, action: "scope_change" })}
                      style={{
                        width: "100%", padding: "12px", borderRadius: 12, border: "none",
                        background: C.info, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer"
                      }}>
                      {isSender ? "Renegotiate remaining scope →" : "Review renegotiation →"}
                    </button>
                  )}

                  {/* SECONDARY — dispute/reject/evidence */}
                  <div style={{ display: "flex", gap: 8 }}>
                    {isActive && isSender && ms === "claimed" && (
                      <button onClick={() => setActiveDeal({ ...d, action: "reject_milestone" })}
                        style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.danger}40`, background: C.danger + "10", color: C.danger, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                        Reject
                      </button>
                    )}
                    {canRaiseDispute && (
                      <button onClick={() => setActiveDeal({ ...d, action: disputeAction })}
                        style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.danger}40`, background: C.danger + "10", color: C.danger, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                        Dispute
                      </button>
                    )}
                    {isDispute && isReceiver && (
                      <>
                        <button type="button" onClick={() => setActiveDeal({ ...d, action: "view_contract" })}
                          style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          View contract
                        </button>
                        <button onClick={() => setActiveDeal({ ...d, action: "evidence" })}
                          style={{ flex: 2, padding: "10px", borderRadius: 12, border: `1px solid ${C.gold}40`, background: C.gold + "12", color: C.gold, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                          Add evidence
                        </button>
                        <button onClick={() => setActiveDeal({ ...d, action: "arbitration_timeline" })}
                          style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          Timeline
                        </button>
                      </>
                    )}
                    {isDispute && isSender && (
                      <>
                        <button type="button" onClick={() => setActiveDeal({ ...d, action: "view_contract" })}
                          style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          View contract
                        </button>
                        <button onClick={() => setActiveDeal({ ...d, action: "evidence" })}
                          style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.danger}40`, background: C.danger + "10", color: C.danger, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                          Submit evidence
                        </button>
                        <button onClick={() => setActiveDeal({ ...d, action: "arbitration_timeline" })}
                          style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          Timeline
                        </button>
                        <button type="button" onClick={() => setActiveDeal({ ...d, action: "release_anyway_confirm" })}
                          style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.lime}40`, background: C.lime + "12", color: C.lime, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>
                          Release anyway
                        </button>
                      </>
                    )}
                    {(isActive || isDispute || isClosed || d.status === "released") && !isDispute && (
                      <button type="button" onClick={() => setActiveDeal({ ...d, action: "view_contract" })}
                        style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        View contract
                      </button>
                    )}
                    {isActive && !isDispute && (
                      <button onClick={() => setActiveDeal({ ...d, action: "scope_chat" })}
                        style={{ flex: 1, padding: "10px", borderRadius: 12, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        Scope change
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {activeDeal?.action === "term_queries" && (
          <TermQueriesRespondModal
            deal={activeDeal}
            queries={termQueriesByDeal[activeDeal.id] || []}
            onClose={() => setActiveDeal(null)}
            onUpdated={loadDeals}
          />
        )}
        {activeDeal?.action === "review_contract" && (
          <div style={{ position: "fixed", inset: 0, ...mobileShell, zIndex: 400, background: C.bg, overflowY: "auto" }}>
            <ReceiverScreen
              deal={activeDeal}
              onBack={() => setActiveDeal(null)}
              onOpenBankSetup={() => {
                setActiveDeal(null);
                setScreen("profile");
              }}
              onComplete={async () => {
                setActiveDeal(null);
                await loadDeals();
              }}
            />
          </div>
        )}

        {activeDeal?.action === "view_contract" && (
          <ContractDetailModal
            deal={activeDeal}
            milestoneStatus={dealStates[activeDeal.id]?.milestoneStatus}
            onClose={() => setActiveDeal(null)}
            onReview={
              activeDeal.role === "receiver" && activeDeal.pendingAccept
                ? () => setActiveDeal({ ...activeDeal, action: "review_contract" })
                : undefined
            }
            onCancel={
              activeDeal.role === "sender" && (activeDeal.pendingAccept || activeDeal.pendingPayment)
                ? () => requestCancelContract(activeDeal.id)
                : undefined
            }
            cancelling={cancellingId === activeDeal.id}
            onTermQueriesUpdated={loadDeals}
            onAction={(action) => setActiveDeal({ ...activeDeal, action })}
            approving={approvingId === activeDeal.id}
            onApprove={async () => {
              if (approvingId) return;
              setApprovingId(activeDeal.id);
              try {
                const res = await approveMilestone(activeDeal.id);
                setDealStates((p) => ({
                  ...p,
                  [activeDeal.id]: {
                    ...p[activeDeal.id],
                    milestoneStatus: res.completed ? "complete" : "in_progress",
                  },
                }));
                const mapped = await loadDeals();
                const updated = (mapped || []).find((row) => row.id === activeDeal.id);
                if (updated) {
                  setActiveDeal({ ...updated, action: "view_contract" });
                }
                showToast(
                  res.completed ? "Contract completed — all funds released" : "Milestone approved and released",
                  "success"
                );
              } catch (e) {
                showToast(e.message || "Could not approve");
              } finally {
                setApprovingId(null);
              }
            }}
          />
        )}

        {activeDeal?.action === "claim" && (
          <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.8)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div style={{ width: "100%", maxWidth: 430, background: C.surface, borderRadius: "20px 20px 0 0", padding: "24px 20px 40px", border: `1px solid ${C.border}` }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 20px" }} />
              <p style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: 4 }}>Claim milestone</p>
              <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Tell the sender what you completed for this milestone. They have {AUTO_RELEASE_HOURS} hours to approve. If they don't respond, funds auto-release to you.</p>
              <div style={{ background: C.lime + "12", border: `1px solid ${C.lime}30`, borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
                <p style={{ color: C.lime, fontSize: 13, fontWeight: 900 }}>{activeDeal.msAmt}</p>
                <p style={{ color: C.muted, fontSize: 11 }}>will be released to your account on approval</p>
              </div>
              <p style={{ color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", marginBottom: 8 }}>WHAT DID YOU DELIVER?</p>
              <textarea
                placeholder="e.g. Completed milestone 1 — delivered initial designs, 3 logo concepts and brand guidelines as agreed..."
                value={claimNote}
                onChange={e => setClaimNote(e.target.value)}
                style={{
                  width: "100%", padding: "13px", borderRadius: 12, marginBottom: 14,
                  background: C.card, border: `1px solid ${C.border}`,
                  color: C.text, fontSize: 13, outline: "none", resize: "none",
                  height: 90, boxSizing: "border-box", lineHeight: 1.5,
                }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setActiveDeal({ ...activeDeal, action: "view_contract" })} style={{ flex: 1, padding: "14px", borderRadius: 12, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                <button type="button" onClick={async () => {
                  try {
                    await claimMilestone(activeDeal.id, claimNote.trim() || undefined);
                    setDealStates((p) => ({
                      ...p,
                      [activeDeal.id]: { ...p[activeDeal.id], milestoneStatus: "claimed", claimedAt: "Now" },
                    }));
                    setClaimNote("");
                    const mapped = await loadDeals();
                    const updated = (mapped || []).find((row) => row.id === activeDeal.id);
                    setActiveDeal(updated ? { ...updated, action: "view_contract" } : null);
                  } catch (e) {
                    showToast(e.message || "Claim failed");
                  }
                }} disabled={!claimNote.trim()} style={{
                  flex: 2, padding: "14px", borderRadius: 12, border: "none",
                  background: claimNote.trim() ? C.lime : C.card,
                  color: claimNote.trim() ? C.bg : C.muted,
                  fontSize: 13, fontWeight: 900, cursor: "pointer",
                }}>Submit claim</button>
              </div>
            </div>
          </div>
        )}

        {activeDeal?.action === "reject_milestone" && (
          <RejectionModal
            deal={activeDeal}
            onClose={() => setActiveDeal({ ...activeDeal, action: "view_contract" })}
            onSubmit={async (reason) => {
              try {
                await rejectMilestone(activeDeal.id, reason);
                const mapped = await loadDeals();
                const updated = (mapped || []).find((row) => row.id === activeDeal.id);
                setActiveDeal(updated ? { ...updated, action: "view_contract" } : null);
              } catch (e) {
                showToast(e.message || "Reject failed");
              }
            }}
          />
        )}

        {(activeDeal?.action === "scope_chat" || activeDeal?.action === "scope_change") && (
          <ScopeChatModal
            deal={activeDeal}
            onClose={() => setActiveDeal({ ...activeDeal, action: "view_contract" })}
            onDealUpdated={loadDeals}
            onRaiseDispute={
              activeDeal.raw?.status === "active"
                ? () => setActiveDeal({
                  ...activeDeal,
                  action: activeDeal.role === "receiver" ? "dispute_receiver" : "dispute_sender",
                })
                : undefined
            }
          />
        )}

        {activeDeal?.action === "arbitration_timeline" && (
          <ArbitrationTimelineModal deal={activeDeal} onClose={() => setActiveDeal({ ...activeDeal, action: "view_contract" })} />
        )}

        {activeDeal?.action === "partial_accept" && (
          <PartialAcceptModal
            deal={activeDeal}
            onClose={() => setActiveDeal({ ...activeDeal, action: "view_contract" })}
            onConfirm={async (amountNaira, note) => {
              await partialReleaseMilestone(activeDeal.id, { amountNaira, note: note || undefined });
              const mapped = await loadDeals();
              const updated = (mapped || []).find((row) => row.id === activeDeal.id);
              setActiveDeal(updated ? { ...updated, action: "view_contract" } : null);
              showToast("Partial amount released — renegotiate remaining scope in chat", "success");
            }}
          />
        )}

        {activeDeal?.action === "release_anyway_confirm" && (
          <ReleaseAnywayModal
            deal={activeDeal}
            releasing={releasingDespiteDisputeId === activeDeal.id}
            onClose={() => setActiveDeal({ ...activeDeal, action: "view_contract" })}
            onConfirm={async () => {
              setReleasingDespiteDisputeId(activeDeal.id);
              try {
                const res = await releaseDespiteDispute(activeDeal.id);
                const mapped = await loadDeals();
                const updated = (mapped || []).find((row) => row.id === activeDeal.id);
                setActiveDeal(updated ? { ...updated, action: "view_contract" } : null);
                showToast(
                  res.completed ? "Funds released — deal completed" : "Funds released — dispute closed",
                  "success"
                );
              } catch (e) {
                showToast(e.message || "Could not release funds");
              } finally {
                setReleasingDespiteDisputeId(null);
              }
            }}
          />
        )}

        {(activeDeal?.action === "dispute_sender" || activeDeal?.action === "dispute_receiver" || activeDeal?.action === "evidence") && (
          <DisputeModal
            deal={activeDeal}
            evidenceInput={evidenceInput}
            setEvidenceInput={setEvidenceInput}
            onClose={() => setActiveDeal({ ...activeDeal, action: "view_contract" })}
            onSubmit={async (reason, fileList) => {
              const files = (fileList || []).filter((f) => f instanceof File);
              let evidenceUrls = [];
              if (files.length) {
                evidenceUrls = await uploadDisputeAttachments(activeDeal.id, files);
              }
              if (activeDeal?.action === "evidence") {
                if (!evidenceUrls.length && !String(reason || "").trim()) {
                  showToast("Add files or a short note.");
                  return;
                }
                try {
                  await appendDisputeEvidence(activeDeal.id, {
                    note: String(reason || "").trim(),
                    evidenceUrls: evidenceUrls.length ? evidenceUrls : [],
                  });
                } catch (e) {
                  showToast(e.message || "Could not submit evidence");
                  return;
                }
                setEvidenceInput("");
                const mapped = await loadDeals();
                const updated = (mapped || []).find((row) => row.id === activeDeal.id);
                setActiveDeal(updated ? { ...updated, action: "view_contract" } : null);
                return;
              }
              if (!String(reason || "").trim() && !evidenceUrls.length) {
                showToast("Describe what happened or attach files.");
                return;
              }
              try {
                await raiseDeal(
                  activeDeal.id,
                  String(reason || "").trim() || "(See attached files)",
                  evidenceUrls
                );
                setDealStates((p) => ({
                  ...p,
                  [activeDeal.id]: { ...p[activeDeal.id], milestoneStatus: "disputed", evidence: reason },
                }));
                setEvidenceInput("");
                const mapped = await loadDeals();
                const updated = (mapped || []).find((row) => row.id === activeDeal.id);
                setActiveDeal(updated ? { ...updated, action: "view_contract" } : null);
                showToast("Dispute raised — funds frozen pending review", "success");
              } catch (e) {
                showToast(e.message || "Could not raise dispute");
              }
            }}
          />
        )}

        {cancelConfirmDeal && (
          <CancelContractModal
            deal={cancelConfirmDeal}
            onClose={() => {
              if (cancellingId) return;
              setCancelConfirmDeal(null);
              setCancelError("");
            }}
            onConfirm={handleCancelContract}
            cancelling={!!cancellingId}
            error={cancelError}
          />
        )}

        {instantTransferDeal && (
          <InstantTransferOverlay
            dealId={instantTransferDeal}
            instructions={instantTransferInstructions}
            onFunded={async () => {
              setInstantTransferDeal(null);
              setInstantTransferInstructions(null);
              await loadDeals();
              showToast("Contract funded — transfer confirmed", "success");
            }}
            onClose={() => {
              setInstantTransferDeal(null);
              setInstantTransferInstructions(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function FavouriteContactsScreen({ onBack, unreadCount, onNotificationsClick, onSelectContact }) {
  const C = useColors();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getFrequentContacts(20);
        if (!cancelled) setContacts(data.contacts || []);
      } catch {
        if (!cancelled) setContacts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, zIndex: 350, background: C.bg, overflowY: "auto", paddingBottom: 100 }}>
      <TopBar unreadCount={unreadCount} onNotificationsClick={onNotificationsClick} />
      <div style={{ padding: "4px 20px 0" }}>
        <button
          type="button"
          onClick={onBack}
          style={{ background: "none", border: "none", color: C.muted, display: "flex", alignItems: "center", gap: 6, padding: "4px 0 12px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}
        >
          {sv(IC.chevron_l, 16, C.muted)} Back
        </button>
        <p style={{ color: C.text, fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Favourite Contacts</p>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px" }}>
          {contacts.length > 0 ? `${contacts.length} recent ${contacts.length === 1 ? "contact" : "contacts"}` : "People you have contracted with recently"}
        </p>
        {loading ? (
          <p style={{ color: C.muted, fontSize: 12, padding: "12px 0" }}>Loading contacts…</p>
        ) : contacts.length === 0 ? (
          <p style={{ color: C.muted, fontSize: 13, padding: "12px 0" }}>No favourite contacts yet. Create a contract to add someone here.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {contacts.map((c) => (
              <button
                key={c.tag || c.id}
                type="button"
                onClick={() => onSelectContact?.(c)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  width: "100%",
                  padding: "14px 16px",
                  borderRadius: 16,
                  border: `1px solid ${C.border}`,
                  background: C.card,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: (c.color || C.lime) + "25",
                  border: `1.5px solid ${(c.color || C.lime)}60`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: c.color || C.lime,
                  fontWeight: 800,
                  flexShrink: 0,
                }}>
                  {c.initials || "?"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: C.text, fontSize: 14, fontWeight: 700, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name || c.tag}
                  </p>
                  <p style={{ color: C.muted, fontSize: 12, margin: "2px 0 0" }}>@{c.tag}</p>
                </div>
                {sv(IC.chevron_r, 16, C.muted)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TransactionsHistoryScreen({ onBack, unreadCount, onNotificationsClick }) {
  const C = useColors();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE = 30;

  const loadPage = useCallback(async (offset = 0, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const data = await getWalletTransactions({ limit: PAGE, offset });
      const rows = data.transactions || [];
      setTotal(data.total ?? rows.length);
      setTransactions((prev) => (append ? [...prev, ...rows] : rows));
    } catch {
      if (!append) {
        setTransactions([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const hasMore = transactions.length < total;

  return (
    <div style={{ position: "fixed", inset: 0, ...mobileShell, zIndex: 350, background: C.bg, overflowY: "auto", paddingBottom: 100 }}>
      <TopBar unreadCount={unreadCount} onNotificationsClick={onNotificationsClick} />
      <div style={{ padding: "4px 20px 0" }}>
        <button
          type="button"
          onClick={onBack}
          style={{ background: "none", border: "none", color: C.muted, display: "flex", alignItems: "center", gap: 6, padding: "4px 0 12px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}
        >
          {sv(IC.chevron_l, 16, C.muted)} Back
        </button>
        <p style={{ color: C.text, fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Transactions</p>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px" }}>
          {total > 0 ? `${total} wallet ${total === 1 ? "entry" : "entries"}` : "Your wallet activity"}
        </p>
        {loading ? (
          <p style={{ color: C.muted, fontSize: 12, padding: "12px 0" }}>Loading transactions…</p>
        ) : (
          <>
            <WalletTransactionList
              transactions={transactions}
              variant="full"
              emptyMessage="No wallet transactions yet. Add funds, fund a contract, or withdraw to see activity here."
            />
            {hasMore && (
              <button
                type="button"
                onClick={() => loadPage(transactions.length, true)}
                disabled={loadingMore}
                style={{
                  width: "100%",
                  marginTop: 8,
                  marginBottom: 24,
                  padding: "13px",
                  borderRadius: 14,
                  border: `1px solid ${C.border}`,
                  background: C.card,
                  color: C.textMid,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: loadingMore ? "default" : "pointer",
                  opacity: loadingMore ? 0.7 : 1,
                }}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function WalletScreen({ pendingWalletModal, clearPendingWalletModal, unreadCount, onNotificationsClick, activeScreen, onViewAllTransactions }) {
  const C = useColors();
  const { config } = useAppConfig();
  const banks = config?.banks || [];
  const tiers = config?.tiers || [];
  const [modal, setModal] = useState(null); // null | "withdraw" | "transfer" | "topup" | "bank" | "tier"
  const [linked, setLinked] = useState(null);
  const [linkedBanks, setLinkedBanks] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [userTier, setUserTier] = useState(1);
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [linkBank, setLinkBank] = useState(null);
  const [linkAcct, setLinkAcct] = useState("");
  const [linkVerifying, setLinkVerifying] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [period, setPeriod] = useState("Month");
  const [analyticsByPeriod, setAnalyticsByPeriod] = useState({});
  const [txExpanded, setTxExpanded] = useState(false);
  const [txHistory, setTxHistory] = useState([]);
  const [txHistoryLoading, setTxHistoryLoading] = useState(false);

  const loadWalletData = useCallback(async () => {
    try {
      const w = await getWallet();
      setWalletData(w);
      return w;
    } catch {
      setWalletData(null);
      return null;
    }
  }, []);

  const loadWalletAnalytics = useCallback(async () => {
    try {
      const analyticsResults = await Promise.all(
        ["Week", "Month", "Year"].map((p) => getDealAnalytics(p).catch(() => null))
      );
      const map = {};
      ["Week", "Month", "Year"].forEach((p, i) => {
        if (analyticsResults[i]) map[p] = analyticsResults[i];
      });
      setAnalyticsByPeriod(map);
    } catch {
      setAnalyticsByPeriod({});
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [w, me, linkedRes] = await Promise.all([
          getWallet(),
          getMe(),
          getLinkedBanks().catch(() => ({ banks: [] })),
        ]);
        if (!cancelled) {
          setWalletData(w);
          setUserTier(me?.tier ?? 1);
          setUserEmail(me?.email || "");
          setUserPhone(me?.phone || "");
          const primary = (linkedRes.banks || []).find((b) => b.isPrimary) || linkedRes.banks?.[0];
          setLinkedBanks(linkedRes.banks || []);
          if (primary) {
            setLinked({
              id: primary.bankId,
              code: primary.bankCode,
              name: primary.bankName,
              color: primary.color,
              accountNumber: primary.accountNumber,
              accountMasked: primary.accountMasked,
              accountName: primary.accountName,
              linkedId: primary.id,
            });
          } else {
            setLinked(null);
          }
        }
      } catch {
        if (!cancelled) {
          setWalletData(null);
          setUserTier(1);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    loadWalletAnalytics();
  }, [loadWalletAnalytics]);

  useEffect(() => {
    if (activeScreen === "wallet") {
      loadWalletData();
      loadWalletAnalytics();
    }
  }, [activeScreen, loadWalletData, loadWalletAnalytics]);

  useEffect(() => {
    const rows = walletData?.transactions || [];
    if (rows.length) setTxHistory(rows);
  }, [walletData?.transactions]);

  const expandTransactions = useCallback(async () => {
    if (txExpanded) {
      setTxExpanded(false);
      return;
    }
    setTxExpanded(true);
    if (txHistory.length >= 20) return;
    setTxHistoryLoading(true);
    try {
      const data = await getWalletTransactions({ limit: 40, offset: 0 });
      setTxHistory(data.transactions || []);
    } catch {
      /* keep preview rows */
    } finally {
      setTxHistoryLoading(false);
    }
  }, [txExpanded, txHistory.length]);

  const nipFeeNaira = walletData?.estimatedNipFeeNaira ?? walletData?.nipFeeNaira ?? config?.fees?.estimatedNipFeeNaira ?? config?.fees?.nipFeeNaira ?? 10.75;
  const minWithdrawNaira = walletData?.minWithdrawalNaira ?? config?.fees?.minWithdrawalNaira ?? 10;

  const balance = walletData ? (walletData.availableBalance ?? walletData.available_balance ?? 0) / 100 : 0;
  const maxWithdrawNaira = Math.max(0, Math.floor(balance - nipFeeNaira));
  const balanceWithdrawable = maxWithdrawNaira >= minWithdrawNaira;

  const currentTier = getTierMeta(tiers, userTier, C);

  // Withdraw state
  const [wAmt, setWAmt] = useState("");
  const [wBank, setWBank] = useState(null);
  const [wAcct, setWAcct] = useState("");
  const [wVerified, setWVerified] = useState(false);
  const [wVerifying, setWVerifying] = useState(false);
  const [wStep, setWStep] = useState(0); // 0=amount, 1=bank+acct, 2=confirm, 3=success
  const [wPin, setWPin] = useState("");
  const [wAccountName, setWAccountName] = useState("");
  const [wChallengeId, setWChallengeId] = useState("");
  const [wOtpHint, setWOtpHint] = useState("");
  const [wOtpSmsFallback, setWOtpSmsFallback] = useState(false);
  const [wOtp, setWOtp] = useState("");
  const [wSubmitting, setWSubmitting] = useState(false);
  const [wError, setWError] = useState("");
  const [wTransferRef, setWTransferRef] = useState("");
  const withdrawPrefilledRef = useRef(false);

  // Transfer state
  const [trAmt, setTrAmt] = useState("");
  const [trRecipientTag, setTrRecipientTag] = useState("");
  const [trRecipientName, setTrRecipientName] = useState("");
  const [trStep, setTrStep] = useState(0); // 0=details, 1=pin, 2=success
  const [trPin, setTrPin] = useState("");
  const [trSubmitting, setTrSubmitting] = useState(false);
  const [trError, setTrError] = useState("");
  const [trTransferRef, setTrTransferRef] = useState("");

  const resetTransfer = () => {
    setTrStep(0);
    setTrAmt("");
    setTrRecipientTag("");
    setTrRecipientName("");
    setTrPin("");
    setTrSubmitting(false);
    setTrError("");
    setTrTransferRef("");
  };

  const resetWithdraw = () => {
    setWStep(0);
    setWAmt("");
    setWBank(null);
    setWAcct("");
    setWVerified(false);
    setWVerifying(false);
    setWPin("");
    setWAccountName("");
    setWChallengeId("");
    setWOtpHint("");
    setWOtpSmsFallback(false);
    setWOtp("");
    setWSubmitting(false);
    setWError("");
    setWTransferRef("");
  };

  const applyLinkedBankForWithdraw = async (lb) => {
    const bank = banks.find((b) => b.code === lb.bankCode || b.id === lb.bankId) || {
      id: lb.bankId || lb.id,
      code: lb.bankCode || lb.code,
      name: lb.bankName || lb.name,
      color: lb.color,
    };
    const acct = String(lb.accountNumber || "").replace(/\D/g, "").slice(0, 10);
    setWBank(bank);
    setWAcct(acct);
    setWVerified(false);
    setWAccountName("");
    setWError("");
    if (!bank.code || acct.length !== 10) return;
    setWVerifying(true);
    try {
      const verified = await verifyBankAccount(bankApiCode(bank), acct);
      const resolvedName = verified.accountName?.trim();
      if (!resolvedName) throw new Error("Could not resolve account name");
      setWAccountName(resolvedName);
      setWVerified(true);
    } catch (err) {
      setWError(err.message || "Could not verify saved account — check the number or link again");
    } finally {
      setWVerifying(false);
    }
  };

  const verifyWithdrawAccount = async () => {
    const acct = String(wAcct).replace(/\D/g, "").slice(0, 10);
    if (!wBank || acct.length !== 10) return;
    if (wAcct !== acct) setWAcct(acct);
    setWVerifying(true);
    setWError("");
    try {
      const verified = await verifyBankAccount(bankApiCode(wBank), acct);
      const resolvedName = verified.accountName?.trim();
      if (!resolvedName) throw new Error("Could not resolve account name from your bank");
      setWAccountName(resolvedName);
      setWVerified(true);
    } catch (err) {
      setWVerified(false);
      setWAccountName("");
      setWError(err.message || "Account verification failed");
    } finally {
      setWVerifying(false);
    }
  };

  // Pre-fill linked bank once when destination step opens
  useEffect(() => {
    if (modal !== "withdraw") {
      withdrawPrefilledRef.current = false;
      return;
    }
    if (wStep !== 1 || wVerified || withdrawPrefilledRef.current) return;
    const primary = linkedBanks.find((b) => b.isPrimary) || linkedBanks[0] || linked;
    if (primary?.accountNumber) {
      applyLinkedBankForWithdraw(primary);
      withdrawPrefilledRef.current = true;
    }
  }, [modal, wStep, linkedBanks, linked?.linkedId, wVerified]);

  // Auto-verify when user finishes typing a 10-digit account number
  useEffect(() => {
    if (modal !== "withdraw" || wStep !== 1 || !wBank || wVerified || wVerifying) return;
    const acct = String(wAcct).replace(/\D/g, "");
    if (acct.length !== 10) return;
    verifyWithdrawAccount();
  }, [modal, wStep, wBank?.id, wAcct, wVerified, wVerifying]);

  const initiateWithdrawFlow = async (pinCode) => {
    const pin = String(pinCode ?? wPin);
    if (!wVerified || pin.length !== 6 || wSubmitting) return;
    setWSubmitting(true);
    setWError("");
    try {
      const amountNaira = parseInt(wAmt || "0", 10);
      const init = await initiateWithdraw({
        amountNaira,
        bankCode: bankApiCode(wBank),
        accountNumber: wAcct,
        accountName: wAccountName || "Verified Account",
        bankName: wBank.name,
        pin,
      });
      setWChallengeId(init.challengeId);
      setWOtpHint(init.deliveryHint || init.message || "Enter the 6-digit code sent to your email.");
      setWOtpSmsFallback(!!init.fallback && init.channel === "sms");
      setWStep(3);
      setWPin("");
    } catch (err) {
      const details = err.apiDetails;
      if (err.code === "INSUFFICIENT_FUNDS" && details?.requiredKobo != null && details?.availableKobo != null) {
        const need = Math.ceil(details.requiredKobo / 100);
        const have = Math.floor(details.availableKobo / 100);
        setWError(`Insufficient funds — need ₦${need.toLocaleString()} (incl. ₦${nipFeeNaira} fee), you have ₦${have.toLocaleString()}`);
      } else {
        setWError(err.message || "Failed to initiate withdrawal");
      }
      setWPin("");
    } finally {
      setWSubmitting(false);
    }
  };

  const confirmWithdrawFlow = async () => {
    if (wOtp.length < 4 || !wChallengeId) return;
    setWSubmitting(true);
    setWError("");
    try {
      const idem = `wd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const result = await confirmWithdraw({
        challengeId: wChallengeId,
        bankOtpCode: wOtp,
        idempotencyKey: idem,
      });
      setWTransferRef(result.transferReference || "Pending");
      setWStep(4);
      await loadWalletData();
    } catch (err) {
      setWError(err.message || "Failed to confirm withdrawal code");
    } finally {
      setWSubmitting(false);
    }
  };

  const submitTransferFlow = async (pinCode) => {
    const pin = String(pinCode ?? trPin);
    const amountNaira = parseInt(trAmt || "0", 10);
    if (!trRecipientTag || amountNaira < minWithdrawNaira || pin.length !== 6 || trSubmitting) return;
    setTrSubmitting(true);
    setTrError("");
    try {
      const idem = `tr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const result = await transferToUser({
        recipientTag: trRecipientTag,
        amountNaira,
        pin,
        idempotencyKey: idem,
      });
      setTrTransferRef(result.reference || "Completed");
      setTrStep(2);
      await loadWalletData();
    } catch (err) {
      setTrError(err.message || "Transfer failed");
      setTrPin("");
    } finally {
      setTrSubmitting(false);
    }
  };

  // TopUp state — 0=methods, 1=pending bank refs, 2=amount, 3=bank detail, 4=success
  const [tAmt, setTAmt] = useState("");
  const [tStep, setTStep] = useState(0);
  const [tTopupMethod, setTTopupMethod] = useState(null);
  const [tTopup, setTTopup] = useState(null);
  const [tTopupLoading, setTTopupLoading] = useState(false);
  const [tTopupError, setTTopupError] = useState("");
  const [tTopupSuccessAmt, setTTopupSuccessAmt] = useState(null);

  const pendingTopups = walletData?.pendingTopups || [];
  const paystackReady = Boolean(paystackPublicKey(config));

  const resetTopupForm = () => {
    setTAmt("");
    setTTopupMethod(null);
    setTTopup(null);
    setTTopupLoading(false);
    setTTopupError("");
    setTTopupSuccessAmt(null);
  };

  const closeTopupModal = () => {
    setModal(null);
    resetTopupForm();
    setTStep(0);
  };

  const openTopupModal = () => {
    resetTopupForm();
    setTStep(0);
    setModal("topup");
  };

  const openTopupDetail = (topup) => {
    setTTopupMethod("bank_transfer");
    setTTopup(topup);
    setTStep(3);
    setModal("topup");
  };

  const selectTopupMethod = (method) => {
    setTTopupMethod(method);
    setTTopupError("");
    if (method === "bank_transfer") {
      setTStep(pendingTopups.length > 0 ? 1 : 2);
    } else {
      setTStep(2);
    }
  };

  useEffect(() => {
    if (!pendingWalletModal) return;
    if (pendingWalletModal === "topup") {
      resetTopupForm();
      setTStep(0);
      setModal("topup");
    } else if (pendingWalletModal === "transfer") {
      resetTransfer();
      setModal("transfer");
    } else if (pendingWalletModal === "tier") {
      setModal("tier");
    }
    clearPendingWalletModal?.();
  }, [pendingWalletModal, clearPendingWalletModal]);

  const generateTopupReference = async () => {
    const amountNaira = parseInt(tAmt || "0", 10);
    if (amountNaira <= 0) return;
    setTTopupLoading(true);
    setTTopupError("");
    try {
      const data = await getTopupReference(amountNaira);
      setTTopup(data);
      setTStep(3);
      await loadWalletData();
    } catch (err) {
      setTTopupError(err.message || "Failed to generate reference");
    } finally {
      setTTopupLoading(false);
    }
  };

  const submitTopupAmount = async () => {
    const amountNaira = parseInt(tAmt || "0", 10);
    if (amountNaira <= 0) return;

    if (tTopupMethod === "bank_transfer") {
      await generateTopupReference();
      return;
    }

    if (!paystackReady) {
      setTTopupError("Card payments are not configured yet");
      return;
    }

    setTTopupLoading(true);
    setTTopupError("");
    try {
      await completeWalletTopupCheckout({
        amountNaira,
        paymentMethod: tTopupMethod,
        config,
        userEmail,
        userPhone,
      });
      setTTopupSuccessAmt(amountNaira);
      setTStep(4);
      await loadWalletData();
    } catch (err) {
      if (err.message !== "Payment cancelled") {
        setTTopupError(err.message || "Payment failed");
      }
    } finally {
      setTTopupLoading(false);
    }
  };

  const topupSheetTitle = (() => {
    if (tStep === 4) return "Funds added";
    if (tStep === 3) return "Your payment reference";
    if (tStep === 2) return "Add funds";
    if (tStep === 1) return "Pending transfers";
    return "Add funds";
  })();

  const formatTopupExpiry = (expiresAt) => (
    expiresAt
      ? new Date(expiresAt).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" })
      : "—"
  );

  // Poll wallet while bank transfer reference is open — auto-detect when Safe Haven credits
  useEffect(() => {
    if (modal !== "topup" || tStep !== 3 || !tTopup?.reference) return undefined;

    const watchRef = tTopup.reference;
    const watchAmountKobo = tTopup.amount;

    const checkForCredit = async () => {
      const w = await loadWalletData();
      if (!w) return;
      const stillPending = (w.pendingTopups || []).some((p) => p.reference === watchRef);
      if (!stillPending) {
        setTTopupSuccessAmt(koboToNaira(watchAmountKobo));
        setTTopup(null);
        setTAmt("");
        setTStep(4);
      }
    };

    checkForCredit().catch(() => {});
    const id = setInterval(() => { checkForCredit().catch(() => {}); }, 10000);
    return () => clearInterval(id);
  }, [modal, tStep, tTopup?.reference, tTopup?.amount, loadWalletData]);

  // Refresh pending transfer list while that screen is open
  useEffect(() => {
    if (modal !== "topup" || tStep !== 1) return undefined;
    const id = setInterval(() => { loadWalletData().catch(() => {}); }, 10000);
    return () => clearInterval(id);
  }, [modal, tStep, loadWalletData]);

  const TopupReferenceDetail = ({ topup, onBack, onDone }) => (
    <div style={{ padding: "0 20px 32px" }}>
      <div style={{ background: C.lime + "12", border: `1px solid ${C.lime}30`, borderRadius: 16, padding: "18px", marginBottom: 16, textAlign: "center" }}>
        <p style={{ color: C.muted, fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, margin: "0 0 8px" }}>TRANSFER EXACTLY</p>
        <p style={{ color: C.lime, fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
          {formatNairaFromKobo(topup.amount)}
        </p>
        <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>Amount must match exactly for auto-credit</p>
      </div>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px", marginBottom: 16 }}>
        {[
          ["Bank name", topup.bankName || "—"],
          ["Account number", topup.accountNumber || "—"],
          ["Account name", topup.accountName || "—"],
          ["Reference", topup.reference || "—"],
          ["Expires", formatTopupExpiry(topup.expiresAt)],
        ].map(([l, v], i, a) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <span style={{ color: C.muted, fontSize: 12 }}>{l}</span>
            <span style={{ color: l === "Reference" ? C.lime : C.text, fontSize: 12, fontWeight: l === "Reference" ? 900 : 700, textAlign: "right", maxWidth: "58%", wordBreak: "break-all" }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ background: C.forest, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
        <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: "0 0 6px" }}>Checking for your transfer…</p>
        <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6, margin: 0 }}>
          {topup.note || "Transfer from any Nigerian bank using the reference above. Your wallet updates automatically once Safe Haven confirms — no need to tap Done."}
        </p>
      </div>
      <button onClick={onDone} style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.lime, border: "none", color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer", marginBottom: 10 }}>
        Done — I've sent the transfer
      </button>
      {onBack && (
        <button onClick={onBack} style={{ width: "100%", padding: "12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted, fontSize: 13, cursor: "pointer" }}>
          Back
        </button>
      )}
    </div>
  );

  const numpad = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "DEL"];
  const pressKey = (k, val, set) => {
    if (k === "DEL") { set(v => v.slice(0, -1)); return; }
    if (k === "." && val.includes(".")) return;
    if (val.length >= 12) return;
    set(v => v + k);
  };

  const PinDots = ({ val }) => (
    <div style={{ display: "flex", gap: 14, justifyContent: "center", margin: "20px 0 8px" }}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", background: i < val.length ? C.lime : "none", border: `2px solid ${i < val.length ? C.lime : C.border}`, transition: "all 0.15s" }} />
      ))}
    </div>
  );

  return (
    <div style={{ paddingBottom: 100 }}>
      <TopBar unreadCount={unreadCount} onNotificationsClick={onNotificationsClick} />
      <div style={{ padding: "4px 20px 0" }}>
        <p style={{ color: C.text, fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 18 }}>Wallet</p>

        {/* Balance card */}
        <WavyCard
          style={{ marginBottom: 12 }}
          cornerAction={(
            <button
              type="button"
              onClick={() => setModal("tier")}
              style={{
                background: currentTier.color + "22",
                border: `1px solid ${currentTier.color}40`,
                borderRadius: 20,
                padding: "3px 10px",
                cursor: "pointer",
              }}
            >
              <span style={{ color: currentTier.color, fontSize: 9, fontWeight: 900 }}>
                {currentTier.name} ↑ Upgrade
              </span>
            </button>
          )}
        >
          <div style={{ textAlign: "center", paddingTop: 4 }}>
            <p style={{ color: C.muted, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 8 }}>AVAILABLE BALANCE</p>
            <p style={{ color: C.text, fontSize: 38, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: (Number(walletData?.lockedInContracts) || 0) > 0 ? 6 : 18 }}>
              ₦{balance.toLocaleString()}
            </p>
            {(Number(walletData?.lockedInContracts) || 0) > 0 && (
              <p style={{ color: C.muted, fontSize: 11, fontWeight: 600, margin: "0 0 16px" }}>
                In contracts {fmtCompactKobo(walletData.lockedInContracts)}
              </p>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => { resetTransfer(); setModal("transfer"); }}
                style={{
                  minWidth: 120, padding: "12px 20px", borderRadius: 14,
                  background: C.forest, border: `1px solid ${C.forestBright}`, color: C.lime,
                  fontSize: 13, fontWeight: 900, cursor: "pointer",
                }}
              >
                Transfer
              </button>
              <button
                onClick={() => { resetWithdraw(); setModal("withdraw"); }}
                style={{
                  minWidth: 120, padding: "12px 28px", borderRadius: 14,
                  background: C.lime, border: "none", color: C.bg,
                  fontSize: 13, fontWeight: 900, cursor: "pointer",
                }}
              >
                Withdraw
              </button>
            </div>
          </div>
        </WavyCard>

        {/* Linked bank */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <p style={{ color: C.text, fontSize: 14, fontWeight: 800 }}>Linked Bank</p>
          {linked && <button onClick={() => setModal("bank")} style={{ background: "none", border: "none", color: C.lime, fontSize: 12, cursor: "pointer", fontWeight: 700 }}>Change</button>}
        </div>
        {linked ? (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 16px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: linked.color }} />
              <div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{linked.name}</p>
                <p style={{ color: C.muted, fontSize: 11 }}>{linked.accountMasked || "—"} — {linked.accountName || "Verified"}</p>
              </div>
            </div>
            <span style={{ background: C.lime + "22", color: C.lime, fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 10 }}>Active</span>
          </div>
        ) : (
          <button onClick={() => setModal("bank")} style={{ width: "100%", padding: "14px", borderRadius: 14, cursor: "pointer", background: C.card, border: `2px dashed ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 600, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {sv(IC.lock, 15, C.muted)} Connect Nigerian Bank
          </button>
        )}

        <DealAnalyticsPanel
          analyticsByPeriod={analyticsByPeriod}
          period={period}
          setPeriod={setPeriod}
        />

        {pendingTopups.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: 0 }}>Pending transfers</p>
              <button onClick={openTopupModal} style={{ background: "none", border: "none", color: C.lime, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Add new</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {pendingTopups.map((topup) => (
                <button
                  key={topup.reference}
                  type="button"
                  onClick={() => openTopupDetail(topup)}
                  style={{
                    width: "100%", textAlign: "left", cursor: "pointer",
                    background: C.card, border: `1px solid ${C.lime}30`, borderRadius: 14, padding: "13px 16px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div>
                      <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: "0 0 4px" }}>{formatNairaFromKobo(topup.amount)}</p>
                      <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: "0 0 2px" }}>{topup.reference}</p>
                      <p style={{ color: C.muted, fontSize: 10, margin: 0 }}>Expires {formatTopupExpiry(topup.expiresAt)}</p>
                    </div>
                    <span style={{ color: C.lime, fontSize: 11, fontWeight: 800, flexShrink: 0 }}>View →</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Transactions */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <button
            type="button"
            onClick={expandTransactions}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8,
            }}
            aria-expanded={txExpanded}
          >
            <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: 0 }}>Transactions</p>
            <span style={{
              color: C.muted, display: "inline-flex",
              transform: txExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}>
              {sv("M6 9L12 15L18 9", 16, C.muted, 2)}
            </span>
          </button>
          <button onClick={onViewAllTransactions} style={{ background: "none", border: "none", color: C.lime, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>View all</button>
        </div>
        <div style={{
          overflow: "hidden",
          maxHeight: txExpanded ? 2000 : 320,
          transition: "max-height 0.35s ease",
        }}>
          <WalletTransactionList
            transactions={txHistory.length ? txHistory : (walletData?.transactions || [])}
            limit={txExpanded ? undefined : 4}
            variant="wallet"
            emptyMessage="No wallet transactions yet."
          />
        </div>
        {txHistoryLoading && (
          <p style={{ color: C.muted, fontSize: 11, margin: "8px 0 0" }}>Loading more…</p>
        )}
        {!txExpanded && (txHistory.length > 4 || (walletData?.transactions || []).length > 4) && (
          <button
            type="button"
            onClick={expandTransactions}
            style={{
              width: "100%", marginTop: 8, marginBottom: 8, padding: "10px",
              borderRadius: 12, border: `1px solid ${C.border}`, background: C.card,
              color: C.lime, fontSize: 12, fontWeight: 800, cursor: "pointer",
            }}
          >
            Show more
          </button>
        )}
        {txExpanded && (
          <button
            type="button"
            onClick={() => setTxExpanded(false)}
            style={{
              width: "100%", marginTop: 8, marginBottom: 8, padding: "10px",
              borderRadius: 12, border: `1px solid ${C.border}`, background: C.card,
              color: C.muted, fontSize: 12, fontWeight: 800, cursor: "pointer",
            }}
          >
            Show less
          </button>
        )}
      </div>

      {/* ── WITHDRAW MODAL ─────────────────────────────────────── */}
      {modal === "withdraw" && (
        <BottomSheet title={wStep === 4 ? "Withdrawal sent" : wStep === 3 ? "Withdrawal verification" : wStep === 2 ? "Confirm withdrawal" : wStep === 1 ? "Withdrawal destination" : "Withdraw funds"} onClose={() => setModal(null)} noPad={wStep === 0 || wStep === 2}>
          {wStep === 0 && (
            <div style={{ padding: "0 20px" }}>
              <p style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Available: <span style={{ color: C.lime, fontWeight: 800 }}>₦{balance.toLocaleString()}</span></p>
              <p style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>Max withdrawal: <span style={{ color: C.textMid, fontWeight: 700 }}>₦{maxWithdrawNaira.toLocaleString()}</span> (bank may charge ~₦{nipFeeNaira} NIP fee on top · min ₦{minWithdrawNaira})</p>
              {balance > 0 && !balanceWithdrawable && (
                <p style={{ color: C.gold, fontSize: 11, marginBottom: 8, lineHeight: 1.5 }}>
                  Your balance is below the minimum withdrawal amount. You can close your account in Profile → Security to forfeit the remaining ₦{balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
                </p>
              )}
              <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
                <p style={{ color: C.muted, fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>AMOUNT</p>
                <p style={{ color: wAmt ? C.text : C.muted, fontSize: 40, fontWeight: 900, letterSpacing: "-0.03em", minHeight: 52 }}>
                  {wAmt ? `₦${parseInt(wAmt || 0).toLocaleString()}` : "₦0"}
                </p>
                {wAmt && parseInt(wAmt) > 0 && parseInt(wAmt) < minWithdrawNaira && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700 }}>
                    Minimum withdrawal is ₦{minWithdrawNaira.toLocaleString()}
                  </p>
                )}
                {wAmt && parseInt(wAmt) > maxWithdrawNaira && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700 }}>
                    Exceeds withdrawable balance (~₦{nipFeeNaira} bank fee reserve applies)
                  </p>
                )}
                {wAmt && parseInt(wAmt) > currentTier.maxDeal && (
                  <p style={{ color: C.gold, fontSize: 12, fontWeight: 700 }}>Exceeds Tier {currentTier.id} limit · upgrade to withdraw more</p>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                {numpad.map(k => (
                  <button key={k} onClick={() => pressKey(k, wAmt, setWAmt)} style={{
                    padding: "14px", borderRadius: 12, border: `1px solid ${k ? "transparent" : "transparent"}`,
                    background: k ? C.card : "none", color: k === "DEL" ? C.muted : C.text,
                    fontSize: k === "DEL" ? 12 : 20, fontWeight: 700, cursor: k ? "pointer" : "default",
                  }}>{k}</button>
                ))}
              </div>
              <div style={{ padding: "0 0 24px" }}>
                <button onClick={() => {
                  const amt = parseInt(wAmt || "0", 10);
                  if (amt >= minWithdrawNaira && amt <= maxWithdrawNaira) setWStep(1);
                }} disabled={!wAmt || parseInt(wAmt) < minWithdrawNaira || parseInt(wAmt) <= 0 || parseInt(wAmt) > maxWithdrawNaira} style={{
                  width: "100%", padding: "15px", borderRadius: 14, border: "none",
                  background: wAmt && parseInt(wAmt) >= minWithdrawNaira && parseInt(wAmt) > 0 && parseInt(wAmt) <= maxWithdrawNaira ? C.lime : C.card,
                  color: wAmt && parseInt(wAmt) >= minWithdrawNaira && parseInt(wAmt) > 0 && parseInt(wAmt) <= maxWithdrawNaira ? C.bg : C.muted,
                  fontSize: 14, fontWeight: 900, cursor: "pointer",
                }}>Continue</button>
              </div>
            </div>
          )}
          {wStep === 1 && (
            <div style={{ padding: "0 20px 32px" }}>
              {linkedBanks.length > 0 && (
                <>
                  <p style={{ color: C.text, fontSize: 13, fontWeight: 800, marginBottom: 10 }}>Saved accounts</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                    {linkedBanks.map((lb) => {
                      const selected =
                        wVerified &&
                        wAcct === String(lb.accountNumber || "").replace(/\D/g, "").slice(0, 10) &&
                        bankApiCode(wBank) === (lb.bankCode || lb.code);
                      return (
                        <button
                          key={lb.id}
                          type="button"
                          onClick={() => { withdrawPrefilledRef.current = true; applyLinkedBankForWithdraw(lb); }}
                          style={{
                            width: "100%", textAlign: "left", cursor: "pointer", padding: "12px 14px",
                            borderRadius: 12, background: selected ? C.lime + "18" : C.card,
                            border: `1px solid ${selected ? C.lime + "60" : C.border}`,
                            display: "flex", alignItems: "center", gap: 10,
                          }}
                        >
                          <div style={{ width: 10, height: 10, borderRadius: "50%", background: lb.color, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: "0 0 2px" }}>{lb.bankName}</p>
                            <p style={{ color: C.muted, fontSize: 11, margin: 0 }}>{lb.accountMasked} · {lb.accountName}</p>
                          </div>
                          {selected && <span style={{ color: C.lime, fontSize: 11, fontWeight: 800 }}>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              <p style={{ color: C.text, fontSize: 13, fontWeight: 800, marginBottom: 12 }}>
                {linkedBanks.length > 0 ? "Or select another bank" : "Select bank"}
              </p>
              <BankSearchPicker
                banks={banks}
                selected={wBank}
                label=""
                onSelect={(b) => {
                  withdrawPrefilledRef.current = true;
                  setWBank(b);
                  setWVerified(false);
                  setWAccountName("");
                  setWAcct("");
                  setWError("");
                }}
              />
              <p style={{ color: C.text, fontSize: 13, fontWeight: 800, marginBottom: 10, marginTop: 4 }}>Account number</p>
              <div style={{ display: "flex", gap: 8, marginBottom: wVerified ? 10 : 16 }}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="0123456789"
                  value={wAcct}
                  onChange={(e) => {
                    setWAcct(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setWVerified(false);
                    setWAccountName("");
                    setWError("");
                  }}
                  style={{ flex: 1, padding: "13px 14px", borderRadius: 12, background: C.card, border: `1px solid ${wVerified ? C.lime + "60" : C.border}`, color: C.text, fontSize: 15, outline: "none", fontFamily: "'Courier New',monospace", letterSpacing: "0.1em" }}
                />
                <button onClick={verifyWithdrawAccount} disabled={!wBank || wAcct.length !== 10 || wVerifying || wVerified} style={{
                  padding: "13px 16px", borderRadius: 12, cursor: "pointer",
                  background: wVerified ? C.lime + "20" : C.lime,
                  border: `1px solid ${wVerified ? C.lime + "40" : "transparent"}`,
                  color: wVerified ? C.lime : C.bg, fontSize: 12, fontWeight: 900,
                  opacity: (!wBank || wAcct.length !== 10) ? 0.3 : 1,
                }}>{wVerifying ? "···" : wVerified ? "✓ OK" : "Verify"}</button>
              </div>
              {!wVerified && wAcct.length === 10 && wBank && !wVerifying && (
                <p style={{ color: C.muted, fontSize: 11, margin: "0 0 12px" }}>Verifying account automatically…</p>
              )}
              {wError && <p style={{ color: C.danger, fontSize: 12, margin: "0 0 12px" }}>{wError}</p>}
              {wVerified && (
                <div style={{ padding: "10px 14px", borderRadius: 12, background: C.lime + "12", border: `1px solid ${C.lime}30`, marginBottom: 16 }}>
                  <p style={{ color: C.muted, fontSize: 10, letterSpacing: "0.08em", margin: "0 0 2px" }}>ACCOUNT NAME</p>
                  <p style={{ color: C.lime, fontSize: 14, fontWeight: 900, margin: 0 }}>{wAccountName}</p>
                </div>
              )}
              <button onClick={() => wVerified && setWStep(2)} disabled={!wVerified} style={{
                width: "100%", padding: "15px", borderRadius: 14, border: "none",
                background: wVerified ? C.lime : C.card, color: wVerified ? C.bg : C.muted,
                fontSize: 14, fontWeight: 900, cursor: "pointer",
              }}>Review withdrawal</button>
            </div>
          )}
          {wStep === 2 && (
            <div style={{ padding: "0 20px" }}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "16px", marginBottom: 16 }}>
                {[
                  ["Withdrawal amount", `₦${parseInt(wAmt || 0).toLocaleString()}`],
                  ["Est. bank transfer fee", `~₦${nipFeeNaira.toLocaleString()}`],
                  ["You receive", `₦${parseInt(wAmt || 0).toLocaleString()}`],
                  ["Total from wallet", `~₦${(parseInt(wAmt || 0) + nipFeeNaira).toLocaleString()}`],
                  ["Destination bank", wBank?.name || "—"],
                  ["Account number", wAcct || "—"],
                  ["Account name", wAccountName || "—"],
                  ["Settlement time", "Within minutes via NIP"],
                ].map(([l, v], i, a) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <span style={{ color: C.muted, fontSize: 12 }}>{l}</span>
                    <span style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </div>
              <p style={{ color: C.text, fontSize: 13, fontWeight: 800, marginBottom: 4, textAlign: "center" }}>Enter your PIN to continue</p>
              <PinDots val={wPin} />
              {wError && <p style={{ color: C.danger, fontSize: 12, textAlign: "center", marginBottom: 8 }}>{wError}</p>}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "DEL"].map((k, i) => (
                  <button key={i} type="button" disabled={wSubmitting} onClick={() => {
                    if (!k || wSubmitting) return;
                    if (k === "DEL") { setWPin(p => p.slice(0, -1)); setWError(""); return; }
                    if (wPin.length >= 6) return;
                    const n = wPin + k;
                    setWPin(n);
                    if (n.length === 6) initiateWithdrawFlow(n);
                  }} style={{ padding: "14px", borderRadius: 12, border: "none", background: k ? C.card : "none", color: k === "DEL" ? C.muted : C.text, fontSize: k === "DEL" ? 12 : 20, fontWeight: 700, cursor: k && !wSubmitting ? "pointer" : "default", opacity: wSubmitting ? 0.5 : 1 }}>{k}</button>
                ))}
              </div>
              <p style={{ color: C.muted, fontSize: 11, textAlign: "center", marginBottom: 16 }}>{wSubmitting ? "Sending verification code..." : "We'll email a verification code after PIN validation."}</p>
              {wPin.length === 6 && !wSubmitting && (
                <button type="button" onClick={() => initiateWithdrawFlow(wPin)} style={{ width: "100%", padding: "15px", borderRadius: 14, border: "none", background: C.lime, color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer", marginBottom: 10 }}>
                  Continue
                </button>
              )}
              <button onClick={() => setWStep(1)} style={{ width: "100%", padding: "12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted, fontSize: 13, cursor: "pointer" }}>Back</button>
            </div>
          )}
          {wStep === 3 && (
            <div style={{ padding: "0 20px 32px", textAlign: "center" }}>
              <p style={{ color: C.text, fontSize: 16, fontWeight: 800, marginBottom: 6 }}>Enter withdrawal code</p>
              {wOtpSmsFallback && (
                <p style={{ color: C.gold, fontSize: 12, marginBottom: 10 }}>
                  Email unavailable — we sent your code by SMS instead.
                </p>
              )}
              <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>
                {wOtpHint || "Enter the 6-digit code sent to your email."}
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={wOtp}
                onChange={(e) => { setWOtp(e.target.value.replace(/\D/g, "").slice(0, 8)); setWError(""); }}
                placeholder="6-digit code"
                style={{ width: "100%", padding: "14px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 16, outline: "none", textAlign: "center", letterSpacing: "0.18em", marginBottom: 12 }}
              />
              {wError && <p style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{wError}</p>}
              <button onClick={confirmWithdrawFlow} disabled={wSubmitting || wOtp.length < 4} style={{ width: "100%", padding: "15px", borderRadius: 14, background: wSubmitting || wOtp.length < 4 ? C.card : C.lime, border: "none", color: wSubmitting || wOtp.length < 4 ? C.muted : C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer", marginBottom: 10 }}>
                {wSubmitting ? "Verifying..." : "Confirm withdrawal"}
              </button>
              <button onClick={() => setWStep(2)} style={{ width: "100%", padding: "12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted, fontSize: 13, cursor: "pointer" }}>Back</button>
            </div>
          )}
          {wStep === 4 && (
            <div style={{ padding: "0 20px 32px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.lime + "22", border: `2px solid ${C.lime}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "8px auto 16px" }}>
                {sv(IC.check, 32, C.lime, 2.5)}
              </div>
              <p style={{ color: C.text, fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Withdrawal sent</p>
              <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>₦{parseInt(wAmt || 0).toLocaleString()} is on its way to {wBank?.name}. Bank transfer fees (~₦{nipFeeNaira}) are charged separately by your bank. Settles within minutes.</p>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px", marginBottom: 20, textAlign: "left" }}>
                <p style={{ color: C.muted, fontSize: 10, letterSpacing: "0.08em", fontWeight: 700, margin: "0 0 8px" }}>TRANSACTION REFERENCE</p>
                <p style={{ color: C.lime, fontSize: 14, fontWeight: 900, margin: 0 }}>{wTransferRef || `PP-WD-${Date.now().toString().slice(-8)}`}</p>
              </div>
              <button onClick={() => { setModal(null); resetWithdraw(); }} style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.lime, border: "none", color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer" }}>Done</button>
            </div>
          )}
        </BottomSheet>
      )}

      {/* ── TRANSFER MODAL ──────────────────────────────────────── */}
      {modal === "transfer" && (
        <BottomSheet
          title={trStep === 2 ? "Transfer sent" : trStep === 1 ? "Confirm transfer" : "Transfer to PendPay user"}
          onClose={() => { setModal(null); resetTransfer(); }}
          noPad={trStep === 1}
        >
          {trStep === 0 && (
            <div style={{ padding: "0 20px 32px" }}>
              <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8 }}>SEND TO</p>
              <TagSearch
                onSelect={(name, tag) => {
                  setTrRecipientName(name);
                  setTrRecipientTag(tag);
                  setTrError("");
                }}
              />
              {trRecipientTag && (
                <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 12, background: C.lime + "12", border: `1px solid ${C.lime}30` }}>
                  <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: 0 }}>{trRecipientName}</p>
                  <p style={{ color: C.muted, fontSize: 12, margin: "2px 0 0" }}>@{trRecipientTag}</p>
                </div>
              )}
              <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", margin: "20px 0 8px" }}>AMOUNT (₦)</p>
              <input
                type="text"
                inputMode="numeric"
                value={trAmt}
                onChange={(e) => { setTrAmt(e.target.value.replace(/\D/g, "")); setTrError(""); }}
                placeholder="0"
                style={{ width: "100%", padding: "16px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 28, fontWeight: 900, outline: "none" }}
              />
              <p style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>
                Available: ₦{Math.floor(balance).toLocaleString()} · Min ₦{minWithdrawNaira.toLocaleString()}
              </p>
              {parseInt(trAmt || "0", 10) > Math.floor(balance) && (
                <p style={{ color: C.danger, fontSize: 12, marginTop: 8 }}>Insufficient balance</p>
              )}
              {trError && <p style={{ color: C.danger, fontSize: 12, marginTop: 8 }}>{trError}</p>}
              <button
                type="button"
                disabled={!trRecipientTag || parseInt(trAmt || "0", 10) < minWithdrawNaira || parseInt(trAmt || "0", 10) > Math.floor(balance)}
                onClick={() => { setTrStep(1); setTrPin(""); setTrError(""); }}
                style={{
                  width: "100%", marginTop: 24, padding: "15px", borderRadius: 14, border: "none",
                  background: trRecipientTag && parseInt(trAmt || "0", 10) >= minWithdrawNaira && parseInt(trAmt || "0", 10) <= Math.floor(balance) ? C.lime : C.card,
                  color: trRecipientTag && parseInt(trAmt || "0", 10) >= minWithdrawNaira && parseInt(trAmt || "0", 10) <= Math.floor(balance) ? C.bg : C.muted,
                  fontSize: 14, fontWeight: 900, cursor: "pointer",
                }}
              >
                Continue
              </button>
            </div>
          )}
          {trStep === 1 && (
            <div style={{ padding: "0 20px" }}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px", marginBottom: 20, textAlign: "center" }}>
                <p style={{ color: C.muted, fontSize: 11, margin: 0 }}>Sending</p>
                <p style={{ color: C.text, fontSize: 32, fontWeight: 900, margin: "6px 0" }}>₦{parseInt(trAmt || "0", 10).toLocaleString()}</p>
                <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>to {trRecipientName || `@${trRecipientTag}`}</p>
              </div>
              <p style={{ color: C.text, fontSize: 13, fontWeight: 800, marginBottom: 4, textAlign: "center" }}>Enter your PIN to confirm</p>
              <PinDots val={trPin} />
              {trError && <p style={{ color: C.danger, fontSize: 12, textAlign: "center", marginBottom: 8 }}>{trError}</p>}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "DEL"].map((k, i) => (
                  <button key={i} type="button" disabled={trSubmitting} onClick={() => {
                    if (!k || trSubmitting) return;
                    if (k === "DEL") { setTrPin(p => p.slice(0, -1)); setTrError(""); return; }
                    if (trPin.length >= 6) return;
                    const n = trPin + k;
                    setTrPin(n);
                    if (n.length === 6) submitTransferFlow(n);
                  }} style={{ padding: "14px", borderRadius: 12, border: "none", background: k ? C.card : "none", color: k === "DEL" ? C.muted : C.text, fontSize: k === "DEL" ? 12 : 20, fontWeight: 700, cursor: k && !trSubmitting ? "pointer" : "default", opacity: trSubmitting ? 0.5 : 1 }}>{k}</button>
                ))}
              </div>
              <p style={{ color: C.muted, fontSize: 11, textAlign: "center", marginBottom: 16 }}>{trSubmitting ? "Processing transfer..." : "Instant transfer between PendPay wallets."}</p>
              <button onClick={() => setTrStep(0)} style={{ width: "100%", padding: "12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted, fontSize: 13, cursor: "pointer" }}>Back</button>
            </div>
          )}
          {trStep === 2 && (
            <div style={{ padding: "0 20px 32px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.lime + "22", border: `2px solid ${C.lime}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "8px auto 16px" }}>
                {sv(IC.check, 32, C.lime, 2.5)}
              </div>
              <p style={{ color: C.text, fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Transfer complete</p>
              <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>₦{parseInt(trAmt || "0", 10).toLocaleString()} sent to @{trRecipientTag}</p>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px", marginBottom: 20, textAlign: "left" }}>
                <p style={{ color: C.muted, fontSize: 10, letterSpacing: "0.08em", fontWeight: 700, margin: "0 0 8px" }}>TRANSACTION REFERENCE</p>
                <p style={{ color: C.lime, fontSize: 14, fontWeight: 900, margin: 0 }}>{trTransferRef || "Completed"}</p>
              </div>
              <button onClick={() => { setModal(null); resetTransfer(); }} style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.lime, border: "none", color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer" }}>Done</button>
            </div>
          )}
        </BottomSheet>
      )}

      {/* ── ADD FUNDS MODAL ─────────────────────────────────────── */}
      {modal === "topup" && (
        <BottomSheet
          title={topupSheetTitle}
          onClose={closeTopupModal}
          noPad={tStep === 2}
        >
          {tStep === 0 && (
            <div style={{ padding: "0 20px 32px" }}>
              <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 12 }}>HOW WOULD YOU LIKE TO ADD FUNDS?</p>
              {tTopupError && (
                <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{tTopupError}</p>
              )}

              <button type="button" disabled={!paystackReady} onClick={() => selectTopupMethod("card")} style={{
                width: "100%", padding: "14px 16px", background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 14, cursor: paystackReady ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 12,
                marginBottom: 8, textAlign: "left", opacity: paystackReady ? 1 : 0.55,
              }}>
                <span style={{ fontSize: 20 }}>💳</span>
                <div style={{ flex: 1 }}>
                  <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: 0 }}>Debit / credit card</p>
                  <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>Verve, Mastercard, Visa · 1.5% card fee applies</p>
                </div>
                <span style={{ color: C.muted, fontSize: 16 }}>›</span>
              </button>

              <button type="button" onClick={() => selectTopupMethod("bank_transfer")} style={{
                width: "100%", padding: "14px 16px", background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12,
                marginBottom: 8, textAlign: "left",
              }}>
                <span style={{ fontSize: 20 }}>🏦</span>
                <div style={{ flex: 1 }}>
                  <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: 0 }}>Bank transfer</p>
                  <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>
                    {pendingTopups.length > 0
                      ? `${pendingTopups.length} pending reference${pendingTopups.length !== 1 ? "s" : ""} · no extra fee`
                      : "Transfer to your PendPay account · no extra fee"}
                  </p>
                </div>
                <span style={{ color: C.muted, fontSize: 16 }}>›</span>
              </button>

              <button type="button" disabled={!paystackReady} onClick={() => selectTopupMethod("ussd")} style={{
                width: "100%", padding: "14px 16px", background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 14, cursor: paystackReady ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 12,
                marginBottom: 20, textAlign: "left", opacity: paystackReady ? 1 : 0.55,
              }}>
                <span style={{ fontSize: 20 }}>📞</span>
                <div style={{ flex: 1 }}>
                  <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: 0 }}>USSD / mobile money</p>
                  <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>*737#, *901#, OPay, Moniepoint, PalmPay · no extra fee</p>
                </div>
                <span style={{ color: C.muted, fontSize: 16 }}>›</span>
              </button>

              {!paystackReady && (
                <p style={{ color: C.gold, fontSize: 11, textAlign: "center", marginBottom: 12, lineHeight: 1.5 }}>
                  Card and USSD require Paystack to be configured. Bank transfer is always available.
                </p>
              )}

              <p style={{ color: C.muted, fontSize: 11, textAlign: "center", margin: 0 }}>
                Funds go <span style={{ color: C.lime, fontWeight: 700 }}>directly into your PendPay wallet balance</span>
              </p>
            </div>
          )}
          {tStep === 1 && (
            <div style={{ padding: "0 20px 32px" }}>
              <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, margin: "0 0 14px" }}>
                Use an existing reference below or create a new one. Transfer the exact amount shown for auto-credit.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {pendingTopups.map((topup) => (
                  <button
                    key={topup.reference}
                    type="button"
                    onClick={() => openTopupDetail(topup)}
                    style={{
                      width: "100%", textAlign: "left", cursor: "pointer",
                      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 16px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                      <div>
                        <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: "0 0 4px" }}>{formatNairaFromKobo(topup.amount)}</p>
                        <p style={{ color: C.lime, fontSize: 11, fontWeight: 700, margin: "0 0 2px" }}>{topup.reference}</p>
                        <p style={{ color: C.muted, fontSize: 10, margin: 0 }}>Expires {formatTopupExpiry(topup.expiresAt)}</p>
                      </div>
                      <span style={{ color: C.lime, fontSize: 11, fontWeight: 800, flexShrink: 0 }}>View →</span>
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => setTStep(2)} style={{ width: "100%", padding: "15px", borderRadius: 14, border: "none", background: C.lime, color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer", marginBottom: 10 }}>
                Create new reference
              </button>
              <button onClick={() => setTStep(0)} style={{ width: "100%", padding: "12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted, fontSize: 13, cursor: "pointer" }}>
                Back
              </button>
            </div>
          )}
          {tStep === 2 && (
            <div style={{ padding: "0 20px" }}>
              <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
                <p style={{ color: C.muted, fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>AMOUNT TO ADD</p>
                <p style={{ color: tAmt ? C.text : C.muted, fontSize: 40, fontWeight: 900, letterSpacing: "-0.03em", minHeight: 52 }}>
                  {tAmt ? `₦${parseInt(tAmt || 0).toLocaleString()}` : "₦0"}
                </p>
                {tTopupMethod === "card" && tAmt && parseInt(tAmt) > 0 && (
                  <p style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
                    Total charge: ₦{Math.ceil(parseInt(tAmt) * 1.015).toLocaleString()} (incl. 1.5% card fee)
                  </p>
                )}
                {tAmt && parseInt(tAmt) + balance > currentTier.maxBalance && (
                  <p style={{ color: C.gold, fontSize: 12, fontWeight: 700 }}>Would exceed Tier {currentTier.id} wallet limit · upgrade first</p>
                )}
              </div>
              {tTopupError && (
                <p style={{ color: C.danger, fontSize: 12, textAlign: "center", marginBottom: 12 }}>{tTopupError}</p>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                {numpad.map(k => (
                  <button key={k} onClick={() => pressKey(k, tAmt, setTAmt)} style={{
                    padding: "14px", borderRadius: 12, border: "none",
                    background: k ? C.card : "none", color: k === "DEL" ? C.muted : C.text,
                    fontSize: k === "DEL" ? 12 : 20, fontWeight: 700, cursor: k ? "pointer" : "default",
                  }}>{k}</button>
                ))}
              </div>
              <div style={{ padding: "0 0 24px", display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={submitTopupAmount} disabled={!tAmt || parseInt(tAmt) <= 0 || tTopupLoading} style={{
                  width: "100%", padding: "15px", borderRadius: 14, border: "none",
                  background: tAmt && parseInt(tAmt) > 0 && !tTopupLoading ? C.lime : C.card,
                  color: tAmt && parseInt(tAmt) > 0 && !tTopupLoading ? C.bg : C.muted,
                  fontSize: 14, fontWeight: 900, cursor: "pointer",
                }}>
                  {tTopupLoading
                    ? (tTopupMethod === "bank_transfer" ? "Generating…" : "Opening checkout…")
                    : tTopupMethod === "bank_transfer"
                      ? "Generate reference"
                      : "Continue to payment"}
                </button>
                <button
                  onClick={() => setTStep(tTopupMethod === "bank_transfer" && pendingTopups.length > 0 ? 1 : 0)}
                  style={{ width: "100%", padding: "12px", background: "none", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted, fontSize: 13, cursor: "pointer" }}
                >
                  Back
                </button>
              </div>
            </div>
          )}
          {tStep === 3 && tTopup && (
            <TopupReferenceDetail
              topup={tTopup}
              onBack={() => {
                setTTopup(null);
                setTStep(pendingTopups.length > 0 ? 1 : 2);
              }}
              onDone={async () => {
                setTTopup(null);
                setTAmt("");
                await loadWalletData();
                setTStep(pendingTopups.length > 0 ? 1 : 0);
              }}
            />
          )}
          {tStep === 4 && (
            <div style={{ padding: "0 20px 32px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.lime + "22", border: `2px solid ${C.lime}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "8px auto 16px" }}>
                {sv(IC.check, 32, C.lime, 2.5)}
              </div>
              <p style={{ color: C.text, fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Wallet funded</p>
              <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>
                ₦{(tTopupSuccessAmt || 0).toLocaleString()} has been added to your PendPay wallet.
              </p>
              <button onClick={closeTopupModal} style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.lime, border: "none", color: C.bg, fontSize: 14, fontWeight: 900, cursor: "pointer" }}>
                Done
              </button>
            </div>
          )}
        </BottomSheet>
      )}

      {/* ── LINK BANK MODAL ─────────────────────────────────────── */}
      {modal === "bank" && (
        <BottomSheet title="Link Bank Account" onClose={() => { setModal(null); setLinkBank(null); setLinkAcct(""); setLinkError(""); }}>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>Your account details are encrypted and stored safely.</p>
          <BankSearchPicker
            banks={banks}
            selected={linkBank}
            onSelect={(b) => { setLinkBank(b); setLinkError(""); }}
          />
          {linkBank && (
            <>
              <p style={{ color: C.text, fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Account number</p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={10}
                value={linkAcct}
                onChange={(e) => setLinkAcct(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="0123456789"
                style={{ width: "100%", padding: "13px 16px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 16, outline: "none", marginBottom: 12, boxSizing: "border-box" }}
              />
              {linkError && <p style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{linkError}</p>}
              <button
                disabled={linkVerifying || linkAcct.length !== 10}
                onClick={async () => {
                  setLinkVerifying(true);
                  setLinkError("");
                  try {
                    const res = await linkBankAccount({
                      bankCode: bankApiCode(linkBank),
                      bankName: linkBank.name,
                      accountNumber: linkAcct,
                    });
                    const b = res.bank;
                    setLinked({
                      id: b.bankId,
                      code: b.bankCode,
                      name: b.bankName,
                      color: b.color,
                      accountNumber: b.accountNumber,
                      accountMasked: b.accountMasked,
                      accountName: b.accountName,
                      linkedId: b.id,
                    });
                    setLinkedBanks((prev) => {
                      const next = prev.filter((row) => row.id !== b.id);
                      return [{ ...b, isPrimary: true }, ...next.map((row) => ({ ...row, isPrimary: false }))];
                    });
                    setModal(null);
                    setLinkBank(null);
                    setLinkAcct("");
                  } catch (err) {
                    setLinkError(err.message || "Could not link bank account");
                  } finally {
                    setLinkVerifying(false);
                  }
                }}
                style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", background: linkAcct.length === 10 ? C.lime : C.card, color: linkAcct.length === 10 ? C.bg : C.muted, fontSize: 14, fontWeight: 900, cursor: "pointer", marginBottom: 12 }}
              >
                {linkVerifying ? "Verifying…" : "Verify & link account"}
              </button>
              {linked?.linkedId && (
                <button
                  onClick={async () => {
                    try {
                      await unlinkBankAccount(linked.linkedId);
                      setLinked(null);
                      setModal(null);
                    } catch (err) {
                      setLinkError(err.message || "Could not unlink bank");
                    }
                  }}
                  style={{ width: "100%", padding: "12px", borderRadius: 12, background: "none", border: `1px solid ${C.danger}40`, color: C.danger, fontSize: 13, cursor: "pointer" }}
                >
                  Unlink current bank
                </button>
              )}
            </>
          )}
        </BottomSheet>
      )}

      {/* ── TIER UPGRADE MODAL ──────────────────────────────────── */}
      {modal === "tier" && (
        <BottomSheet title="Wallet Tiers" onClose={() => setModal(null)}>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>Your tier determines deal limits and wallet balance caps. Upgrade by completing higher KYC.</p>
          {tiers.map(t => {
            const isCurrent = t.id === currentTier.id;
            const isLower = t.id < currentTier.id;
            return (
              <div key={t.id} style={{
                background: isCurrent ? t.color + "15" : C.card,
                border: `2px solid ${isCurrent ? t.color + "60" : C.border}`,
                borderRadius: 16, padding: "16px", marginBottom: 12,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div>
                    <p style={{ color: t.color, fontSize: 14, fontWeight: 900, margin: 0 }}>{t.name}</p>
                    <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{t.kyc}</p>
                  </div>
                  {isCurrent && <span style={{ background: t.color + "25", color: t.color, fontSize: 10, fontWeight: 900, padding: "3px 10px", borderRadius: 20 }}>CURRENT</span>}
                  {isLower && <span style={{ background: C.card, color: C.muted, fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>✓ Done</span>}
                </div>
                {t.features.map(f => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ color: t.color, fontSize: 12 }}>✓</span>
                    <p style={{ color: C.textMid, fontSize: 12, margin: 0 }}>{f}</p>
                  </div>
                ))}
                {!isCurrent && !isLower && (
                  <button style={{
                    width: "100%", marginTop: 10, padding: "11px", borderRadius: 12, border: "none",
                    background: t.color, color: t.id === 3 ? "#000" : C.bg,
                    fontSize: 13, fontWeight: 900, cursor: "pointer",
                  }}>Upgrade to {t.name}</button>
                )}
              </div>
            );
          })}
          <div style={{ background: C.forest, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px" }}>
            <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6, margin: 0 }}>Tier 3 upgrade requires proof of address and, for businesses, CAC registration documents. Contact support@pendpay.ng to start the process.</p>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

function ProfileScreen({ onSignOut, unreadCount, onNotificationsClick }) {
  const C = useColors();
  const { isDark, toggleTheme } = useTheme();
  const showToast = useToast();
  const { config } = useAppConfig();
  const faqs = config?.faqs || [];
  const helpContacts = [
    { icon: "💬", label: "Live Chat", sub: "Message us on WhatsApp", action: "https://wa.me/2349130134757" },
    { icon: "✉️", label: "Email", sub: "support@usependpay.com", action: "mailto:support@usependpay.com" },
  ];
  const legalLinks = config?.legalLinks || [];
  const appVersion = config?.appVersion || "1.0.0";
  const [panel, setPanel] = useState(null); // null | "notifications" | "disputes" | "security" | "help" | "signout"
  const [profile, setProfile] = useState(null);
  const minPlatformFee = config?.fees?.platformMinFeeNaira
    ?? (profile?.platformFee?.minFeeKobo ? profile.platformFee.minFeeKobo / 100 : 650);
  const [profileLoading, setProfileLoading] = useState(true);
  const [disputes, setDisputes] = useState([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [notifs, setNotifs] = useState({
    dealAlerts: true, milestoneUpdates: true, disputes: true, walletAlerts: true, eventAlerts: true,
    marketing: false, sms: true, email: true, push: true,
  });
  const [showChangePin, setShowChangePin] = useState(false);
  const [oldPin, setOldPin] = useState("");
  const [storedOldPin, setStoredOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [pinPhase, setPinPhase] = useState("old"); // old | new | confirm
  const [newPinConfirm, setNewPinConfirm] = useState("");
  const [pinMsg, setPinMsg] = useState("");
  const [pinSubmitting, setPinSubmitting] = useState(false);
  const [showSignOut, setShowSignOut] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [freezeSubmitting, setFreezeSubmitting] = useState(false);
  const [showFreezeConfirm, setShowFreezeConfirm] = useState(false);
  const [freezeError, setFreezeError] = useState("");
  const [closeEligibility, setCloseEligibility] = useState(null);
  const [closeEligibilityLoading, setCloseEligibilityLoading] = useState(false);
  const [showCloseAccount, setShowCloseAccount] = useState(false);
  const [closeStep, setCloseStep] = useState("warning");
  const [closePin, setClosePin] = useState("");
  const [closeOtp, setCloseOtp] = useState("");
  const [closeStepToken, setCloseStepToken] = useState("");
  const [closeOtpSent, setCloseOtpSent] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [closeError, setCloseError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [showBankSetup, setShowBankSetup] = useState(false);
  const [bankSetupIdentityType, setBankSetupIdentityType] = useState("BVN");
  const [bankSetupIdentityNumber, setBankSetupIdentityNumber] = useState("");
  const [bankSetupOtp, setBankSetupOtp] = useState("");
  const [bankSetupReference, setBankSetupReference] = useState("");
  const [bankSetupStep, setBankSetupStep] = useState("bvn");
  const [bankSetupSubmitting, setBankSetupSubmitting] = useState(false);
  const [bankSetupError, setBankSetupError] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    customerType: "",
    line1: "",
    city: "",
    state: "",
    country: "Nigeria",
    postalCode: "",
  });

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const me = await getMe();
      setProfile(me);
      if (me.notificationPrefs) setNotifs(me.notificationPrefs);
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (panel !== "disputes") return;
    let cancelled = false;
    (async () => {
      setDisputesLoading(true);
      try {
        const data = await getProfileDisputes();
        if (!cancelled) setDisputes(data.disputes || []);
      } catch {
        if (!cancelled) setDisputes([]);
      } finally {
        if (!cancelled) setDisputesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [panel]);

  useEffect(() => {
    if (!showSessions) return;
    let cancelled = false;
    (async () => {
      setSessionsLoading(true);
      try {
        const data = await getSessions();
        if (!cancelled) setSessions(data.sessions || []);
      } catch {
        if (!cancelled) setSessions([]);
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showSessions]);

  useEffect(() => {
    if (panel !== "security") return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getSessions();
        if (!cancelled) setSessions(data.sessions || []);
      } catch {
        if (!cancelled) setSessions([]);
      }
    })();
    (async () => {
      setCloseEligibilityLoading(true);
      try {
        const data = await getCloseAccountEligibility();
        if (!cancelled) setCloseEligibility(data);
      } catch {
        if (!cancelled) setCloseEligibility(null);
      } finally {
        if (!cancelled) setCloseEligibilityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [panel]);

  const persistNotifPref = async (key, value) => {
    const next = { ...notifs, [key]: value };
    setNotifs(next);
    try {
      await updateNotificationPreferences({ [key]: value });
    } catch {
      setNotifs(notifs);
    }
  };

  const openEditProfile = () => {
    setEditError("");
    setEditForm({
      firstName: profile?.first_name || "",
      lastName: profile?.last_name || "",
      dateOfBirth: profile?.dateOfBirth || "",
      gender: profile?.gender || "",
      customerType: profile?.customerType || "",
      line1: profile?.address?.line1 || "",
      city: profile?.address?.city || "",
      state: profile?.address?.state || "",
      country: profile?.address?.country || "Nigeria",
      postalCode: profile?.address?.postalCode || "",
    });
    setPanel("editProfile");
  };

  const saveEditProfile = async () => {
    const fn = editForm.firstName.trim();
    if (fn.length < 2) {
      setEditError("First name must be at least 2 characters");
      return;
    }
    if (editForm.dateOfBirth && !/^\d{4}-\d{2}-\d{2}$/.test(editForm.dateOfBirth)) {
      setEditError("Date of birth must be YYYY-MM-DD");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      const updated = await updateProfile({
        firstName: fn,
        lastName: editForm.lastName.trim() || null,
        dateOfBirth: editForm.dateOfBirth || null,
        gender: editForm.gender || null,
        customerType: editForm.customerType || null,
        address: {
          line1: editForm.line1.trim() || null,
          city: editForm.city.trim() || null,
          state: editForm.state.trim() || null,
          country: editForm.country.trim() || "Nigeria",
          postalCode: editForm.postalCode.trim() || null,
        },
      });
      setProfile((prev) => ({ ...prev, ...updated }));
      showToast("Profile updated");
      setPanel(null);
    } catch (err) {
      setEditError(err.message || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  const resetCloseAccountFlow = () => {
    setShowCloseAccount(false);
    setCloseStep("warning");
    setClosePin("");
    setCloseOtp("");
    setCloseStepToken("");
    setCloseOtpSent(false);
    setCloseSubmitting(false);
    setCloseError("");
  };

  const openCloseAccount = () => {
    setCloseError("");
    if (!closeEligibility?.eligible) return;
    setCloseStep("warning");
    setClosePin("");
    setCloseOtp("");
    setCloseStepToken("");
    setCloseOtpSent(false);
    setShowCloseAccount(true);
  };

  useEffect(() => {
    if (!showCloseAccount || closeStep !== "otp" || closeOtpSent || !profile?.phone) return;
    let cancelled = false;
    (async () => {
      try {
        await requestOTP(profile.phone, "security_action", profile.email || undefined);
        if (!cancelled) setCloseOtpSent(true);
      } catch (err) {
        if (!cancelled) setCloseError(err.message || "Could not send verification code");
      }
    })();
    return () => { cancelled = true; };
  }, [showCloseAccount, closeStep, closeOtpSent, profile]);

  const handleClosePinPress = (k) => {
    if (closeSubmitting) return;
    if (k === "DEL") {
      setClosePin((p) => p.slice(0, -1));
      return;
    }
    if (closePin.length >= 6) return;
    const next = closePin + k;
    setClosePin(next);
    if (next.length === 6) {
      setCloseError("");
      setCloseStep("otp");
    }
  };

  const verifyCloseOtp = async () => {
    if (closeOtp.length !== OTP_CODE_LENGTH || !profile?.phone) return;
    setCloseSubmitting(true);
    setCloseError("");
    try {
      const data = await verifyOTP(profile.phone, closeOtp, "security_action");
      if (!data.stepToken) throw new Error("Invalid verification response");
      setCloseStepToken(data.stepToken);
      setCloseStep("confirm");
    } catch (err) {
      setCloseError(err.message || "Invalid verification code");
      setCloseOtp("");
    } finally {
      setCloseSubmitting(false);
    }
  };

  const submitCloseAccount = async () => {
    if (!closePin || !closeStepToken) return;
    setCloseSubmitting(true);
    setCloseError("");
    try {
      await closeAccount(closePin, closeStepToken);
      resetCloseAccountFlow();
      showToast("Your account has been closed.", "success");
      onSignOut?.();
    } catch (err) {
      setCloseError(err.message || "Could not close account");
    } finally {
      setCloseSubmitting(false);
    }
  };

  const disputeStatusColor = (status) => {
    if (status === "resolved_receiver") return C.lime;
    if (status === "resolved_sender") return C.muted;
    return C.gold;
  };

  const Toggle = ({ on, onToggle }) => (
    <div onClick={onToggle} style={{
      width: 44, height: 26, borderRadius: 13, cursor: "pointer",
      background: on ? C.lime : C.border,
      position: "relative", transition: "background 0.2s", flexShrink: 0,
    }}>
      <div style={{
        position: "absolute", top: 3, left: on ? 21 : 3,
        width: 20, height: 20, borderRadius: "50%",
        background: "#fff", transition: "left 0.2s",
        boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
      }} />
    </div>
  );

  const Row = ({ label, sub, right, onPress, danger }) => (
    <button onClick={onPress} style={{
      width: "100%", padding: "15px 0", background: "none", border: "none",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      cursor: onPress ? "pointer" : "default", textAlign: "left",
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ flex: 1 }}>
        <p style={{ color: danger ? C.danger : C.text, fontSize: 13, fontWeight: 600, margin: 0 }}>{label}</p>
        {sub && <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{sub}</p>}
      </div>
      {right}
    </button>
  );

  // PIN pad for change PIN flow
  const PinDots = ({ val }) => (
    <div style={{ display: "flex", gap: 14, justifyContent: "center", margin: "20px 0 8px" }}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div key={i} style={{ width: 16, height: 16, borderRadius: "50%", background: i < val.length ? C.lime : "none", border: `2px solid ${i < val.length ? C.lime : C.border}`, transition: "all 0.15s" }} />
      ))}
    </div>
  );

  const PinPad = ({ onPress }) => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 12 }}>
      {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "DEL"].map((k, i) => (
        <button key={i} onClick={() => k && onPress(k)} style={{
          padding: "16px", borderRadius: 12,
          border: `1px solid ${k ? "transparent" : "transparent"}`,
          background: k ? C.card : "none",
          color: k === "DEL" ? C.muted : C.text,
          fontSize: k === "DEL" ? 12 : 20, fontWeight: 700, cursor: k ? "pointer" : "default",
        }}>{k}</button>
      ))}
    </div>
  );

  const handlePinPress = async (k) => {
    if (pinSubmitting) return;
    if (pinPhase === "old") {
      if (k === "DEL") { setOldPin((p) => p.slice(0, -1)); return; }
      if (oldPin.length >= 6) return;
      const n = oldPin + k;
      setOldPin(n);
      if (n.length === 6) {
        setStoredOldPin(n);
        setOldPin("");
        setPinPhase("new");
      }
      return;
    }
    if (pinPhase === "new") {
      if (k === "DEL") { setNewPin((p) => p.slice(0, -1)); return; }
      if (newPin.length >= 6) return;
      const n = newPin + k;
      setNewPin(n);
      if (n.length === 6) setPinPhase("confirm");
      return;
    }
    if (k === "DEL") { setNewPinConfirm((p) => p.slice(0, -1)); return; }
    if (newPinConfirm.length >= 6) return;
    const n = newPinConfirm + k;
    setNewPinConfirm(n);
    if (n.length !== 6) return;
    if (n !== newPin) {
      setPinMsg("PINs don't match");
      setTimeout(() => { setPinMsg(""); setNewPin(""); setNewPinConfirm(""); setPinPhase("new"); }, 1200);
      return;
    }
    setPinSubmitting(true);
    setPinMsg("");
    try {
      await changePin(storedOldPin, newPin);
      setPinMsg("PIN changed successfully ✓");
      setTimeout(() => {
        setShowChangePin(false);
        setOldPin("");
        setStoredOldPin("");
        setNewPin("");
        setNewPinConfirm("");
        setPinPhase("old");
        setPinMsg("");
        setPinSubmitting(false);
      }, 1500);
    } catch (err) {
      setPinMsg(err.message || "Could not change PIN");
      setTimeout(() => {
        setPinMsg("");
        setOldPin("");
        setStoredOldPin("");
        setNewPin("");
        setNewPinConfirm("");
        setPinPhase("old");
        setPinSubmitting(false);
      }, 1500);
    }
  };

  return (
    <div style={{ paddingBottom: 100 }}>
      <TopBar unreadCount={unreadCount} onNotificationsClick={onNotificationsClick} />
      <div style={{ padding: "4px 20px 0" }}>
        <p style={{ color: C.text, fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 18 }}>Profile</p>

        {profile?.wallet?.virtualAccountPending && (
          <div style={{ background: C.gold + "12", border: `1px solid ${C.gold}40`, borderRadius: 18, padding: "16px", marginBottom: 12 }}>
            <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: "0 0 6px" }}>Bank account setup required</p>
            <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, margin: "0 0 12px" }}>
              Complete Safe Haven verification to get your PendPay account number for wallet funding.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowBankSetup(true);
                setBankSetupStep("bvn");
                setBankSetupIdentityType("BVN");
                setBankSetupIdentityNumber("");
                setBankSetupOtp("");
                setBankSetupReference("");
                setBankSetupError("");
              }}
              style={{ width: "100%", padding: "12px", borderRadius: 12, border: "none", background: C.gold, color: C.bg, fontSize: 13, fontWeight: 900, cursor: "pointer" }}
            >
              Set up bank account
            </button>
          </div>
        )}

        {/* Avatar card */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 22, padding: "24px 20px", marginBottom: 14, textAlign: "center" }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: C.forestMid, border: `2px solid ${C.lime}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, color: C.lime, fontWeight: 900, margin: "0 auto 12px",
          }}>{profile?.initials || "∙∙"}</div>
          <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 2, letterSpacing: "-0.01em" }}>
            {profileLoading ? "Loading…" : (profile?.displayName || "Your profile")}
          </p>
          <p style={{ color: C.lime, fontSize: 13, fontWeight: 800, marginBottom: 3 }}>
            {profile?.tag ? `#${profile.tag}` : ""}
          </p>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 14 }}>{profile?.email || profile?.phoneDisplay || ""}</p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            {profile?.verified && (
              <span style={{ background: C.lime + "22", color: C.lime, fontSize: 10, fontWeight: 800, padding: "4px 12px", borderRadius: 20 }}>Verified</span>
            )}
            <span style={{ background: C.gold + "22", color: C.gold, fontSize: 10, fontWeight: 800, padding: "4px 12px", borderRadius: 20 }}>
              {profile?.kycBadge || `Tier ${profile?.tier || 1} KYC`}
            </span>
          </div>
        </div>

        {/* Account info */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, overflow: "hidden", marginBottom: 12 }}>
          {[
            ["Full name", `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() || profile?.displayName || "Not set", C.textMid],
            ["Date of birth", formatDobLabel(profile?.dateOfBirth), C.textMid],
            ["Gender", profile?.genderLabel || "Not set", C.textMid],
            ["Email", profile?.email || "Not set", C.textMid],
            ["Phone", profile?.phoneDisplay || profile?.phone || "Not set", C.textMid],
            ["Address", formatAddressLines(profile?.address), C.textMid],
            ["Customer type", profile?.customerTypeLabel || "Not set", C.textMid],
            ["Identity (Safe Haven)", (profile?.bvn_verified || profile?.nin_verified) ? "Verified ✓" : "Not verified", (profile?.bvn_verified || profile?.nin_verified) ? C.lime : C.muted],
            ["Wallet tier", profile?.tierSummary || `Tier ${profile?.tier || 1}`, C.gold],
            ["Platform fee", profile?.platformFee?.label || "1% · split 50/50", C.textMid],
          ].map(([l, v, vc], i, a) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.muted, fontSize: 13, flexShrink: 0 }}>{l}</span>
              <span style={{ color: vc, fontSize: 12, fontWeight: 700, textAlign: "right", maxWidth: 200 }}>{v}</span>
            </div>
          ))}
          <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}` }}>
            <p style={{ color: C.muted, fontSize: 11, margin: 0 }}>
              Minimum contract fees = ₦{minPlatformFee.toLocaleString("en-NG")}
            </p>
          </div>
          {[
            ["Country", profile?.country ? `${profile.country} 🇳🇬` : "Nigeria 🇳🇬", C.textMid],
          ].map(([l, v, vc], i, a) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ color: C.muted, fontSize: 13 }}>{l}</span>
              <span style={{ color: vc, fontSize: 12, fontWeight: 700, textAlign: "right", maxWidth: 180 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Settings menu */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, overflow: "hidden", marginBottom: 20 }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            width: "100%", padding: "15px 16px",
            borderBottom: `1px solid ${C.border}`,
          }}>
            <div>
              <p style={{ color: C.textMid, fontSize: 13, fontWeight: 600, margin: 0 }}>Dark mode</p>
              <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{isDark ? "On" : "Off — using light mode"}</p>
            </div>
            <Toggle on={isDark} onToggle={toggleTheme} />
          </div>
          {[
            { label: "Edit profile", sub: "Name, DOB, gender, address & customer type", key: "editProfile", action: openEditProfile },
            { label: "Invite Friends", sub: "Share PendPay with clients & collaborators", key: "invite", action: () => setShowInvite(true) },
            { label: "Notification Preferences", sub: "Deal alerts, SMS, push", key: "notifications" },
            { label: "Dispute History", sub: `${profile?.disputeCount ?? 0} dispute${(profile?.disputeCount ?? 0) === 1 ? "" : "s"} on record`, key: "disputes" },
            { label: "Security Settings", sub: "PIN, biometrics, sessions", key: "security" },
            { label: "Help & Support", sub: "FAQs, contact, policies", key: "help" },
          ].map((item, i, arr) => (
            <button key={item.key} onClick={() => (item.action ? item.action() : setPanel(item.key))} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              width: "100%", padding: "15px 16px", background: "none",
              border: "none", borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none",
              cursor: "pointer", textAlign: "left",
            }}>
              <div>
                <p style={{ color: C.textMid, fontSize: 13, fontWeight: 600, margin: 0 }}>{item.label}</p>
                <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{item.sub}</p>
              </div>
              {sv(IC.chevron_r, 14, C.muted)}
            </button>
          ))}
          <button onClick={() => setShowSignOut(true)} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            width: "100%", padding: "15px 16px", background: "none",
            border: "none", cursor: "pointer", textAlign: "left",
          }}>
            <p style={{ color: C.danger, fontSize: 13, fontWeight: 800, margin: 0 }}>Sign Out</p>
          </button>
        </div>
      </div>

      {/* ── EDIT PROFILE PANEL ─────────────────────────────────── */}
      {panel === "editProfile" && (
        <BottomSheet title="Edit profile" onClose={() => setPanel(null)}>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 14 }}>
            Update your details. Email and phone cannot be changed here.
          </p>

          <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: "0 0 8px" }}>Full name</p>
          <label style={{ display: "block", color: C.muted, fontSize: 11, marginBottom: 4 }}>First name</label>
          <input
            value={editForm.firstName}
            onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", marginBottom: 10, padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14 }}
          />
          <label style={{ display: "block", color: C.muted, fontSize: 11, marginBottom: 4 }}>Last name</label>
          <input
            value={editForm.lastName}
            onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", marginBottom: 14, padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14 }}
          />

          <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: "0 0 8px" }}>Date of birth</p>
          <input
            type="date"
            value={editForm.dateOfBirth}
            onChange={(e) => setEditForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", marginBottom: 14, padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14 }}
          />

          <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: "0 0 8px" }}>Gender</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {GENDER_OPTIONS.map((opt) => {
              const selected = editForm.gender === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setEditForm((f) => ({ ...f, gender: opt.key }))}
                  style={{
                    padding: "8px 12px", borderRadius: 20, cursor: "pointer",
                    border: `1px solid ${selected ? C.lime : C.border}`,
                    background: selected ? C.lime + "22" : C.card,
                    color: selected ? C.lime : C.textMid, fontSize: 12, fontWeight: selected ? 800 : 600,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: "0 0 4px" }}>Customer type</p>
          <p style={{ color: C.muted, fontSize: 11, margin: "0 0 8px" }}>How you mainly use PendPay</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {CUSTOMER_TYPE_OPTIONS.map((opt) => {
              const selected = editForm.customerType === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setEditForm((f) => ({ ...f, customerType: opt.key }))}
                  style={{
                    padding: "8px 12px", borderRadius: 20, cursor: "pointer",
                    border: `1px solid ${selected ? C.lime : C.border}`,
                    background: selected ? C.lime + "22" : C.card,
                    color: selected ? C.lime : C.textMid, fontSize: 12, fontWeight: selected ? 800 : 600,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: "0 0 8px" }}>Address</p>
          {[
            ["line1", "Street address"],
            ["city", "City"],
            ["state", "State"],
            ["country", "Country"],
            ["postalCode", "Postal code / ZIP (optional)"],
          ].map(([key, label]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <label style={{ display: "block", color: C.muted, fontSize: 11, marginBottom: 4 }}>{label}</label>
              <input
                value={editForm[key]}
                onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14 }}
              />
            </div>
          ))}

          <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: "12px 0 8px" }}>Contact</p>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.muted, fontSize: 13 }}>Email</span>
              <span style={{ color: C.textMid, fontSize: 12, fontWeight: 700, textAlign: "right" }}>{profile?.email || "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 14px" }}>
              <span style={{ color: C.muted, fontSize: 13 }}>Phone</span>
              <span style={{ color: C.textMid, fontSize: 12, fontWeight: 700, textAlign: "right" }}>{profile?.phoneDisplay || profile?.phone || "—"}</span>
            </div>
          </div>

          {editError ? (
            <p style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{editError}</p>
          ) : null}

          <button
            type="button"
            disabled={editSaving}
            onClick={saveEditProfile}
            style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none",
              background: C.lime, color: C.bg, fontSize: 14, fontWeight: 900,
              cursor: editSaving ? "default" : "pointer", opacity: editSaving ? 0.7 : 1,
            }}
          >
            {editSaving ? "Saving…" : "Save profile"}
          </button>
        </BottomSheet>
      )}

      {/* ── NOTIFICATIONS PANEL ─────────────────────────────────── */}
      {panel === "notifications" && (
        <BottomSheet title="Notification Preferences" onClose={() => setPanel(null)}>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>Choose what you get notified about and how.</p>
          <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 10 }}>DEAL ACTIVITY</p>
          {[
            { key: "dealAlerts", label: "Deal requests", sub: "When someone sends you a deal" },
            { key: "milestoneUpdates", label: "Milestone updates", sub: "Claims, approvals, auto-releases" },
            { key: "disputes", label: "Dispute updates", sub: "Arbitration progress and decisions" },
          ].map(item => (
            <div key={item.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 600, margin: 0 }}>{item.label}</p>
                <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{item.sub}</p>
              </div>
              <Toggle on={notifs[item.key]} onToggle={() => persistNotifPref(item.key, !notifs[item.key])} />
            </div>
          ))}
          <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 10, marginTop: 4 }}>WALLET ACTIVITY</p>
          {[
            { key: "walletAlerts", label: "Wallet & funding", sub: "Top-ups, withdrawals, escrow funding" },
          ].map(item => (
            <div key={item.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 600, margin: 0 }}>{item.label}</p>
                <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{item.sub}</p>
              </div>
              <Toggle on={notifs[item.key]} onToggle={() => persistNotifPref(item.key, !notifs[item.key])} />
            </div>
          ))}
          <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 10, marginTop: 4 }}>EVENTS</p>
          {[
            { key: "eventAlerts", label: "Event tickets", sub: "Ticket sales and purchase confirmations" },
          ].map(item => (
            <div key={item.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 600, margin: 0 }}>{item.label}</p>
                <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{item.sub}</p>
              </div>
              <Toggle on={notifs[item.key]} onToggle={() => persistNotifPref(item.key, !notifs[item.key])} />
            </div>
          ))}
          <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 10, marginTop: 4 }}>CHANNELS</p>
          {[
            { key: "push", label: "Push notifications", sub: "In-app and device alerts" },
            { key: "sms", label: "SMS", sub: `Text alerts to ${profile?.phoneDisplay || "your phone"}` },
            { key: "email", label: "Email", sub: profile?.email || "Add an email in settings" },
            { key: "marketing", label: "Product updates & tips", sub: "Occasional PendPay news" },
          ].map(item => (
            <div key={item.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 600, margin: 0 }}>{item.label}</p>
                <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{item.sub}</p>
              </div>
              <Toggle on={notifs[item.key]} onToggle={() => persistNotifPref(item.key, !notifs[item.key])} />
            </div>
          ))}
        </BottomSheet>
      )}

      {/* ── DISPUTE HISTORY PANEL ───────────────────────────────── */}
      {panel === "disputes" && (
        <BottomSheet title="Dispute History" onClose={() => setPanel(null)}>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>All disputes are archived for 90 days after resolution.</p>
          {disputesLoading && <p style={{ color: C.muted, fontSize: 12 }}>Loading disputes…</p>}
          {!disputesLoading && disputes.length === 0 && (
            <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>No disputes on your account yet.</p>
          )}
          {disputes.map(d => (
            <div key={d.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: 0 }}>{d.dealTitle}</p>
                  <p style={{ color: C.muted, fontSize: 11, margin: "2px 0 0" }}>{d.dealReference} · {d.dateLabel}</p>
                </div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 900, margin: 0 }}>{d.amountNaira}</p>
              </div>
              <span style={{ background: disputeStatusColor(d.status) + "20", color: disputeStatusColor(d.status), fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 20 }}>{d.statusLabel}</span>
            </div>
          ))}
          <div style={{ background: C.forest, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginTop: 8 }}>
            <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6 }}>To raise a new dispute, go to the deal in History and tap the Dispute button. Disputes must be raised within 7 days of a milestone claim.</p>
          </div>
        </BottomSheet>
      )}

      {/* ── SECURITY PANEL ──────────────────────────────────────── */}
      {panel === "security" && (
        <BottomSheet title="Security Settings" onClose={() => setPanel(null)}>
          {!showChangePin ? (
            <>
              <Row
                label="Change PIN"
                sub="Update your 6-digit login PIN"
                onPress={() => { setShowChangePin(true); setPinPhase("old"); setOldPin(""); setNewPin(""); setNewPinConfirm(""); setPinMsg(""); }}
                right={sv(IC.chevron_r, 14, C.muted)}
              />
              <Row
                label="Face ID / Biometrics"
                sub="Use biometrics instead of PIN"
                right={<Toggle on={true} onToggle={() => { }} />}
              />
              <Row
                label="Active Sessions"
                sub={`${sessions.length || profile?.activeSessionCount || 0} device${(sessions.length || profile?.activeSessionCount || 0) === 1 ? "" : "s"} logged in`}
                onPress={() => setShowSessions(true)}
                right={<span style={{ color: C.lime, fontSize: 11, fontWeight: 800 }}>Manage →</span>}
              />
              <Row
                label="Transaction PIN"
                sub="Require PIN for every transfer"
                right={<Toggle on={true} onToggle={() => { }} />}
              />
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginTop: 16 }}>
                <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6 }}>🔒 Your account is protected by 256-bit encryption. PendPay never stores your PIN in plain text.</p>
              </div>
              <div style={{ marginTop: 16, padding: "14px", borderRadius: 12, background: C.danger + "10", border: `1px solid ${C.danger}30` }}>
                <p style={{ color: C.danger, fontSize: 12, fontWeight: 800, marginBottom: 4 }}>Freeze Account</p>
                <p style={{ color: C.muted, fontSize: 11, marginBottom: 10 }}>Temporarily disable all transactions if you suspect unauthorised access.</p>
                <button
                  disabled={freezeSubmitting}
                  onClick={() => { setFreezeError(""); setShowFreezeConfirm(true); }}
                  style={{ padding: "10px 20px", borderRadius: 10, background: C.danger, border: "none", color: "#fff", fontSize: 12, fontWeight: 900, cursor: freezeSubmitting ? "default" : "pointer", opacity: freezeSubmitting ? 0.7 : 1 }}
                >Freeze my account</button>
              </div>
              <div style={{ marginTop: 12, padding: "14px", borderRadius: 12, background: C.danger + "08", border: `1px solid ${C.danger}25` }}>
                <p style={{ color: C.danger, fontSize: 12, fontWeight: 800, marginBottom: 4 }}>Close account</p>
                <p style={{ color: C.muted, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
                  Permanently close your PendPay account. Your personal details will be removed and this cannot be undone.
                </p>
                {closeEligibilityLoading && (
                  <p style={{ color: C.muted, fontSize: 11, marginBottom: 10 }}>Checking eligibility…</p>
                )}
                {!closeEligibilityLoading && closeEligibility && !closeEligibility.eligible && (
                  <div style={{ marginBottom: 10 }}>
                    {closeEligibility.blockers.map((b) => (
                      <p key={b.code} style={{ color: C.danger, fontSize: 11, fontWeight: 700, margin: "0 0 6px", lineHeight: 1.5 }}>{b.message}</p>
                    ))}
                  </div>
                )}
                {!closeEligibilityLoading && closeEligibility?.dustBalanceKobo > 0 && (
                  <p style={{ color: C.gold, fontSize: 11, fontWeight: 700, marginBottom: 10, lineHeight: 1.5 }}>
                    Your remaining {formatNairaFromKobo(closeEligibility.dustBalanceKobo)} is below the minimum withdrawal amount and will be forfeited when you close your account.
                  </p>
                )}
                <button
                  disabled={closeEligibilityLoading || closeSubmitting || !closeEligibility?.eligible}
                  onClick={openCloseAccount}
                  style={{ padding: "10px 20px", borderRadius: 10, background: "none", border: `1px solid ${C.danger}55`, color: C.danger, fontSize: 12, fontWeight: 900, cursor: closeEligibilityLoading || !closeEligibility?.eligible ? "default" : "pointer", opacity: closeEligibilityLoading || !closeEligibility?.eligible ? 0.5 : 1 }}
                >Close my account</button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center" }}>
              <p style={{ color: C.text, fontSize: 15, fontWeight: 900, marginBottom: 4 }}>
                {pinPhase === "old" ? "Enter current PIN" : pinPhase === "new" ? "Enter new PIN" : "Confirm new PIN"}
              </p>
              <p style={{ color: C.muted, fontSize: 12, marginBottom: 0 }}>
                {pinPhase === "old" ? "Enter your current 6-digit PIN" : pinPhase === "new" ? "Choose a new 6-digit PIN" : "Re-enter your new PIN"}
              </p>
              <PinDots val={pinPhase === "old" ? oldPin : pinPhase === "new" ? newPin : newPinConfirm} />
              {pinMsg && <p style={{ color: pinMsg.includes("✓") ? C.lime : C.danger, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{pinMsg}</p>}
              <PinPad onPress={handlePinPress} />
              <button onClick={() => setShowChangePin(false)} style={{ marginTop: 16, background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer" }}>Cancel</button>
            </div>
          )}
        </BottomSheet>
      )}

      {/* ── HELP & SUPPORT PANEL ────────────────────────────────── */}
      {panel === "help" && (
        <BottomSheet title="Help & Support" onClose={() => setPanel(null)}>
          {/* Contact options */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {helpContacts.map(c => (
              <a
                key={c.label}
                href={c.action?.startsWith("mailto:") || c.action?.startsWith("tel:") || c.action?.startsWith("http") ? c.action : undefined}
                target={c.action?.startsWith("http") ? "_blank" : undefined}
                rel={c.action?.startsWith("http") ? "noopener noreferrer" : undefined}
                style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 8px", textAlign: "center", cursor: "pointer", textDecoration: "none" }}
              >
                <p style={{ fontSize: 22, margin: "0 0 4px" }}>{c.icon}</p>
                <p style={{ color: C.text, fontSize: 11, fontWeight: 800, margin: 0 }}>{c.label}</p>
                <p style={{ color: C.muted, fontSize: 9, margin: "2px 0 0" }}>{c.sub}</p>
              </a>
            ))}
          </div>

          <p style={{ color: C.muted, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 12 }}>FREQUENTLY ASKED QUESTIONS</p>
          {faqs.map((f, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <button onClick={() => setFaqOpen(faqOpen === i ? null : i)} style={{
                width: "100%", padding: "13px 14px", borderRadius: 12,
                background: faqOpen === i ? C.lime + "12" : C.card,
                border: `1px solid ${faqOpen === i ? C.lime + "40" : C.border}`,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", textAlign: "left",
              }}>
                <p style={{ color: C.text, fontSize: 12, fontWeight: 700, margin: 0, flex: 1, paddingRight: 10 }}>{f.q}</p>
                <span style={{ color: C.muted, fontSize: 16, flexShrink: 0, transform: faqOpen === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
              </button>
              {faqOpen === i && (
                <div style={{ padding: "12px 14px", background: C.forest, border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 12px 12px", marginTop: -2 }}>
                  <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.7, margin: 0 }}>{f.a}</p>
                </div>
              )}
            </div>
          ))}

          <div style={{ marginTop: 16, padding: "14px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}` }}>
            <p style={{ color: C.textMid, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Legal</p>
            {legalLinks.map((l) => (
              <a
                key={l.label}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(l.url, "_blank", "noopener,noreferrer");
                }}
                style={{ display: "block", width: "100%", padding: "8px 0", borderBottom: `1px solid ${C.border}`, color: C.info, fontSize: 12, textAlign: "left", textDecoration: "none" }}
              >
                {l.label} ↗
              </a>
            ))}
          </div>
          <p style={{ color: C.muted, fontSize: 10, textAlign: "center", marginTop: 16 }}>PendPay v{appVersion} · © 2026 PendPay Nigeria Ltd</p>
        </BottomSheet>
      )}

      {/* ── ACTIVE SESSIONS ─────────────────────────────────────── */}
      {showSessions && (
        <BottomSheet title="Active Sessions" onClose={() => setShowSessions(false)}>
          <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>These devices are currently logged into your PendPay account.</p>
          {sessionsLoading && <p style={{ color: C.muted, fontSize: 12 }}>Loading sessions…</p>}
          {!sessionsLoading && sessions.length === 0 && (
            <p style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>No other active sessions found.</p>
          )}
          {sessions.map((s) => (
            <div key={s.id} style={{ background: C.card, border: `1px solid ${s.current ? C.lime + "40" : C.border}`, borderRadius: 14, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <p style={{ color: C.text, fontSize: 13, fontWeight: 800, margin: "0 0 3px" }}>{s.device}</p>
                  <p style={{ color: C.muted, fontSize: 11, margin: "0 0 3px" }}>{s.ipAddress || "Unknown location"}</p>
                  <p style={{ color: C.muted, fontSize: 10, margin: 0 }}>{s.lastActiveLabel}</p>
                </div>
                {s.current
                  ? <span style={{ background: C.lime + "22", color: C.lime, fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 20 }}>This device</span>
                  : <button
                    onClick={async () => {
                      try {
                        await revokeSession(s.id);
                        setSessions((prev) => prev.filter((row) => row.id !== s.id));
                      } catch (err) {
                        showToast(err.message || "Could not sign out session");
                      }
                    }}
                    style={{ background: C.danger + "12", border: `1px solid ${C.danger}30`, borderRadius: 10, padding: "5px 12px", color: C.danger, fontSize: 11, fontWeight: 800, cursor: "pointer" }}
                  >Sign out</button>
                }
              </div>
            </div>
          ))}
          <div style={{ background: C.forest, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginTop: 8 }}>
            <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6, margin: 0 }}>If you don't recognise a session, sign it out immediately and change your PIN.</p>
          </div>
        </BottomSheet>
      )}

      {/* ── FREEZE ACCOUNT CONFIRMATION ─────────────────────────── */}
      {showFreezeConfirm && (
        <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ width: "100%", maxWidth: 380, background: C.surface, borderRadius: 24, padding: "28px 24px", border: `1px solid ${C.border}`, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.danger + "18", border: `2px solid ${C.danger}30`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              {sv(IC.warn, 24, C.danger)}
            </div>
            <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Freeze your account?</p>
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>All transactions will be blocked until support restores access.</p>
            {freezeError && (
              <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "-12px 0 16px", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{freezeError}</p>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                disabled={freezeSubmitting}
                onClick={() => { if (!freezeSubmitting) { setShowFreezeConfirm(false); setFreezeError(""); } }}
                style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: freezeSubmitting ? "default" : "pointer", opacity: freezeSubmitting ? 0.7 : 1 }}
              >Cancel</button>
              <button
                disabled={freezeSubmitting}
                onClick={async () => {
                  setFreezeSubmitting(true);
                  setFreezeError("");
                  try {
                    await freezeAccount();
                    setShowFreezeConfirm(false);
                    showToast("Account frozen. Contact support@pendpay.ng to restore access.", "success");
                    onSignOut?.();
                  } catch (err) {
                    setFreezeError(err.message || "Could not freeze account");
                  } finally {
                    setFreezeSubmitting(false);
                  }
                }}
                style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: freezeSubmitting ? "default" : "pointer", opacity: freezeSubmitting ? 0.7 : 1 }}
              >{freezeSubmitting ? "Freezing…" : "Freeze account"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CLOSE ACCOUNT FLOW ──────────────────────────────────── */}
      {showCloseAccount && (
        <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ width: "100%", maxWidth: 380, background: C.surface, borderRadius: 24, padding: "28px 24px", border: `1px solid ${C.border}`, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.danger + "18", border: `2px solid ${C.danger}30`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              {sv(IC.warn, 24, C.danger)}
            </div>

            {closeStep === "warning" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Close your account?</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
                  This permanently closes your account, removes your personal details, and signs you out. Support cannot restore access after closure.
                  {closeEligibility?.dustBalanceKobo > 0 && (
                    <> Your remaining {formatNairaFromKobo(closeEligibility.dustBalanceKobo)} cannot be withdrawn and will be forfeited.</>
                  )}
                </p>
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "-12px 0 16px", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{closeError}</p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    disabled={closeSubmitting}
                    onClick={resetCloseAccountFlow}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  >Cancel</button>
                  <button
                    onClick={() => { setCloseError(""); setCloseStep("pin"); }}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: "pointer" }}
                  >I understand</button>
                </div>
              </>
            )}

            {closeStep === "pin" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Enter your PIN</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 0 }}>Confirm your identity with your 6-digit PIN.</p>
                <PinDots val={closePin} />
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "0 0 12px" }}>{closeError}</p>
                )}
                <PinPad onPress={handleClosePinPress} />
                <button onClick={() => { setCloseStep("warning"); setClosePin(""); setCloseError(""); }} style={{ marginTop: 16, background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer" }}>Back</button>
              </>
            )}

            {closeStep === "otp" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Enter verification code</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
                  {closeOtpSent ? "We sent a code to your email." : "Sending verification code…"}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={OTP_CODE_LENGTH}
                  value={closeOtp}
                  onChange={(e) => setCloseOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_CODE_LENGTH))}
                  placeholder="6-digit code"
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 14, marginBottom: 16, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 18, fontWeight: 800, letterSpacing: "0.2em", textAlign: "center", outline: "none", boxSizing: "border-box" }}
                />
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "-8px 0 16px", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{closeError}</p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    disabled={closeSubmitting}
                    onClick={() => { setCloseStep("pin"); setCloseOtp(""); setCloseOtpSent(false); setCloseError(""); }}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  >Back</button>
                  <button
                    disabled={closeSubmitting || closeOtp.length !== OTP_CODE_LENGTH}
                    onClick={verifyCloseOtp}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: closeSubmitting || closeOtp.length !== OTP_CODE_LENGTH ? "default" : "pointer", opacity: closeSubmitting || closeOtp.length !== OTP_CODE_LENGTH ? 0.7 : 1 }}
                  >{closeSubmitting ? "Verifying…" : "Continue"}</button>
                </div>
              </>
            )}

            {closeStep === "confirm" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Final confirmation</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
                  Your account will be closed immediately and you will be signed out.
                </p>
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "-12px 0 16px", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{closeError}</p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    disabled={closeSubmitting}
                    onClick={() => { setCloseStep("otp"); setCloseStepToken(""); setCloseError(""); }}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  >Back</button>
                  <button
                    disabled={closeSubmitting}
                    onClick={submitCloseAccount}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: closeSubmitting ? "default" : "pointer", opacity: closeSubmitting ? 0.7 : 1 }}
                  >{closeSubmitting ? "Closing…" : "Close account"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showBankSetup && (
        <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 480, background: C.surface, borderRadius: "24px 24px 0 0", padding: "24px", border: `1px solid ${C.border}` }}>
            <p style={{ color: C.text, fontSize: 18, fontWeight: 900, margin: "0 0 8px" }}>
              {bankSetupStep === "otp" ? "Enter Safe Haven OTP" : "Set up bank account"}
            </p>
            <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, margin: "0 0 16px" }}>
              {bankSetupStep === "otp"
                ? "Enter the code sent to your registered phone to create your PendPay account number."
                : "Enter your BVN or NIN to start Safe Haven verification."}
            </p>
            {bankSetupStep === "bvn" ? (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                  {[
                    { type: "BVN", label: "BVN" },
                    { type: "NIN", label: "NIN" },
                  ].map((opt) => (
                    <button
                      key={opt.type}
                      type="button"
                      onClick={() => { setBankSetupIdentityType(opt.type); setBankSetupIdentityNumber(""); }}
                      style={{
                        flex: 1, padding: "12px", borderRadius: 12, cursor: "pointer",
                        background: bankSetupIdentityType === opt.type ? C.info + "22" : C.card,
                        border: `2px solid ${bankSetupIdentityType === opt.type ? C.info : C.border}`,
                        color: bankSetupIdentityType === opt.type ? C.info : C.muted,
                        fontSize: 13, fontWeight: 800,
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
                <input
                  type="tel"
                  value={bankSetupIdentityNumber}
                  onChange={(e) => setBankSetupIdentityNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 11))}
                  placeholder={`11-digit ${bankSetupIdentityType}`}
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 15, marginBottom: 12, boxSizing: "border-box" }}
                />
              </>
            ) : (
              <input
                type="tel"
                value={bankSetupOtp}
                onChange={(e) => setBankSetupOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                placeholder="OTP code"
                style={{ width: "100%", padding: "14px 16px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 18, fontWeight: 700, letterSpacing: "0.15em", textAlign: "center", marginBottom: 12, boxSizing: "border-box" }}
              />
            )}
            {bankSetupError && <p style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{bankSetupError}</p>}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowBankSetup(false)}
                style={{ flex: 1, padding: "14px", borderRadius: 12, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bankSetupSubmitting}
                onClick={async () => {
                  setBankSetupError("");
                  setBankSetupSubmitting(true);
                  try {
                    if (bankSetupStep === "bvn") {
                      if (bankSetupIdentityNumber.length !== 11) {
                        setBankSetupError(`Enter your 11-digit ${bankSetupIdentityType}`);
                        return;
                      }
                      const init = await initiateSafeHavenIdentity(bankSetupIdentityNumber, bankSetupIdentityType);
                      if (init.otpRequired && init.reference) {
                        setBankSetupReference(init.reference);
                        setBankSetupStep("otp");
                      } else if (init.accountNumber) {
                        showToast("Bank account ready");
                        setShowBankSetup(false);
                        loadProfile();
                      } else {
                        const msg = formatSafeHavenInitError(init);
                        if (msg) setBankSetupError(msg);
                      }
                    } else {
                      if (bankSetupOtp.length < 4) {
                        setBankSetupError("Enter the OTP code");
                        return;
                      }
                      const result = await validateSafeHavenIdentity(bankSetupReference, bankSetupOtp, bankSetupIdentityType);
                      if (result.accountNumber) {
                        showToast(`Account ${result.accountNumber} ready`);
                      } else {
                        showToast("Bank account setup complete");
                      }
                      setShowBankSetup(false);
                      loadProfile();
                    }
                  } catch (err) {
                    setBankSetupError(err.message || "Setup failed");
                  } finally {
                    setBankSetupSubmitting(false);
                  }
                }}
                style={{ flex: 1, padding: "14px", borderRadius: 12, border: "none", background: C.lime, color: C.bg, fontSize: 14, fontWeight: 900, cursor: bankSetupSubmitting ? "default" : "pointer", opacity: bankSetupSubmitting ? 0.7 : 1 }}
              >
                {bankSetupSubmitting ? "Please wait…" : bankSetupStep === "bvn" ? "Continue" : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <InviteSheet context="general" onClose={() => setShowInvite(false)} />
      )}

      {/* ── CLOSE ACCOUNT FLOW ──────────────────────────────────── */}
      {showCloseAccount && (
        <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ width: "100%", maxWidth: 380, background: C.surface, borderRadius: 24, padding: "28px 24px", border: `1px solid ${C.border}`, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.danger + "18", border: `2px solid ${C.danger}30`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              {sv(IC.warn, 24, C.danger)}
            </div>

            {closeStep === "warning" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Close your account?</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
                  This permanently closes your account, removes your personal details, and signs you out. Support cannot restore access after closure.
                  {closeEligibility?.dustBalanceKobo > 0 && (
                    <> Your remaining {formatNairaFromKobo(closeEligibility.dustBalanceKobo)} cannot be withdrawn and will be forfeited.</>
                  )}
                </p>
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "-12px 0 16px", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{closeError}</p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    disabled={closeSubmitting}
                    onClick={resetCloseAccountFlow}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  >Cancel</button>
                  <button
                    onClick={() => { setCloseError(""); setCloseStep("pin"); }}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: "pointer" }}
                  >I understand</button>
                </div>
              </>
            )}

            {closeStep === "pin" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Enter your PIN</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 0 }}>Confirm your identity with your 6-digit PIN.</p>
                <PinDots val={closePin} />
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "0 0 12px" }}>{closeError}</p>
                )}
                <PinPad onPress={handleClosePinPress} />
                <button onClick={() => { setCloseStep("warning"); setClosePin(""); setCloseError(""); }} style={{ marginTop: 16, background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer" }}>Back</button>
              </>
            )}

            {closeStep === "otp" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Enter verification code</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
                  {closeOtpSent ? "We sent a code to your email." : "Sending verification code…"}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={OTP_CODE_LENGTH}
                  value={closeOtp}
                  onChange={(e) => setCloseOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_CODE_LENGTH))}
                  placeholder="6-digit code"
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 14, marginBottom: 16, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 18, fontWeight: 800, letterSpacing: "0.2em", textAlign: "center", outline: "none", boxSizing: "border-box" }}
                />
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "-8px 0 16px", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{closeError}</p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    disabled={closeSubmitting}
                    onClick={() => { setCloseStep("pin"); setCloseOtp(""); setCloseOtpSent(false); setCloseError(""); }}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  >Back</button>
                  <button
                    disabled={closeSubmitting || closeOtp.length !== OTP_CODE_LENGTH}
                    onClick={verifyCloseOtp}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: closeSubmitting || closeOtp.length !== OTP_CODE_LENGTH ? "default" : "pointer", opacity: closeSubmitting || closeOtp.length !== OTP_CODE_LENGTH ? 0.7 : 1 }}
                  >{closeSubmitting ? "Verifying…" : "Continue"}</button>
                </div>
              </>
            )}

            {closeStep === "confirm" && (
              <>
                <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Final confirmation</p>
                <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
                  Your account will be closed immediately and you will be signed out.
                </p>
                {closeError && (
                  <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, margin: "-12px 0 16px", padding: "10px 12px", borderRadius: 12, background: C.danger + "12", border: `1px solid ${C.danger}35` }}>{closeError}</p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    disabled={closeSubmitting}
                    onClick={() => { setCloseStep("otp"); setCloseStepToken(""); setCloseError(""); }}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  >Back</button>
                  <button
                    disabled={closeSubmitting}
                    onClick={submitCloseAccount}
                    style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: closeSubmitting ? "default" : "pointer", opacity: closeSubmitting ? 0.7 : 1 }}
                  >{closeSubmitting ? "Closing…" : "Close account"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── SIGN OUT CONFIRMATION ───────────────────────────────── */}
      {showSignOut && (
        <div style={{ position: "fixed", inset: 0, ...mobileShell, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ width: "100%", maxWidth: 380, background: C.surface, borderRadius: 24, padding: "28px 24px", border: `1px solid ${C.border}`, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.danger + "18", border: `2px solid ${C.danger}30`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              {sv(IC.warn, 24, C.danger)}
            </div>
            <p style={{ color: C.text, fontSize: 17, fontWeight: 900, marginBottom: 8 }}>Sign out?</p>
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>You'll need your phone number and PIN to sign back in. Any active deals are safe and will continue running.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowSignOut(false)} style={{ flex: 1, padding: "14px", borderRadius: 14, background: "none", border: `1px solid ${C.border}`, color: C.textMid, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button
                onClick={async () => {
                  await signout();
                  onSignOut?.();
                }}
                style={{ flex: 1, padding: "14px", borderRadius: 14, background: C.danger, border: "none", color: "#fff", fontSize: 14, fontWeight: 900, cursor: "pointer" }}
              >Sign out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Reusable signup input (defined outside to prevent keyboard dismissal) ──
function SuInput({ val, set, placeholder, type = "text", maxLen = 100 }) {
  const C = useColors();
  return (
    <input
      type={type}
      value={val}
      onChange={e => set(e.target.value.slice(0, maxLen))}
      placeholder={placeholder}
      autoComplete="off"
      style={{
        width: "100%", padding: "15px 16px", borderRadius: 14, marginBottom: 14,
        background: C.card, border: `1px solid ${C.border}`,
        color: C.text, fontSize: 15, outline: "none", boxSizing: "border-box",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    />
  );
}

// ── Reusable signup step wrapper (defined outside to prevent keyboard dismissal) ──
function SuWrap({ title, sub, step, total, onBack, canNext, onNext, nextLabel = "Continue", loading, nextError, footer, children }) {
  const C = useColors();
  return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ padding: "56px 20px 0", display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        {onBack && <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>{sv(IC.chevron_l, 22, C.textMid)}</button>}
        <div style={{ flex: 1, height: 3, background: C.card, borderRadius: 2 }}>
          <div style={{ height: "100%", width: `${(step / total) * 100}%`, background: C.lime, borderRadius: 2, transition: "width 0.3s" }} />
        </div>
        <span style={{ color: C.muted, fontSize: 11, fontWeight: 700 }}>{step}/{total}</span>
      </div>
      <div style={{ padding: "24px 24px 0" }}>
        <p style={{ color: C.text, fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6 }}>{title}</p>
        {sub && <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>{sub}</p>}
      </div>
      <div style={{ flex: 1, padding: "0 24px", overflowY: "auto" }}>{children}</div>
      <div style={{ padding: "16px 24px 40px" }}>
        {nextError && (
          <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12, lineHeight: 1.5 }}>{nextError}</p>
        )}
        <button onClick={onNext} disabled={!canNext || loading} style={{
          width: "100%", padding: "17px", borderRadius: 16, border: "none",
          background: canNext && !loading ? C.lime : C.card,
          color: canNext && !loading ? C.bg : C.muted,
          fontSize: 15, fontWeight: 900, cursor: canNext && !loading ? "pointer" : "default",
          transition: "all 0.2s",
        }}>{loading ? "Checking…" : nextLabel}</button>
        {footer}
      </div>
    </div>
  );
}

// ── Sign In Screen ───────────────────────────────────────────────
/** otpPurpose: backend `/auth/request-otp` purpose (`signup` | `recover` | …). */
function OTPScreen({ phoneDigits, otpPurpose, email, onVerified, onBack }) {
  const C = useColors();
  const [otp, setOtp] = useState(emptyOtpDigits);
  const [status, setStatus] = useState(null); // null | "verifying" | "error" | "send_error"
  const [sendErr, setSendErr] = useState("");
  const [deliveryChannels, setDeliveryChannels] = useState(null);
  const [deliveryDestination, setDeliveryDestination] = useState("");
  const [emailFallback, setEmailFallback] = useState(false);
  const [resent, setResent] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const refs = Array.from({ length: OTP_CODE_LENGTH }, () => useRef(null));
  const phoneApi = formatPhoneForApi(phoneDigits);

  useEffect(() => {
    refs[0].current?.focus();
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (phoneApi.length < 12) return;
      setSendErr("");
      try {
        const data = await requestOTP(phoneApi, otpPurpose, email || undefined);
        if (cancelled) return;
        setDeliveryChannels(otpChannelsFromResponse(data));
        setDeliveryDestination(data.destination || "");
        setEmailFallback(!!data.fallback);
        if (!data.fallback) setSendErr("");
      } catch (err) {
        if (!cancelled) setSendErr(err.message || "Could not send code");
      }
    })();
    return () => { cancelled = true; };
  }, [phoneApi, otpPurpose, email]);

  const runVerify = async (code) => {
    setStatus("verifying");
    try {
      const data = await verifyOTP(phoneApi, code, otpPurpose);
      setStatus(null);
      if (data.stepToken) onVerified(data.stepToken);
      else throw new Error("Invalid verification response");
    } catch {
      setStatus("error");
      setOtp(emptyOtpDigits());
      refs[0].current?.focus();
    }
  };

  const handleKey = (i, val) => {
    const clean = val.replace(/[^0-9]/g, "").slice(-1);
    const next = [...otp];
    next[i] = clean;
    setOtp(next);
    if (clean && i < OTP_CODE_LENGTH - 1) refs[i + 1].current?.focus();
    if (next.every((d) => d !== "") && clean) {
      const code = next.join("");
      runVerify(code);
    }
  };

  const handleBackspace = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) refs[i - 1].current?.focus();
  };

  const resend = async () => {
    if (countdown > 0) return;
    setSendErr("");
    try {
      const data = await requestOTP(phoneApi, otpPurpose, email || undefined);
      setDeliveryChannels(otpChannelsFromResponse(data));
      setDeliveryDestination(data.destination || "");
      setEmailFallback(!!data.fallback);
      setResent(true);
      setCountdown(30);
      setOtp(emptyOtpDigits());
      refs[0].current?.focus();
      if (!data.fallback) setSendErr("");
    } catch (err) {
      setSendErr(err.message || "Resend failed");
    }
  };

  const purposeText = {
    signup: { title: "Verify your email", sub: "Enter the PendPay code sent to" },
    recover: { title: "Account recovery", sub: "Enter the code sent to" },
    signin_challenge: { title: "Verify it's you", sub: "Enter the code sent to" },
  }[otpPurpose] || { title: "Verification", sub: "Enter the code sent to" };

  const displayPhone = isCompleteNgPhone(phoneDigits)
    ? displayPhoneLocal(phoneDigits)
    : phoneApi || "your number";

  const deliveryCopy = describeOtpDelivery({
    purpose: otpPurpose,
    channels: deliveryChannels,
    email,
    destination: deliveryDestination,
    phoneDisplay: displayPhone,
  });
  const deliveryTitle = deliveryCopy.title || purposeText.title;
  const deliveryLead = deliveryCopy.lead || purposeText.sub;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "60px 24px 40px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", textAlign: "left", marginBottom: 32, display: "flex", alignItems: "center", gap: 6 }}>
        ‹ Back
      </button>
      <div style={{ width: 56, height: 56, borderRadius: 18, background: C.lime + "18", border: `1px solid ${C.lime}30`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        {sv("M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z", 24, C.lime, 1.5)}
      </div>
      <p style={{ color: C.text, fontSize: 22, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6 }}>{deliveryTitle}</p>
      <p style={{ color: C.muted, fontSize: 13, marginBottom: 32, lineHeight: 1.6 }}>
        {deliveryLead}{" "}
        <span style={{ color: C.textMid, fontWeight: 700 }}>{deliveryCopy.hint}</span>
      </p>

      {emailFallback && (
        <p style={{ color: C.gold, fontSize: 12, marginBottom: 16 }}>
          SMS unavailable — we sent your code by email instead.
        </p>
      )}

      {(sendErr || status === "send_error") && (
        <p style={{ color: C.danger, fontSize: 12, marginBottom: 16 }}>{sendErr || "Could not send code"}</p>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 20 }}>
        {otp.map((d, i) => (
          <input
            key={i}
            ref={refs[i]}
            value={d}
            onChange={(e) => handleKey(i, e.target.value)}
            onKeyDown={(e) => handleBackspace(i, e)}
            maxLength={1}
            inputMode="numeric"
            style={{
              width: 44,
              height: 54,
              borderRadius: 12,
              textAlign: "center",
              background: C.card,
              border: `2px solid ${status === "error" ? C.danger : d ? C.lime + "60" : C.border}`,
              color: status === "error" ? C.danger : C.text,
              fontSize: 22,
              fontWeight: 900,
              outline: "none",
              transition: "border-color 0.15s",
            }}
          />
        ))}
      </div>

      {status === "verifying" && (
        <p style={{ color: C.muted, fontSize: 13, textAlign: "center", marginBottom: 16 }}>Verifying···</p>
      )}
      {status === "error" && (
        <p style={{ color: C.danger, fontSize: 13, fontWeight: 700, textAlign: "center", marginBottom: 16 }}>Incorrect code — try again</p>
      )}

      <button
        onClick={resend}
        disabled={countdown > 0}
        style={{
          background: "none",
          border: "none",
          cursor: countdown > 0 ? "default" : "pointer",
          color: countdown > 0 ? C.muted : C.lime,
          fontSize: 13,
          fontWeight: 700,
          textAlign: "center",
          marginBottom: 32,
        }}
      >
        {countdown > 0 ? `Resend code in ${countdown}s` : resent ? "Resend code again" : "Resend code"}
      </button>
    </div>
  );
}

function SignInScreen({ onDone, onSignUp }) {
  const C = useColors();
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [pinStep, setPinStep] = useState(false);
  const [error, setError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [recoverStepToken, setRecoverStepToken] = useState("");
  const [recoverNewPin, setRecoverNewPin] = useState("");
  const [recoverConfirmPin, setRecoverConfirmPin] = useState("");
  const [recoverPinMsg, setRecoverPinMsg] = useState("");

  const PinDots = ({ value }) => (
    <div style={{ display: "flex", gap: 16, justifyContent: "center", margin: "28px 0 8px" }}>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div key={i} style={{
          width: 18, height: 18, borderRadius: "50%",
          background: i < value.length ? C.lime : "none",
          border: `2px solid ${i < value.length ? C.lime : C.border}`,
          transition: "all 0.15s",
        }} />
      ))}
    </div>
  );

  const PinPad = ({ onPress }) => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
      {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "DEL"].map((k, i) => (
        <button key={i} onClick={() => k && onPress(k)} style={{
          padding: "18px", borderRadius: 14, border: `1px solid ${k ? "" : "transparent"}`,
          background: k ? C.card : "none",
          color: k === "DEL" ? C.muted : C.text,
          fontSize: k === "DEL" ? 13 : 22, fontWeight: 700,
          cursor: k ? "pointer" : "default",
        }}>{k}</button>
      ))}
    </div>
  );

  const handlePin = async (k) => {
    if (signingIn) return;
    if (k === "DEL") {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (pin.length >= 6) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 6) {
      setSigningIn(true);
      setError("");
      try {
        let fcmToken = null;
        try { fcmToken = await registerPushToken(); } catch { /* optional */ }
        await signin(formatPhoneForApi(phone), next, fcmToken);
        onDone();
      } catch (err) {
        setError(err.message || "Incorrect PIN or sign-in failed.");
        setPin("");
      } finally {
        setSigningIn(false);
      }
    }
  };

  if (pinStep === "recover") return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", padding: "60px 24px 40px" }}>
      <button onClick={() => setPinStep(false)} style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", textAlign: "left", marginBottom: 32 }}>‹ Back to sign in</button>
      <p style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Account recovery</p>
      <p style={{ color: C.muted, fontSize: 13, marginBottom: 28, lineHeight: 1.6 }}>We'll send a 6-digit code to your registered phone number. If SMS fails, we'll send it to your email instead.</p>
      <div style={{ display: "flex", alignItems: "center", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", marginBottom: 16, gap: 8 }}>
        <span style={{ color: C.muted, fontSize: 14, fontWeight: 700 }}>+234</span>
        <input value={phone} onChange={e => setPhone(sanitizeNgLocalPhoneInput(e.target.value))} placeholder="8012345678" maxLength={11}
          style={{ flex: 1, background: "none", border: "none", color: C.text, fontSize: 16, outline: "none" }} />
      </div>
      <button onClick={() => {
        setError("");
        setRecoverStepToken("");
        setPinStep("recover_otp");
      }} disabled={!isCompleteNgPhone(phone)} style={{
        width: "100%", padding: "16px", borderRadius: 14, border: "none",
        background: isCompleteNgPhone(phone) ? C.lime : C.card,
        color: isCompleteNgPhone(phone) ? C.bg : C.muted,
        fontSize: 14, fontWeight: 900, cursor: "pointer", marginBottom: 16,
      }}>Send recovery code</button>
      {error && <p style={{ color: C.danger, fontSize: 12, marginBottom: 12 }}>{error}</p>}
      <div style={{ background: C.gold + "12", border: `1px solid ${C.gold}30`, borderRadius: 12, padding: "12px 14px" }}>
        <p style={{ color: C.gold, fontSize: 11, fontWeight: 800, margin: "0 0 4px" }}>No phone access?</p>
        <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6, margin: 0 }}>Email support@pendpay.ng with your BVN and a government-issued ID. Recovery takes 2–5 business days.</p>
      </div>
    </div>
  );

  if (pinStep === "recover_otp") return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <OTPScreen
        phoneDigits={phone}
        otpPurpose="recover"
        onVerified={(token) => {
          setRecoverStepToken(token);
          setRecoverNewPin("");
          setRecoverConfirmPin("");
          setRecoverPinMsg("");
          setPinStep("recover_newpin");
        }}
        onBack={() => setPinStep("recover")}
      />
    </div>
  );

  if (pinStep === "recover_newpin") return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", padding: "80px 24px 40px" }}>
      <p style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: 4 }}>Set new PIN</p>
      <p style={{ color: C.muted, fontSize: 13, marginBottom: 4 }}>Choose a new 6-digit PIN</p>
      <div style={{ display: "flex", gap: 14, justifyContent: "center", margin: "20px 0" }}>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", background: i < recoverNewPin.length ? C.lime : "none", border: `2px solid ${i < recoverNewPin.length ? C.lime : C.border}`, transition: "all 0.15s" }} />
        ))}
      </div>
      {recoverPinMsg && <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{recoverPinMsg}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, width: "100%", maxWidth: 280 }}>
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "DEL"].map((k, i) => (
          <button key={i} onClick={() => {
            if (!k) return;
            if (k === "DEL") { setRecoverNewPin(p => p.slice(0, -1)); return; }
            if (recoverNewPin.length >= 6) return;
            const n = recoverNewPin + k;
            setRecoverNewPin(n);
            if (n.length === 6) { setPinStep("recover_confirm"); setRecoverConfirmPin(""); }
          }} style={{ padding: "16px", borderRadius: 12, border: "none", background: k ? C.card : "none", color: k === "DEL" ? C.muted : C.text, fontSize: k === "DEL" ? 12 : 20, fontWeight: 700, cursor: k ? "pointer" : "default" }}>{k}</button>
        ))}
      </div>
    </div>
  );

  if (pinStep === "recover_confirm") return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", padding: "80px 24px 40px" }}>
      <p style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: 4 }}>Confirm new PIN</p>
      <p style={{ color: C.muted, fontSize: 13, marginBottom: 4 }}>Enter the same PIN again</p>
      <div style={{ display: "flex", gap: 14, justifyContent: "center", margin: "20px 0" }}>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", background: i < recoverConfirmPin.length ? C.lime : "none", border: `2px solid ${i < recoverConfirmPin.length ? C.lime : C.border}`, transition: "all 0.15s" }} />
        ))}
      </div>
      {recoverPinMsg && <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{recoverPinMsg}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, width: "100%", maxWidth: 280 }}>
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "DEL"].map((k, i) => (
          <button key={i} onClick={async () => {
            if (!k) return;
            if (k === "DEL") { setRecoverConfirmPin(p => p.slice(0, -1)); return; }
            if (recoverConfirmPin.length >= 6) return;
            const n = recoverConfirmPin + k;
            setRecoverConfirmPin(n);
            if (n.length === 6) {
              if (n !== recoverNewPin) {
                setRecoverPinMsg("PINs don't match — try again");
                setRecoverConfirmPin("");
                setPinStep("recover_newpin");
                setRecoverNewPin("");
                return;
              }
              setRecoverPinMsg("");
              try {
                await recoverPin(formatPhoneForApi(phone), n, recoverStepToken);
                onDone();
              } catch (err) {
                setRecoverPinMsg(err.message || "Could not reset PIN");
              }
            }
          }} style={{ padding: "16px", borderRadius: 12, border: "none", background: k ? C.card : "none", color: k === "DEL" ? C.muted : C.text, fontSize: k === "DEL" ? 12 : 20, fontWeight: 700, cursor: k ? "pointer" : "default" }}>{k}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ padding: "64px 24px 0", display: "flex", justifyContent: "center", marginBottom: 40 }}>
        <LogoFull size={80} />
      </div>
      {!pinStep ? (
        <div style={{ flex: 1, padding: "0 24px" }}>
          <p style={{ color: C.text, fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6 }}>Welcome back</p>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 28 }}>Enter your phone number to continue.</p>
          <div style={{ position: "relative", marginBottom: 20 }}>
            <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 15, fontWeight: 700 }}>+234</span>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(sanitizeNgLocalPhoneInput(e.target.value))}
              placeholder="8012345678"
              maxLength={11}
              style={{ width: "100%", padding: "15px 16px 15px 64px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 15, outline: "none", boxSizing: "border-box" }}
            />
          </div>
          <button onClick={() => isCompleteNgPhone(phone) && setPinStep(true)} disabled={!isCompleteNgPhone(phone)} style={{
            width: "100%", padding: "17px", borderRadius: 16, border: "none",
            background: isCompleteNgPhone(phone) ? C.lime : C.card,
            color: isCompleteNgPhone(phone) ? C.bg : C.muted,
            fontSize: 15, fontWeight: 900, cursor: isCompleteNgPhone(phone) ? "pointer" : "default", marginBottom: 16,
          }}>Continue</button>
        </div>
      ) : (
        <div style={{ flex: 1, padding: "0 24px", display: "flex", flexDirection: "column" }}>
          <button onClick={() => { setPinStep(false); setPin(""); setError(""); }} style={{ background: "none", border: "none", cursor: "pointer", alignSelf: "flex-start", marginBottom: 16 }}>
            {sv(IC.chevron_l, 22, C.textMid)}
          </button>
          <p style={{ color: C.text, fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 4 }}>Enter your PIN</p>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 4 }}>{formatPhoneForApi(phone)}</p>
          <PinDots value={pin} />
          {error && <p style={{ color: C.danger, fontSize: 13, textAlign: "center", marginBottom: 8 }}>{error}</p>}
          <div style={{ marginTop: 8 }}><PinPad onPress={handlePin} /></div>
          <p style={{ color: C.muted, fontSize: 12, textAlign: "center", marginTop: 16 }}>{signingIn ? "Signing in…" : "Use your PendPay PIN"}</p>
        </div>
      )}
      <div style={{ padding: "16px 24px 40px", textAlign: "center" }}>
        {pinStep && (
          <button onClick={() => setPinStep("recover")} style={{ background: "none", border: "none", color: C.lime, fontSize: 12, fontWeight: 700, cursor: "pointer", marginBottom: 12, display: "block", width: "100%" }}>
            Forgot PIN / lost access?
          </button>
        )}
        <p style={{ color: C.muted, fontSize: 13 }}>Don't have an account?{" "}
          <button onClick={onSignUp} style={{ background: "none", border: "none", color: C.lime, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>Sign up</button>
        </p>
      </div>
    </div>
  );
}

// ── Auth landing screen ──────────────────────────────────────────
function AuthScreen({ onSignIn, onSignUp, onBrowseEvents }) {
  const C = useColors();
  return (
    <div style={{
      background: C.bg, minHeight: "100vh",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "48px 28px", fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ marginBottom: 48 }}><LogoFull size={80} /></div>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ color: C.text, fontSize: 28, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 10 }}>Building Trust into Transfers</p>
          <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7 }}>Escrow-backed deals for Nigerian freelancers, vendors, and businesses.</p>
        </div>
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 14 }}>
          <button onClick={onSignUp} style={{
            width: "100%", padding: "18px", background: C.lime, border: "none",
            borderRadius: 18, color: C.bg, fontSize: 16, fontWeight: 900, cursor: "pointer",
          }}>Create account</button>
          <button onClick={onSignIn} style={{
            width: "100%", padding: "18px", background: "none",
            border: `1px solid ${C.border}`,
            borderRadius: 18, color: C.text, fontSize: 16, fontWeight: 700, cursor: "pointer",
          }}>Sign in</button>
          {onBrowseEvents && (
            <button type="button" onClick={onBrowseEvents} style={{
              width: "100%", padding: "18px", background: "none",
              border: `1px solid ${C.border}`,
              borderRadius: 18, color: C.text, fontSize: 16, fontWeight: 700, cursor: "pointer",
            }}>Browse Events</button>
          )}
        </div>
      </div>
    </div>
  );
}


function Onboarding({ onDone, onSignIn, inviteContext = null }) {
  const C = useColors();
  // phase: "feature" = feature slides, "signup" = registration flow
  const [phase, setPhase] = useState(inviteContext ? "signup" : "feature");
  const [featureStep, setFeatureStep] = useState(0);

  // Sign-up state
  const [signupStep, setSignupStep] = useState(0);
  const [accountType, setAccountType] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [bizName, setBizName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [pinStep, setPinStep] = useState("create"); // create | confirm
  const [pinError, setPinError] = useState("");
  const [tag, setTag] = useState("");
  const [tagStatus, setTagStatus] = useState(null); // null | checking | available | taken
  const tagTimerRef = useRef(null);
  const [signupOtpToken, setSignupOtpToken] = useState("");
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupSubmitError, setSignupSubmitError] = useState("");
  const [contactChecking, setContactChecking] = useState(false);
  const [contactError, setContactError] = useState("");

  useEffect(() => {
    const suggested = inviteContext?.suggestedTag?.toLowerCase().replace(/^#/, "").replace(/[^a-z0-9]/g, "").slice(0, 20);
    if (suggested && suggested.length >= 3) {
      setTag(suggested);
      setTagStatus("checking");
      checkTagAvailability(suggested)
        .then(({ available }) => setTagStatus(available ? "available" : "taken"))
        .catch(() => setTagStatus(null));
    }
  }, [inviteContext?.suggestedTag]);

  // Suggest a tag from name
  const suggestTag = (first, last, biz) => {
    const base = (biz || `${first}${last}`).toLowerCase().replace(/[^a-z0-9]/g, "");
    return base.slice(0, 20);
  };

  const checkTag = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 20);
    setTag(clean);
    if (clean.length < 3) { setTagStatus(null); return; }
    setTagStatus("checking");
    if (tagTimerRef.current) clearTimeout(tagTimerRef.current);
    tagTimerRef.current = setTimeout(async () => {
      try {
        const { available } = await checkTagAvailability(clean);
        setTagStatus(available ? "available" : "taken");
      } catch {
        setTagStatus(null);
      }
    }, 450);
  };

  const proceedFromContact = async () => {
    if (contactChecking) return;
    setContactChecking(true);
    setContactError("");
    try {
      const { available, phoneTaken, emailTaken } = await checkSignupAvailability(
        formatPhoneForApi(phone),
        email.trim().toLowerCase()
      );
      if (!available) {
        if (phoneTaken && emailTaken) {
          setContactError("This phone or email are already registered. Sign in instead.");
        } else if (phoneTaken) {
          setContactError("This phone number is already registered. Sign in instead.");
        } else if (emailTaken) {
          setContactError("This email is already registered. Sign in instead.");
        }
        return;
      }
      setSignupStep("signup_otp");
    } catch (err) {
      setContactError(err.message || "Could not verify contact details");
    } finally {
      setContactChecking(false);
    }
  };

  const slides = [
    { icon: IC.lock, title: "Safe deals,\nevery time", sub: "Funds held via licensed banking partner. No more payment disputes or 419.", color: C.lime },
    { icon: IC.bank, title: "Works with\nyour bank", sub: "Every major Nigerian bank connected via Open Banking — GTBank, Kuda, OPay, Moniepoint and more.", color: C.gold },
    { icon: IC.shield, title: "Milestone\npayments", sub: "Break jobs into stages. Pay per milestone. Dispute and freeze funds before anything goes wrong.", color: C.info },
  ];
  const fs = slides[featureStep];

  // SuInput and SuWrap are defined outside this component to prevent keyboard dismissal

  // ── FEATURE SLIDES ──────────────────────────────────────────
  if (phase === "feature") return (
    <div style={{
      background: C.bg, minHeight: "100vh",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "48px 28px", fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{ marginBottom: 48 }}><LogoFull size={80} /></div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center", width: "100%" }}>
        <WavyCard style={{ marginBottom: 36, paddingBottom: 48 }}>
          <div style={{ width: 72, height: 72, borderRadius: 22, background: C.forest, border: `1px solid ${fs.color}40`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            {sv(fs.icon, 32, fs.color, 1.5)}
          </div>
          <p style={{ color: C.text, fontSize: 30, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.15, marginBottom: 14, whiteSpace: "pre-line" }}>{fs.title}</p>
          <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, maxWidth: 280, margin: "0 auto" }}>{fs.sub}</p>
        </WavyCard>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
        {slides.map((_, i) => (
          <div key={i} style={{ width: i === featureStep ? 28 : 8, height: 6, borderRadius: 3, background: i === featureStep ? fs.color : C.border, transition: "all 0.3s" }} />
        ))}
      </div>
      <button onClick={() => featureStep < slides.length - 1 ? setFeatureStep(s => s + 1) : setPhase("signup")} style={{
        width: "100%", padding: "18px", background: fs.color, border: "none",
        borderRadius: 18, color: C.bg, fontSize: 16, fontWeight: 900, cursor: "pointer", marginBottom: 14,
      }}>{featureStep < slides.length - 1 ? "Next" : "Create Account"}</button>
      {onSignIn && (
        <p style={{ color: C.muted, fontSize: 13, textAlign: "center" }}>Already have an account?{" "}
          <button onClick={onSignIn} style={{ background: "none", border: "none", color: C.lime, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>Sign in</button>
        </p>
      )}
    </div>
  );

  // ── SIGN-UP STEP 0: Account type ────────────────────────────
  if (signupStep === 0) return (
    <SuWrap
      title="Who are you?"
      sub="This helps us tailor your KYC and contract templates."
      step={1}
      total={6}
      canNext={!!accountType}
      onNext={() => setSignupStep(1)}
      footer={onSignIn ? (
        <p style={{ color: C.muted, fontSize: 13, textAlign: "center", marginTop: 16 }}>Already have an account?{" "}
          <button onClick={onSignIn} style={{ background: "none", border: "none", color: C.lime, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>Sign in</button>
        </p>
      ) : null}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
        {[
          { type: "individual", icon: IC.user, label: "Individual", sub: "Freelancer, contractor, or personal use" },
          { type: "business", icon: IC.bank, label: "Business", sub: "Company, agency, or registered vendor" },
        ].map(opt => (
          <button key={opt.type} onClick={() => setAccountType(opt.type)} style={{
            width: "100%", padding: "20px", borderRadius: 18, cursor: "pointer", textAlign: "left",
            background: accountType === opt.type ? C.lime + "15" : C.card,
            border: `2px solid ${accountType === opt.type ? C.lime : C.border}`,
            display: "flex", alignItems: "center", gap: 16,
          }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: C.forest, border: `1px solid ${C.lime}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {sv(opt.icon, 22, accountType === opt.type ? C.lime : C.muted, 1.5)}
            </div>
            <div>
              <p style={{ color: C.text, fontSize: 16, fontWeight: 900, marginBottom: 3 }}>{opt.label}</p>
              <p style={{ color: C.muted, fontSize: 12 }}>{opt.sub}</p>
            </div>
            {accountType === opt.type && <div style={{ marginLeft: "auto" }}>{sv(IC.check, 18, C.lime, 2.5)}</div>}
          </button>
        ))}
      </div>
    </SuWrap>
  );

  // ── SIGN-UP STEP 1: Name ─────────────────────────────────────
  if (signupStep === 1) return (
    <SuWrap
      title={accountType === "business" ? "Business details" : "Your name"}
      sub={accountType === "business" ? "Enter your registered business name and your full legal name." : "Enter your legal name as it appears on your ID."}
      step={2} total={6}
      onBack={() => setSignupStep(0)}
      canNext={firstName.trim().length > 1 && lastName.trim().length > 1 && (accountType !== "business" || bizName.trim().length > 1)}
      onNext={() => { setTag(suggestTag(firstName, lastName, bizName)); setTagStatus(null); setSignupStep("tag"); }}
    >
      {accountType === "business" && <SuInput val={bizName} set={setBizName} placeholder="Business / company name" />}
      <SuInput val={firstName} set={setFirstName} placeholder={accountType === "business" ? "Your first name" : "First name"} />
      <SuInput val={lastName} set={setLastName} placeholder={accountType === "business" ? "Your last name" : "Last name"} />
    </SuWrap>
  );

  // ── SIGN-UP STEP TAG: Choose your #tag ───────────────────────
  if (signupStep === "tag") return (
    <SuWrap
      title="Choose your #tag"
      sub={null}
      step={3} total={6}
      onBack={() => setSignupStep(1)}
      canNext={tagStatus === "available" && tag.length >= 3}
      onNext={() => setSignupStep(2)}
    >
      <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>
        Your tag is how people find and create contract with you on PendPay — instead of entering bank details.
      </p>

      {/* Tag input */}
      <div style={{
        display: "flex", alignItems: "center",
        background: C.card, border: `2px solid ${tagStatus === "available" ? C.lime :
            tagStatus === "taken" ? C.danger :
              tagStatus === "checking" ? C.gold :
                C.border
          }`,
        borderRadius: 14, padding: "0 16px", marginBottom: 10,
        transition: "border-color 0.2s",
      }}>
        <span style={{ color: C.lime, fontSize: 22, fontWeight: 900, userSelect: "none", paddingRight: 4 }}>#</span>
        <input
          value={tag}
          onChange={e => checkTag(e.target.value)}
          placeholder="yourtag"
          maxLength={20}
          autoCapitalize="none"
          autoCorrect="off"
          style={{
            flex: 1, padding: "15px 0", background: "none", border: "none",
            color: C.text, fontSize: 20, fontWeight: 800, outline: "none",
            letterSpacing: "0.01em",
          }}
        />
        {tagStatus === "checking" && <span style={{ color: C.gold, fontSize: 12 }}>···</span>}
        {tagStatus === "available" && <span style={{ color: C.lime, fontSize: 18 }}>✓</span>}
        {tagStatus === "taken" && <span style={{ color: C.danger, fontSize: 18 }}>✕</span>}
      </div>

      {/* Status message */}
      {tagStatus === "available" && (
        <p style={{ color: C.lime, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
          ✓ #{tag} is available
        </p>
      )}
      {tagStatus === "taken" && (
        <p style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
          ✕ #{tag} is not available — try another
        </p>
      )}
      {tagStatus === "checking" && (
        <p style={{ color: C.gold, fontSize: 12, marginBottom: 12 }}>Checking availability...</p>
      )}
      {!tagStatus && tag.length > 0 && tag.length < 3 && (
        <p style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>Minimum 3 characters</p>
      )}
      {!tagStatus && tag.length === 0 && (
        <p style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>Letters and numbers only, no spaces</p>
      )}

      {/* Rules */}
      <div style={{ background: C.forest, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
        <p style={{ color: C.gold, fontSize: 11, fontWeight: 800, marginBottom: 6 }}>⚠ This cannot be changed later — choose carefully</p>
        <p style={{ color: C.muted, fontSize: 11, lineHeight: 1.6 }}>
          3–20 characters · lowercase letters and numbers only · no spaces or symbols
        </p>
      </div>

      {/* Suggestions based on name */}
      {(firstName || bizName) && (
        <div>
          <p style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8 }}>SUGGESTIONS</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {[
              suggestTag(firstName, lastName, bizName),
              firstName.toLowerCase().replace(/[^a-z0-9]/g, ""),
              `${firstName.toLowerCase().replace(/[^a-z0-9]/g, "")}${(bizName || lastName).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 6)}`,
            ].filter((s, i, arr) => s.length >= 3 && arr.indexOf(s) === i).slice(0, 3).map(s => (
              <button key={s} onClick={() => checkTag(s)} style={{
                padding: "7px 14px", borderRadius: 20,
                background: tag === s ? C.lime + "20" : C.card,
                border: `1px solid ${tag === s ? C.lime + "60" : C.border}`,
                color: tag === s ? C.lime : C.textMid,
                fontSize: 13, fontWeight: 700, cursor: "pointer",
              }}>#{s}</button>
            ))}
          </div>
        </div>
      )}
    </SuWrap>
  );

  // ── SIGN-UP: Phone OTP (PendPay SMS) ──────────────────────────
  if (signupStep === "signup_otp") {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ padding: "24px 24px 0" }}>
          <button
            type="button"
            onClick={() => { setSignupOtpToken(""); setSignupStep(2); }}
            style={{ background: "none", border: "none", color: C.muted, fontSize: 13, cursor: "pointer", marginBottom: 8 }}
          >
            ‹ Back
          </button>
        </div>
        <OTPScreen
          phoneDigits={phone}
          email={email}
          otpPurpose="signup"
          onVerified={(token) => {
            setSignupOtpToken(token);
            setSignupStep(4);
          }}
          onBack={() => { setSignupOtpToken(""); setSignupStep(2); }}
        />
      </div>
    );
  }

  // ── SIGN-UP STEP 2: Contact ──────────────────────────────────
  if (signupStep === 2) return (
    <SuWrap
      title="Contact details"
      sub="We'll send a verification code to your email. Your phone is used for deal alerts."
      step={4} total={6}
      onBack={() => { setContactError(""); setSignupStep("tag"); }}
      canNext={email.includes("@") && isCompleteNgPhone(phone)}
      onNext={proceedFromContact}
      loading={contactChecking}
      nextError={contactError}
    >
      <SuInput val={email} set={(v) => { setEmail(v); setContactError(""); }} placeholder="Email address" type="email" />
      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 15, fontWeight: 700 }}>+234</span>
        <input
          type="tel"
          value={phone}
          onChange={e => { setPhone(sanitizeNgLocalPhoneInput(e.target.value)); setContactError(""); }}
          placeholder="8012345678"
          maxLength={11}
          style={{ width: "100%", padding: "15px 16px 15px 64px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 15, outline: "none", boxSizing: "border-box" }}
        />
      </div>
    </SuWrap>
  );

  // ── SIGN-UP STEP 4: Create PIN ───────────────────────────────
  if (signupStep === 4) {
    const PinDots = ({ value }) => (
      <div style={{ display: "flex", gap: 16, justifyContent: "center", margin: "24px 0" }}>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{
            width: 18, height: 18, borderRadius: "50%",
            background: i < value.length ? C.lime : "none",
            border: `2px solid ${i < value.length ? C.lime : C.border}`,
            transition: "all 0.15s",
          }} />
        ))}
      </div>
    );
    const PinPad = ({ onPress }) => (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 8 }}>
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "DEL"].map((k, i) => (
          <button key={i} onClick={() => k && onPress(k)} style={{
            padding: "18px", borderRadius: 14, border: `1px solid ${C.border}`,
            background: k ? C.card : "none",
            color: k === "DEL" ? C.muted : C.text,
            fontSize: k === "DEL" ? 13 : 22, fontWeight: 700,
            cursor: k ? "pointer" : "default",
          }}>{k}</button>
        ))}
      </div>
    );
    const handlePin = async (k) => {
      if (signupSubmitting) return;
      if (pinStep === "create") {
        if (k === "DEL") { setPin(p => p.slice(0, -1)); return; }
        if (pin.length >= 6) return;
        const next = pin + k;
        setPin(next);
        if (next.length === 6) { setPinStep("confirm"); setPinError(""); }
      } else {
        if (k === "DEL") { setPinConfirm(p => p.slice(0, -1)); return; }
        if (pinConfirm.length >= 6) return;
        const next = pinConfirm + k;
        setPinConfirm(next);
        if (next.length === 6) {
          if (next !== pin) {
            setPinError("PINs don't match. Try again.");
            setPinConfirm("");
            setPinStep("create");
            setPin("");
            return;
          }
          if (!signupOtpToken) {
            setPinError("Verification expired. Go back and request a new code.");
            return;
          }
          setSignupSubmitting(true);
          setSignupSubmitError("");
          try {
            const payload = {
              phone: formatPhoneForApi(phone),
              email,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              businessName: accountType === "business" ? bizName.trim() : undefined,
              accountType,
              tag,
              pin: next,
              stepToken: signupOtpToken,
            };
            const storedInvite = localStorage.getItem("pp_invite_token");
            if (storedInvite) payload.inviteToken = storedInvite;
            await signup(payload);
            if (storedInvite) localStorage.removeItem("pp_invite_token");
            setSignupStep(5);
          } catch (err) {
            setSignupSubmitError(err.message || "Could not create account");
            setPinConfirm("");
            setPinStep("create");
            setPin("");
          } finally {
            setSignupSubmitting(false);
          }
        }
      }
    };
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ padding: "56px 20px 0", display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <button onClick={() => setSignupStep("signup_otp")} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>{sv(IC.chevron_l, 22, C.textMid)}</button>
          <div style={{ flex: 1, height: 3, background: C.card, borderRadius: 2 }}>
            <div style={{ height: "100%", width: "100%", background: C.lime, borderRadius: 2 }} />
          </div>
          <span style={{ color: C.muted, fontSize: 11, fontWeight: 700 }}>6/6</span>
        </div>
        <div style={{ padding: "24px 24px 0", textAlign: "center" }}>
          <p style={{ color: C.text, fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6 }}>
            {pinStep === "create" ? "Create your PIN" : "Confirm your PIN"}
          </p>
          <p style={{ color: C.muted, fontSize: 13 }}>{pinStep === "create" ? "Choose a 6-digit PIN to secure your account." : "Enter the same PIN again to confirm."}</p>
          <PinDots value={pinStep === "create" ? pin : pinConfirm} />
          {pinError && <p style={{ color: C.danger, fontSize: 13, marginBottom: 8 }}>{pinError}</p>}
        </div>
        {signupSubmitError && (
          <p style={{ color: C.danger, fontSize: 13, textAlign: "center", padding: "0 24px" }}>{signupSubmitError}</p>
        )}
        <div style={{ padding: "0 24px" }}><PinPad onPress={handlePin} /></div>
        <div style={{ padding: "16px 24px 40px" }}>
          <p style={{ color: C.muted, fontSize: 12, textAlign: "center" }}>{signupSubmitting ? "Creating your account…" : ""}</p>
        </div>
      </div>
    );
  }

  // ── SIGN-UP STEP 5: All done ─────────────────────────────────
  return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 28px", fontFamily: "'Segoe UI', system-ui, sans-serif", textAlign: "center" }}>
      <div style={{ width: 88, height: 88, borderRadius: "50%", background: C.lime + "22", border: `2px solid ${C.lime}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24 }}>
        {sv(IC.check, 40, C.lime, 2.5)}
      </div>
      <p style={{ color: C.text, fontSize: 28, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 8 }}>You're all set!</p>
      <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, marginBottom: 12 }}>
        Welcome, {firstName} {lastName}{accountType === "business" ? ` · ${bizName}` : ""}.<br />
        Your account is ready. Set up your bank account in Profile when you need to fund or withdraw.
      </p>
      <div style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: "18px 20px", marginBottom: 32, textAlign: "left" }}>
        {[
          ["Account type", accountType === "business" ? "Business" : "Individual"],
          ...(accountType === "business"
            ? [["Business", bizName], ["Contact name", `${firstName} ${lastName}`]]
            : [["Name", `${firstName} ${lastName}`]]),
          ["PendPay tag", `#${tag || "not set"}`],
          ["Email", email],
          ["Phone", formatPhoneForApi(phone)],
          ["Bank account", "Set up in Profile"],
          ["PIN", "Set ✓"],
        ].map(([l, v], i, a) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i < a.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <span style={{ color: C.muted, fontSize: 13 }}>{l}</span>
            <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{v}</span>
          </div>
        ))}
      </div>
      <button onClick={onDone} style={{ width: "100%", padding: "18px", background: C.lime, border: "none", borderRadius: 18, color: C.bg, fontSize: 16, fontWeight: 900, cursor: "pointer" }}>
        Go to PendPay
      </button>
    </div>
  );
}

function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState(0);
  // phase 0: black hold, 1: fade in logo, 2: hold, 3: fade out

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 400);
    const t2 = setTimeout(() => setPhase(2), 1800);
    const t3 = setTimeout(() => setPhase(3), 3800);
    const t4 = setTimeout(() => onDone(), 4800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, []);

  const logoOpacity = phase === 0 ? 0 : phase === 1 ? 1 : phase === 2 ? 1 : 0;
  const logoTransition = phase === 1 ? "opacity 1.2s ease-in" : phase === 3 ? "opacity 1.0s ease-out" : "none";

  return (
    <div style={{
      position: "fixed", inset: 0, ...mobileShell,
      background: "#000",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      zIndex: 999,
    }}>
      <div style={{
        opacity: logoOpacity,
        transition: logoTransition,
        display: "flex", flexDirection: "column", alignItems: "center",
      }}>
        <img
          src={FAVICON_URL}
          alt="PendPay mark"
          style={{ width: 160, height: 160, objectFit: "contain" }}
        />
        <img
          src={LOGO_URL}
          alt="PendPay"
          style={{ width: 280, height: "auto", objectFit: "contain", marginTop: 24 }}
        />
        <p style={{ color: "#4A7A8A", fontSize: 11, letterSpacing: "0.2em", marginTop: 16, fontFamily: "system-ui", fontWeight: 500 }}>SECURE · SMART · SIMPLE</p>
      </div>
    </div>
  );
}

function formatNotifTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-NG", { month: "short", day: "numeric" });
}

function NotificationCentre({ open, onClose, notifications, loading, onMarkRead, onMarkAllRead, onNavigate }) {
  const C = useColors();
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, ...mobileShell, zIndex: 200,
      background: "#00000088", display: "flex", flexDirection: "column", justifyContent: "flex-end",
    }} onClick={onClose}>
      <div style={{
        background: C.bg, borderRadius: "24px 24px 0 0",
        maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column",
        border: `1px solid ${C.border}`,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 20px 12px", borderBottom: `1px solid ${C.border}`,
        }}>
          <p style={{ color: C.text, fontSize: 17, fontWeight: 900, margin: 0 }}>Notifications</p>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {notifications.some((n) => !n.read) && (
              <button onClick={onMarkAllRead} style={{
                background: "none", border: "none", color: C.lime, fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>Mark all read</button>
            )}
            <button onClick={onClose} style={{
              background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
              width: 32, height: 32, cursor: "pointer", color: C.muted, fontSize: 16,
            }}>×</button>
          </div>
        </div>
        <div style={{ overflowY: "auto", padding: "8px 0 24px" }}>
          {loading && !notifications.length && (
            <p style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 24 }}>Loading…</p>
          )}
          {!loading && !notifications.length && (
            <p style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 32 }}>
              No notifications yet. You'll see deal, wallet, and payment updates here.
            </p>
          )}
          {notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => onNavigate(n)}
              style={{
                width: "100%", textAlign: "left", background: n.read ? "none" : C.limeDim,
                border: "none", borderBottom: `1px solid ${C.border}`,
                padding: "14px 20px", cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                {!n.read && (
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%", background: C.lime,
                    marginTop: 5, flexShrink: 0,
                  }} />
                )}
                <div style={{ flex: 1, marginLeft: n.read ? 18 : 0 }}>
                  <p style={{ color: C.text, fontSize: 13, fontWeight: 700, margin: "0 0 4px" }}>{n.title}</p>
                  <p style={{ color: C.textMid, fontSize: 12, margin: "0 0 6px", lineHeight: 1.45 }}>{n.body}</p>
                  <p style={{ color: C.muted, fontSize: 10, margin: 0 }}>{formatNotifTime(n.created_at || n.createdAt)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function NotificationBanner({ notification, moreCount = 0, onDismiss, onOpen, onViewAll }) {
  const C = useColors();
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!notification) {
      setEntered(false);
      return undefined;
    }
    setEntered(false);
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [notification?.id]);

  if (!notification) return null;

  return (
    <div style={{
      position: "fixed",
      top: 14,
      left: "50%",
      width: "calc(100% - 24px)",
      maxWidth: 406,
      zIndex: 420,
      transform: `translateX(-50%) translateY(${entered ? "0" : "-18px"}) scale(${entered ? 1 : 0.97})`,
      opacity: entered ? 1 : 0,
      transition: "opacity 280ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 18,
      boxShadow: "0 18px 45px rgba(0,0,0,0.42)",
      padding: "14px 14px 14px 16px",
      backdropFilter: "blur(10px)",
    }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{
          width: 38,
          height: 38,
          borderRadius: 14,
          background: C.limeDim || C.cardMid,
          color: C.lime,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}>
          {sv(IC.bell, 18, C.lime)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: C.text, fontSize: 14, fontWeight: 800, margin: "0 0 4px" }}>
            {notification.title || "New notification"}
          </p>
          <p style={{
            color: C.textMid,
            fontSize: 12,
            lineHeight: 1.5,
            margin: 0,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {notification.body}
          </p>
          {moreCount > 0 && (
            <button
              onClick={onViewAll}
              style={{
                background: "none",
                border: "none",
                color: C.lime,
                fontSize: 12,
                fontWeight: 700,
                padding: 0,
                marginTop: 8,
                cursor: "pointer",
              }}
            >
              +{moreCount} more unread
            </button>
          )}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
            <button onClick={onOpen} style={{
              border: "none",
              borderRadius: 999,
              padding: "9px 14px",
              background: C.lime,
              color: C.bg,
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
            }}>View</button>
            <button onClick={onDismiss} style={{
              border: `1px solid ${C.border}`,
              borderRadius: 999,
              padding: "9px 14px",
              background: C.card,
              color: C.textMid,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}>Dismiss</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const C = useColors();
  const [splashed, setSplashed] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [authStep, setAuthStep] = useState(() => {
    if (typeof window === "undefined" || getToken()) return "landing";
    const params = new URLSearchParams(window.location.search);
    return params.get("signup") === "1" ? "signup" : "landing";
  });
  const [boarded, setBoarded] = useState(false);
  const [screen, setScreen] = useState("home");
  const [pendingWalletModal, setPendingWalletModal] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [liveBanner, setLiveBanner] = useState(null);
  const [liveBannerMore, setLiveBannerMore] = useState(0);
  const [showTxHistory, setShowTxHistory] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [pendingSendContact, setPendingSendContact] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [pendingDealAction, setPendingDealAction] = useState(null);
  const [pendingEventId, setPendingEventId] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteResolution, setInviteResolution] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteAccepting, setInviteAccepting] = useState(false);
  const [inviteDismissed, setInviteDismissed] = useState(false);
  const [inviteSignupContext, setInviteSignupContext] = useState(null);
  const liveBannerTimerRef = useRef(null);
  const bannerQueueRef = useRef([]);
  const shownBannerIdsRef = useRef(new Set());
  const showNotificationsRef = useRef(false);

  useEffect(() => {
    showNotificationsRef.current = showNotifications;
  }, [showNotifications]);

  const pumpBannerQueue = useCallback(() => {
    if (showNotificationsRef.current) return;
    if (liveBannerTimerRef.current) return;
    const next = bannerQueueRef.current.shift();
    if (!next) {
      setLiveBanner(null);
      setLiveBannerMore(0);
      return;
    }
    setLiveBanner(next.notification);
    setLiveBannerMore(next.moreCount || 0);
    liveBannerTimerRef.current = setTimeout(() => {
      liveBannerTimerRef.current = null;
      setLiveBanner(null);
      setLiveBannerMore(0);
      window.setTimeout(() => pumpBannerQueue(), 220);
    }, 12000);
  }, []);

  const dismissLiveBanner = useCallback(() => {
    if (liveBannerTimerRef.current) {
      clearTimeout(liveBannerTimerRef.current);
      liveBannerTimerRef.current = null;
    }
    setLiveBanner(null);
    setLiveBannerMore(0);
    window.setTimeout(() => pumpBannerQueue(), 220);
  }, [pumpBannerQueue]);

  const enqueueBanner = useCallback((notification, moreCount = 0) => {
    if (!notification?.id || showNotificationsRef.current) return;
    if (shownBannerIdsRef.current.has(notification.id)) return;
    shownBannerIdsRef.current.add(notification.id);
    bannerQueueRef.current.push({ notification, moreCount });
    pumpBannerQueue();
  }, [pumpBannerQueue]);

  const showLiveBanner = useCallback((notification) => {
    enqueueBanner(notification, 0);
  }, [enqueueBanner]);

  const handleInitialNotifications = useCallback((list) => {
    const unread = (list || []).filter((n) => n && !n.read);
    if (!unread.length) return;
    const newest = unread[0];
    enqueueBanner(newest, Math.max(0, unread.length - 1));
  }, [enqueueBanner]);

  const notif = useNotifications({
    enabled: boarded && !!getToken(),
    onNotification: showLiveBanner,
    onInitialLoad: handleInitialNotifications,
  });
  const clearPendingWalletModal = useCallback(() => setPendingWalletModal(null), []);
  const clearPendingDealAction = useCallback(() => setPendingDealAction(null), []);
  const clearPendingSendContact = useCallback(() => setPendingSendContact(null), []);
  const clearPendingEventId = useCallback(() => setPendingEventId(null), []);
  const handleContactSelect = useCallback((contact) => {
    if (!contact?.tag) return;
    setPendingSendContact({ name: contact.name, tag: contact.tag });
    setShowContacts(false);
    setScreen("send");
  }, []);
  const openWalletModal = useCallback((modal) => {
    setPendingWalletModal(modal);
    setScreen("wallet");
  }, []);
  const clearInviteFlow = useCallback(() => {
    setInviteDismissed(true);
    setInviteToken(null);
    setInviteResolution(null);
    localStorage.removeItem("pp_invite_token");
  }, []);

  const handleSignOut = useCallback(() => {
    if (liveBannerTimerRef.current) {
      clearTimeout(liveBannerTimerRef.current);
      liveBannerTimerRef.current = null;
    }
    bannerQueueRef.current = [];
    shownBannerIdsRef.current.clear();
    setLiveBanner(null);
    setLiveBannerMore(0);
    setBoarded(false);
    setAuthStep("landing");
    setScreen("home");
    setPendingWalletModal(null);
    setShowNotifications(false);
    setSelectedDealId(null);
    setPendingDealAction(null);
    setInviteDismissed(false);
  }, []);

  useEffect(() => () => {
    if (liveBannerTimerRef.current) clearTimeout(liveBannerTimerRef.current);
  }, []);

  useEffect(() => {
    if (!showNotifications) return;
    bannerQueueRef.current = [];
    dismissLiveBanner();
  }, [dismissLiveBanner, showNotifications]);

  useEffect(() => {
    if (!boarded || !getToken()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const fcmToken = await registerPushToken();
        if (!cancelled && fcmToken) await updateProfile({ fcmToken });
      } catch {
        /* push optional */
      }
    })();
    let unsub = () => { };
    onForegroundPush((payload) => {
      notif.refresh();
      const data = payload?.data || {};
      const title = payload?.notification?.title || data.title;
      const body = payload?.notification?.body || data.body;
      const id = data.notificationId || data.id;
      if (id && (title || body)) {
        showLiveBanner({
          id,
          title: title || "New notification",
          body: body || "",
          type: data.type,
          dealId: data.dealId || undefined,
          metadata: {
            screen: data.screen || undefined,
            dealId: data.dealId || undefined,
            action: data.action || undefined,
            orderId: data.orderId || undefined,
            eventId: data.eventId || undefined,
            notificationId: id,
          },
          read: false,
        });
      }
    }).then((fn) => { unsub = fn || (() => { }); });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [boarded, notif.refresh, showLiveBanner]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get("invite");
    if (invite) {
      localStorage.setItem("pp_invite_token", invite);
      setInviteToken(invite);
      setInviteDismissed(false);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!inviteToken || inviteDismissed) return undefined;
    let cancelled = false;
    (async () => {
      setInviteLoading(true);
      try {
        const resolved = await resolveInvitationLink(inviteToken);
        if (!cancelled) setInviteResolution(resolved);
      } catch {
        if (!cancelled) {
          setInviteResolution({ status: "not_found", scenario: "not_found" });
        }
      } finally {
        if (!cancelled) setInviteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken, inviteDismissed, boarded, authChecking]);

  const showInviteScreen = inviteToken && !inviteDismissed && !authChecking
    && authStep !== "signin" && authStep !== "signup"
    && (inviteLoading || inviteResolution)
    && !(boarded && inviteResolution?.scenario === "signup");

  const handleInviteSignUp = useCallback(() => {
    if (inviteResolution?.invitation) {
      setInviteSignupContext({
        inviterName: inviteResolution.invitation.inviterName,
        message: inviteResolution.invitation.message,
        suggestedTag: inviteResolution.invitation.suggestedTag,
      });
    }
    setInviteDismissed(true);
    setAuthStep("signup");
  }, [inviteResolution]);

  const handleInviteSignIn = useCallback(() => {
    setAuthStep("signin");
  }, []);

  const handleInviteAccept = useCallback(async () => {
    if (!inviteToken) return;
    setInviteAccepting(true);
    try {
      await acceptInvitationLink(inviteToken);
      clearInviteFlow();
      setScreen("home");
    } catch (e) {
      const resolved = await resolveInvitationLink(inviteToken).catch(() => null);
      if (resolved) setInviteResolution(resolved);
    } finally {
      setInviteAccepting(false);
    }
  }, [inviteToken, clearInviteFlow]);

  const handleInviteSignOut = useCallback(async () => {
    await signout();
    setBoarded(false);
    setAuthStep("landing");
    setInviteDismissed(false);
  }, []);

  useEffect(() => {
    if (!boarded || !getToken()) return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get("wallet_topup") === "1") {
      const reference = params.get("reference") || params.get("trxref");
      if (reference) {
        let cancelled = false;
        (async () => {
          try {
            await confirmWalletTopup(reference);
            if (!cancelled) setScreen("wallet");
          } catch (e) {
            console.error("Wallet topup callback failed", e);
          } finally {
            window.history.replaceState({}, "", window.location.pathname);
          }
        })();
        return () => { cancelled = true; };
      }
    }
    if (params.get("deal_payment") !== "1" && params.get("deal_checkout") !== "1") return undefined;
    const dealId = params.get("dealId");
    const reference = params.get("reference") || params.get("trxref");
    const referenceCode = params.get("referenceCode") || reference;
    if (!dealId || !referenceCode) return undefined;

    let cancelled = false;
    (async () => {
      try {
        if (params.get("deal_checkout") === "1") {
          await confirmDealCheckout(dealId, referenceCode);
        } else {
          await confirmDealPayment(dealId, reference);
        }
        if (!cancelled) {
          setSelectedDealId(dealId);
          setScreen("deals");
        }
      } catch (e) {
        console.error("Deal payment callback failed", e);
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
      }
    })();
    return () => { cancelled = true; };
  }, [boarded]);

  const handleNotificationNavigate = useCallback(async (n) => {
    bannerQueueRef.current = [];
    if (liveBannerTimerRef.current) {
      clearTimeout(liveBannerTimerRef.current);
      liveBannerTimerRef.current = null;
    }
    setLiveBanner(null);
    setLiveBannerMore(0);
    if (!n.read) await notif.markRead(n.id);
    setShowNotifications(false);
    const meta = typeof n.metadata === "object" && n.metadata ? n.metadata : {};
    const dealId = n.deal_id || n.dealId || meta.dealId;
    const targetScreen = meta.screen || (dealId ? "deals" : "wallet");
    if (targetScreen === "deals" && dealId) {
      if (meta.action === "scope_chat" || n.type === "amount_proposal_received" || n.type === "scope_message_received") {
        setPendingDealAction({ dealId, action: "scope_change" });
      }
      setSelectedDealId(dealId);
      setScreen("deals");
    } else if (targetScreen === "events") {
      if (meta.eventId) setPendingEventId(meta.eventId);
      setScreen("events");
    } else {
      setScreen("wallet");
    }
  }, [notif]);

  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setAuthChecking(false);
        return;
      }
      try {
        await getMe();
      } catch {
        /* network blip — still restore if tokens remain */
      }
      if (getToken()) {
        setAuthStep("done");
        setBoarded(true);
      }
      setAuthChecking(false);
    })();
  }, []);
  const SS = { home: HomeScreen, send: SendScreen, deals: DealsScreen, events: EventsScreen, wallet: WalletScreen, profile: ProfileScreen };
  const Active = SS[screen] || HomeScreen;
  const screenProps = {
    setScreen,
    activeScreen: screen,
    onViewAllTransactions: () => setShowTxHistory(true),
    onViewAllContacts: () => setShowContacts(true),
    onContactSelect: handleContactSelect,
    onViewAllEvents: () => setScreen("events"),
    onEventSelect: (ev) => {
      if (ev?.id) setPendingEventId(ev.id);
      setScreen("events");
    },
    pendingSendContact,
    clearPendingSendContact,
    unreadCount: notif.unreadCount,
    onNotificationsClick: () => setShowNotifications(true),
    selectedDealId,
    onDealSelected: () => setSelectedDealId(null),
    pendingDealAction,
    clearPendingDealAction,
    ...(screen === "home" || screen === "send" ? { openWalletModal } : {}),
    ...(screen === "wallet" ? { pendingWalletModal, clearPendingWalletModal } : {}),
    ...(screen === "events" ? { pendingEventId, clearPendingEventId } : {}),
    ...(screen === "profile" ? { onSignOut: handleSignOut } : {}),
  };

  let content;
  if (!splashed) content = <SplashScreen onDone={() => setSplashed(true)} />;
  else if (authChecking) content = <div style={{ background: C.bg, minHeight: "100vh" }} />;
  else if (authStep === "landing") content = (
    <AuthScreen
      onSignIn={() => setAuthStep("signin")}
      onSignUp={() => setAuthStep("signup")}
      onBrowseEvents={() => setAuthStep("guest_events")}
    />
  );
  else if (authStep === "guest_events") content = (
    <div style={{ background: C.bg, minHeight: "100vh", maxWidth: MOBILE_MAX_WIDTH, margin: "0 auto", color: C.text }}>
      <EventsScreen
        guest
        onBack={() => setAuthStep("landing")}
        onRequireAuth={() => setAuthStep("signin")}
        onCreateAccount={() => setAuthStep("signup")}
      />
    </div>
  );
  else if (authStep === "signin") content = <SignInScreen onDone={() => { setAuthStep("done"); setBoarded(true); }} onSignUp={() => setAuthStep("signup")} />;
  else if (!boarded) content = <Onboarding onDone={() => setBoarded(true)} onSignIn={() => setAuthStep("signin")} inviteContext={inviteSignupContext} />;
  else {
    content = (
      <div style={{
        background: C.bg, minHeight: "100vh", maxWidth: MOBILE_MAX_WIDTH, margin: "0 auto",
        fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        color: C.text,
      }}>
        <div style={{ overflowY: "auto", paddingBottom: 100 }}>
          <Active {...screenProps} />
        </div>
        <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: MOBILE_MAX_WIDTH }}>
          <PillNav screen={screen} setScreen={setScreen} />
        </div>
        <NotificationCentre
          open={showNotifications}
          onClose={() => setShowNotifications(false)}
          notifications={notif.notifications}
          loading={notif.loading}
          onMarkRead={notif.markRead}
          onMarkAllRead={notif.markAllRead}
          onNavigate={handleNotificationNavigate}
        />
        <NotificationBanner
          notification={liveBanner}
          moreCount={liveBannerMore}
          onDismiss={dismissLiveBanner}
          onViewAll={() => {
            bannerQueueRef.current = [];
            dismissLiveBanner();
            setShowNotifications(true);
          }}
          onOpen={() => {
            if (liveBanner) handleNotificationNavigate(liveBanner);
          }}
        />
        {showTxHistory && (
          <TransactionsHistoryScreen
            onBack={() => setShowTxHistory(false)}
            unreadCount={notif.unreadCount}
            onNotificationsClick={() => setShowNotifications(true)}
          />
        )}
        {showContacts && (
          <FavouriteContactsScreen
            onBack={() => setShowContacts(false)}
            unreadCount={notif.unreadCount}
            onNotificationsClick={() => setShowNotifications(true)}
            onSelectContact={handleContactSelect}
          />
        )}
      </div>
    );
  }

  return (
    <ToastProvider>
      {content}
      {showInviteScreen && (
        <InviteLinkScreen
          resolution={inviteResolution}
          inviteUrl={inviteResolution?.inviteUrl}
          loading={inviteLoading}
          accepting={inviteAccepting}
          onSignUp={handleInviteSignUp}
          onSignIn={handleInviteSignIn}
          onAccept={handleInviteAccept}
          onContinue={clearInviteFlow}
          onSignOut={handleInviteSignOut}
        />
      )}
    </ToastProvider>
  );
}
